// Edify app source. Bundled to light/app.js by `npm run build` (esbuild).
// React and ReactDOM used to be UMD globals from a CDN with the JSX compiled in
// the browser by @babel/standalone; both are now bundled and compiled at build
// time. The shim keeps the historic `ReactDOM.createPortal` + `ReactDOM.createRoot`
// call sites working unchanged — in ESM those live in two separate entrypoints.
import React from 'react';
import * as ReactDOMLegacy from 'react-dom';
import { createRoot } from 'react-dom/client';
// The product's name, from the one place it is written (brand.json at the repo
// root — docs/BRAND.md). Never type the name into a string in this file.
import BRAND from '../../brand.json';
// The published list of advisors, by the size of company they led (Marco,
// 30 Oct 2026) — shared by A and C as brand.json is. Shown on Ask a CEO.
import ADVISORS from '../advisors.json';
const ADVISOR_COUNT = ADVISORS.tiers.reduce((n, t) => n + t.advisors.length, 0);

// The logo: the Insight mark (chosen 1 Nov 2026 — artwork and the full set of
// logo files in assets/logo/) with the product's name set beside it. The name
// comes from brand.json, so a rename changes the words, not the art. `byline`
// is the sign-in screen's version, as drawn on the logo board: the name in
// Cinzel capitals and, under it, "by" the company's wordmark. One accessible
// name for the whole, so a phone that hides the words still announces the
// product.
const BrandLockup = ({ size, byline = false, style = null }) => (
    <div role="img" aria-label={byline ? `${BRAND.name} by ${BRAND.company}` : BRAND.name} style={{ display: 'flex', alignItems: 'center', gap: byline ? '14px' : '10px', flexShrink: 0, ...style }}>
        <img src="../images/ceo-wisdom-mark.svg" alt="" style={{ height: `${size}px`, width: 'auto', display: 'block' }} />
        {byline ? (
            <div>
                <div style={{ fontFamily: "'Cinzel', var(--font-heading)", fontWeight: 600, fontSize: `${Math.round(size * 0.39)}px`, lineHeight: 1.1, letterSpacing: '0.2em', marginRight: '-0.2em', textTransform: 'uppercase', color: 'var(--navy)' }}>{BRAND.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                    <span style={{ fontSize: '12px', fontStyle: 'italic', color: 'var(--text-secondary)' }}>by</span>
                    <img src="../images/edify-wordmark.png" alt="" style={{ height: '14px', width: 'auto', display: 'block' }} />
                </div>
            </div>
        ) : (
            <span className="ed-brand-name" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: `${Math.round(size * 0.62)}px`, lineHeight: 1, color: 'var(--navy)', whiteSpace: 'nowrap' }}>{BRAND.name}</span>
        )}
    </div>
);

// One recording at a time (Marco, Oct 2026): starting any player pauses every
// other one on the page — the clips under Molly's replies, her quote boxes, a
// case's own audio. Each player's button follows its <audio>'s pause event.
if (typeof document !== 'undefined') {
    document.addEventListener('play', (e) => {
        const started = e.target;
        if (!started || typeof started.pause !== 'function') return;
        document.querySelectorAll('audio, video').forEach(m => { if (m !== started && !m.paused) m.pause(); });
    }, true);
}
// A newer version (Oct 2026). An open page — a phone's Home-screen app above
// all — keeps the code it loaded, so a release is invisible until the page is
// reloaded: Marco asked Molly straight after one and got the old behaviour.
// When the page comes back into view, and every ten minutes while it is open,
// it asks for its own bundle's headers; once they change, a bar offers the new
// version. Nothing reloads by itself, so nobody loses what they are typing.
if (typeof document !== 'undefined' && typeof fetch === 'function') {
    const bundle = (document.currentScript && document.currentScript.src) || new URL('app.js', location.href).href;
    const stamp = (r) => r.headers.get('etag') || r.headers.get('last-modified') || '';
    let loaded = null, offered = false;
    const offer = () => {
        if (offered || !document.body) return;
        offered = true;
        const bar = document.createElement('div');
        bar.setAttribute('role', 'status');
        bar.setAttribute('data-testid', 'update-ready');
        bar.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);top:calc(env(safe-area-inset-top, 0px) + 10px);z-index:2147483000;display:flex;gap:12px;align-items:center;max-width:calc(100vw - 32px);box-sizing:border-box;padding:10px 12px 10px 16px;border-radius:999px;background:var(--navy, #1a2744);color:#fff;font:500 13.5px/1.3 system-ui,-apple-system,sans-serif;box-shadow:0 6px 24px rgba(0,0,0,.25)';
        const text = document.createElement('span');
        text.textContent = `A new version of ${BRAND.name} is ready.`;
        const go = document.createElement('button');
        go.type = 'button';
        go.textContent = 'Refresh';
        go.style.cssText = 'flex:none;border:0;border-radius:999px;padding:7px 14px;background:var(--gold, #d4b668);color:var(--navy, #1a2744);font:600 13px/1 system-ui,-apple-system,sans-serif;cursor:pointer;min-height:32px';
        go.addEventListener('click', () => location.reload());
        bar.append(text, go);
        document.body.appendChild(bar);
    };
    const check = () => {
        if (offered || document.visibilityState === 'hidden') return;
        fetch(bundle, { method: 'HEAD', cache: 'no-store' }).then(r => {
            const now = r.ok ? stamp(r) : '';
            if (!now) return;
            if (loaded == null) loaded = now;
            else if (now !== loaded) offer();
        }).catch(() => {});
    };
    check();
    setInterval(check, 10 * 60 * 1000);
    document.addEventListener('visibilitychange', check);
}
const ReactDOM = { ...ReactDOMLegacy, createRoot };

        const { useState, useEffect, useRef, createContext, useContext, useCallback } = React;

        // ============================================
        // API CONFIGURATION
        // ============================================
        // Set API_URL to your Cloudflare Worker URL to load data from API
        // Leave empty to use embedded data (default)
        // Served from localhost — a static server over the repo, next to
        // `wrangler dev` — the app talks to the LOCAL worker, the way the portal
        // already does. That is what lets a full rehearsal (docs/REHEARSAL.md)
        // pile questions onto test advisors without one reaching a real one.
        const API_URL = (typeof location !== 'undefined' && (location.hostname === 'localhost' || location.hostname === '127.0.0.1'))
            ? 'http://127.0.0.1:8787'
            : 'https://edify-api.webmdt.workers.dev';

        // How many interviews the library holds — counted by the worker and
        // rounded DOWN to the nearest 50 there (GET /api/library/size), so the
        // figure is never more than a member can open. It replaced "~1,500"
        // typed into the sign-in page. Asked once per page load; until it
        // answers, or if it cannot, no number is shown rather than a guess.
        let librarySizeAsk = null;
        const useLibrarySize = () => {
            const [size, setSize] = useState(null);
            useEffect(() => {
                let gone = false;
                if (!librarySizeAsk) {
                    librarySizeAsk = fetch(`${API_URL}/api/library/size`)
                        .then(r => (r.ok ? r.json() : null))
                        .then(d => { const n = Number(d && d.data && d.data.interviews); return Number.isFinite(n) && n > 0 ? n : null; })
                        .catch(() => null);
                    // A failed ask is not remembered: the next screen tries again.
                    librarySizeAsk.then(n => { if (n === null) librarySizeAsk = null; });
                }
                librarySizeAsk.then(n => { if (!gone && n !== null) setSize(n); });
                return () => { gone = true; };
            }, []);
            return size;
        };

        // ============================================
        // STORAGE KEYS
        // ============================================
        const STORAGE_KEYS = {
            USER_PROFILE: 'edify_user_profile',
            POWER_INDEX: 'edify_power_index',
            STREAK: 'edify_streak',
            COMPLETED_SIMULATIONS: 'edify_completed_simulations',
            SAVED_MOMENTS: 'edify_saved_moments',
            CI_SESSIONS: 'edify_ci_sessions',
            LAST_ACTIVE_DATE: 'edify_last_active_date',
            CEO_ANSWERS: 'edify_ceo_answers',
            API_DATABASE_CACHE: 'edify_api_database_cache',
            API_CACHE_TIME: 'edify_api_cache_time',
            APP_TOKEN: 'edify_app_token',
            APP_USER: 'edify_app_user',
            // The OTHER apps' session keys, for the door hand-off (docs/IDENTITY.md).
            PORTAL_TOKEN: 'edify_mentor_token',
            CONSOLE_TOKEN: 'edify_token',
            CEO_WISDOM_CONVERSATIONS: 'edify_ceo_wisdom_conversations',
            SAVED_NEXT_MOVES: 'edify_saved_next_moves',
            QUOTE_SHOWN_DATE: 'edify_quote_shown_date',
            FEEDBACK_STATE: 'edify_feedback_state'
        };

        // Decision practice vocabularies (docs/DECISION_PRACTICE.md §2). A copy
        // of the worker's literal, compared by test/practice-vocab.test.mjs; any
        // derived lookup goes BELOW the END marker.
        // PRACTICE_VOCAB BEGIN — MUST match cloudflare/worker/src/practice.ts
        const PRACTICE_VOCAB = {
            "industries": [
                ["technology", "Technology"],
                ["manufacturing", "Manufacturing & Industrial"],
                ["healthcare", "Healthcare & Life Sciences"],
                ["financial", "Financial Services"],
                ["retail", "Retail"],
                ["food", "Food & Beverage"],
                ["consumer", "Consumer Goods"],
                ["media", "Media & Entertainment"],
                ["professional", "Professional Services"],
                ["hospitality", "Hospitality & Travel"],
                ["logistics", "Logistics & Transportation"],
                ["energy", "Energy & Infrastructure"],
                ["workforce", "Workforce & Staffing"],
                ["education", "Education"],
                ["nonprofit", "Nonprofit"]
            ],
            "decisionTypes": [
                ["crisis", "Crisis & survival"],
                ["strategy", "Strategy & growth"],
                ["innovation", "Product & innovation"],
                ["people", "People & culture"],
                ["capital", "Capital & deals"],
                ["customers", "Customers & brand"],
                ["operations", "Operations & execution"],
                ["integrity", "Ethics & stakeholders"],
                ["leadership", "Leading yourself"]
            ],
            "stages": [
                ["startup", "Startup / early"],
                ["growth", "Scaling"],
                ["established", "Established"],
                ["turnaround", "Turnaround"]
            ],
            "roles": [
                ["ceo", "CEO / President"],
                ["founder", "Founder / Owner"],
                ["csuite", "C-suite executive"],
                ["vp", "VP / Director"],
                ["manager", "Manager / team lead"],
                ["emerging", "Emerging leader"],
                ["other", "Other"]
            ],
            "sizes": [
                "Under 50 people",
                "50–200 people",
                "200–500 people",
                "500–2,000 people",
                "2,000+ people"
            ],
            "axes": [
                "risk",
                "reach",
                "horizon"
            ]
        };
        // PRACTICE_VOCAB END

        // A slug's label, or null. An unknown slug — a tag from a newer worker,
        // a hand-edited profile — has no label and renders as nothing, never as
        // the raw slug.
        const practiceLabel = (list, slug) => {
            const hit = typeof slug === 'string' ? list.find(([s]) => s === slug) : null;
            return hit ? hit[1] : null;
        };
        const industryLabel = (slug) => practiceLabel(PRACTICE_VOCAB.industries, slug);
        const decisionTypeLabel = (slug) => practiceLabel(PRACTICE_VOCAB.decisionTypes, slug);
        const stageLabel = (slug) => practiceLabel(PRACTICE_VOCAB.stages, slug);
        const roleLabel = (slug) => practiceLabel(PRACTICE_VOCAB.roles, slug);

        /**
         * The other doors this person holds a hat for (docs/IDENTITY.md), and
         * how to walk through one without signing in again. Same origin, so
         * the other app's storage key is ours to write — the same hand-off the
         * console already uses to open this app as a member. The receiving app
         * validates the token on its own /me call; nothing is trusted here.
         */
        /**
         * The hats this person holds, and so which other doors to offer
         * (docs/IDENTITY.md).
         *
         * The fallback is not decoration. `roles` is attached by the worker and
         * arrives with a sign-in or the next GET /api/me — but a session handed
         * over from the portal writes only the token, an offline boot reads
         * whatever was cached last, and the two-factor sign-in used to answer
         * without it. In every one of those the role string is still there and
         * still says what it says, and reading only `roles` meant a mentor sat
         * in the app with no way back to their own portal. hatsFromRole() in the
         * worker, hatsOf() in the console and the portal's own check all fall
         * back this way; this was the one that did not.
         */
        const hatsOfUser = (user) => {
            if (user && Array.isArray(user.roles) && user.roles.length) return user.roles;
            const role = user && user.role;
            return role === 'admin' ? ['member', 'admin']
                 : role === 'ceo_mentor' ? ['member', 'mentor']
                 : ['member'];
        };
        // What a question may be, in characters. MUST match MAX_QUESTION_CHARS /
        // MIN_QUESTION_CHARS in cloudflare/worker/src/routes/ceoQuestions.ts —
        // the server is the authority and refuses either bound, so a number that
        // drifted here would either hide a usable limit or offer a button that
        // 400s. test/ask-limits.test.mjs reads both and fails when they part.
        const ASK_MAX_CHARS = 8000;
        const ASK_MIN_CHARS = 20;

        const otherDoors = (user) => {
            const roles = hatsOfUser(user);
            return { portal: roles.includes('mentor'), console: roles.includes('admin') };
        };
        function openDoor(door, token) {
            const t = token || storage.get(STORAGE_KEYS.APP_TOKEN, null);
            if (!t) return;
            try {
                if (door === 'portal') window.localStorage.setItem(STORAGE_KEYS.PORTAL_TOKEN, t);
                else if (door === 'console') window.localStorage.setItem(STORAGE_KEYS.CONSOLE_TOKEN, t);
            } catch (e) { /* private mode: they sign in again over there */ }
            window.location.href = door === 'portal' ? '/mentor/' : '/admin.html';
        }

        // Where a user who has never played stands. Must match STARTING_INDEX /
        // STARTING_STREAK in cloudflare/worker/src/routes/simulations.ts — the
        // server owns both, hands them back from GET /api/profile and after
        // every run, so these only govern the instant before that first response.
        //
        // They used to be 72 and 18, agreeing with nothing. A new account's
        // profile came back empty, nothing overwrote them, and the debounced
        // sync then wrote them to the server — stamping a power index of 72 and
        // an 18-day practice streak onto someone who had done nothing. It also
        // meant the first correct case DROPPED the score, from 72 to 53, as the
        // server's real number took over. Migration 0010 repaired the rows.
        const STARTING_INDEX = 50;
        const STARTING_STREAK = 0;

        // ============================================
        // A/B VARIANT
        // ============================================
        // This file is ONE variant's source. src/a/app.jsx and src/b/app.jsx are
        // independent forks — identical today apart from this constant, and free to
        // diverge as experiments land. Nothing else in the app branches on VARIANT:
        // a difference between A and B is expressed by editing that fork's file, not
        // by a conditional here.
        //
        // The assignment itself is server-side (users.variant), so a user always
        // lands on the same build regardless of the device or link they arrive on.
        const VARIANT = 'A';

        // Where each variant is served from. Every arm that HAS a build belongs here
        // in EVERY fork: a member signs in wherever the router sent them — which is
        // A for anyone whose browser has no cached variant — and is forwarded from
        // there once the server says which arm they are on. A fork that does not
        // know a path leaves them where they are, which is exactly how variant C
        // would have silently collected its members inside build A.
        const VARIANT_PATHS = { A: '/a/', B: '/b/', C: '/c/' };

        const normalizeVariant = (v) => {
            const s = String(v || '').trim().toUpperCase();
            return VARIANT_PATHS[s] ? s : null;
        };

        // Send a signed-in user to their assigned build. The bearer token lives in
        // localStorage, which is origin-scoped and path-independent, so the target
        // build picks the session straight up — no re-login across the hop.
        // Returns true when a navigation has been started.
        const routeToVariant = (v) => {
            const target = normalizeVariant(v);
            if (!target || target === VARIANT) return false;
            window.location.replace(VARIANT_PATHS[target] + window.location.search + window.location.hash);
            return true;
        };

        // ============================================
        // FEEDBACK
        // ============================================
        // One prompt, one question set, injected at the end of several journeys.
        // What changes per surface is only WHERE the user was — captured as context
        // and stored beside the A/B variant, so responses can be read per scenario,
        // per surface and per variant.
        const FEEDBACK_SURFACES = {
            simulator: { subject: 'the Decision Simulator', contextType: 'scenario' },
            mentor:    { subject: 'Molly, your CI Mentor', contextType: 'mentorSession' },
            askCeo:    { subject: 'Ask a CEO', contextType: 'ceoQuestion' },
            manual:    { subject: BRAND.name, contextType: 'view' }
        };

        // The three questions are the same everywhere; only the noun in Q2 follows
        // the surface. The rendered text is submitted with the answer so a response
        // stays self-describing if the wording is ever changed.
        const feedbackQuestions = (subject) => ([
            {
                id: 'insight', type: 'rating',
                question: 'Do you feel you gained insights that will help elevate your company or your personal effectiveness?',
                low: 'Not at all', high: 'A great deal'
            },
            {
                id: 'improvements', type: 'text',
                question: `What improvements would you suggest for ${subject}?`,
                placeholder: 'Optional — anything that would make this more useful to you.'
            },
            {
                id: 'intuitive', type: 'rating',
                question: 'How intuitive did you find the application to use?',
                low: 'Not at all intuitive', high: 'Very intuitive'
            }
        ]);

        // A context label is a headline, not a transcript: the whole question or
        // issue can be long, and the console lists these one per row.
        const feedbackLabel = (s) => String(s || '').trim().replace(/\s+/g, ' ').slice(0, 280);

        // Cadence: asked on the first completion of a surface, then every third one
        // (1st, 4th, 7th…). Frequent enough to track a variant over a pilot, rare
        // enough that finishing a case doesn't start to feel like a survey.
        const FEEDBACK_EVERY = 3;

        // Counts THIS completion and answers whether to prompt for it. The counter
        // is per surface, so cases and mentor sessions don't crowd each other out.
        const bumpFeedbackCounter = (surface) => {
            const state = storage.get(STORAGE_KEYS.FEEDBACK_STATE, {}) || {};
            const counts = { ...(state.counts || {}) };
            const before = counts[surface] || 0;
            counts[surface] = before + 1;
            storage.set(STORAGE_KEYS.FEEDBACK_STATE, { ...state, counts });
            return before % FEEDBACK_EVERY === 0;
        };

        // One throttled prompt per SESSION (the telemetry session — one tab, from
        // open to close, rotated on sign-out). sessionStorage, so a reload does
        // not re-arm it; private mode without storage simply never caps.
        const FEEDBACK_SESSION_KEY = 'edify_feedback_prompted_session';
        const feedbackShownThisSession = () => {
            try { return window.sessionStorage.getItem(FEEDBACK_SESSION_KEY) === Telemetry.sessionId; } catch (e) { return false; }
        };
        const markFeedbackShownThisSession = () => {
            try { window.sessionStorage.setItem(FEEDBACK_SESSION_KEY, Telemetry.sessionId); } catch (e) { /* fine */ }
        };

        // ============================================
        // API HELPER FUNCTIONS
        // ============================================
        const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

        // Content reads are auth-gated server-side (the library is the product),
        // so this needs the signed-in user's token and cannot run before login.
        async function loadDatabaseFromAPI(token) {
            if (!API_URL || !token) return null;

            // Check cache first
            const cachedTime = localStorage.getItem(STORAGE_KEYS.API_CACHE_TIME);
            const cachedData = localStorage.getItem(STORAGE_KEYS.API_DATABASE_CACHE);

            if (cachedTime && cachedData) {
                const age = Date.now() - parseInt(cachedTime, 10);
                if (age < CACHE_TTL) {
                    try {
                        return JSON.parse(cachedData);
                    } catch (e) {
                        console.warn('Failed to parse cached database:', e);
                    }
                }
            }

            try {
                const authHeaders = { Authorization: `Bearer ${token}` };
                const [scenariosRes, momentsRes, insightsRes] = await Promise.all([
                    fetch(`${API_URL}/api/scenarios`, { headers: authHeaders }).then(r => r.json()),
                    fetch(`${API_URL}/api/moments`, { headers: authHeaders }).then(r => r.json()),
                    fetch(`${API_URL}/api/insights`, { headers: authHeaders }).then(r => r.json())
                ]);

                if (scenariosRes.success && momentsRes.success && insightsRes.success) {
                    const apiData = {
                        scenarios: scenariosRes.data,
                        moments: momentsRes.data,
                        insights: insightsRes.data
                    };

                    // Cache the data
                    localStorage.setItem(STORAGE_KEYS.API_DATABASE_CACHE, JSON.stringify(apiData));
                    localStorage.setItem(STORAGE_KEYS.API_CACHE_TIME, Date.now().toString());

                    return apiData;
                }
            } catch (error) {
                console.warn('Failed to load from API, using embedded data:', error);
            }

            return null;
        }

        // Fix audio paths. Embedded data uses local "../audio/...". The API and
        // admin store R2 keys like "audio/<ts>-name.mp3" (admin sometimes wraps
        // them as "/api/audio/audio/...") that must be served by the Worker at
        // ${API_URL}/api/audio/<key> — the clips live in R2, not next to light/.
        // Module-scoped: also used by chat "sources" audio cards at render time.
        // A quote box and a clip that play the same moment of the same recording
        // are one thing: the clip is shown in the box (worker: sameRecording).
        const sameRecording = (a, b) => !!(a && b && a.audioFile && a.audioFile === b.audioFile
            && (a.startS == null || b.startS == null || Math.abs(a.startS - b.startS) < 60));
        // Quotes travel with a stored turn inside its `sources`, marked why:'quote',
        // so a past session shows them again without a new column; they are taken
        // back out here, so nothing that reads clips ever sees one.
        const storedSourcesOf = (list) => {
            const all = Array.isArray(list) ? list.filter(Boolean) : [];
            const clips = all.filter(x => x.why !== 'quote');
            const quotes = all.filter(x => x.why === 'quote');
            return { ...(clips.length ? { sources: clips } : {}), ...(quotes.length ? { quotes } : {}) };
        };
        // The whole interview behind a clip: the worker says which (fullAudioFile);
        // a timed clip already IS it, played from a moment — so a clip stored before
        // the worker said so still offers it.
        const sameCeo = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
        const wholeOf = (it) => (it && (it.fullAudioFile || (it.startS != null ? it.audioFile : null))) || null;
        // A Molly reply as stored: its text without the closing Action line or the clip
        // markers (the live screen renders those as "Your next move" and chips).
        const pastReplyParts = (text) => {
            const plain = String(text || '').replace(/\s*\[\s*c\s*\d{1,2}\s*\]/gi, '').replace(/(?:^|\n)\s*Stage:\s*[^\n]*\s*$/i, '');
            const m = plain.match(/(?:^|\n)\s*Action:\s*([^\n]+?)\s*$/i);
            return m ? { main: plain.replace(m[0], '').trim(), action: m[1].trim() } : { main: plain.trim(), action: null };
        };
        const fixAudioPath = (path) => {
            if (!path) return path;
            if (path.startsWith('http')) return path;   // already an absolute URL
            if (path.startsWith('../')) return path;     // embedded local clip
            // normalize to the bare R2 key (strip any leading /api/audio/ wrapper)
            const m = path.match(/\/api\/audio\/(.+)$/);
            const key = m ? m[1] : path;
            if (key.startsWith('audio/')) {
                return API_URL ? `${API_URL}/api/audio/${key}` : '../' + key;
            }
            return path;
        };

        function mergeAPIData(embeddedDB, apiData) {
            if (!apiData) return embeddedDB;

            // Transform API scenarios to match embedded format
            const transformedScenarios = apiData.scenarios.map(s => ({
                id: s.id,
                active: s.active,
                scenarioType: s.scenarioType,   // 'match' (default when absent) | 'spotBad'
                year: s.year,
                ceo: s.ceo,
                ceoTitle: s.ceoTitle,
                company: s.company,
                title: s.title,
                situation: s.situation,
                timeContext: s.timeContext,
                // Decision practice (0027): the tags the pick reads, and the case
                // file. Null from a worker that predates them. `optionStyles` is
                // deliberately not mapped — the worker sends it to admins only,
                // and in a member's hands it reads as an answer key.
                industry: s.industry || null,
                decisionType: s.decisionType || null,
                companyStage: s.companyStage || null,
                exhibits: s.exhibits || null,
                // Research (0028, docs/RESEARCH_PACKAGE.md §2): whether the case
                // has an approved return to fetch when it opens. The research
                // itself stays off this cached list — the run asks for it.
                researchStatus: s.researchStatus || null,
                hasResearch: s.hasResearch === true,
                options: s.options || [],
                actualChoice: s.actualChoice,
                actualDecision: s.actualDecision,
                lesson: s.lesson,
                factoid: s.factoid,
                actionItem: s.actionItem,
                audioTranscript: s.audioTranscript,
                audioFile: fixAudioPath(s.audioFile),
                audioFileFull: fixAudioPath(s.audioFileFull)
            }));

            // Transform API moments to match embedded format
            const transformedMoments = apiData.moments.map(m => ({
                id: m.id,
                active: m.active,
                ceo: m.ceo,
                ceoTitle: m.ceoTitle,
                company: m.company,
                title: m.title,
                moment: m.moment,
                context: m.context,
                pivotType: m.pivotType,
                keyDecision: m.keyDecision,
                outcome: m.outcome,
                lesson: m.lesson,
                audioFile: fixAudioPath(m.audioFile),
                audioTranscript: m.audioTranscript,
                audioFileFull: fixAudioPath(m.audioFileFull)
            }));

            // Transform API insights to match embedded format
            const transformedInsights = apiData.insights.map(i => ({
                ceo: i.ceo,
                ceoTitle: i.ceoTitle,
                company: i.company,
                insight: i.insight,
                topic: i.topic,
                audioFile: fixAudioPath(i.audioFile)
            }));

            return {
                ...embeddedDB,
                decisionSimulator: {
                    ...embeddedDB.decisionSimulator,
                    // No length check here, unlike moments/insights below: when the
                    // admin turns on "only show approved situations", a legitimately
                    // EMPTY list is the correct answer, and falling back to the
                    // embedded scenarios would show exactly the unreviewed audio the
                    // gate exists to hide. loadDatabaseFromAPI() returns null (not an
                    // empty array) when the API is actually unreachable, and that
                    // case never reaches this function.
                    scenarios: transformedScenarios
                },
                transformativeMoments: {
                    ...embeddedDB.transformativeMoments,
                    moments: transformedMoments.length > 0 ? transformedMoments : embeddedDB.transformativeMoments.moments
                },
                ciMentor: {
                    ...embeddedDB.ciMentor,
                    ceoInsights: transformedInsights.length > 0 ? transformedInsights : embeddedDB.ciMentor.ceoInsights
                }
            };
        }

        // ============================================
        // LOCAL DATABASE - All content stored here (FALLBACK)
        // ============================================
        const DATABASE = {
            // CEO Intelligence Mentor Data
            ciMentor: {
                agent: {
                    name: "Molly",
                    title: "Your AI CEO Intelligence Mentor",
                    avatar: "fa-solid fa-fire",
                    greeting: "I'm Molly. Ask me anything — I search the whole " + BRAND.name + " library of CEO interviews and bring you what the CEOs actually said and did, with the recordings to hear it in their own words. Bring me a live challenge and we'll work it the way they worked theirs: clarify, stress-test, pattern-match, then an action plan. What's on your mind today?"
                },
                challengeCategories: [
                    { id: "market", label: "Market & Strategy", icon: "fa-solid fa-chart-column" },
                    { id: "team", label: "Team & Leadership", icon: "fa-solid fa-users" },
                    { id: "customer", label: "Customer & Revenue", icon: "fa-solid fa-money-bill-wave" },
                    { id: "growth", label: "Growth & Scale", icon: "fa-solid fa-chart-line" },
                    { id: "pivot", label: "Pivot & Transform", icon: "fa-solid fa-rotate" },
                    { id: "other", label: "Other Challenge", icon: "fa-solid fa-lightbulb" }
                ],
                sampleChallenges: [
                    "The market has shifted and we need a new model without losing our market leadership",
                    "My co-founder/executive is now the bottleneck - how do I fix this?",
                    "A big customer wants something off-strategy - do I say yes for revenue?",
                    "Our growth has stalled - how do I know when to pivot, focus, or expand?"
                ],
                ceoInsights: [
                    {
                        ceo: "Ron Shaich",
                        ceoTitle: "Founding CEO",
                        company: "Panera Bread",
                        insight: "Throughout corporate America, we spend a lot of time reacting to and responding to the wrong things. We don't have a future-oriented view of where we need to be with precision. The time to be judging your life is not in the ninth inning with two outs - it's in the seventh inning, the fifth inning, the third.",
                        topic: "market",
                        audioFile: "../audio/ron-shaich-ci-mentor.mp3"
                    },
                    {
                        ceo: "Dan Amos",
                        ceoTitle: "Chairman, CEO & President",
                        company: "Aflac",
                        insight: "Don't risk a lot for a little, don't risk more than you can afford to lose, and consider the odds. Bad news does not improve with age - when you have an issue that's not good, the more you wait and try to hold it back, the worse it gets.",
                        topic: "pivot",
                        audioFile: "../audio/dan-amos-ci-mentor.mp3"
                    },
                    {
                        ceo: "Caroline Feeney",
                        ceoTitle: "CEO, Prudential U.S. Businesses",
                        company: "Prudential Financial",
                        insight: "Remember to be your authentic self. Understand who you are as a leader and remain true to that. I learned very early on that I was not one of the men and I didn't need to pretend to be one. You can have a diverse team, but you're not necessarily going to be a winning team - that's where the inclusive nature of the team is so important.",
                        topic: "team"
                    },
                    {
                        ceo: "Suneera Madhani",
                        ceoTitle: "Founder & CEO",
                        company: "Stax Payments",
                        insight: "It doesn't get easier. You just get better. Don't play the victim - if they don't understand you, if you don't get that opportunity, you're not gonna get invited to have a seat at the table, so build your own. The power of no gives you the ability to have the best yeses.",
                        topic: "growth"
                    },
                    {
                        ceo: "Bernie Marcus",
                        ceoTitle: "Co-Founder",
                        company: "The Home Depot",
                        insight: "The CEO who is successful is the one who listens. I learned more listening to kids on the floor of the store than anywhere else. Surround yourself with people who are smarter than you. A lot of CEOs are very egocentric - they believe they know everything. That just doesn't work.",
                        topic: "customer"
                    },
                    {
                        ceo: "Brooks Tingle",
                        ceoTitle: "CEO",
                        company: "John Hancock",
                        insight: "It occurred to us that life insurance should be as much about living as dying. How irrational was it that for the whole history of the industry we would hope customers lived long healthy lives, but do absolutely nothing to achieve that outcome? When your customers' success is identical to your success, innovation follows naturally.",
                        topic: "innovation"
                    }
                ],
                processSteps: [
                    { step: 1, title: "Clarify", description: "Uncover the systemic problem through targeted iterative questions" },
                    { step: 2, title: "Stress-Test", description: "Pressure-test your choice with potential consequences and blind spots" },
                    { step: 3, title: "Pattern-Match", description: "See how your instincts line up with common executive decision patterns" },
                    { step: 4, title: "Action Plan", description: "Get a clear, step-by-step plan: what to do next, what to communicate" }
                ]
            },

            // CEO Decision Simulator Data
            decisionSimulator: {
                scenarios: [
                    {
                        id: 1,
                        active: true,
                        year: 1981,
                        ceo: "John Mackey",
                        ceoTitle: "Founder & CEO",
                        company: "Whole Foods Market",
                        title: "Eight Feet Under",
                        situation: "You are 3 years into building your natural foods grocery store. You started with $45,000. You've barely survived your first stretch of cash constraints. Then disaster hits. A 100-year flood. Your store is eight feet underwater. Inventory destroyed. Equipment ruined. No flood insurance. You are bankrupt. You cannot pay employees. You owe suppliers money. Your entire company could die this week.",
                        timeContext: "You've lost everything. No insurance. No backup plan. The business you built is drowning.",
                        options: [
                            { id: "A", text: "Shut down permanently: Cut losses and protect what little reputation remains" },
                            { id: "B", text: "Reopen only if investors inject emergency capital immediately: Protect shareholders first" },
                            { id: "C", text: "Call your stakeholders — customers, employees, suppliers, investors — and ask them to help rebuild together" }
                        ],
                        actualChoice: "C",
                        actualDecision: "I leaned into community. Dozens of customers showed up to help clean the store. Our team members worked for free. Our suppliers fronted us new inventory on credit. Our investors kicked in new capital. Our banks loaned us additional money. All the stakeholders came together to make sure Whole Foods didn't die.",
                        lesson: "Build trust, loyalty, and shared purpose before crisis hits. A business is a community of people — not just a vehicle to make money. In crisis, transactional leaders collapse while community leaders compound.",
                        factoid: "Whole Foods grew from a single flooded store in 1981 to a $13.7 billion acquisition by Amazon in 2017 — built on the foundation of stakeholder relationships forged during crisis.",
                        actionItem: "Map your stakeholder ecosystem this week: customers, employees, suppliers, and partners. Identify three relationships you can deepen before crisis hits — because the goodwill you build now becomes your insurance policy later.",
                        audioTranscript: "We had dozens of customers that showed up to help us clean up the store because they loved us so much and they did not want the store to fail… Our team members worked for free. Our suppliers fronted us new inventory on credit. Our investors kicked in new capital. Our banks loaned us additional money. All the stakeholders came together to make sure Whole Foods didn't die.",
                        audioFile: "../audio/john-mackey-8feet-under.mp3",
                        audioFileFull: "../audio/john-mackey-full.mp3"
                    },
                    {
                        id: 2,
                        active: true,
                        year: 1989,
                        ceo: "John Mackey",
                        ceoTitle: "Founder & CEO",
                        company: "Whole Foods Market",
                        title: "The Capital Squeeze",
                        situation: "Your business is profitable. But you are constantly cash-strapped. Every new store drains working capital. For 14 years, you've never had enough money to grow at full speed. You are offered venture capital funding. But it means: loss of control, outside oversight, and pressure to scale.",
                        timeContext: "Profitable but constrained. Capital is the bottleneck holding back your vision.",
                        options: [
                            { id: "A", text: "Decline outside money: Stay small and controlled" },
                            { id: "B", text: "Take venture capital to accelerate growth" },
                            { id: "C", text: "Pause expansion and optimize existing stores before making a capital decision" }
                        ],
                        actualChoice: "B",
                        actualDecision: "Whole Foods raised venture capital, then went public three years later. Lack of capital was constraining opportunity. I realized capital is oxygen — even a strong company suffocates without it.",
                        lesson: "Identify your true constraint before making decisions. The real question isn't 'Do I want dilution?' — it's 'Is capital my bottleneck?' If your constraint is talent, fix talent. If it's culture, fix culture. If it's capital, raise capital. Fix the constraint — not your ego.",
                        factoid: "Within 3 years of raising venture capital, Whole Foods went public at $18/share. The stock eventually reached over $500/share (split-adjusted), proving that the right capital at the right time multiplies opportunity.",
                        actionItem: "Identify your #1 constraint this quarter: Is it capital, talent, technology, or market access? Don't let ego drive the decision — be honest about what's actually holding you back, then allocate resources to fix that specific bottleneck.",
                        audioTranscript: "Whole Foods raised venture capital in 1989, then went public in 1992. Lack of capital was constraining opportunity. I realized capital is oxygen — even a strong company suffocates without it.",
                        audioFile: "../audio/john-mackey-capital-squeeze.mp3",
                        audioFileFull: "../audio/john-mackey-full.mp3"
                    },
                    {
                        id: 3,
                        active: false,
                        ceo: "Bernie Marcus",
                        ceoTitle: "Co-Founder",
                        company: "The Home Depot",
                        title: "Fired at 49",
                        situation: "You're 49 years old with a family to support. You just got fired as CEO of a home improvement chain after building it from 4 to 80 stores. A corporate power struggle eliminated you. You have no money. You're devastated. A trusted colleague calls you that Friday night.",
                        timeContext: "You've lost everything at an age when most executives seek safe harbor. Starting over seems impossible.",
                        options: [
                            { id: "A", text: "Find another CEO job: leverage your track record to land safely at another company" },
                            { id: "B", text: "Start something new: take the biggest risk of your life and build your own company from scratch" },
                            { id: "C", text: "Retire early: you've had a good run, focus on family and lower-risk opportunities" }
                        ],
                        actualChoice: "B",
                        actualDecision: "Arthur Blank said: 'You've just been hit in the rear end with a golden horseshoe. Let's go into business for ourselves.' Ken Langone secured $2 million. We opened the first two Home Depot stores in Atlanta.",
                        lesson: "The worst moment of your career can become the foundation of your greatest achievement. Never dwell - dwelling is for losers.",
                        audioTranscript: "My mother always said: 'Bernie, you could be anything you want. This is America.' When crowds were sparse, I sent my kids to hand out $1 bills in the parking lot. Today we have 2,300 stores and 475,000 employees."
                    },
                    {
                        id: 4,
                        active: false,
                        ceo: "Suneera Madhani",
                        ceoTitle: "Founder & CEO",
                        company: "Stax Payments",
                        title: "Laughed Out of the Room",
                        situation: "You're 25, selling payment terminals from your car. You've had an insight: offer flat subscription pricing instead of complex fees. You pitch this revolutionary idea to your employer's C-suite. They laugh in your face and make clear they don't take you seriously as a leader.",
                        timeContext: "You have no money, no network, no investors. Less than 3% of VC goes to women founders.",
                        options: [
                            { id: "A", text: "Accept their feedback: maybe the idea isn't ready, keep your job and refine the concept" },
                            { id: "B", text: "Start anyway: use $50,000 from friends and family, build from your parents' home" },
                            { id: "C", text: "Find a partner: team up with someone who has more credibility and resources" }
                        ],
                        actualChoice: "B",
                        actualDecision: "I started Stax with $50,000 from friends and family, working from my parents' Orlando home with my brother Sal. When offered $17.5 million to sell with only 4 months of payroll left, I turned it down and took out a $500,000 loan instead.",
                        lesson: "Being dismissed can be the catalyst for transformation. The people who say 'no' often aren't the right audience for your vision. It doesn't get easier - you just get better.",
                        audioTranscript: "Men are given capital for their potential while women are given capital for what they've done. So I had to prove myself with the business. We hit unicorn status - $1 billion valuation. One of only 23 fintech unicorns with a woman founder."
                    },
                    {
                        id: 5,
                        active: false,
                        ceo: "Brooks Tingle",
                        ceoTitle: "CEO",
                        company: "John Hancock",
                        title: "Reimagining Life Insurance",
                        situation: "You lead a legacy life insurance company. You connect with customers only twice a year - when you send a bill. You realize something irrational: for hundreds of years, insurers hoped customers lived long healthy lives but did nothing to help achieve that outcome. You want to transform the entire customer relationship.",
                        timeContext: "Insurance is not known for innovation. Behavioral science and gamification are unproven in financial services.",
                        options: [
                            { id: "A", text: "Incremental improvement: add wellness tips to mailings, create a health newsletter" },
                            { id: "B", text: "Full transformation: launch Vitality program using behavioral science to actively engage customers in health" },
                            { id: "C", text: "Partner cautiously: pilot with a small segment before committing significant resources" }
                        ],
                        actualChoice: "B",
                        actualDecision: "We launched John Hancock Vitality. We borrowed gamification from the casino industry - if it works for slot machines, why not for health? Points for steps, screenings, healthy food purchases. Rewards like Apple Watches and premium discounts.",
                        lesson: "When you recognize that your customers' success is identical to your success, innovation follows naturally. Find the shared value.",
                        audioTranscript: "Customer engagement went from 1-2 times per year to 20-30 times per month. A woman wrote me: 'Vitality saved my life - I wouldn't have gone for that mammogram if I didn't want your silly points.' We've outgrown our industry almost every quarter for seven consecutive years."
                    },
                    {
                        id: 6,
                        active: true,
                        ceo: "Caroline Feeney",
                        ceoTitle: "CEO, Prudential U.S. Businesses",
                        company: "Prudential Financial",
                        title: "The Authenticity Test",
                        situation: "Early in your career, a senior leader gives you advice: 'Lose your empathy gene.' The message is clear - to advance in male-dominated financial services, you should act more like a man. You're facing a defining choice about your leadership identity.",
                        timeContext: "Financial services is predominantly male. Traditional leadership models favor aggressive, hierarchical styles.",
                        options: [
                            { id: "A", text: "Adapt your style: follow the advice, blend in with the dominant culture to advance" },
                            { id: "B", text: "Stay authentic: keep your empathetic style, believing it makes you more effective" },
                            { id: "C", text: "Find a middle ground: be empathetic privately but project toughness publicly" }
                        ],
                        actualChoice: "B",
                        actualDecision: "I already knew that having empathy makes me a far more effective leader. I learned early that I was not one of the men and I didn't need to pretend to be one. You can care about people and be very direct - they're not mutually exclusive.",
                        lesson: "Authenticity is not a weakness - it's a superpower. You don't have to lose who you are when you're a leader.",
                        factoid: "Caroline Feeney became the only woman with P&L responsibility on Prudential's Executive Leadership Team — responsible for half of the company's earnings across 12 countries, all while leading with empathy.",
                        actionItem: "Audit your leadership style: Are you suppressing traits that could be strengths? This week, identify one authentic quality you've been hiding and find a way to bring it into your leadership. Empathy and directness aren't opposites — they're multipliers.",
                        audioTranscript: "I built what our CEO calls an 'extraordinary followership.' I became the only woman with P&L responsibility on the Executive Leadership Team - responsible for half of Prudential's earnings across 12 countries. When my promotion was announced, I received a standing ovation.",
                        audioFile: "../audio/caroline-feeney-simulator.mp3"
                    },
                    {
                        id: 7,
                        active: true,
                        year: 2024,
                        ceo: "Fred Laluyaux",
                        ceoTitle: "CEO",
                        company: "Aera Technology",
                        title: "Investing Ahead of Proof",
                        situation: "You're building what you call a 'self-driving enterprise' — AI that makes and executes business decisions autonomously. The technology works, but most companies don't fully understand it yet. Boards want clear ROI before investing in AI. You can wait for the market to catch up, or push forward before validation exists.",
                        timeContext: "AI hype is everywhere, but enterprise adoption lags. Boards demand proof, not vision. Capital is expensive.",
                        options: [
                            { id: "A", text: "Wait for market readiness: let larger players validate the category, then compete on execution" },
                            { id: "B", text: "Build ahead of the market: invest in the vision now, even without near-term ROI clarity" },
                            { id: "C", text: "Pivot to simpler offerings: sell what companies understand today, delay the bigger vision" }
                        ],
                        actualChoice: "B",
                        actualDecision: "We kept building the self-driving enterprise before the market fully demanded it. When you're creating a new category, waiting for consensus means you've already lost. The market doesn't reward followers in enterprise AI — it rewards those who defined the space.",
                        lesson: "Category creators must invest before proof exists. If you wait for the market to validate your vision, you're no longer leading — you're following. The gap between conviction and validation is where market leadership is won.",
                        factoid: "Aera Technology's decision intelligence platform now serves Fortune 500 companies, automating thousands of daily business decisions — a category that didn't exist when they started building.",
                        actionItem: "Identify one emerging trend in your industry where you have conviction but the market lacks consensus. Invest a small, protected budget to build capability there now — because by the time everyone agrees, the opportunity window closes.",
                        audioTranscript: "We create a category that's defined as decision intelligence — the digitization of the decisions that you make every day in order to run your business. From supply chain to operations to demand creation, companies make thousands of decisions. People are overwhelmed. Our technology enables the digitization of that process — it analyzes your data, delivers recommendations in plain English, like having a digital analyst working 24/7 for you.",
                        audioFile: "../audio/fred-laluyaux-simulator.mp3",
                        audioFileFull: "../audio/fred-laluyaux-simulator-full.mp3"
                    },
                    {
                        id: 8,
                        active: true,
                        year: 2024,
                        ceo: "Kamau Witherspoon",
                        ceoTitle: "CEO",
                        company: "Shipt",
                        title: "Beyond Delivery",
                        situation: "Your company is known as a grocery delivery service. It works. Customers know you for one thing. But you see a bigger opportunity: becoming a broader retail and logistics platform. The shift requires rethinking market positioning, capabilities, and identity. Your investors and team know 'delivery' — they don't know 'platform.'",
                        timeContext: "Markets reward focus. Brand clarity drives growth. Expanding too fast risks diluting what made you successful.",
                        options: [
                            { id: "A", text: "Stay focused: double down on delivery excellence, defend your core identity" },
                            { id: "B", text: "Expand the identity: evolve from delivery service to retail and logistics platform" },
                            { id: "C", text: "Test quietly: run pilots in new areas without publicly repositioning the brand" }
                        ],
                        actualChoice: "B",
                        actualDecision: "We pushed Shipt beyond delivery into a broader retail and logistics platform. The market knows us for one thing, but staying there means capping our potential. Identity-level decisions are uncomfortable — but necessary if you want to define where the industry goes, not just follow it.",
                        lesson: "Growth often requires moving beyond your original identity. Markets reward focus — until they don't. The companies that matter most are those willing to evolve what they are, not just what they do.",
                        factoid: "Shipt expanded from grocery delivery to serving over 100 retail partners, becoming a core logistics capability for Target while maintaining its independent growth trajectory.",
                        actionItem: "Ask yourself: 'What are we known for today?' versus 'What could we become?' If there's a gap, start testing new capabilities with existing customers before publicly repositioning — evolution beats revolution.",
                        audioTranscript: "At our core, Shipt is a retail technology company. We leverage our retail expertise to drive growth for any number of our over 100 retail partners. Being able to drive growth for parent company Target, as well as our 100 retail partners, is what we live and breathe each and every day.",
                        audioFile: "../audio/kamau-witherspoon-simulator.mp3",
                        audioFileFull: "../audio/kamau-witherspoon-simulator-full.mp3"
                    },
                    {
                        id: 9,
                        active: true,
                        year: 2023,
                        ceo: "Matt Navarro",
                        ceoTitle: "President",
                        company: "Stanley 1913",
                        title: "Reinventing a Century-Old Brand",
                        situation: "You lead a century-old brand known for durable, utilitarian drinkware. Sales are stable but flat. The brand is invisible to younger consumers. You could modernize incrementally — or make a bold repositioning bet that risks alienating your loyal base while chasing cultural relevance.",
                        timeContext: "Legacy brands rarely reinvent successfully. Consumer attention is fragmented. Brand missteps go viral instantly.",
                        options: [
                            { id: "A", text: "Protect the heritage: focus on core customers, make incremental updates" },
                            { id: "B", text: "Bold repositioning: transform into a culturally relevant lifestyle brand for a new generation" },
                            { id: "C", text: "Dual strategy: create a separate sub-brand for younger audiences, keep the core intact" }
                        ],
                        actualChoice: "B",
                        actualDecision: "We reimagined Stanley from a heritage utility brand into a cultural phenomenon. New colors, influencer partnerships, limited drops. The Quencher became a status symbol. We didn't just update the product — we repositioned the entire brand identity for a new generation.",
                        lesson: "Reinvention requires letting go of what historically worked. Protecting heritage can become protecting irrelevance. The boldest move is transforming what the brand means — not just what it makes.",
                        factoid: "Stanley's Quencher tumbler drove the company from $70 million in revenue to over $750 million in just 3 years — a 10x growth through brand repositioning and influencer partnerships.",
                        actionItem: "Audit your brand positioning: Is your current identity resonating with your next generation of customers? Consider one bold move this quarter to test a new demographic — whether through social media, influencer partnerships, or a product refresh that signals cultural relevance.",
                        audioTranscript: "We were looking for another reimagination of the brand. We saw a white space in an area to disrupt in hydration. Focused on the female consumer, a disruptor in what we thought was a bit of a stale category. Through some innovative products and what I call the color revolution — bringing cool, vibrant, fashion-connected style and color to hydration — has really led to incredible growth and three or four years of surprising and delighting consumers around the world.",
                        audioFile: "../audio/matt-navarro-simulator.mp3",
                        audioFileFull: "../audio/matt-navarro-full.mp3"
                    },
                    {
                        id: 10,
                        active: true,
                        year: 2024,
                        ceo: "Jim Holthouser",
                        ceoTitle: "CEO",
                        company: "GoTo Foods",
                        title: "Modernizing a Multi-Brand Empire",
                        situation: "You run a portfolio of 7+ well-known restaurant brands. Each brand has equity, history, and franchisee relationships. You need to modernize operations, technology, and customer experience across all of them — simultaneously. Moving too fast risks breaking what works. Moving too slow means competitors pass you.",
                        timeContext: "Franchise systems resist change. Each brand has different needs. Complexity slows transformation.",
                        options: [
                            { id: "A", text: "Sequential transformation: modernize one brand fully before touching others" },
                            { id: "B", text: "Unified platform push: drive transformation across all brands simultaneously" },
                            { id: "C", text: "Selective focus: prioritize top 2-3 brands, let others operate as-is" }
                        ],
                        actualChoice: "B",
                        actualDecision: "We drove modernization across all brands at once — unified technology, shared capabilities, coordinated evolution. Complexity is real, but sequential transformation means some brands get left behind. The portfolio is stronger when it moves together.",
                        lesson: "Multi-brand leadership requires accepting complexity, not avoiding it. The decision isn't whether to modernize — it's how aggressively to do so without breaking what already works. Speed and stability aren't opposites; they're both leadership requirements.",
                        factoid: "GoTo Foods manages over 2,800 restaurant locations across 7+ brands including Auntie Anne's, Cinnabon, and Jamba — all sharing unified technology and capabilities while preserving individual brand identity.",
                        actionItem: "If you manage multiple products, teams, or divisions: identify one capability that could be shared across all of them. Shared infrastructure doesn't mean losing uniqueness — it means giving each unit more resources than it could build alone.",
                        audioTranscript: "The big news for us is how we've learned to protect each individual brand and protect its unique identity while at the same time giving it more than it could ever have on its own. We're learning to give the brands more by leveraging the collective scale while protecting what makes each brand special and unique.",
                        audioFile: "../audio/jim-holthouser-simulator.mp3",
                        audioFileFull: "../audio/jim-holthouser-simulator-full.mp3"
                    },
                    {
                        id: 11,
                        active: true,
                        year: 2024,
                        ceo: "Kate Johnson",
                        ceoTitle: "CEO",
                        company: "Lumen Technologies",
                        title: "Building for the AI Future",
                        situation: "You lead a network infrastructure company. AI is reshaping how data moves — but the massive infrastructure investments required won't pay off immediately. Your board wants near-term results. Wall Street tracks quarterly performance. You can optimize for today's metrics or build for where the market is going.",
                        timeContext: "Capital intensity is high. Timing infrastructure investments is notoriously difficult. The penalty for being wrong is severe.",
                        options: [
                            { id: "A", text: "Optimize current assets: focus on profitability and returns from existing infrastructure" },
                            { id: "B", text: "Invest for the future: re-architect networks for AI and data demands before demand fully materializes" },
                            { id: "C", text: "Balanced approach: modest future investments while protecting current margins" }
                        ],
                        actualChoice: "B",
                        actualDecision: "We're rebuilding network architecture for AI and large-scale data movement — before the revenue fully materializes. Infrastructure leaders don't get to wait for demand to appear. By the time everyone sees it, you're already behind. Conviction investments require committing resources ahead of clear demand.",
                        lesson: "Infrastructure leadership means building for where the market is going, not where it is. The gap between investment and payoff is where conviction is tested. CEOs who wait for certainty in infrastructure are CEOs who fall behind.",
                        factoid: "Lumen operates one of the world's largest fiber networks spanning 450,000 route miles — now being re-architected for AI workloads that didn't exist when most of the infrastructure was built.",
                        actionItem: "Ask yourself: What infrastructure (technology, talent, partnerships) will my business need in 3 years? Start one small investment now — before the demand is obvious — because infrastructure built under pressure is always more expensive and less strategic.",
                        audioFile: "../audio/kate-johnson-simulator.mp3"
                    },
                    {
                        // Embedded-only demo. Id deliberately outside the D1 id
                        // space (live scenario ids run 1-149+) so completing it
                        // offline can never mark a real D1 case as played.
                        id: 9001,
                        active: true,
                        scenarioType: "spotBad",
                        year: 1985,
                        ceo: "Roberto Goizueta",
                        ceoTitle: "Chairman & CEO",
                        company: "The Coca-Cola Company",
                        title: "The Taste Test That Lied",
                        situation: "Atlanta, the early 1980s. You have just taken charge of the most valuable trademark on earth — and it is losing ground. Your rival's televised blind taste challenge keeps declaring their sweeter cola the winner, and your flagship's share of the U.S. market has drifted from roughly 60% after World War II to under 24%. You have told your executives there are no sacred cows — nothing is off the table, not even the century-old secret formula locked in a bank vault downtown. Over a few bold years, this corner office produces a string of decisions that reshape the company.",
                        timeContext: "Two of the calls made from that desk built enormous value. One became the most famous blunder in consumer-goods history.",
                        options: [
                            { id: "A", text: "Extend the sacred trademark: put the flagship name on a new diet cola — the first extension of the brand in its 96-year history" },
                            { id: "B", text: "Replace the formula: retire the 99-year-old original recipe nationwide and relaunch the flagship with a new, sweeter taste backed by more than 190,000 blind taste tests" },
                            { id: "C", text: "Diversify beyond beverages: spend roughly $700 million to acquire a major Hollywood studio and its film library" }
                        ],
                        actualChoice: "B",
                        actualDecision: "On April 23, 1985, Coca-Cola announced that the original formula — the recipe it had guarded for 99 years — would be replaced nationwide by New Coke. More than 190,000 blind taste tests said the new formula would win. But the tests measured sips; they never measured what it means to take an icon away from people. Protest calls climbed to 8,000 a day, and 79 days later, on July 11, 1985, the company brought the original back as Coca-Cola Classic.",
                        lesson: "Data can be flawless and still answer the wrong question. The taste tests measured preference in a sip and never measured attachment — nobody asked drinkers how they would feel if the original disappeared. The second lesson is the recovery: admitting the mistake within 79 days turned a fiasco into one of history's great brand reaffirmations.",
                        factoid: "Coca-Cola logged roughly 400,000 protest calls and letters before the reversal. ABC's Peter Jennings broke into General Hospital to announce the original's return, and by year's end Coca-Cola Classic was decisively outselling New Coke.",
                        actionItem: "Audit your most data-backed decision currently in flight. Ask the question the research probably skipped: have we tested how customers feel about losing what they have — or only whether they like what we're proposing? If reversal would be costly, design the rollback plan before you launch."
                    }
                ],
                powerIndex: {
                    description: "Your Daily CEO Power Index evaluates how closely aligned your decision-making is with top CEOs.",
                    scoreRanges: [
                        { min: 90, max: 100, label: "CEO Ready", color: "#4caf50" },
                        { min: 70, max: 89, label: "Executive Track", color: "#8bc34a" },
                        { min: 50, max: 69, label: "Rising Leader", color: "#ffc107" },
                        { min: 30, max: 49, label: "Emerging", color: "#ff9800" },
                        { min: 0, max: 29, label: "Foundation", color: "#f44336" }
                    ]
                }
            },

            // Transformative CEO Moments Data
            transformativeMoments: {
                definition: "A CEO who creates new value that reinvigorates a company, reinvents an industry, or reboots society.",
                moments: [
                    {
                        id: 1,
                        active: true,
                        ceo: "Brooks Tingle",
                        ceoTitle: "CEO",
                        company: "John Hancock Insurance",
                        title: "Vitality: Reimagining Life Insurance",
                        moment: "It occurred to us that life insurance should be as much about living as dying. How irrational was it that for hundreds of years of this industry's history, we would hope that customers lived long, healthy lives, but we would do absolutely nothing to try to achieve that outcome?",
                        context: "John Hancock only connected with customers about twice a year (typically when sending a bill), yet both insurer and customer shared the same goal—a long, healthy life.",
                        pivotType: "Customer Engagement",
                        keyDecision: "Use behavioral science and gamification techniques, borrowed from the casino industry, to help policyholders make healthier choices through the Vitality program.",
                        outcome: "Customer engagement transformed from 1-2 times per year to 20-30 times per month. Two-thirds of customers now use Vitality, with nearly 80% reporting improved health. A customer wrote: 'You've literally saved my life - I wouldn't have gone for that screening if it wasn't for you guys offering this test.'",
                        lesson: "When you recognize that your customers' success is identical to your success, innovation follows naturally.",
                        audioFile: "../audio/brooks-tingle-transformative.mp3",
                        audioTranscript: "It occurred to us that life insurance should be as much about living as dying. How irrational was it that for hundreds of years of this industry's history, we would hope that customers lived long, healthy lives, but we would do absolutely nothing to try to achieve that outcome? We should be actively engaging our customers from the outset, helping to achieve the very outcome that's good for them that they want and that we want too, a longer, healthier life."
                    },
                    {
                        id: 2,
                        active: true,
                        ceo: "Ron Shaich",
                        ceoTitle: "Founding CEO",
                        company: "Panera Bread",
                        title: "Betting the House on Panera",
                        moment: "If I had any guts, I'd sell the other divisions of Au Bon Pain, take the capital and the best people, and bet everything on ensuring Panera is able to fulfill its destiny.",
                        context: "As CEO of Au Bon Pain, Shaich couldn't access enough capital to both revitalize the ailing Au Bon Pain chain AND grow the promising Panera concept he had acquired.",
                        pivotType: "Strategic Focus",
                        keyDecision: "Sell Au Bon Pain to private equity and bet everything on Panera, sacrificing his original creation to nurture the greater opportunity.",
                        outcome: "The stock grew from $6 per share to $73.56. Over his last two decades as CEO, Panera generated 25% annualized shareholder returns. He ultimately sold Panera for $7.5 billion—an 86-fold total shareholder return.",
                        lesson: "Sometimes the hardest decision is choosing between two things you've built. There's no such thing as a billion-dollar idea—only a billion-dollar execution."
                    },
                    {
                        id: 3,
                        active: true,
                        ceo: "Caroline Feeney",
                        ceoTitle: "CEO, Prudential U.S. Businesses",
                        company: "Prudential Financial",
                        title: "Authentic Leadership",
                        moment: "A senior leader gave me advice: 'Lose your empathy gene.' I already knew that having empathy makes me a far more effective leader in connecting with my team.",
                        context: "Early in her career, Feeney was told that to advance in the male-dominated financial services industry, she should act more like a man and abandon her empathetic leadership style.",
                        pivotType: "Leadership Philosophy",
                        keyDecision: "Choose authenticity over conformity—stay true to her empathetic leadership style rather than adopting a traditional masculine leadership model.",
                        outcome: "Built what Prudential's CEO calls an 'extraordinary followership.' Became the only woman with P&L responsibility on Prudential's eight-person Executive Leadership Team, responsible for half of Prudential's earnings across 12 countries.",
                        lesson: "Authenticity is not a weakness—it's a superpower. You don't have to lose who you are when you're a leader."
                    },
                    {
                        id: 4,
                        active: true,
                        ceo: "Suneera Madhani",
                        ceoTitle: "Founder & CEO",
                        company: "Stax Payments",
                        title: "Laughed Out of the Room",
                        moment: "I took my idea to the payments processor I was working for at the time. They laughed in my face. It was clear they didn't take me seriously as a leader.",
                        context: "While selling payment terminals out of her Volkswagen Beetle, Madhani had an insight: why not offer a flat subscription model for payments instead of complex fee structures?",
                        pivotType: "Market Disruption",
                        keyDecision: "Start Stax with $50,000 from friends and family after being dismissed. When offered $17.5 million to sell with only 4 months of payroll left, she turned it down and took out a $500,000 loan instead.",
                        outcome: "Stax achieved unicorn status with a $1 billion valuation, becoming Orlando's first fintech unicorn. The company has processed over $23 billion in payments and is one of only 23 fintech unicorns with a woman founder.",
                        lesson: "Being dismissed can be the catalyst for transformation. The people who say 'no' often aren't the right audience for your vision. It doesn't get easier—you just get better."
                    },
                    {
                        id: 5,
                        active: true,
                        ceo: "Dan Amos",
                        ceoTitle: "Chairman, CEO & President",
                        company: "Aflac",
                        title: "Betting on a Duck",
                        moment: "I realized at that moment that I had bet my entire life on a duck, and I was scared to death it wasn't going to work.",
                        context: "Despite 10 years of advertising, Aflac's name recognition was stuck at around 9-10%. The safe choice was comedian Ray Romano, but an advertising team came back with a wild idea: a duck whose quack sounded like 'Aflac.'",
                        pivotType: "Brand Transformation",
                        keyDecision: "Commit to following the research data, even when it led somewhere terrifying. Choose the duck over the safe celebrity option because it tested nearly three times higher.",
                        outcome: "Name recognition skyrocketed from about 9% to 90%—Nike and Coca-Cola territory. Sales doubled in three years. The duck changed the entire insurance advertising industry, paving the way for Geico's Gecko, Flo from Progressive, and Jake from State Farm.",
                        lesson: "Data can give you permission to take risks you'd never take on gut instinct alone. Sometimes the 'crazy' option is the right one—if you've done your homework first.",
                        audioFile: "../audio/dan-amos-transformative.mp3",
                        audioFileFull: "../audio/dan-amos-full.mp3",
                        audioTranscript: "The AFLAC duck was in fact taking a big chance. By having a duck quack the name, no one ever dreamed that would work. I didn't even dream it would work but we tested it. I used what I called the three principles of risk management insurance: Don't risk a lot for a little. Don't risk more than you can afford to lose. And consider the odds."
                    },
                    {
                        id: 6,
                        active: true,
                        ceo: "Bernie Marcus",
                        ceoTitle: "Co-Founder",
                        company: "The Home Depot",
                        title: "Fired at 49, Building an Empire",
                        moment: "You've just been hit in the rear end with a golden horseshoe. Let's go into business for ourselves.",
                        context: "At 49 years old, with a family to support, Bernie Marcus was unceremoniously fired as CEO of Handy Dan Home Improvement Centers during a corporate power struggle, despite having built the chain from 4 stores to over 80.",
                        pivotType: "Entrepreneurial Pivot",
                        keyDecision: "Instead of seeking safe harbor at nearly 50, take the biggest risk of his life. Partner with Arthur Blank and secure $2 million in startup capital from Ken Langone.",
                        outcome: "The first two Home Depot stores opened in Atlanta. Today, Home Depot has 2,300+ stores, employs 475,000 people, and generates $153 billion in annual sales. Marcus gave away over $2 billion to philanthropy.",
                        lesson: "The worst moment of your career can become the foundation of your greatest achievement. Never dwell—dwelling is for losers."
                    }
                ],
                tenKeyQuestions: [
                    "What is your company's core purpose beyond making money?",
                    "What would put you out of business in the next 5 years?",
                    "What are you not doing that you should be?",
                    "What are you doing that you should stop?",
                    "Who is your real competition (not just direct competitors)?",
                    "What does your best customer love about you?",
                    "What would your employees say about your culture?",
                    "What's the biggest bet you're not making?",
                    "What would it take to 10x your business?",
                    "What's your transformative moment waiting to happen?"
                ]
            },

            // User Profile Data Structure
            userProfile: {
                name: "",
                company: "",
                role: "",
                industry: "",
                companySize: "",
                topChallenges: [],
                powerIndexHistory: [],
                completedSimulations: [],
                savedMoments: [],
                ciSessions: []
            }
        };

        // ============================================
        // LOCALSTORAGE PERSISTENCE LAYER
        // ============================================
        const storage = {
            get: (key, defaultValue = null) => {
                try {
                    const item = localStorage.getItem(key);
                    return item ? JSON.parse(item) : defaultValue;
                } catch (error) {
                    return defaultValue;
                }
            },
            set: (key, value) => {
                try {
                    localStorage.setItem(key, JSON.stringify(value));
                    return true;
                } catch (error) {
                    return false;
                }
            },
            remove: (key) => {
                try {
                    localStorage.removeItem(key);
                    return true;
                } catch (error) {
                    return false;
                }
            }
        };

        // ============================================
        // TELEMETRY
        // ============================================
        // Usage events, batched to POST /api/events. The contract — every event
        // name, its props, and what the console does with them — is
        // docs/EVENTS_CONTRACT.md. Read it before adding an event.
        //
        // BUILD is this fork's revision. Bump it (a1 → a2) whenever this fork
        // changes in a way the numbers should be able to see; the server records
        // the assigned arm (users.variant) beside it, so reports group by arm and
        // can split by build. Its first letter IS the arm — keep it that way.
        const BUILD = 'a2';

        const uuid = () => {
            if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
            // Ancient WebViews: good enough for a client-side dedup key.
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
                const r = Math.random() * 16 | 0;
                return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
            });
        };

        const Telemetry = (() => {
            const QUEUE_KEY = 'edify_events_queue';
            const SESSION_KEY = 'edify_events_session';
            const BATCH = 40;          // events per POST
            const UNLOAD_BATCH = 100;  // the server's per-batch cap; used only on pagehide
            const FLUSH_MS = 4000;
            const MAX_QUEUE = 500;     // oldest are dropped past this — telemetry is not worth a full localStorage

            let token = null;
            let queue = (() => { const q = storage.get(QUEUE_KEY, []); return Array.isArray(q) ? q : []; })();
            let timer = null;
            let inflight = false;
            // Callbacks that get one last chance to record something before the
            // unload flush — the simulator uses this to log an abandoned run. Only
            // pagehide runs them: a tab merely going to the background is not an
            // abandonment, and firing there would count every glance at email.
            const pageHideHooks = new Set();

            // One id per browser tab, alive across reloads of that tab — and
            // rotated on sign-out, so two people sharing a tab are two sessions.
            let sessionId = (() => {
                try {
                    let s = window.sessionStorage.getItem(SESSION_KEY);
                    if (!s) { s = uuid(); window.sessionStorage.setItem(SESSION_KEY, s); }
                    return s;
                } catch (e) { return uuid(); }
            })();
            const newSession = () => {
                sessionId = uuid();
                try {
                    window.sessionStorage.setItem(SESSION_KEY, sessionId);
                    window.sessionStorage.removeItem('edify_events_session_started');
                } catch (e) { /* private mode */ }
                return sessionId;
            };

            const persist = () => { storage.set(QUEUE_KEY, queue); };
            const schedule = () => {
                if (timer) return;
                timer = setTimeout(() => { timer = null; flush(); }, FLUSH_MS);
            };

            const flush = async ({ keepalive = false } = {}) => {
                if (!API_URL || !token || queue.length === 0) return;
                // A normal flush yields to one already in the air. The unload flush
                // does not: the page is going, and the server dedups on id, so an
                // overlap costs nothing and a skipped send could cost the batch.
                if (inflight && !keepalive) return;
                const batch = queue.slice(0, keepalive ? UNLOAD_BATCH : BATCH);
                inflight = true;
                try {
                    const res = await fetch(`${API_URL}/api/events`, {
                        method: 'POST',
                        keepalive,
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({ events: batch })
                    });
                    if (res.status === 401) {
                        // The token is dead. Keep the queue — the next sign-in flushes it
                        // — but stop trying with this token.
                        token = null;
                        return;
                    }
                    if (res.ok || res.status === 400) {
                        // 400 means the ENVELOPE was malformed. That is a client bug, and
                        // retrying the same bytes forever would just be a poison pill.
                        const sent = new Set(batch.map(e => e.id));
                        queue = queue.filter(e => !sent.has(e.id));
                        persist();
                        if (queue.length) schedule();
                    } else {
                        schedule(); // 429 / 5xx: try again later, keep everything
                    }
                } catch (e) {
                    schedule(); // offline
                } finally {
                    inflight = false;
                }
            };

            // Attach the identity. Nothing is sent without one; a queue built up
            // before sign-in goes the moment the token arrives.
            const setToken = (t) => { token = t || null; if (token) flush(); };

            /**
             * Record one event. `props` is anything JSON; `meta` ties it to a
             * simulation run and/or a content entity so the console can group on it.
             *   Telemetry.track('sim_started', { scenarioId: 12, source: 'today' },
             *                   { runId, entityType: 'scenario', entityId: 12 });
             */
            const track = (type, props = {}, meta = {}) => {
                if (!type) return;
                queue.push({
                    id: uuid(),
                    type,
                    props: (props && typeof props === 'object') ? props : {},
                    runId: meta.runId || null,
                    entityType: meta.entityType || null,
                    entityId: meta.entityId === undefined || meta.entityId === null ? null : String(meta.entityId),
                    sessionId,
                    build: BUILD,
                    clientTs: new Date().toISOString()
                });
                if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
                persist();
                if (queue.length >= BATCH) flush(); else schedule();
            };

            // Leaving: send what is queued while the page can still make a request.
            // keepalive lets the fetch outlive the document (Chromium, WebKit; and
            // Firefox from 133). Where it can't, the queue is in localStorage and
            // goes on the next load — nothing is lost, only delayed.
            window.addEventListener('pagehide', () => {
                if (timer) { clearTimeout(timer); timer = null; }
                pageHideHooks.forEach(fn => { try { fn(); } catch (e) { /* never block the flush */ } });
                flush({ keepalive: true });
            });
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') flush({ keepalive: true });
            });

            // Register a pagehide hook; returns the unsubscribe.
            const onPageHide = (fn) => { pageHideHooks.add(fn); return () => pageHideHooks.delete(fn); };

            return { track, flush, setToken, onPageHide, newSession, get sessionId() { return sessionId; } };
        })();

        // Cumulative-listen tracker for one <audio> element in one run.
        //
        // "Listened to 80 %" is measured, not assumed: only real playback counts,
        // as timeupdate deltas under two seconds — a seek to the end is not a
        // listen, and neither is leaving the tab open on pause. Emits:
        //   audio_play    the first time this clip plays in this run
        //   audio_80      once, the moment cumulative listening crosses 80 %
        //                 (fired at the threshold so a killed tab still records it)
        //   audio_listen  the summary, when the owner calls finish() on leaving
        // Wire it with: onPlay / onPause / onTimeUpdate / onSeeked / onLoadedMetadata
        // / onEnded, each passed the <audio> element (e.target).
        const makeListenTracker = ({ kind, source, runId, entityId, clip }) => {
            let listened = 0;      // seconds actually heard
            let last = null;       // currentTime at the previous tick, or null when not playing
            let duration = 0;
            let plays = 0;
            let playFired = false;
            let fired80 = false;
            let finished = false;
            const meta = { runId: runId || null, entityType: entityId == null ? null : 'scenario', entityId };
            // WHICH clip, on every audio_* row. Without this the events answer
            // "was audio played on this run" and nothing more — so "which CEO
            // clips actually get listened to", the question about the product's
            // core asset, had no answer at all. `clipKey` is the R2 key (stable
            // across re-publishes of the same recording); `ceo` is for reading.
            const clipId = clip && clip.key
                ? String(clip.key).replace(/^.*\/api\/audio\//, '').slice(0, 200)
                : null;
            // `why` (6 Sep 2026, Molly's cards only): cited | named | library —
            // whether library picks get listened to is the question they exist to answer.
            const who = { clipKey: clipId, ceo: (clip && clip.ceo) || null, ...(clip && clip.why ? { why: clip.why } : {}) };
            const snapshot = () => ({
                kind,
                ...who,
                listenedMs: Math.round(listened * 1000),
                durationMs: Math.round(duration * 1000),
                pct: duration > 0 ? Math.min(1, listened / duration) : 0
            });
            const learnDuration = (el) => {
                if (el && isFinite(el.duration) && el.duration > 0) duration = el.duration;
            };
            return {
                onLoadedMetadata: (el) => learnDuration(el),
                onPlay: (el) => {
                    learnDuration(el);
                    last = el.currentTime;
                    plays += 1;
                    if (!playFired) {
                        playFired = true;
                        Telemetry.track('audio_play', { kind, source, ...who, durationSec: Math.round(duration) }, meta);
                    }
                },
                onTimeUpdate: (el) => {
                    if (!el || el.paused) return;
                    learnDuration(el);
                    const t = el.currentTime;
                    if (last !== null) {
                        const d = t - last;
                        if (d > 0 && d < 2) listened += d;
                    }
                    last = t;
                    if (!fired80 && duration > 0 && listened / duration >= 0.8) {
                        fired80 = true;
                        Telemetry.track('audio_80', snapshot(), meta);
                    }
                },
                onSeeked: (el) => { last = el ? el.currentTime : null; },
                onPause: () => { last = null; },
                onEnded: () => { last = null; },
                // Idempotent, and silent when the clip was never played — a run
                // that skipped the audio produces no audio_* rows at all, which is
                // exactly how the console detects the skip.
                finish: () => {
                    if (finished || plays === 0) return;
                    finished = true;
                    Telemetry.track('audio_listen', { ...snapshot(), source, reached80: fired80, plays }, meta);
                },
                get played() { return plays > 0; }
            };
        };

        // Debounce utility for saving
        const debounce = (func, wait) => {
            let timeout;
            return (...args) => {
                clearTimeout(timeout);
                timeout = setTimeout(() => func.apply(this, args), wait);
            };
        };

        // ============================================
        // CONTEXT & STATE MANAGEMENT
        // ============================================
        const AppContext = createContext();

        const useApp = () => {
            const context = useContext(AppContext);
            if (!context) {
                throw new Error('useApp must be used within an AppProvider');
            }
            return context;
        };

        // ============================================
        // HELPER FUNCTIONS
        // ============================================

        // Get tier information based on power index score
        const getTierInfo = (score, scoreRanges) => {
            for (const range of scoreRanges) {
                if (score >= range.min && score <= range.max) {
                    return range;
                }
            }
            return scoreRanges[scoreRanges.length - 1];
        };

        // Get tier description based on tier label
        const getTierDescription = (tierLabel) => {
            const descriptions = {
                'CEO Ready': "You're thinking like a top CEO. Keep your streak going!",
                'Executive Track': "You're on the path to CEO-level decision making.",
                'Rising Leader': "Your strategic thinking is developing nicely.",
                'Emerging': "Building strong foundations for leadership.",
                'Foundation': "Every great CEO starts here. Keep learning!"
            };
            return descriptions[tierLabel] || "Continue developing your CEO mindset.";
        };

        // ============================================
        // LEADERBOARD POPUP COMPONENT
        // ============================================
        // ============================================
        // NAV ICONS — inline SVG line icons (24 viewBox, stroke 1.8, round caps)
        // ============================================
        const NAV_ICON_PATHS = {
            home: 'M3 11.5L12 4l9 7.5M5.5 10V20h13v-10',
            simulator: 'M4.5 17.5a8 8 0 1 1 15 0M12 17.5l3.5-4.5M12 17.5h.01',
            'ceo-wisdom': 'M4 5.5h16V16H10l-6 4.5V5.5zM12 9v2.5M12 13.5h.01',
            // Molly: a round bubble, three dots — a conversation in progress.
            'ci-mentor': 'M12 3.5a8.5 8.5 0 0 0-7.3 12.9L4 20.5l4.3-1.4A8.5 8.5 0 1 0 12 3.5zM8.5 12h.01M12 12h.01M15.5 12h.01',
            profile: 'M12 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM5 20a7 7 0 0 1 14 0',
            bolt: 'M13 2 3 14h9l-1 8 10-12h-9l1-8z',
            flame: 'M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z',
            trophy: 'M8 21h8M12 21v-4M17 4H7v5a5 5 0 0 0 10 0V4zM17 5h3a1 1 0 0 1 1 1c0 2.5-2 4-4 4M7 5H4a1 1 0 0 0-1 1c0 2.5 2 4 4 4',
            // A folded map: the pill that says where you are in the day.
            map: 'M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2V6zM9 4v14M15 6v14',
            // A headphone: the interview library is the corpus everything else
            // is built on, and it was reachable only from a Profile submenu.
            interviews: 'M4 14v-2a8 8 0 0 1 16 0v2M4 14h2.5a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-5zM20 14h-2.5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1H19a1 1 0 0 0 1-1v-5z',
            // An arrow leaving a box: this entry is a DOOR, not a view. The
            // portal is a different application at /mentor/.
            'mentor-portal': 'M14 4h6v6M20 4l-8.5 8.5M18 13.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5.5'
        };

        const NavIcon = ({ name, size = 21, color = 'currentColor', strokeWidth = 1.8 }) => (
            <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
                strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={NAV_ICON_PATHS[name]} />
            </svg>
        );

        // Overlays must escape the animated view containers: .ed-pagein's animation
        // creates a stacking context that would trap a fixed overlay's z-index
        // beneath the FAB/nav. Portaling to <body> keeps modals above everything.
        const ModalPortal = ({ children, onEscape }) => {
            const ref = useRef(null);
            // Captured during render: React applies the dialog's autoFocus in the
            // commit phase, before effects run, so reading activeElement in an
            // effect would capture the dialog's own button instead of the opener.
            const openerRef = useRef(null);
            if (openerRef.current === null) openerRef.current = document.activeElement;
            // The escape handler is read through a ref so the effect below can have
            // EMPTY deps.
            //
            // With `[onEscape]`, any call site passing an inline arrow re-ran this
            // effect on every render of its parent — and the effect's cleanup pulls
            // focus back to the opener while its body pushes focus to the dialog's
            // first focusable. In the feedback prompt, whose own state changes on
            // every character, that meant: type one letter, focus jumps to the "1"
            // rating button, the rest of the sentence goes nowhere, and a typed space
            // presses that button and sets a rating nobody chose. The free-text half
            // of the feedback instrument was unusable, and it is the channel the
            // pilot is read from.
            //
            // Fixing it call site by call site works until the next inline arrow.
            // Holding it in a ref makes every call site safe, including the ones not
            // written yet.
            const escapeRef = useRef(onEscape);
            escapeRef.current = onEscape;
            useEffect(() => {
                // Restore focus to whatever the user was on when the dialog closes,
                // and keep Tab inside the dialog while it is open.
                const previouslyFocused = openerRef.current;
                const onKeyDown = (e) => {
                    if (e.key === 'Escape' && escapeRef.current) { e.preventDefault(); escapeRef.current(); return; }
                    if (e.key !== 'Tab' || !ref.current) return;
                    const focusable = ref.current.querySelectorAll(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                    );
                    if (!focusable.length) return;
                    const first = focusable[0];
                    const last = focusable[focusable.length - 1];
                    if (e.shiftKey && document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    } else if (!e.shiftKey && document.activeElement === last) {
                        e.preventDefault();
                        first.focus();
                    }
                };
                document.addEventListener('keydown', onKeyDown, true);

                // Move focus IN. The trap kept it inside and the cleanup put it
                // back, but nothing ever brought it here — so a dialog opened
                // with focus still on the page behind it, and a screen-reader
                // user was told nothing had happened.
                //
                // Only when it is not already inside: StatsModal's "Got it"
                // carries autoFocus, which React applies before this effect
                // runs, and stealing that would be a regression.
                if (ref.current && !ref.current.contains(document.activeElement)) {
                    const target = ref.current.querySelector(
                        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
                    );
                    if (target) target.focus();
                    else {
                        ref.current.setAttribute('tabindex', '-1');
                        ref.current.focus();
                    }
                }

                return () => {
                    document.removeEventListener('keydown', onKeyDown, true);
                    if (previouslyFocused && previouslyFocused.isConnected && typeof previouslyFocused.focus === 'function') {
                        previouslyFocused.focus();
                    }
                };
                // Empty deps ON PURPOSE: this runs once per open. See escapeRef above.
            }, []);
            return ReactDOM.createPortal(<div ref={ref}>{children}</div>, document.body);
        };

        // Keyboard activation for role="button" surfaces: Enter AND Space
        // (Space needs preventDefault or the page scrolls).
        const btnKey = (fn) => (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(e); }
        };

        // Respect reduced motion for programmatic scrolling too — an explicit
        // behavior:'smooth' bypasses the CSS kill switch.
        const scrollBehavior = () =>
            window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

        // Format seconds as m:ss for audio-clip length/position readouts.
        const fmtTime = (secs) => {
            if (!isFinite(secs) || secs < 0) return '';
            const m = Math.floor(secs / 60);
            const s = Math.floor(secs % 60);
            return `${m}:${String(s).padStart(2, '0')}`;
        };

        // ---- Today's pick (docs/DECISION_PRACTICE.md §7.1) -------------------
        // Shared by the Home card, the Simulator and the TestRunner, so the case
        // Home advertises is the case that opens. Deterministic, and fed the same
        // App state everywhere (world, decisionProfile) — that is what keeps the
        // three agreeing. test/practice-pick.test.mjs lifts these out and pins
        // every rule.

        // A case with no options can't be decided — never serve one, or the
        // decision becomes a dead end the member can't get past.
        const isPracticeCase = (s) => !!s && s.active !== false && Array.isArray(s.options) && s.options.length > 0;

        // The member's world as every reader must see it (§3): nothing at all
        // until they have pressed Save (`contextSetAt` an ISO timestamp), then
        // only known slugs. The old edit card saved its pre-filled defaults
        // untouched, so a value without that stamp was never an answer. Mirrors
        // worldOf in cloudflare/worker/src/practice.ts, which gates Molly's
        // member block the same way.
        const PRACTICE_ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|[+-]\d{2}:?\d{2})$/;
        const worldOf = (userProfile) => {
            const p = userProfile && typeof userProfile === 'object' && !Array.isArray(userProfile) ? userProfile : {};
            const stamped = typeof p.contextSetAt === 'string' && PRACTICE_ISO_TIMESTAMP.test(p.contextSetAt) && !Number.isNaN(Date.parse(p.contextSetAt));
            if (!stamped) return { industries: [], role: null, companyStage: null, companySize: null, focusTypes: [], set: false };
            const known = (list, x) => typeof x === 'string' && list.some(([slug]) => slug === x);
            const upToThree = (xs, list) => Array.isArray(xs) ? [...new Set(xs.filter(x => known(list, x)))].slice(0, 3) : [];
            return {
                industries: upToThree(p.industries, PRACTICE_VOCAB.industries),
                role: known(PRACTICE_VOCAB.roles, p.role) ? p.role : null,
                companyStage: known(PRACTICE_VOCAB.stages, p.companyStage) ? p.companyStage : null,
                companySize: PRACTICE_VOCAB.sizes.includes(p.companySize) ? p.companySize : null,
                focusTypes: upToThree(p.focusTypes, PRACTICE_VOCAB.decisionTypes),
                set: true
            };
        };

        // Every key a world Save writes, together (the world sheet and the
        // Profile edit card both stamp contextSetAt over all of them).
        const PRACTICE_WORLD_KEYS = ['industries', 'role', 'companyStage', 'companySize', 'focusTypes', 'industry', 'contextSetAt'];

        /**
         * "Try again" on the could-not-reach-your-account banner: the server's
         * profile and the one this session kept while saving was down, merged.
         * The local copy started from the sign-in clean slate, so a key still at
         * that default (`defaults`, DATABASE.userProfile) is not an edit and
         * never overrides the server — spreading it on top saved role '' and
         * companySize '' over a member's real answers. The world is one answer
         * (§3): all of it comes from whichever side saved it last (the newer
         * contextSetAt; the server's on a tie), never a mix of the two.
         */
        const mergeRetriedProfile = (local, remote, defaults) => {
            const obj = (x) => (x && typeof x === 'object' && !Array.isArray(x) ? x : {});
            const mine = obj(local), theirs = obj(remote), base = obj(defaults);
            const own = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
            const isDefault = (k, v) => own(base, k) && JSON.stringify(v) === JSON.stringify(base[k]);
            const edits = Object.fromEntries(Object.entries(mine).filter(([k, v]) => v !== undefined && !isDefault(k, v)));
            const merged = { ...theirs, ...edits };
            const savedAt = (o) => {
                const t = typeof o.contextSetAt === 'string' ? Date.parse(o.contextSetAt) : NaN;
                return Number.isFinite(t) ? t : 0;
            };
            const world = savedAt(mine) > savedAt(theirs) ? mine : theirs;
            for (const k of PRACTICE_WORLD_KEYS) {
                if (own(world, k) && world[k] !== undefined) merged[k] = world[k];
                else delete merged[k];
            }
            return merged;
        };

        // The pick's two hints, read from a loaded decision profile: the types
        // flagged hardest by §6's rule, and the type of the case worked last.
        // While the profile is loading or unavailable there are none.
        const practiceOptsOf = (decisionProfile) => {
            const profile = decisionProfile && decisionProfile.profile;
            const history = decisionProfile && Array.isArray(decisionProfile.history) ? decisionProfile.history : [];
            return {
                struggles: profile && Array.isArray(profile.struggles)
                    ? profile.struggles.map(r => r && r.key).filter(k => typeof k === 'string')
                    : [],
                lastDecisionType: (history[0] && history[0].decisionType) || null
            };
        };

        // → { scenario, why, tier }. `why` names the rule that chose it ('unset',
        // 'industry', 'focus', 'struggle', 'travels', 'replay'); `tier` is 1–4
        // for a pick from the member's world, else null.
        //   World not set: ids 1 and 2 first, then the lowest unworked id.
        //   World set, among unworked cases, the first tier with anything in it:
        //     T1 their industry AND a focus type · T2 their industry ·
        //     T3 a focus type or a type they find hardest · T4 anything (untagged
        //     cases land here). Within a tier: a type other than the last case's,
        //     then their company stage, then the lowest id.
        //   Nothing unworked: rotate by the day, and call it a replay.
        const pickPracticeCase = (scenarios, completedIds, world, opts = {}) => {
            const eligible = (scenarios || []).filter(isPracticeCase);
            if (!eligible.length) return { scenario: null, why: null, tier: null };
            const done = new Set(completedIds || []);
            const unworked = eligible.filter(s => !done.has(s.id));
            if (!unworked.length) {
                const now = typeof opts.now === 'number' ? opts.now : Date.now();
                return { scenario: eligible[Math.floor(now / 86400000) % eligible.length], why: 'replay', tier: null };
            }
            const lowestId = (list) => [...list].sort((a, b) => a.id - b.id)[0];
            if (!world || !world.set) {
                const first = [1, 2].map(id => unworked.find(s => s.id === id)).find(Boolean);
                return { scenario: first || lowestId(unworked), why: 'unset', tier: null };
            }
            const industries = world.industries || [];
            const focus = world.focusTypes || [];
            const struggles = opts.struggles || [];
            const inIndustry = (s) => s.industry != null && industries.includes(s.industry);
            const inFocus = (s) => s.decisionType != null && focus.includes(s.decisionType);
            const inStruggles = (s) => s.decisionType != null && struggles.includes(s.decisionType);
            const tiers = [
                (s) => inIndustry(s) && inFocus(s),
                (s) => inIndustry(s),
                (s) => inFocus(s) || inStruggles(s),
                () => true
            ];
            const last = opts.lastDecisionType || null;
            const rank = (s) => [
                last && s.decisionType === last ? 1 : 0,
                world.companyStage && s.companyStage === world.companyStage ? 0 : 1,
                s.id
            ];
            const before = (a, b) => {
                const ra = rank(a), rb = rank(b);
                for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return ra[i] - rb[i];
                return 0;
            };
            for (let t = 0; t < tiers.length; t++) {
                const inTier = unworked.filter(tiers[t]);
                if (!inTier.length) continue;
                const scenario = [...inTier].sort(before)[0];
                const why = t < 2 ? 'industry' : t === 2 ? (inFocus(scenario) ? 'focus' : 'struggle') : 'travels';
                return { scenario, why, tier: t + 1 };
            }
            return { scenario: null, why: null, tier: null };
        };

        // The line under today's pick that says why it was picked (§7.1). Pure,
        // so the Home card, the landing and the picker say the same thing.
        const whyLineFor = (result, world) => {
            const s = result && result.scenario;
            if (!s) return null;
            const type = decisionTypeLabel(s.decisionType);
            const aType = type ? `${/^[aeiou]/i.test(type) ? 'An' : 'A'} ${type} decision` : null;
            switch (result.why) {
                case 'unset': return 'Tell us your world and cases come from it';
                case 'replay': return 'You\'ve worked every case — play one again';
                case 'industry': {
                    const label = industryLabel(s.industry);
                    return label ? `From your industry: ${label}` : null;
                }
                case 'focus': return aType ? `${aType} — one of your focus areas` : null;
                case 'struggle': return aType ? `${aType} — the kind you've found hardest` : null;
                case 'travels': {
                    const home = world && Array.isArray(world.industries) ? industryLabel(world.industries[0]) : null;
                    if (!home) return type ? `${aType} — it travels` : 'The decision travels';
                    return type ? `Nothing new from ${home} — this ${type} decision travels` : 'Outside your industries — the decision travels';
                }
                default: return null;
            }
        };

        // Scenario game modes. 'match' (default when scenarioType is absent) =
        // guess what the CEO actually did; 'spotBad' = real decisions from the
        // same desk, pick the one that backfired. Scoring is identical in both
        // modes: actualChoice always holds the "correct pick" option id.
        const isSpotBad = (s) => !!s && s.scenarioType === 'spotBad';

        // The day's stops, in the day's order (docs/design/05-guided-member-flow.md):
        // Today, Cases, Molly, Ask a CEO, You. Molly had no entry at all — she was
        // reached from a Home card, a Profile row and the inside of a case.
        const NAV_ITEMS = [
            { id: 'home', label: 'Today' },
            { id: 'simulator', label: 'Cases' },
            { id: 'ci-mentor', label: 'Molly', molly: true },
            // The interview library is the corpus the whole product is built on
            // — 181 recordings, 173 CEOs — and it had no entry in the nav at
            // all: you reached it through a row in the Profile menu, if you knew.
            { id: 'interviews', label: 'Interviews', adminOnly: true },
            { id: 'ceo-wisdom', label: 'Ask a CEO' },
            // Not a view — a door out to the CEO-mentor portal, for the people
            // who hold that hat. It was a row four items down the Profile menu,
            // which is a strange place to keep the screen someone signs in to
            // do their job.
            { id: 'mentor-portal', label: 'Mentor desk', door: 'portal' },
            { id: 'profile', label: 'You' }
        ];

        /**
         * The library is admin-only for now.
         *
         * The `interviews` table has been empty in production since the schema
         * created it, so every library door — the nav item, the Profile rows,
         * the fallback link on an empty Home — led to a screen that could only
         * ever say "being prepared". It is built and it works; it has nothing in
         * it yet. Admins keep the door so the entries can be checked as they are
         * published, and the day the library has content this flag is one line
         * to remove.
         */
        const canSeeLibrary = (user) => otherDoors(user).console;
        const navItemsFor = (user) => NAV_ITEMS.filter(i =>
            (!i.adminOnly || canSeeLibrary(user)) &&
            // Molly's tab goes when Molly is off for this member (users.mentor_enabled).
            (!i.molly || !user || user.mentorEnabled !== false) &&
            // A door only exists for someone who holds the hat behind it. Under
            // impersonation the principal wears the member hat alone, by design
            // (routes/impersonate.ts), so this correctly does not appear.
            (!i.door || otherDoors(user)[i.door]));

        // Each of the three stops introduces itself the first time a member opens
        // it (23 Oct 2026). Keyed by the view id; the heading is the stop's own
        // name, and the product's is read from brand.json like everywhere else.
        // Seen-ness lives in the profile blob (`userProfile.tabIntrosSeen`), so
        // it follows the member across devices and sign-ins; see TabIntroModal.
        const TAB_INTROS = {
            simulator: {
                title: 'Decision Simulator',
                body: 'Your 3-minute daily challenge: Can you identify which decision a top CEO actually made? Unearth hidden gems — often counterintuitive — behind-the-scenes decisions that transformed companies and careers.'
            },
            'ci-mentor': {
                title: 'Ask Molly — Your Personal AI CEO Guide',
                body: 'Think deeper. Decide smarter. Engage in interactive AI conversations powered by verified CEO wisdom — not just AI. Discover verbatim insights and hear top CEOs reveal, in their own voices, how they tackled challenges like yours.'
            },
            'ceo-wisdom': {
                title: `${BRAND.name} Advisors`,
                body: 'Need more than AI? Ask a top CEO directly. Get personalized, real-world wisdom from an experienced CEO advisor whose identity remains anonymous.'
            }
        };

        // ============================================
        // STATS MODAL — one overlay treatment for Power Index + Day Streak
        // ============================================
        /**
         * "We could not load this" — the third state every list needs.
         *
         * The library screens collapsed their error path into their EMPTY
         * state: a dropped connection, a 500 or an expired token produced "The
         * interview library is being prepared. Check back shortly." or "No
         * playlists yet." A user who had built ten playlists watched them
         * vanish and was told they had never made any. That is a confident
         * statement about someone's own data, and it was wrong.
         */
        /**
         * What was wrong with that reply — and, separately, whether anyone at
         * Edify may read the conversation it came from.
         *
         * The tick is the whole privacy design (docs/MOLLY_REVIEW.md): without
         * it the report is the reason chip and these words and nothing else.
         * It is worded as what it does, not as a permission the product wants:
         * the default is unticked, and going back to 👍 takes it away again.
         */
        const MENTOR_REPORT_REASONS = [
            { key: 'wrong', label: 'It got the facts wrong' },
            { key: 'not_grounded', label: 'It made something up' },
            { key: 'generic', label: 'Too generic to use' },
            { key: 'tone', label: 'Wrong tone' },
            { key: 'other', label: 'Something else' }
        ];

        // ============================================
        // PRIVACY — the promise, and what an advisor sees (tester feedback,
        // 30 Sep 2026: "Not having front and center a privacy policy with clear
        // confidentiality and anonymity assured ... made me less comfortable in
        // asking meaningful questions"). The level is enforced by the worker
        // (src/sharing.ts) and frozen on each question when it is asked; these
        // words must say exactly what that code discloses.
        // ============================================
        const SHARE_LEVELS = [
            { id: 'anonymized', label: 'Anonymized business specifics', tag: 'Recommended',
                body: 'Advisors see your industry, company size and stage under a code name. Never your name or your company.' },
            { id: 'full', label: 'Full disclosure',
                body: 'Advisors see your name, your company and your business details.' },
            { id: 'identity', label: 'Identity only',
                body: 'Advisors see your name and role. No company, no business details.' }
        ];
        const shareLevelOf = (id) => SHARE_LEVELS.find(l => l.id === id) || SHARE_LEVELS[0];
        const PRIVACY_PROMISE = `What you ask and tell ${BRAND.name} is confidential. We never share your questions, your conversations with Molly or your business details outside ${BRAND.name} unless you choose to.`;

        const SharingChoice = ({ value, onChange, disabled = false }) => (
            <div role="radiogroup" aria-label="What CEO advisors see about you" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {SHARE_LEVELS.map(l => {
                    const on = value === l.id;
                    const pick = () => { if (!disabled) onChange(l.id); };
                    return (
                        <div key={l.id} role="radio" aria-checked={on} aria-disabled={disabled || undefined} tabIndex={0}
                            data-testid={`share-${l.id}`}
                            onClick={pick} onKeyDown={btnKey(pick)}
                            style={{
                                display: 'flex', gap: '12px', alignItems: 'flex-start', textAlign: 'left',
                                padding: '12px 14px', borderRadius: '12px', cursor: disabled ? 'default' : 'pointer',
                                border: `1.5px solid ${on ? 'var(--gold-dark)' : 'var(--border-input)'}`,
                                background: on ? 'var(--cream-dark)' : 'var(--white)', opacity: disabled ? 0.6 : 1
                            }}>
                            <span aria-hidden="true" style={{
                                flexShrink: 0, width: '16px', height: '16px', marginTop: '2px', borderRadius: '50%',
                                border: `2px solid ${on ? 'var(--gold-dark)' : 'var(--border-input)'}`,
                                boxShadow: on ? 'inset 0 0 0 3px var(--white)' : 'none',
                                background: on ? 'var(--gold-dark)' : 'transparent', boxSizing: 'border-box'
                            }} />
                            <span style={{ minWidth: 0 }}>
                                <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--navy)' }}>{l.label}</span>
                                {l.tag && <span style={{ marginLeft: '8px', fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--gold-darker)' }}>{l.tag}</span>}
                                <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: '2px' }}>{l.body}</span>
                            </span>
                        </div>
                    );
                })}
            </div>
        );

        // Asked once, on the first sign-in after 0029 (share_level NULL), before
        // anything else: the feedback was that trust has to be built "as soon as
        // people login for the first time". "Decide later" keeps the default
        // (anonymized) and asks again next time; nothing is stored until they
        // choose.
        const PrivacyWelcome = ({ onLater }) => {
            const { saveShareLevel } = useApp();
            const [choice, setChoice] = useState('anonymized');
            const [saving, setSaving] = useState(false);
            const [failed, setFailed] = useState(false);
            const go = async () => {
                setSaving(true); setFailed(false);
                const ok = await saveShareLevel(choice);
                setSaving(false);
                if (!ok) setFailed(true);
            };
            const bullet = { display: 'flex', gap: '10px', fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.55, marginBottom: '8px' };
            return (
                <ModalPortal onEscape={onLater}>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                        <div role="dialog" aria-modal="true" aria-labelledby="ed-privacy-title" data-testid="privacy-welcome" className="ed-pagein"
                            style={{ width: '100%', maxWidth: '500px', maxHeight: '92vh', overflowY: 'auto', background: 'var(--white)', borderRadius: '20px', boxShadow: 'var(--shadow-modal)', padding: '26px 24px 22px', boxSizing: 'border-box' }}>
                            <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '6px' }}>Before you begin</div>
                            <h2 id="ed-privacy-title" style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', lineHeight: 1.3, color: 'var(--navy)', fontWeight: 600, margin: '0 0 10px' }}>Your privacy comes first.</h2>
                            <p style={{ fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.6, margin: '0 0 14px', fontWeight: 600 }}>{PRIVACY_PROMISE}</p>
                            <div style={bullet}><span aria-hidden="true">🔒</span><span>Molly is private to you. CEO advisors never see your conversations with her.</span></div>
                            <div style={bullet}><span aria-hidden="true">🔒</span><span>When you ask a real CEO, you decide what they see about you. You can change this any time under Data &amp; privacy.</span></div>
                            <div style={{ ...bullet, marginBottom: '18px' }}><span aria-hidden="true">🔒</span><span>The more specific your question, the better the answer — so be candid. To stay anonymous, leave names out of the question itself.</span></div>
                            {/* The reason to say more (Marco, 30 Oct 2026). True because
                                Molly keeps a profile of each member and tunes it from what
                                they tell her (docs/PERSONAL_ASSISTANT.md); keep these words
                                to what that does. */}
                            <div data-testid="privacy-welcome-memory" style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderLeft: '3px solid var(--gold)', borderRadius: '12px', padding: '12px 14px', marginBottom: '18px' }}>
                                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--navy)', marginBottom: '3px' }}>Molly remembers you.</div>
                                <div style={{ fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.55 }}>
                                    The more you tell her about yourself, your company and what you are working on, the better she knows you. She remembers it from one conversation to the next and tailors every answer to you. See or change what she knows any time under <em>Your prompt</em>.
                                </div>
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)', marginBottom: '8px' }}>When you ask a CEO, what should they see?</div>
                            <SharingChoice value={choice} onChange={setChoice} disabled={saving} />
                            {failed && (
                                <div role="alert" style={{ marginTop: '12px', fontSize: '12.5px', color: 'var(--error-ink)' }}>That did not save. Check your connection and try again.</div>
                            )}
                            <button type="button" className="ed-btn ed-btn-navy" data-testid="privacy-welcome-save" onClick={go} disabled={saving}
                                style={{ width: '100%', marginTop: '18px', fontWeight: 600 }}>
                                {saving ? 'Saving…' : 'Continue'}
                            </button>
                            <button type="button" onClick={onLater} data-testid="privacy-welcome-later"
                                style={{ width: '100%', marginTop: '8px', background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '12.5px', cursor: 'pointer', padding: '8px', fontFamily: 'inherit' }}>
                                Decide later — anonymized until I choose
                            </button>
                        </div>
                    </div>
                </ModalPortal>
            );
        };

        // A stop's introduction, shown once: the first time the member opens
        // that tab. One dialog for the three (TAB_INTROS), the same treatment as
        // PrivacyWelcome. Closing it by any route — the button, the backdrop,
        // Escape — is what marks it seen; the App owns that (closeTabIntro) so
        // the mark lands in the synced profile rather than this device alone.
        const TabIntroModal = ({ tab, onClose }) => {
            const intro = TAB_INTROS[tab];
            useEffect(() => {
                Telemetry.track('ui_open', { what: 'tab_intro' }, { entityType: 'view', entityId: tab });
            }, [tab]);
            if (!intro) return null;
            return (
                <ModalPortal onEscape={onClose}>
                    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                        <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ed-tab-intro-title" data-testid={`tab-intro-${tab}`} className="ed-pagein"
                            style={{ width: '100%', maxWidth: '460px', maxHeight: '92vh', overflowY: 'auto', background: 'var(--white)', borderRadius: '20px', boxShadow: 'var(--shadow-modal)', padding: '26px 24px 22px', boxSizing: 'border-box' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                                <div aria-hidden="true" style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <NavIcon name={tab} size={22} color="var(--gold)" strokeWidth={2} />
                                </div>
                                <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 }}>Welcome</div>
                            </div>
                            <h2 id="ed-tab-intro-title" style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', lineHeight: 1.3, color: 'var(--navy)', fontWeight: 600, margin: '0 0 12px' }}>{intro.title}</h2>
                            <p style={{ fontSize: '15px', color: 'var(--text-body)', lineHeight: 1.6, margin: '0 0 20px' }}>{intro.body}</p>
                            <button type="button" className="ed-btn ed-btn-navy" data-testid="tab-intro-close" onClick={onClose}
                                style={{ width: '100%', fontWeight: 600 }}>
                                Got it
                            </button>
                        </div>
                    </div>
                </ModalPortal>
            );
        };

        const MentorReportPanel = ({ onSubmit, onCancel }) => {
            const [reason, setReason] = useState(null);
            const [note, setNote] = useState('');
            const [share, setShare] = useState(false);
            const [sending, setSending] = useState(false);

            return (
                <ModalPortal onEscape={onCancel}>
                    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
                        <div onClick={(e) => e.stopPropagation()}
                            role="dialog" aria-modal="true" aria-label="Report this reply"
                            data-testid="mentor-report"
                            className="ed-pagein"
                            style={{ width: '100%', maxWidth: '420px', margin: 'auto', background: 'var(--white)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-modal)' }}>
                            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '19px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 4px' }}>
                                What was wrong with it?
                            </h3>
                            <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', margin: '0 0 16px', lineHeight: 1.5 }}>
                                This goes to the team that trains Molly.
                            </p>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                                {MENTOR_REPORT_REASONS.map(r => (
                                    <span key={r.key}
                                        onClick={() => setReason(r.key)}
                                        onKeyDown={btnKey(() => setReason(r.key))}
                                        role="radio" aria-checked={reason === r.key} tabIndex={0}
                                        style={{
                                            fontSize: '13px', padding: '8px 12px', borderRadius: '999px', cursor: 'pointer',
                                            border: `1px solid ${reason === r.key ? 'var(--gold-dark)' : 'var(--border-input)'}`,
                                            background: reason === r.key ? 'var(--cream-dark)' : 'transparent',
                                            color: reason === r.key ? 'var(--navy)' : 'var(--text-secondary)',
                                            fontWeight: reason === r.key ? 600 : 500
                                        }}>{r.label}</span>
                                ))}
                            </div>

                            <label htmlFor="mentor-report-note" style={{ display: 'block', fontSize: '12.5px', color: 'var(--text-tertiary)', marginBottom: '6px' }}>
                                Anything else? (optional)
                            </label>
                            <textarea id="mentor-report-note" rows={3} className="ed-input"
                                value={note} onChange={(e) => setNote(e.target.value)}
                                style={{ width: '100%', borderRadius: '12px', padding: '12px 14px', resize: 'none', lineHeight: 1.6, fontFamily: 'inherit', marginBottom: '16px' }} />

                            <div
                                onClick={() => setShare(v => !v)}
                                onKeyDown={btnKey(() => setShare(v => !v))}
                                role="checkbox" aria-checked={share} tabIndex={0}
                                data-testid="mentor-report-share"
                                style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', cursor: 'pointer', padding: '12px', borderRadius: '12px', background: 'var(--cream-dark)', marginBottom: '18px' }}>
                                <div style={{ width: '19px', height: '19px', flexShrink: 0, marginTop: '1px', borderRadius: '6px', border: `1.5px solid ${share ? 'var(--success-ink)' : 'var(--text-faint)'}`, background: share ? 'var(--success-ink)' : 'transparent', color: 'var(--cream)', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{share ? '✓' : ''}</div>
                                <span style={{ fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.55 }}>
                                    Let the {BRAND.name} team read this conversation so they can fix it.
                                    <span style={{ display: 'block', color: 'var(--text-tertiary)', fontSize: '12px', marginTop: '3px' }}>
                                        Off by default. Nobody can open it unless you tick this, and changing your rating back takes it away again.
                                    </span>
                                </span>
                            </div>

                            <button
                                onClick={async () => { setSending(true); await onSubmit({ reason, note: note.trim(), share }); }}
                                disabled={sending}
                                className="ed-btn ed-btn-navy" style={{ width: '100%', fontWeight: 600 }}>
                                {sending ? 'Sending…' : 'Send'}
                            </button>
                            <div onClick={onCancel} onKeyDown={btnKey(onCancel)} role="button" tabIndex={0}
                                style={{ marginTop: '12px', textAlign: 'center', fontSize: '13px', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '8px' }}>
                                Not now
                            </div>
                        </div>
                    </div>
                </ModalPortal>
            );
        };

        const LoadFailedCard = ({ what, onRetry }) => (
            <div data-testid="load-failed" role="status" style={{
                background: 'var(--white)', border: '1px solid var(--gold)', borderRadius: '14px',
                padding: '22px', textAlign: 'center'
            }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    We could not load {what}. This is us, not you — nothing has been lost.
                </div>
                <div
                    onClick={onRetry} onKeyDown={btnKey(onRetry)} role="button" tabIndex={0}
                    style={{ marginTop: '12px', display: 'inline-block', color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer', padding: '6px 10px' }}>
                    Try again
                </div>
            </div>
        );

        const StatsModal = ({ kind, onClose }) => {
            const { powerIndex, streak, authToken, completedSimulations } = useApp();
            useEffect(() => {
                Telemetry.track('ui_open', { what: kind === 'index' ? 'stats_index' : 'stats_streak' });
            }, []);
            // Real standings from /api/leaderboard: signed-in users who have played
            // at least one case, ranked alongside seeded demo entries so the board
            // reads as a cohort before there is enough real usage. The seed below
            // is the offline fallback and mirrors the demo_leaderboard rows.
            const [leaderboard, setLeaderboard] = useState(() => [
                { id: 1, name: 'Sarah M.', score: 96 }, { id: 2, name: 'James K.', score: 94 },
                { id: 3, name: 'Michelle T.', score: 91 }, { id: 4, name: 'David R.', score: 88 },
                { id: 5, name: 'Emily C.', score: 85 }, { id: 6, name: 'Michael P.', score: 82 },
                { id: 7, name: 'Jessica L.', score: 79 }, { id: 8, name: 'Robert W.', score: 76 },
                { id: 9, name: 'Amanda S.', score: 73 }, { id: 10, name: 'Chris B.', score: 70 },
                { id: 11, name: 'Laura H.', score: 67 }, { id: 12, name: 'Daniel F.', score: 64 }
            ]);

            // The server's own verdict on this user: score, rank and field size,
            // measured against the WHOLE board rather than the page returned.
            // Null until a real response arrives — see the ranking below for why
            // that distinction is load-bearing.
            const [you, setYou] = useState(null);

            useEffect(() => {
                if (kind !== 'index' || !API_URL || !authToken) return;
                let cancelled = false;
                fetch(`${API_URL}/api/leaderboard?limit=25`, {
                    headers: { Authorization: `Bearer ${authToken}` }
                })
                    .then(r => r.json())
                    .then(res => {
                        if (cancelled || !res || !res.success || !res.data) return;
                        if (res.data.you) setYou(res.data.you);
                        // The user's own row is rendered separately from `powerIndex`,
                        // so drop it here to avoid showing them twice.
                        setLeaderboard(res.data.entries
                            .filter(e => !e.isYou)
                            .map((e, i) => ({ id: `${e.name}-${i}`, name: e.name, score: e.score })));
                    })
                    .catch(() => { /* offline: keep the seeded board */ });
                return () => { cancelled = true; };
            }, [kind, authToken]);

            useEffect(() => {
                const onKey = (e) => { if (e.key === 'Escape') onClose(); };
                window.addEventListener('keydown', onKey);
                return () => window.removeEventListener('keydown', onKey);
            }, [onClose]);

            // The board used to drift scores at random every 3.5s to look alive.
            // Now that entries are real people, inventing movement on their names
            // would be a fabrication — the board only moves when someone plays.

            const initials = (n) => n.split(' ').map(w => w[0]).join('').replace('.', '');
            // The user's score comes from the SERVER when it answered — the same
            // column every other row on this board is ranked on. Falling back to
            // the local `powerIndex` only matters offline, where the seeded board
            // is a placeholder anyway.
            //
            // Mixing the two is what put a brand-new user at "#17 of 23" on 72:
            // their local number was a client-side default the server had never
            // seen, so the rank was computed against a field it did not belong to.
            const myScore = you ? you.score : powerIndex;
            const all = [...leaderboard.map(e => ({ ...e, isUser: false })), { id: 'user', name: 'You', score: myScore, isUser: true }]
                .sort((a, b) => b.score - a.score);
            // Rank against the whole field when the server said so; the local
            // sort only covers the page that was fetched.
            const serverRank = you && you.ranked ? you.rank : null;
            const userRank = all.findIndex(e => e.isUser) + 1;
            const boardTop = all.slice(0, 3);
            let boardContext = [];
            let contextStart = 0;
            if (userRank > 3) {
                contextStart = Math.max(3, userRank - 2);
                boardContext = all.slice(contextStart, Math.min(all.length, userRank + 1));
            }
            const above = all[userRank - 2];

            const row = (entry, rank, topThree) => (
                <div key={entry.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '10px',
                    background: entry.isUser ? 'rgba(212,182,104,0.14)' : 'transparent',
                    border: entry.isUser ? '2px solid var(--gold)' : '1px solid transparent'
                }}>
                    {topThree ? (
                        <span style={{
                            width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0,
                            background: rank === 1 ? 'var(--gold)' : rank === 2 ? 'var(--border-card)' : 'var(--gold-lighter)',
                            color: 'var(--navy)'
                        }}>{rank}</span>
                    ) : (
                        <span style={{ width: '24px', textAlign: 'center', fontSize: '12px', color: 'var(--text-tertiary)', flexShrink: 0 }}>#{rank}</span>
                    )}
                    <span style={{
                        width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0,
                        background: entry.isUser ? 'var(--navy)' : '#f0ece3',
                        color: entry.isUser ? 'var(--gold)' : 'var(--text-body)'
                    }}>{entry.isUser ? 'Y' : initials(entry.name)}</span>
                    <span style={{ flex: 1, fontSize: '13.5px', fontWeight: entry.isUser ? 700 : 500, color: entry.isUser ? 'var(--gold-darker)' : '#2b2f3a' }}>{entry.name}</span>
                    <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--navy)' }}>{entry.score}</span>
                </div>
            );

            const streakMessage = streak >= 7
                ? `${streak} consecutive days. Your decision reps are compounding — this is how instinct is built.`
                : streak >= 3
                    ? `${streak} consecutive days. Strong momentum — habits form in the second week.`
                    : streak === 1
                        ? 'One day in. Daily practice is where executive judgment sharpens.'
                        : streak === 0
                            ? 'No streak yet. Work a case today and it starts.'
                            : `${streak} consecutive days. Daily practice is where executive judgment sharpens.`;

            // The last seven days, ending today, marked from the cases actually
            // completed. This was a hardcoded literal — `mark: i < 6 ? '✓' : ''`
            // — so every user who tapped the flame saw six ticked days and
            // "today" outlined, including a brand-new account under a 56px 0.
            // It also always started the week on Monday, which was wrong for
            // anyone whose week does not.
            const DAY_LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
            const workedDays = new Set(
                (completedSimulations || [])
                    .map(c => {
                        const d = new Date(c && c.date);
                        return isNaN(d) ? null : d.toDateString();
                    })
                    .filter(Boolean)
            );
            const weekDots = Array.from({ length: 7 }, (_, i) => {
                const d = new Date();
                d.setDate(d.getDate() - (6 - i));       // oldest first, today last
                const isToday = i === 6;
                const worked = workedDays.has(d.toDateString());
                return {
                    key: i,
                    day: isToday ? 'Today' : DAY_LETTER[d.getDay()],
                    mark: worked ? '✓' : '',
                    bg: worked ? 'var(--gold)' : isToday ? 'var(--cream)' : '#f0ece3',
                    color: worked ? 'var(--navy)' : 'var(--text-meta)',
                    border: isToday ? 'var(--gold-dark)' : 'transparent'
                };
            });

            return (
                <ModalPortal onEscape={onClose}>
                <div onClick={onClose} style={{
                    position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                    <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
                        aria-label={kind === 'index' ? 'CEO Power Index' : 'Day Streak'} className="ed-pagein"
                        style={{ width: '100%', maxWidth: '340px', maxHeight: '86vh', overflowY: 'auto', background: 'var(--white)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-modal)' }}>
                        {kind === 'index' && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <NavIcon name="trophy" size={20} color="var(--gold)" strokeWidth={2} />
                                    </div>
                                    <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '19px', color: 'var(--navy)', fontWeight: 600, margin: 0 }}>CEO Power Index</h3>
                                    <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, color: 'var(--success-ink)', letterSpacing: '0.06em' }}>
                                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--success-ink)', animation: 'edPulse 2s infinite' }}></span>LIVE
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(135deg, var(--navy), var(--navy-light))', borderRadius: '14px', padding: '16px 18px', marginBottom: '16px' }}>
                                    <div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-on-navy-muted)', marginBottom: '2px' }}>Your score</div>
                                        <div style={{ fontFamily: 'var(--font-heading)', fontSize: '30px', fontWeight: 700, color: 'var(--gold)', lineHeight: 1 }}>{myScore}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '11px', color: 'var(--text-on-navy-muted)', marginBottom: '2px' }}>Your rank</div>
                                        {/* Unranked until they have played a case — the server says so
                                            explicitly. A number here for someone with no attempts is the
                                            fabrication this whole modal used to commit. */}
                                        {you && !you.ranked ? (
                                            <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-on-navy-muted)', lineHeight: 1.3 }}>
                                                Unranked<br />
                                                <span style={{ fontSize: '12px', fontWeight: 500 }}>Play a case to join the board</span>
                                            </div>
                                        ) : (
                                            <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--white)', lineHeight: 1 }}>
                                                #{serverRank || userRank} <span style={{ fontSize: '13px', color: 'var(--text-on-navy-muted)', fontWeight: 500 }}>of {you ? you.fieldSize : all.length}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-tertiary)', fontWeight: 700 }}>Leaderboard</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '6px' }}>
                                    {boardTop.map((e, i) => row(e, i + 1, true))}
                                    {boardContext.length > 0 && (
                                        <>
                                            <div style={{ textAlign: 'center', color: 'var(--text-faint)', fontSize: '11px', padding: '2px' }}>• • •</div>
                                            {boardContext.map((e, i) => row(e, contextStart + i + 1, false))}
                                        </>
                                    )}
                                </div>
                                <div style={{ background: 'var(--cream-dark)', borderRadius: '10px', padding: '11px 14px', marginBottom: '14px', fontSize: '12.5px', color: 'var(--text-body)', textAlign: 'center' }}>
                                    {/* An unranked user is not "top of their cohort". The
                                        header said Unranked and this line then told them
                                        they held the top rank, or how far they were from
                                        passing someone — on a board they are not on. */}
                                    {you && !you.ranked
                                        ? 'Work a case and you join the board.'
                                        : above
                                            ? `${above.score - myScore + 1} points to pass ${above.name}`
                                            : 'You hold the top rank in your cohort.'}
                                </div>
                            </>
                        )}
                        {kind === 'streak' && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '14px' }}>
                                    <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: 'linear-gradient(135deg, var(--flame-from), var(--flame-to))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <NavIcon name="flame" size={20} color="var(--white)" strokeWidth={2} />
                                    </div>
                                    <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '19px', color: 'var(--navy)', fontWeight: 600, margin: 0 }}>Day Streak</h3>
                                </div>
                                <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                                    <span style={{ fontFamily: 'var(--font-heading)', fontSize: '56px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1 }}>{streak}</span>
                                    <span style={{ fontSize: '17px', color: 'var(--text-tertiary)', marginLeft: '5px' }}>days</span>
                                </div>
                                <p style={{ textAlign: 'center', fontSize: '14.5px', color: 'var(--text-body)', lineHeight: 1.6, margin: '0 0 12px' }}>{streakMessage}</p>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px', padding: '0 6px' }}>
                                    {weekDots.map(d => (
                                        <div key={d.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                                            <div style={{ width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', background: d.bg, color: d.color, border: `1px solid ${d.border}` }}>{d.mark}</div>
                                            <span style={{ fontSize: '10px', color: 'var(--text-meta)' }}>{d.day}</span>
                                        </div>
                                    ))}
                                </div>
                                <p style={{ textAlign: 'center', fontSize: '12.5px', color: 'var(--text-meta)', fontStyle: 'italic', lineHeight: 1.55, margin: '0 0 18px' }}>
                                    A daily case keeps executive judgment sharp — consistency is the discipline.
                                </p>
                            </>
                        )}
                        <button autoFocus className="ed-btn ed-btn-gold" style={{ width: '100%', borderRadius: '12px', minHeight: '46px', fontWeight: 600 }} onClick={onClose}>
                            Got it
                        </button>
                    </div>
                </div>
                </ModalPortal>
            );
        };

        // ============================================
        // HEADER — sticky translucent topbar (mobile)
        // ============================================
        // `barRef`: the App measures the bar, so <main> can keep a jump to a
        // heading from landing underneath it (scroll-padding-top).
        // The pill (docs/design/05): one control in the header on every screen —
        // practice days so far and where the member is in the day — that opens
        // "Where you are today". It replaces the index and streak pills; the
        // index and the streak live on You, with the rest of the record.
        const WhereSheet = ({ onClose }) => {
            const { world, decisionProfile, mentorEnabled, practicePhase, notifications, notifUnseen, savedNextMoves, launchCase, setCurrentView,
                setMentorOpening, setJournalFocus, database, completedSimulations, currentUser } = useApp();
            useEffect(() => { Telemetry.track('ui_open', { what: 'where' }); }, []);
            const record = decisionProfile && decisionProfile.profile ? decisionProfile : null;
            const today = todayFromRecord(record, practiceLocalDay());
            const stops = practiceStops({ today, phase: practicePhase, mentorEnabled });
            const history = record && Array.isArray(record.history) ? record.history : [];
            const worked = today && today.decided ? (history.find(h => h && Number(h.attemptId) === Number(today.lastAttemptId)) || null) : null;
            // The same eligibility filter and the same pick as Home's hero, so the
            // sheet names the case that actually opens.
            const eligible = ((database && database.decisionSimulator && database.decisionSimulator.scenarios) || []).filter(
                s => s && s.active !== false && Array.isArray(s.options) && s.options.length > 0
            );
            const pick = pickPracticeCase(eligible, (completedSimulations || []).map(x => x.id), world, practiceOptsOf(decisionProfile)).scenario;
            const caseTitle = worked ? worked.title : (today && today.decided ? ((eligible.find(x => x.id === today.lastScenarioId) || {}).title || null) : (pick ? pick.title : null));
            const yourCall = worked && worked.chosen ? `you chose ${worked.chosen}` : null;
            const impersonating = !!(currentUser && currentUser.impersonatedBy);
            const answered = notifUnseen > 0 ? (notifications || []).find(x => x && x.kind === 'question.answered' && !x.seen) || null : null;
            const lastMove = (savedNextMoves || []).find(m => m && !m.done && typeof m.source === 'string' && m.source.startsWith('Case ·')) || null;
            const go = (fn) => () => { onClose(); fn(); };
            const openCase = go(() => { if (pick) launchCase(pick.id, 'home'); else setCurrentView('simulator'); });
            const toJournal = go(() => { if (today && today.lastAttemptId != null) setJournalFocus(today.lastAttemptId); setCurrentView('profile'); });
            const talk = go(() => {
                const opening = worked ? revisitOpening(worked) : null;
                if (opening && !(today && today.discussed)) setMentorOpening(opening);
                setCurrentView('ci-mentor');
            });
            const ask = go(() => setCurrentView('ceo-wisdom'));
            const actionFor = (stop, i) => {
                if (!stops) return null;
                if (i === 0) return stop.done ? { label: 'Done', to: null } : { label: stop.now && practicePhase ? 'Back to the case' : 'Open the case file', to: openCase };
                if (i === 1 || i === 2) return stop.done ? { label: 'In your journal', to: toJournal } : stop.now ? { label: 'Back to the case', to: openCase } : { label: 'After you decide', to: null };
                if (!stops.decided) return { label: 'After the takeaway', to: null };
                return stop.done ? { label: 'Keep going with Molly', to: talk } : { label: 'Talk it through with Molly', to: talk };
            };
            const row = { display: 'flex', alignItems: 'center', gap: '12px', minHeight: '52px', padding: '6px 10px', borderRadius: '12px' };
            const mark = (stop, i) => (
                <span aria-hidden="true" style={{ flex: 'none', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, boxSizing: 'border-box',
                    background: stop.done ? 'var(--success-ink)' : stop.now ? 'var(--navy)' : 'transparent',
                    color: stop.done || stop.now ? 'var(--white)' : 'var(--text-secondary)',
                    border: stop.done || stop.now ? 'none' : `1.5px ${stop.optional ? 'dashed' : 'solid'} var(--border-card)` }}>{stop.done ? '✓' : i + 1}</span>
            );
            return (
                <ModalPortal onEscape={onClose}>
                    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                        <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ed-where-title" data-testid="where-sheet" className="ed-pagein"
                            style={{ width: '100%', maxWidth: '560px', maxHeight: '88vh', overflowY: 'auto', background: 'var(--white)', borderRadius: '20px 20px 0 0', boxShadow: 'var(--shadow-modal)', padding: '14px 20px 24px', display: 'flex', flexDirection: 'column', gap: '12px', boxSizing: 'border-box' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <h3 id="ed-where-title" style={{ fontFamily: 'var(--font-heading)', fontSize: '21px', color: 'var(--navy)', fontWeight: 600, margin: 0 }}>Where you are today</h3>
                                <button type="button" onClick={onClose} aria-label="Close" autoFocus className="ed-btn ed-btn-quiet" style={{ width: '44px', height: '44px', padding: 0, borderRadius: '50%', fontSize: '18px', lineHeight: 1 }}>×</button>
                            </div>
                            {/* A bare case title under "Where you are today" reads as an
                                instruction — "Remote the New Acquisition" is a real stored
                                title, and on its own it parses as an order rather than the
                                name of the thing you are working. So the line says what the
                                title IS, and whether it is still ahead of them or behind. */}
                            <div style={{ fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.5 }} data-testid="where-line">
                                {stops ? (caseTitle ? (
                                    <>
                                        <span style={{ color: 'var(--text-tertiary)' }}>{today && today.decided ? 'The case you worked:' : 'Today’s case:'}</span>
                                        {' '}
                                        <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{caseTitle}</span>
                                        {yourCall ? <span style={{ color: 'var(--text-secondary)' }}>{` · ${yourCall}`}</span> : null}
                                    </>
                                ) : 'Today’s case is being picked for you.') : 'Your record is loading.'}
                            </div>
                            {stops && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }} data-testid="where-stops">
                                    {stops.stops.map((stop, i) => {
                                        const act = actionFor(stop, i);
                                        const inner = (
                                            <>
                                                {mark(stop, i)}
                                                <span style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontSize: '15px', fontWeight: 700, color: stop.optional && !stop.done && !stop.now ? 'var(--text-secondary)' : 'var(--navy)' }}>{stop.name}{stop.optional ? ' · optional' : ''}</span>
                                                    <span style={{ fontSize: '12px', color: stop.done ? 'var(--success-ink)' : 'var(--text-secondary)' }}>{stop.now ? 'You are here' : act ? act.label : ''}</span>
                                                </span>
                                                {act && act.to && <span aria-hidden="true" style={{ color: 'var(--gold-darker)', fontWeight: 700 }}>→</span>}
                                            </>
                                        );
                                        return act && act.to
                                            ? <button key={stop.name} type="button" onClick={act.to} data-testid={`where-stop-${i}`} style={{ ...row, background: stop.now ? 'var(--gold-transparent)' : 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: 'var(--font-body)' }}>{inner}</button>
                                            : <div key={stop.name} data-testid={`where-stop-${i}`} style={{ ...row, background: stop.now ? 'var(--gold-transparent)' : 'transparent' }}>{inner}</div>;
                                    })}
                                    {stops.decided && (
                                        <button type="button" onClick={ask} data-testid="where-ask" style={{ ...row, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: 'var(--font-body)', color: 'var(--navy)', fontSize: '14px', fontWeight: 600, paddingLeft: '50px' }}>Ask a real CEO about this case →</button>
                                    )}
                                </div>
                            )}
                            {(answered || lastMove) && (
                                <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 }}>Also waiting for you</div>
                                    {answered && (
                                        <button type="button" onClick={go(() => setCurrentView('ceo-wisdom'))} data-testid="where-answered" style={{ ...row, minHeight: '44px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: 'var(--font-body)', color: 'var(--navy)', fontSize: '14px', fontWeight: 600 }}>A CEO answered your question →</button>
                                    )}
                                    {lastMove && (
                                        <button type="button" onClick={go(() => setCurrentView('home'))} data-testid="where-move" style={{ ...row, minHeight: '44px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', width: '100%', fontFamily: 'var(--font-body)', color: 'var(--navy)', fontSize: '14px', fontWeight: 600 }}>Your last move: did you do it? →</button>
                                    )}
                                </div>
                            )}
                            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{impersonating ? 'Viewing as this member.' : 'Tap the pill at the top of any screen to come back here.'}</div>
                        </div>
                    </div>
                </ModalPortal>
            );
        };

        const WherePill = () => {
            const { decisionProfile, mentorEnabled, practicePhase } = useApp();
            const [open, setOpen] = useState(false);
            const record = decisionProfile && decisionProfile.profile ? decisionProfile : null;
            const days = record && record.profile.totals && typeof record.profile.totals.days === 'number' ? record.profile.totals.days : null;
            const stops = practiceStops({ today: todayFromRecord(record, practiceLocalDay()), phase: practicePhase, mentorEnabled });
            const head = days === null ? 'Today' : `${days} day${days === 1 ? '' : 's'}`;
            const sub = stops ? stops.label : null;
            return (
                <>
                    <button type="button" onClick={() => setOpen(true)} data-testid="where-pill"
                        aria-label={`Where you are today${days === null ? '' : `: ${days} practice day${days === 1 ? '' : 's'}`}${sub ? `, ${sub}` : ''}`}
                        className="ed-hover-gold-border"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: '40px', padding: '0 12px 0 10px', borderRadius: '999px', background: 'var(--white)', border: '1px solid var(--border-card)', color: 'var(--navy)', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                        <NavIcon name="map" size={15} color="var(--gold-darker)" strokeWidth={2} />
                        <span>{head}</span>
                        {sub && <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{sub}</span>}
                    </button>
                    {open && <WhereSheet onClose={() => setOpen(false)} />}
                </>
            );
        };

        const Header = ({ barRef = null } = {}) => {
            return (
                <>
                    <header ref={barRef} style={{
                        position: 'sticky', top: 0, zIndex: 'var(--z-header)',
                        background: 'rgba(250, 248, 245, 0.94)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                        borderBottom: '1px solid var(--border-card)', padding: '14px 20px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}>
                        <BrandLockup size={28} />
                        {/* One pill, not two (docs/design/05): where you are in the day.
                            The index and the streak are on You. */}
                        <WherePill />
                    </header>
                </>
            );
        };

        // ============================================
        // SIDEBAR — desktop (≥980px) replaces topbar + bottom nav
        // ============================================
        const Sidebar = () => {
            const { currentView, setCurrentView, currentUser, userProfile, handleLogout, requestFeedback, notifUnseen } = useApp();
            const navItems = navItemsFor(currentUser);
            const displayName = (currentUser && currentUser.name) || (userProfile && userProfile.name) || 'Executive';

            return (
                <>
                <nav aria-label="Main navigation" style={{
                    width: '248px', flexShrink: 0, borderRight: '1px solid var(--border-card)', background: 'var(--cream)',
                    padding: '28px 20px', display: 'flex', flexDirection: 'column', gap: '4px',
                    position: 'sticky', top: 0, height: '100%', boxSizing: 'border-box', overflowY: 'auto'
                }}>
                    <BrandLockup size={32} style={{ margin: '4px 4px 28px' }} />
                    {navItems.map(item => {
                        // A door is never the current page: pressing it leaves.
                        const active = !item.door && currentView === item.id;
                        const go = () => (item.door ? openDoor(item.door) : setCurrentView(item.id));
                        return (
                            <div key={item.id}
                                onClick={go}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
                                role="button" tabIndex={0} aria-current={active ? 'page' : undefined}
                                className="ed-sidebar-item"
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '11px', padding: '11px 14px', borderRadius: '10px',
                                    cursor: 'pointer', fontSize: '14.5px', fontWeight: active ? 700 : 500,
                                    color: active ? 'var(--navy)' : 'var(--text-tertiary)',
                                    background: active ? '#f0ece3' : 'transparent'
                                }}>
                                <NavIcon name={item.id} size={19} color={active ? 'var(--gold-dark)' : 'var(--text-tertiary)'} />
                                <span style={{ flex: 1 }}>{item.label}</span>
                                {/* An answer from a real CEO arrives hours later,
                                    on a screen nobody is looking at. Without a
                                    mark here it is found by accident. */}
                                {item.id === 'ceo-wisdom' && notifUnseen > 0 && (
                                    <span data-testid="nav-unseen" aria-label={`${notifUnseen} new`}
                                        style={{
                                            minWidth: '19px', height: '19px', borderRadius: '10px', padding: '0 6px',
                                            background: 'var(--claret)', color: '#fff', fontSize: '11.5px', fontWeight: 700,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                                        }}>{notifUnseen}</span>
                                )}
                            </div>
                        );
                    })}
                    {/* Molly is in the nav now, with the day's other stops. The
                        pill (docs/design/05) replaces the index and streak pills;
                        both numbers live on You. */}
                    <div style={{ marginTop: 'auto', paddingTop: '18px', padding: '18px 0 0 6px' }}>
                        <WherePill />
                    </div>
                    <div style={{ marginTop: '18px', fontSize: '12px', color: 'var(--text-meta)', padding: '0 14px' }}>
                        <div style={{ marginBottom: '6px' }}>{displayName}</div>
                        {/* Desktop's always-on feedback entry — the mobile one lives
                            in the Profile menu. Records whatever view they're on. */}
                        <div
                            onClick={() => requestFeedback('manual', { type: 'view', id: currentView, extra: { view: currentView } }, { force: true })}
                            onKeyDown={btnKey(() => requestFeedback('manual', { type: 'view', id: currentView, extra: { view: currentView } }, { force: true }))}
                            role="button" tabIndex={0} className="ed-link" data-testid="sidebar-feedback"
                            style={{ cursor: 'pointer', marginBottom: '6px' }}>Send feedback</div>
                        {/* The other doors this person holds a hat for
                            (docs/IDENTITY.md). The mentor's is up in the nav
                            now, with an icon, because it is the screen they
                            sign in to work in — not a footnote under Sign out. */}
                        {otherDoors(currentUser).console && (
                            <div onClick={() => openDoor('console')} onKeyDown={btnKey(() => openDoor('console'))} role="button" tabIndex={0} className="ed-link" data-testid="door-console" style={{ cursor: 'pointer', marginBottom: '6px' }}>Admin console</div>
                        )}
                        <div onClick={handleLogout} onKeyDown={btnKey(() => handleLogout())} role="button" tabIndex={0} className="ed-link" style={{ cursor: 'pointer' }}>Sign out</div>
                    </div>
                </nav>
                </>
            );
        };

        // ============================================
        // FLOATING MOLLY PILL (mobile FAB)
        // ============================================
        const MollyFab = () => {
            const { setCurrentView } = useApp();
            return (
                <div
                    onClick={() => setCurrentView('ci-mentor')}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCurrentView('ci-mentor'); } }}
                    role="button" tabIndex={0} aria-label="Ask Molly, your CI mentor"
                    className="ed-hover-navy" data-testid="molly-fab"
                    style={{
                        position: 'fixed', right: '16px', bottom: '96px', display: 'flex', alignItems: 'center', gap: '9px',
                        padding: '8px 16px 8px 8px', borderRadius: '999px', background: 'var(--navy)',
                        boxShadow: 'var(--shadow-fab)', cursor: 'pointer', zIndex: 'var(--z-fab)',
                        border: '1.5px solid rgba(212, 182, 104, 0.5)', minHeight: '48px', boxSizing: 'border-box'
                    }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 600, flexShrink: 0 }}>M</div>
                    <div style={{ lineHeight: 1.15 }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-on-navy)' }}>Ask Molly</div>
                        <div style={{ fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)' }}>Your CI mentor</div>
                    </div>
                </div>
            );
        };

        // ============================================
        // BOTTOM NAV — fixed, translucent (mobile)
        // ============================================
        const BottomNav = () => {
            const { currentView, setCurrentView, notifUnseen, currentUser } = useApp();
            const navItems = navItemsFor(currentUser);

            return (
                <nav style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 'var(--z-nav)',
                    background: 'rgba(250, 248, 245, 0.96)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
                    borderTop: '1px solid var(--border-card)',
                    display: 'flex', justifyContent: 'space-around',
                    padding: '6px 8px calc(10px + env(safe-area-inset-bottom, 0px))'
                }} aria-label="Main navigation">
                    {navItems.map(item => {
                        const isActive = !item.door && currentView === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => (item.door ? openDoor(item.door) : setCurrentView(item.id))}
                                className="nav-item"
                                style={{
                                    minWidth: '64px', minHeight: '48px', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center', gap: '3px',
                                    background: 'transparent', border: 'none', cursor: 'pointer'
                                }}
                                aria-label={item.id === 'ceo-wisdom' && notifUnseen > 0
                                    ? `${item.label}, ${notifUnseen} new` : item.label}
                                aria-current={isActive ? 'page' : undefined}
                            >
                                <span style={{ position: 'relative', display: 'inline-flex' }}>
                                    <NavIcon name={item.id} size={21} color={isActive ? 'var(--gold-dark)' : 'var(--text-tertiary)'} />
                                    {item.id === 'ceo-wisdom' && notifUnseen > 0 && (
                                        <span data-testid="nav-unseen" aria-hidden="true" style={{
                                            position: 'absolute', top: '-4px', right: '-7px',
                                            minWidth: '16px', height: '16px', borderRadius: '9px', padding: '0 4px',
                                            background: 'var(--claret)', color: '#fff', fontSize: '10.5px', fontWeight: 700,
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1
                                        }}>{notifUnseen}</span>
                                    )}
                                </span>
                                <span style={{
                                    fontSize: '11px', letterSpacing: '0.02em', whiteSpace: 'nowrap',
                                    fontWeight: isActive ? 700 : 500,
                                    color: isActive ? 'var(--navy)' : 'var(--text-tertiary)'
                                }}>
                                    {item.label}
                                </span>
                            </button>
                        );
                    })}
                </nav>
            );
        };

        // ============================================
        // TOAST NOTIFICATION COMPONENT
        // ============================================
        const Toast = ({ message, type = 'info', onClose }) => {
            useEffect(() => {
                const timer = setTimeout(() => {
                    if (onClose) onClose();
                }, 3000);
                return () => clearTimeout(timer);
            }, [onClose]);

            const getToastStyles = () => {
                switch (type) {
                    case 'success':
                        return { background: 'var(--success)', icon: 'fa-check-circle' };
                    case 'error':
                        return { background: 'var(--error)', icon: 'fa-exclamation-circle' };
                    case 'warning':
                        return { background: 'var(--warning)', icon: 'fa-exclamation-triangle' };
                    default:
                        return { background: 'var(--navy)', icon: 'fa-info-circle' };
                }
            };

            const { background, icon } = getToastStyles();

            return (
                <div
                    className="toast-animate-in"
                    role="alert"
                    aria-live="polite"
                    style={{
                        position: 'fixed',
                        top: 'calc(var(--header-height) + var(--space-4))',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background,
                        color: 'var(--white)',
                        padding: 'var(--space-3) var(--space-5)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-3)',
                        zIndex: 'var(--z-toast)',
                        maxWidth: 'calc(100% - 40px)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)'
                    }}
                >
                    <i className={`fa-solid ${icon}`} />
                    <span>{message}</span>
                </div>
            );
        };

        // ============================================
        // FEEDBACK PROMPT
        // ============================================
        // Rendered once, at app level, for whichever surface asked. It is the same
        // three questions everywhere — the surface only decides the noun in Q2 and
        // the context recorded with the answer.
        const FeedbackPrompt = ({ request, onClose }) => {
            const { authToken, handleSessionExpired, showToast } = useApp();
            const surface = FEEDBACK_SURFACES[request.surface] || FEEDBACK_SURFACES.manual;
            const questions = feedbackQuestions(surface.subject);
            const [answers, setAnswers] = useState({});
            const [sending, setSending] = useState(false);
            const submittedRef = useRef(false);

            // Prompted on mount; dismissed on unmount unless Send was pressed.
            // Unmount is the one place every close path converges — backdrop,
            // Escape, Not now — so the two events cannot disagree.
            // The simulator passes the run it is asking about; the three events
            // carry it as run_id so a rating can be joined to the run (the Pulse
            // contract). Other surfaces have no run and send null.
            const feedbackMeta = { runId: (request.context && request.context.runId) || null };
            useEffect(() => {
                const ctxId = request.context && request.context.id != null ? String(request.context.id) : null;
                Telemetry.track('feedback_prompted', { surface: request.surface, contextId: ctxId }, feedbackMeta);
                return () => {
                    if (!submittedRef.current) {
                        Telemetry.track('feedback_dismissed', { surface: request.surface, contextId: ctxId }, feedbackMeta);
                    }
                };
            }, []);

            useEffect(() => {
                // Escape dismisses — a feedback prompt must never feel like a trap.
                const onKey = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
                window.addEventListener('keydown', onKey);
                return () => window.removeEventListener('keydown', onKey);
            }, [onClose, sending]);

            const setAnswer = (id, value) => setAnswers(prev => ({ ...prev, [id]: value }));
            // Ratings are what make A and B comparable, so Send waits for at least
            // one of them; the free text stays optional.
            const canSend = questions.some(q => q.type === 'rating' && answers[q.id]);

            const submit = async () => {
                if (sending || !canSend) return;
                setSending(true);
                const ctx = request.context || {};
                const payload = {
                    surface: request.surface,
                    contextType: ctx.type || surface.contextType,
                    contextId: ctx.id === undefined || ctx.id === null ? null : String(ctx.id),
                    contextLabel: ctx.label || null,
                    contextExtra: ctx.extra || null,
                    // The BUILD the user actually saw (fork revision, e.g. 'a1').
                    // Their assigned variant is read server-side from their account —
                    // if the two ever disagree, that itself is the finding.
                    appVersion: BUILD,
                    answers: questions.map(q => ({
                        id: q.id, type: q.type, question: q.question,
                        value: q.type === 'rating'
                            ? (answers[q.id] || null)
                            : (answers[q.id] || '').trim()
                    }))
                };
                // "Submitted" is the press of Send, whether or not the network
                // agrees — that is what the prompt-to-answer rate should measure.
                submittedRef.current = true;
                Telemetry.track('feedback_submitted', {
                    surface: request.surface,
                    contextId: payload.contextId,
                    ratingInsight: answers.insight || null,
                    ratingIntuitive: answers.intuitive || null,
                    hasText: !!(answers.improvements && String(answers.improvements).trim())
                }, feedbackMeta);
                try {
                    const res = await fetch(`${API_URL}/api/feedback`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify(payload)
                    });
                    if (res.status === 401) { handleSessionExpired(); return; }
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    showToast('Thank you — that goes straight to the team.', 'success');
                } catch (e) {
                    // Their words are already gone from the screen by the time this
                    // resolves; say so rather than pretending it was received.
                    console.warn('[edify] feedback send failed:', e);
                    showToast('Your feedback could not be sent. Please try again later.', 'warning');
                }
                onClose();
            };

            const ratingRow = (q) => (
                <div role="radiogroup" aria-label={q.question}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {[1, 2, 3, 4, 5].map(n => {
                            const on = answers[q.id] === n;
                            return (
                                <button
                                    key={n}
                                    type="button"
                                    role="radio"
                                    aria-checked={on}
                                    onClick={() => setAnswer(q.id, n)}
                                    data-testid={`feedback-${q.id}-${n}`}
                                    style={{
                                        flex: 1, minHeight: '46px', borderRadius: '11px', cursor: 'pointer',
                                        fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 600,
                                        background: on ? 'var(--navy)' : 'var(--white)',
                                        color: on ? 'var(--text-on-navy)' : 'var(--navy)',
                                        border: `1px solid ${on ? 'var(--navy)' : 'var(--border-card)'}`,
                                        transition: 'background 0.15s ease, border-color 0.15s ease'
                                    }}>
                                    {n}
                                </button>
                            );
                        })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '7px', fontSize: '11.5px', color: 'var(--text-meta)' }}>
                        <span>{q.low}</span><span>{q.high}</span>
                    </div>
                </div>
            );

            return (
                <ModalPortal onEscape={() => { if (!sending) onClose(); }}>
                    <div
                        onClick={() => { if (!sending) onClose(); }}
                        style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}>
                        <div
                            onClick={(e) => e.stopPropagation()}
                            role="dialog" aria-modal="true" aria-label="Share your feedback"
                            data-testid="feedback-prompt"
                            className="ed-pagein"
                            style={{ width: '100%', maxWidth: '420px', margin: 'auto', background: 'var(--white)', borderRadius: '20px', padding: '26px 24px', boxShadow: 'var(--shadow-modal)' }}>

                            <div style={{ fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '8px' }}>Your read on this</div>
                            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '21px', lineHeight: 1.3, color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                                Two ratings and a thought.
                            </h3>
                            {request.context && request.context.label && (
                                <p data-testid="feedback-context" style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', margin: '0 0 18px', lineHeight: 1.5 }}>
                                    On: <span style={{ color: 'var(--text-body)', fontWeight: 600 }}>{request.context.label}</span>
                                </p>
                            )}
                            {(!request.context || !request.context.label) && (
                                <p style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', margin: '0 0 18px', lineHeight: 1.5 }}>
                                    It shapes what we build next.
                                </p>
                            )}

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                {questions.map(q => (
                                    <div key={q.id}>
                                        <div style={{ fontSize: '13.5px', color: 'var(--navy)', fontWeight: 600, lineHeight: 1.5, marginBottom: '10px' }}>
                                            {q.question}
                                        </div>
                                        {q.type === 'rating' ? ratingRow(q) : (
                                            <textarea
                                                value={answers[q.id] || ''}
                                                onChange={(e) => setAnswer(q.id, e.target.value.slice(0, 4000))}
                                                rows={3}
                                                placeholder={q.placeholder}
                                                data-testid={`feedback-${q.id}`}
                                                className="ed-input"
                                                style={{ borderRadius: '12px', padding: '12px 14px', resize: 'none', lineHeight: 1.55, fontFamily: 'inherit', fontSize: '14px' }}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>

                            <button
                                type="button"
                                onClick={submit}
                                disabled={!canSend || sending}
                                data-testid="feedback-send"
                                className="ed-btn ed-btn-navy"
                                style={{ marginTop: '24px', width: '100%', fontWeight: 600, opacity: canSend ? 1 : 0.5 }}>
                                {sending ? 'Sending…' : 'Send feedback'}
                            </button>
                            <div
                                onClick={() => { if (!sending) onClose(); }}
                                onKeyDown={btnKey(() => { if (!sending) onClose(); })}
                                role="button" tabIndex={0} data-testid="feedback-skip"
                                style={{ marginTop: '12px', textAlign: 'center', fontSize: '13.5px', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '8px' }}>
                                Not now
                            </div>
                        </div>
                    </div>
                </ModalPortal>
            );
        };

        // ============================================
        // DECISION PRACTICE — HOME, YOUR WORLD, YOUR PROFILE (docs/DECISION_PRACTICE.md §7.2, §7.3, §7.5)
        // ============================================
        // Module scope, like the run's pieces: the journal entry holds a note
        // field, and a component declared inside ProfileView would be a new
        // type on every render — the field would drop focus mid-word. The pure
        // helpers are lifted out and pinned by test/practice-home-profile.test.mjs.

        // The old edit card's values, read as a suggestion, never as an answer.
        // It pre-filled CEO / Healthcare / 50–200 people and saved them
        // untouched, so a stored "Healthcare" may be nobody's choice: the
        // editors may start from the mapped slug, and only Save makes it count.
        // "Consumer & retail" maps to nothing (it is two industries now), and the
        // size is not carried at all — 50–200 was the default.
        const LEGACY_ROLE_SLUGS = { 'CEO': 'ceo', 'President': 'ceo', 'Founder': 'founder', 'COO': 'csuite', 'Managing Director': 'csuite' };
        const LEGACY_INDUSTRY_SLUGS = { 'Technology': 'technology', 'Healthcare': 'healthcare', 'Financial services': 'financial', 'Manufacturing': 'manufacturing', 'Professional services': 'professional' };

        // What the world editors open on: the saved world once there is one,
        // else the mapped legacy role and industry. Never a default.
        const worldDraftFrom = (userProfile) => {
            const p = userProfile && typeof userProfile === 'object' && !Array.isArray(userProfile) ? userProfile : {};
            const w = worldOf(p);
            if (w.set) return { industries: w.industries, role: w.role, companyStage: w.companyStage, companySize: w.companySize, focusTypes: w.focusTypes };
            const own = (map, k) => typeof k === 'string' && Object.prototype.hasOwnProperty.call(map, k) ? map[k] : null;
            const industry = own(LEGACY_INDUSTRY_SLUGS, p.industry);
            return { industries: industry ? [industry] : [], role: own(LEGACY_ROLE_SLUGS, p.role), companyStage: null, companySize: null, focusTypes: [] };
        };

        // "Technology · Manufacturing & Industrial · Founder / Owner · Scaling",
        // or null while the world is not set (or set to nothing).
        const worldLineOf = (world) => {
            if (!world || !world.set) return null;
            return [...(world.industries || []).map(industryLabel), roleLabel(world.role), stageLabel(world.companyStage)]
                .filter(Boolean).join(' · ') || null;
        };

        // context_set — counts and booleans only, read back through worldOf so
        // they count what the readers will count. The values themselves live in
        // the profile blob, which account deletion removes; an event outlives it.
        const trackContextSet = (userProfile, place) => {
            const w = worldOf(userProfile);
            Telemetry.track('context_set', { industriesN: w.industries.length, focusN: w.focusTypes.length, roleSet: !!w.role, stageSet: !!w.companyStage, sizeSet: !!w.companySize, place });
        };

        // "A People & culture decision", "An Operations & execution decision".
        const aDecisionOf = (typeLabel, lower = false) => {
            if (!typeLabel) return null;
            const article = /^[aeiou]/i.test(typeLabel) ? 'An' : 'A';
            return `${lower ? article.toLowerCase() : article} ${typeLabel} decision`;
        };

        // The member's local day, YYYY-MM-DD, the way GET /api/decision-profile
        // stamps `today.date`: in the time zone the App sends as ?tz= (the
        // browser's), UTC when it has none. Null if it cannot be worked out.
        const practiceLocalDay = (now = new Date()) => {
            try {
                let tz = '';
                try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { /* UTC, as the worker would */ }
                const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
                const part = (type) => (parts.find(p => p.type === type) || {}).value;
                const day = `${part('year')}-${part('month')}-${part('day')}`;
                return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
            } catch (e) {
                return null;
            }
        };

        // A `today` is about the day it was fetched on. One from an EARLIER local
        // day (the tab stayed open past midnight) says nothing about today, and
        // is read as unknown until it is fetched again.
        const todayFromRecord = (record, localDay) => {
            const today = record && record.today && typeof record.today === 'object' ? record.today : null;
            if (!today) return null;
            return localDay && typeof today.date === 'string' && today.date < localDay ? null : today;
        };

        // Today's state line on the Home card, from the server's `today`:
        // "Decide ✓ · Takeaway · Molly (optional)". The Takeaway is ticked by a
        // saved reflection, Molly by a conversation from that case today — the
        // two things the record can actually see. Null while `today` is unknown:
        // nothing about today is guessed from the local blob.
        const todayStateLine = (today, mentorEnabled) => {
            if (!today || typeof today !== 'object') return null;
            const tick = (on) => on ? ' ✓' : '';
            const parts = [`Decide${tick(today.decided)}`, `Takeaway${tick(today.decided && today.reflected)}`];
            if (mentorEnabled) parts.push(today.decided && today.discussed ? 'Molly ✓' : 'Molly (optional)');
            return parts.join(' · ');
        };

        // Where the member is in the day (docs/design/05, "the pill"): the four
        // stops, which are done, which is now, and what comes next. From a live
        // run's phase when a case is open, else from the record's `today`. Pure,
        // so it is one helper for the pill, the sheet and the tests. `null` while
        // nothing is known (the record has not loaded): the pill then says "Today".
        const PRACTICE_STOPS = ['Decide', 'The CEO', 'Takeaway', 'Keep going'];
        const practiceStops = ({ today, phase, mentorEnabled }) => {
            const n = mentorEnabled ? 4 : 3;
            const names = PRACTICE_STOPS.slice(0, n);
            const state = (doneUpto, current) => names.map((name, i) => ({
                name, done: i <= doneUpto, now: i === current, optional: mentorEnabled && i === 3
            }));
            const live = phase === 'situation' || phase === 'decision' ? 0
                : phase === 'reveal' ? 1 : phase === 'lesson' ? 2 : phase === 'handoff' ? 3 : null;
            if (live !== null) {
                const current = Math.min(live, n - 1);
                return { known: true, decided: live >= 1, label: `${current + 1} of ${n} · ${names[current]}`,
                    next: current + 1 < n ? names[current + 1] : null, stops: state(current - 1, current) };
            }
            if (!today || typeof today !== 'object') return null;
            if (!today.decided) return { known: true, decided: false, label: `1 of ${n} · Decide`, next: 'The CEO', stops: state(-1, 0) };
            const talked = !!(mentorEnabled && today.discussed);
            return { known: true, decided: true, label: 'Done for today', next: mentorEnabled && !talked ? 'Molly, a note, or tomorrow' : 'a note, or tomorrow',
                stops: state(talked ? 3 : 2, -1) };
        };

        // "Next up: a People & culture decision from Manufacturing & Industrial" —
        // what Another case opens (the next pick), without the case itself.
        const nextUpLine = (scenario) => {
            if (!scenario) return null;
            const type = aDecisionOf(decisionTypeLabel(scenario.decisionType), true);
            const industry = industryLabel(scenario.industry);
            if (type && industry) return `Next up: ${type} from ${industry}`;
            if (type) return `Next up: ${type}`;
            if (industry) return `Next up: a case from ${industry}`;
            return scenario.title ? `Next up: ${scenario.title}` : null;
        };

        // The case a question was asked about, read back from its attachment
        // (caseAttachmentText's first line). The notification carries only the
        // question id, and a member's question list no scenario id — so the title
        // comes from the text the member sent, or not at all.
        const caseTitleFromContext = (context) => {
            const first = typeof context === 'string' ? context.split('\n')[0] : '';
            // "From a <name> case" under whatever the product was called when it was sent:
            // questions already in the queue keep the name they were written with.
            const m = first.match(/^From an? [^"]+? case: "(.+)"(?: \([^()]*\))?$/);
            return m ? m[1] : null;
        };

        // The nearest read still to open, in words (§6 `unlocks`; see nearestUnlock).
        const nearestUnlockLine = (profile) => {
            const nearest = nearestUnlock(profile && profile.unlocks);
            return nearest ? practiceUnlockLine(nearest) : null;
        };

        // "Strongest so far: People & culture (6 of 7)" — only for rows §6's
        // rule flagged. Counts, never a rate.
        const flaggedTypesLine = (rows, lead) => {
            const named = (Array.isArray(rows) ? rows : [])
                .filter(r => r && decisionTypeLabel(r.key) && typeof r.called === 'number' && typeof r.n === 'number')
                .map(r => `${decisionTypeLabel(r.key)} (${r.called} of ${r.n})`);
            return named.length ? `${lead}: ${named.join('; ')}` : null;
        };

        // Picture so far: "12 decisions · 4 of 9 decision types · 6 days · 2
        // talked through with Molly · 1 put to a real CEO (1 answered) · 3 moves
        // saved (1 done)". The moves are the client's; everything else is the
        // server's record. A part the record doesn't know is left out, not zeroed.
        const pictureSoFarLine = (profile, moves, { mentorEnabled = true } = {}) => {
            const t = profile && profile.totals;
            if (!t) return null;
            const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
            const types = (Array.isArray(profile.byType) ? profile.byType : []).filter(r => r && r.n > 0 && decisionTypeLabel(r.key)).length;
            const list = Array.isArray(moves) ? moves.filter(Boolean) : [];
            const q = t.ceoQuestions;
            return [
                typeof t.decisions === 'number' ? count(t.decisions, 'decision', 'decisions') : null,
                `${types} of ${PRACTICE_VOCAB.decisionTypes.length} decision types`,
                typeof t.days === 'number' ? count(t.days, 'day', 'days') : null,
                typeof t.discussedCases === 'number' && (mentorEnabled || t.discussedCases > 0) ? `${t.discussedCases} talked through with Molly` : null,
                q && typeof q.asked === 'number' ? `${q.asked} put to a real CEO (${q.answered || 0} answered)` : null,
                `${count(list.length, 'move', 'moves')} saved (${list.filter(m => m.done).length} done)`
            ].filter(Boolean).join(' · ');
        };

        // The reads a record opens, in the order Profile shows them.
        const PROFILE_READS = [
            ['strengths', 'The decision types you call best and find hardest'],
            ['passedOver', 'When you differed'],
            ['confidence', 'Sure vs. right'],
            ['trend', 'Earlier vs. recent cases'],
            ['secondThoughts', 'After the reveal']
        ];

        // When you differed (§6 passedOver): per axis, [up, down] in words. The
        // CEOs' calls in this library are usually the boldest on the table, so
        // this compares the member's call with the OTHER option they passed over
        // — never with the CEO's.
        const PASSED_OVER_AXES = [
            ['risk', 'Boldness', 'bolder', 'more protective', 'equally bold'],
            ['reach', 'Bringing others in', 'more inclusive', 'more directive', 'equally inclusive'],
            ['horizon', 'Time horizon', 'longer-term', 'nearer-term', 'equally long-term']
        ];
        const passedOverLines = (passedOver) => {
            if (!passedOver || typeof passedOver !== 'object') return [];
            return PASSED_OVER_AXES.map(([axis, title, up, down, level]) => {
                const a = passedOver[axis];
                if (!a || typeof a.n !== 'number' || a.n < 1) return null;
                const lead = 'When your call differed from the CEO\'s,';
                const line = a.up > a.down ? `${lead} it was the ${up} of the two remaining options ${a.up} of ${a.n} times.`
                    : a.down > a.up ? `${lead} it was the ${down} of the two remaining options ${a.down} of ${a.n} times.`
                    : a.up > 0 ? `${lead} it was the ${up} of the two remaining options ${a.up} of ${a.n} times, and the ${down} ${a.down} of ${a.n}.`
                    : `${lead} the two remaining options were ${level} ${a.same} of ${a.n} times.`;
                const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
                return { axis, title, line, detail: `${cap(up)}: ${a.up} · ${cap(down)}: ${a.down} · Level: ${a.same}` };
            }).filter(Boolean);
        };

        // Sure vs. right: count lines only — no sentence about what they mean.
        const confidenceLine = (confidence) => {
            if (!confidence || typeof confidence !== 'object') return null;
            return [['Sure', confidence.sure], ['Leaning', confidence.leaning], ['Guessing', confidence.guessing]]
                .filter(([, t]) => t && typeof t.n === 'number' && t.n > 0)
                .map(([label, t]) => `${label}: ${t.called} of ${t.n} called`).join(' · ') || null;
        };

        // Earlier vs. recent cases: the two windows side by side, and no verdict.
        const trendLines = (trend) => {
            if (!trend || !trend.earlier || !trend.recent) return null;
            return [
                `Earliest ${trend.earlier.n}: ${trend.earlier.called} of ${trend.earlier.n} called`,
                `Most recent ${trend.recent.n}: ${trend.recent.called} of ${trend.recent.n} called`
            ];
        };

        // After the reveal (§6 secondThoughts).
        const secondThoughtsLine = (st) => {
            if (!st || typeof st.asked !== 'number' || st.asked < 1) return null;
            return `When your call differed, you kept yours ${st.kept} of ${st.asked} times after hearing the CEO`;
        };

        // Home's "for this session" memory: which saved move was put off, and
        // whether the decision-profile introduction has been shown. In
        // sessionStorage, stamped with the telemetry session — which rotates on
        // sign-out — so the next person on this browser starts clean. Not a
        // STORAGE_KEYS entry: nothing here is meant to outlive the tab.
        const HOME_SESSION_KEY = 'edify_home_session';
        const homeSession = {
            read() {
                try {
                    const v = JSON.parse(window.sessionStorage.getItem(HOME_SESSION_KEY) || 'null');
                    if (v && typeof v === 'object' && v.session === Telemetry.sessionId) return v;
                } catch (e) { /* private mode: nothing remembered */ }
                return { session: Telemetry.sessionId };
            },
            write(patch) {
                try { window.sessionStorage.setItem(HOME_SESSION_KEY, JSON.stringify({ ...homeSession.read(), ...patch, session: Telemetry.sessionId })); } catch (e) { /* fine */ }
            }
        };

        // A toggle chip, for the world editors.
        const PracticeChip = ({ on, disabled = false, onClick, testid, children }) => (
            <button type="button" aria-pressed={!!on} disabled={disabled} onClick={onClick} data-testid={testid}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px', minHeight: '36px', padding: '6px 13px',
                    borderRadius: '999px', fontSize: '13px', fontWeight: 600, lineHeight: 1.25, textAlign: 'left', opacity: 1,
                    cursor: disabled ? 'default' : 'pointer',
                    border: `1.5px solid ${on ? 'var(--navy)' : 'var(--border-input)'}`,
                    background: on ? 'var(--navy)' : 'var(--white)',
                    color: on ? 'var(--text-on-navy)' : disabled ? 'var(--text-faint)' : 'var(--navy)'
                }}>
                {on && <span aria-hidden="true" style={{ color: 'var(--gold)' }}>✓</span>}
                {children}
            </button>
        );

        const PRACTICE_FIELD_LEGEND = { fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-tertiary)', fontWeight: 600, marginBottom: '8px', padding: 0 };

        /**
         * The profile a world-sheet Save writes. Size and focus areas are not on
         * the sheet, so they are carried only as a saved world already had them
         * (worldOf: null and [] until then). Spreading the profile alone would
         * stamp whatever the old edit card left behind — its pre-filled
         * "50–200 people" is a real size label — and the stamp is what turns a
         * stored value into an answer Molly is told and context_set counts (§3).
         */
        const worldSheetProfile = (userProfile, draft) => {
            const prior = worldOf(userProfile);
            return {
                ...userProfile,
                industries: draft.industries.slice(0, 3),
                role: draft.role || null,
                companyStage: draft.companyStage || null,
                companySize: prior.companySize,
                focusTypes: prior.focusTypes,
                industry: industryLabel(draft.industries[0]),
                contextSetAt: new Date().toISOString()
            };
        };

        /**
         * The member's prompt (docs/design/06-who-you-are-and-ai-at-work.md,
         * build step 1): lines in their own words about who they are, each
         * with a `shared` switch, saved as a version. Molly reads the shared
         * lines under her WHO YOU ARE TALKING TO heading (practice.ts
         * memberBlock); nothing else reads it yet. Lives under `userProfile`
         * so it rides the existing autosave, and the worker keeps an admin
         * viewing as the member from rewriting it.
         */
        const MEMBER_PROMPT_MAX_LINES = 12;
        const MEMBER_PROMPT_MAX_CHARS = 240;
        const MEMBER_PROMPT_KEEP_VERSIONS = 5;
        const memberPromptOf = (userProfile) => {
            const mp = userProfile && userProfile.memberPrompt && typeof userProfile.memberPrompt === 'object' ? userProfile.memberPrompt : null;
            const lines = mp && Array.isArray(mp.lines)
                ? mp.lines.filter(l => l && typeof l === 'object' && typeof l.text === 'string')
                    .map((l, i) => ({ id: typeof l.id === 'string' && l.id ? l.id : `l${i}`, text: l.text, source: typeof l.source === 'string' && l.source ? l.source : 'you', shared: l.shared !== false }))
                : [];
            const versions = mp && Array.isArray(mp.versions) ? mp.versions.filter(v => v && typeof v === 'object' && Number.isFinite(v.version)) : [];
            return {
                version: mp && Number.isFinite(mp.version) ? mp.version : 0,
                savedAt: mp && typeof mp.savedAt === 'string' ? mp.savedAt : null,
                lines, versions
            };
        };
        // What Molly will receive of a line: one line, trimmed, cut at the cap.
        const memberPromptClean = (text) => String(text || '').replace(/\s+/g, ' ').trim().slice(0, MEMBER_PROMPT_MAX_CHARS).trim();
        const memberPromptSave = (userProfile, draftLines, now = new Date()) => {
            const prior = memberPromptOf(userProfile);
            const lines = (draftLines || [])
                .map((l, i) => ({ id: typeof l.id === 'string' && l.id ? l.id : `l${now.getTime().toString(36)}${i}`, text: memberPromptClean(l.text), source: typeof l.source === 'string' && l.source ? l.source : 'you', shared: l.shared !== false }))
                .filter(l => l.text)
                .slice(0, MEMBER_PROMPT_MAX_LINES);
            const versions = prior.version > 0
                ? [...prior.versions, { version: prior.version, savedAt: prior.savedAt, lines: prior.lines }].slice(-MEMBER_PROMPT_KEEP_VERSIONS)
                : prior.versions.slice(-MEMBER_PROMPT_KEEP_VERSIONS);
            return { ...(userProfile || {}), memberPrompt: { version: prior.version + 1, savedAt: now.toISOString(), lines, versions } };
        };

        /**
         * The world sheet (§7.3): industries, role and company stage on one
         * screen. Size and focus areas live in the Profile edit card. Save is
         * what makes the world count — it stamps `contextSetAt`, writes the
         * legacy single `industry` label, and reports context_set as counts.
         * Never rendered while an admin is viewing as the member: the App gates
         * it, and so does this.
         */
        // What the sheet can honestly promise (docs/FEEDBACK_ROADMAP.md §4): how
        // many live cases each chosen industry has today, and what happens when
        // they run out. The pick falls back to decision type once a member has
        // worked every case from their industries (pickPracticeCase, tier 3), and
        // for the thinner industries that is a couple of weeks of daily
        // practice — better said on the sheet than discovered on the day.
        // Counts come from the loaded library, so the line is right for the
        // build the member is on. Null until the library is loaded or an
        // industry is chosen; unknown slugs (a newer worker's tag) are skipped.
        const WORLD_COVERAGE_THIN = 15;
        const worldCoverageLine = (scenarios, industries) => {
            const live = (scenarios || []).filter(s => s && s.active !== false && Array.isArray(s.options) && s.options.length > 0);
            const picked = (industries || []).filter(slug => practiceLabel(PRACTICE_VOCAB.industries, slug));
            if (!live.length || !picked.length) return null;
            const countOf = (slug) => live.filter(s => s.industry === slug).length;
            const parts = picked.map(slug => `${practiceLabel(PRACTICE_VOCAB.industries, slug)} ${countOf(slug)}`);
            const total = picked.reduce((t, slug) => t + countOf(slug), 0);
            const head = `Cases from your industries today: ${parts.join(' · ')}.`;
            if (total === 0) return `${head} Until there are some, today's case comes by decision type instead.`;
            if (total < WORLD_COVERAGE_THIN) return `${head} That is about ${total} day${total === 1 ? '' : 's'} of daily practice; after that, today's case comes by decision type instead.`;
            return head;
        };

        const WorldSheet = ({ place, onClose }) => {
            const { userProfile, setUserProfile, currentUser, showToast, mentorEnabled, database } = useApp();
            const impersonating = !!(currentUser && currentUser.impersonatedBy);
            const [draft, setDraft] = useState(() => worldDraftFrom(userProfile));
            useEffect(() => {
                if (!impersonating) Telemetry.track('ui_open', { what: 'context' });
            }, []);
            if (impersonating) return null;
            const toggleIndustry = (slug) => setDraft(d => d.industries.includes(slug)
                ? { ...d, industries: d.industries.filter(x => x !== slug) }
                : d.industries.length >= 3 ? d : { ...d, industries: [...d.industries, slug] });
            const pick = (key, slug) => setDraft(d => ({ ...d, [key]: d[key] === slug ? null : slug }));
            const chosen = draft.industries.length > 0 || !!draft.role || !!draft.companyStage;
            const save = () => {
                if (!chosen) return;
                const next = worldSheetProfile(userProfile, draft);
                setUserProfile(next);
                trackContextSet(next, place);
                // Only industries decide which case comes up; role and stage
                // are for Molly, so a save of those alone does not promise cases.
                if (showToast) showToast(next.industries.length ? 'Saved — cases will come from your industries.' : 'Saved.', 'success');
                onClose();
            };
            // "Not now" from Home's prompt puts the prompt away too; from
            // anywhere else it only closes the sheet.
            const notNow = () => {
                if (place === 'home' && !(userProfile && userProfile.contextPromptDismissedAt)) {
                    setUserProfile({ ...userProfile, contextPromptDismissedAt: new Date().toISOString() });
                }
                onClose();
            };
            const full = draft.industries.length >= 3;
            const coverage = worldCoverageLine(database && database.decisionSimulator ? database.decisionSimulator.scenarios : [], draft.industries);
            return (
                <ModalPortal onEscape={onClose}>
                    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                        <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ed-world-title" aria-describedby="ed-world-sub"
                            data-testid="world-sheet" className="ed-pagein"
                            style={{ width: '100%', maxWidth: '560px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', background: 'var(--white)', borderRadius: '20px 20px 0 0', boxShadow: 'var(--shadow-modal)' }}>
                            <div style={{ padding: '20px 22px 12px', borderBottom: '1px solid var(--hairline)' }}>
                                <h3 id="ed-world-title" style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 4px' }}>Make the cases about your world</h3>
                                <p id="ed-world-sub" style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                    {mentorEnabled
                                        ? 'Today\'s decision is picked from your industries, and Molly uses your role and stage to translate each case into your world. Change it any time in Profile.'
                                        : 'Today\'s decision is picked from your industries. Change it any time in Profile.'}
                                </p>
                            </div>
                            <div style={{ overflowY: 'auto', padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                                <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
                                    <legend style={PRACTICE_FIELD_LEGEND}>Industries · up to 3</legend>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {PRACTICE_VOCAB.industries.map(([slug, label]) => {
                                            const on = draft.industries.includes(slug);
                                            return <PracticeChip key={slug} on={on} disabled={!on && full} onClick={() => toggleIndustry(slug)} testid={`world-industry-${slug}`}>{label}</PracticeChip>;
                                        })}
                                    </div>
                                    {coverage && (
                                        <p data-testid="world-coverage" aria-live="polite" style={{ margin: '10px 0 0', fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{coverage}</p>
                                    )}
                                </fieldset>
                                <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
                                    <legend style={PRACTICE_FIELD_LEGEND}>Your role</legend>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {PRACTICE_VOCAB.roles.map(([slug, label]) => (
                                            <PracticeChip key={slug} on={draft.role === slug} onClick={() => pick('role', slug)} testid={`world-role-${slug}`}>{label}</PracticeChip>
                                        ))}
                                    </div>
                                </fieldset>
                                <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
                                    <legend style={PRACTICE_FIELD_LEGEND}>Company stage</legend>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {PRACTICE_VOCAB.stages.map(([slug, label]) => (
                                            <PracticeChip key={slug} on={draft.companyStage === slug} onClick={() => pick('companyStage', slug)} testid={`world-stage-${slug}`}>{label}</PracticeChip>
                                        ))}
                                    </div>
                                </fieldset>
                            </div>
                            <div style={{ padding: '12px 22px 20px', borderTop: '1px solid var(--hairline)', display: 'flex', gap: '10px' }}>
                                <button type="button" onClick={save} disabled={!chosen} className="ed-btn ed-btn-navy" data-testid="world-save"
                                    style={{ flex: 1, padding: '12px', fontSize: '14.5px', minHeight: '46px' }}>Save</button>
                                <button type="button" onClick={notNow} className="ed-btn ed-btn-quiet" data-testid="world-not-now"
                                    style={{ padding: '12px 18px', fontSize: '14.5px', minHeight: '46px' }}>Not now</button>
                            </div>
                        </div>
                    </div>
                </ModalPortal>
            );
        };

        const JOURNAL_CONFIDENCE = { 5: 'Sure', 3: 'Leaning', 1: 'Guessing' };

        /**
         * One case in the decision journal (§7.5): when, what, the two calls,
         * how sure, what happened after, the member's own note — and the ways
         * back into it. The note text is on the row only for the member
         * themselves; the route sends `noted` instead to anyone else, so an
         * admin viewing as the member sees that a note exists and nothing more.
         */
        const JournalEntry = ({ row, scenario, mentorEnabled, impersonating, note, onOpenNote, onNoteChange, onSaveNote, onTalk, onAsk, onWorkAgain }) => {
            const spot = row.scenarioType === 'spotBad';
            const t = Date.parse(row.createdAt || '');
            const when = isFinite(t) ? new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
            const call = (id) => {
                if (!id) return null;
                const label = optionLabelOf({ options: row.options || [] }, id);
                return label ? `${id} — ${label}` : String(id);
            };
            const chips = [industryLabel(row.industry), decisionTypeLabel(row.decisionType)].filter(Boolean);
            const sure = JOURNAL_CONFIDENCE[row.confidence] || null;
            const hasNote = typeof row.note === 'string' && row.note.trim() !== '';
            const chip = { fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '999px', padding: '2px 9px' };
            const mark = { fontSize: '11.5px', fontWeight: 600, color: 'var(--success-ink)' };
            // The four ways back in (docs/design/05 §You) were bare text in a
            // wrapped row, so they read as a sentence fragment — "Add a note Talk
            // it through with Molly Ask a real CEO about this Work it again" — and
            // nothing about them said they could be pressed. Same four actions,
            // now shaped like controls: a bordered chip each, 40px of target, and
            // an icon so the row scans without being read word by word.
            const action = { fontSize: '12.5px', fontWeight: 700, color: 'var(--navy)', cursor: 'pointer', minHeight: '40px', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 13px', borderRadius: '999px', border: '1.5px solid var(--border-input)', background: 'var(--white)', fontFamily: 'var(--font-body)', lineHeight: 1, whiteSpace: 'nowrap' };
            return (
                <li data-testid={`journal-entry-${row.attemptId}`} style={{ listStyle: 'none', padding: '14px 0', borderTop: '1px solid var(--hairline)' }}>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-meta)', marginBottom: '3px' }}>
                        {[when, row.counted === false ? 'Replay — not counted again' : null].filter(Boolean).join(' · ')}
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.35 }}>{row.title || 'A case'}</div>
                    {(chips.length > 0 || spot) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '6px' }}>
                            {spot && <span style={{ ...chip, color: 'var(--text-on-claret)', background: 'var(--claret)', border: 'none' }}>Bad Call</span>}
                            {chips.map(c => <span key={c} style={chip}>{c}</span>)}
                        </div>
                    )}
                    <div data-testid="journal-calls" style={{ marginTop: '8px', fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.55 }}>
                        {/* One call, said once: the same option on two lines read
                            as two different answers. */}
                        {row.chosen && row.chosen === row.correctChoice ? (
                            <div>{spot ? 'You caught the bad call' : 'You and the CEO'}: {call(row.chosen)}</div>
                        ) : (
                            <>
                                {row.chosen && <div>{spot ? 'You flagged' : 'You'}: {call(row.chosen)}</div>}
                                {row.correctChoice && <div>{spot ? 'The bad call' : 'CEO'}: {call(row.correctChoice)}</div>}
                            </>
                        )}
                    </div>
                    {(sure || row.discussed || row.askedCeo || (!hasNote && row.noted)) && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: '6px' }}>
                            {sure && <span style={{ ...mark, color: 'var(--text-tertiary)' }}>{sure}</span>}
                            {row.discussed && <span style={mark}>Talked through</span>}
                            {row.askedCeo && <span style={mark}>Asked a CEO</span>}
                            {!hasNote && row.noted && <span style={{ ...mark, color: 'var(--text-tertiary)' }}>A private note</span>}
                        </div>
                    )}
                    {hasNote && !note.open && (
                        <div data-testid="journal-note" style={{ marginTop: '8px', background: 'var(--cream-dark)', borderRadius: '10px', padding: '9px 12px', fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                            <div style={{ fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-meta)', fontWeight: 600, marginBottom: '2px' }}>Your note · private to you</div>
                            {row.note}
                        </div>
                    )}
                    {note.open && (
                        <div style={{ marginTop: '10px' }}>
                            <ReflectNote value={note.text} saved={note.saved} status={note.status}
                                blocked={note.status === 'readonly' ? 'Read-only while viewing as this member.' : null}
                                onChange={onNoteChange} onSave={onSaveNote} />
                        </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                        {!impersonating && !note.open && (
                            <button type="button" onClick={onOpenNote} className="ed-hover-gold-border" style={action} data-testid="journal-add-note">
                                <span aria-hidden="true" style={{ color: 'var(--gold-darker)' }}>✎</span>{hasNote ? 'Edit note' : 'Add a note'}
                            </button>
                        )}
                        {mentorEnabled && row.scenarioId && (
                            <button type="button" onClick={onTalk} className="ed-hover-gold-border" style={action} data-testid="journal-talk">
                                <span aria-hidden="true" style={{ color: 'var(--gold-darker)' }}>✻</span>Talk it through with Molly
                            </button>
                        )}
                        <button type="button" onClick={onAsk} className="ed-hover-gold-border" style={action} data-testid="journal-ask">
                            <span aria-hidden="true" style={{ color: 'var(--gold-darker)' }}>☏</span>Ask a real CEO about this
                        </button>
                        {scenario && (
                            <button type="button" onClick={onWorkAgain} className="ed-hover-gold-border" style={action} data-testid="journal-work-again">
                                <span aria-hidden="true" style={{ color: 'var(--gold-darker)' }}>↻</span>Work it again
                            </button>
                        )}
                    </div>
                </li>
            );
        };

        // A titled card on the Profile, for the reads.
        const ProfileReadCard = ({ testid, title, children }) => (
            <section data-testid={testid} style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '18px 20px' }}>
                <h2 style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-meta)', fontWeight: 700, margin: '0 0 12px' }}>{title}</h2>
                {children}
            </section>
        );

        // ============================================
        // HOME VIEW COMPONENT
        // ============================================
        const HomeView = () => {
            const { setCurrentView, powerIndex, database, currentUser, userProfile, setUserProfile, homeLoading, completedSimulations, world, decisionProfile,
                profileReady, launchCase, mentorEnabled, setMentorOpening, setJournalFocus, setWorldSheetOpen, savedNextMoves, toggleNextMoveDone,
                notifications, notifUnseen, authToken, refreshDecisionProfile } = useApp();
            const scoreRanges = database.decisionSimulator.powerIndex.scoreRanges;
            const tierInfo = getTierInfo(powerIndex, scoreRanges);
            // Which band of how many, counted from the top: scoreRanges is ordered
            // best first, so CEO Ready is band 1. getTierInfo can fall through to
            // the last range, and indexOf would return -1 for a label that is not
            // in the list, so the fallback is the bottom band rather than 0.
            const tierBand = (() => {
                const i = scoreRanges.indexOf(tierInfo);
                return i >= 0 ? i + 1 : scoreRanges.length;
            })();
            const impersonating = !!(currentUser && currentUser.impersonatedBy);

            // Counted from the library actually loaded. Every figure here is
            // derivable from what the client already holds, which is why none of
            // them needed to be a claim.
            const librarySize = useLibrarySize();
            const corpus = (() => {
                const cases = (database.decisionSimulator.scenarios || []).filter(x => x && x.active !== false);
                const moments = (database.transformativeMoments?.moments || []).filter(x => x && x.active !== false);
                const named = [...cases, ...moments];
                const uniq = (key) => new Set(named.map(x => (x[key] || '').trim().toLowerCase()).filter(Boolean)).size;
                return { companies: uniq('company'), decisions: cases.length };
            })();

            // Today's case — the same deterministic pick the Simulator will open.
            // The CEO and company are never identified before the reveal, so the
            // hero shows only anonymized copy. Once today's practice is done this
            // is the NEXT pick (today's case is among the worked ones), which is
            // what "Another case" opens and "Next up" describes.
            const eligible = database.decisionSimulator.scenarios.filter(
                // same eligibility filter the Simulator uses, so the hero always
                // advertises the case that actually opens
                s => s.active !== false && Array.isArray(s.options) && s.options.length > 0
            );
            const todaysPick = pickPracticeCase(
                eligible,
                (completedSimulations || []).map(s => s.id),
                world,
                practiceOptsOf(decisionProfile)
            );
            const scenario = todaysPick.scenario;
            const heroSpot = isSpotBad(scenario);
            const whyLine = whyLineFor(todaysPick, world);

            // What the record says about today (§7.0). While it is loading or
            // could not be had, nothing is inferred from the local blob and the
            // done state is never shown.
            const record = decisionProfile && decisionProfile.profile ? decisionProfile : null;
            // Home re-renders on the notification poll, so this is checked
            // every 45 s: past local midnight yesterday's "done" is dropped and
            // the record fetched again, once per stale day.
            const localDay = practiceLocalDay();
            const today = todayFromRecord(record, localDay);
            const staleDay = record && record.today && !today ? record.today.date : null;
            useEffect(() => { if (staleDay) refreshDecisionProfile(); }, [staleDay, localDay]);
            const history = record && Array.isArray(record.history) ? record.history : [];
            const doneToday = !!(today && today.decided);
            const workedToday = doneToday ? (history.find(h => h && Number(h.attemptId) === Number(today.lastAttemptId)) || null) : null;
            const workedTodayTitle = (workedToday && workedToday.title)
                || (doneToday ? ((database.decisionSimulator.scenarios || []).find(s => s && s.id === today.lastScenarioId) || {}).title : null)
                || null;
            const stateLine = todayStateLine(today, mentorEnabled);

            // Points earned in the last 7 days (honest delta; hidden when zero)
            const weekDelta = (completedSimulations || []).reduce((sum, s) => {
                if (!s.date) return sum;
                const age = (Date.now() - new Date(s.date).getTime()) / 86400000;
                return age <= 7 && typeof s.pointsEarned === 'number' ? sum + s.pointsEarned : sum;
            }, 0);

            const firstName = ((currentUser && currentUser.name) || (userProfile && userProfile.name) || 'Executive').split(' ')[0];
            const hour = new Date().getHours();
            const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
            const todayLine = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

            // Offline awareness
            const [offline, setOffline] = useState(() => !navigator.onLine);
            useEffect(() => {
                const on = () => setOffline(false);
                const off = () => setOffline(true);
                window.addEventListener('online', on);
                window.addEventListener('offline', off);
                return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
            }, []);

            // ---- 1. A CEO answered --------------------------------------------
            // An answer to the member's own question that they have not been to
            // see. `notifUnseen` drops to 0 the moment Ask a CEO opens, so the card
            // goes with the badge rather than lingering until the next poll.
            const answered = notifUnseen > 0
                ? (notifications || []).find(n => n && n.kind === 'question.answered' && !n.seen) || null
                : null;
            const answeredId = answered ? Number(answered.entityId) || null : null;
            const [answeredCase, setAnsweredCase] = useState(null);   // { id, title|null }
            useEffect(() => {
                if (!answeredId || !API_URL || !authToken) return;
                let off = false;
                fetch(`${API_URL}/api/ceo-questions/mine`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => (r.ok ? r.json() : null))
                    .then(res => {
                        if (off || !res || !res.success || !res.data) return;
                        const q = (res.data.items || []).find(x => x && Number(x.id) === answeredId);
                        setAnsweredCase({ id: answeredId, title: caseTitleFromContext(q && q.context) });
                    })
                    .catch(() => { /* the line reads fine without the title */ });
                return () => { off = true; };
            }, [answeredId, authToken]);
            const answeredTitle = answeredCase && answeredCase.id === answeredId ? answeredCase.title : null;

            // ---- 2. Your world ------------------------------------------------
            const contextPromptDismissedAt = userProfile && userProfile.contextPromptDismissedAt;
            const showWorldCard = profileReady && !world.set && !contextPromptDismissedAt && !impersonating;
            const dismissWorldCard = () => setUserProfile({ ...userProfile, contextPromptDismissedAt: new Date().toISOString() });

            // ---- 3. From your last case ---------------------------------------
            // The newest move saved from a case and not yet done. "Still on it"
            // only puts it away for this session.
            const [session] = useState(() => homeSession.read());
            const [snoozedMove, setSnoozedMove] = useState(session.snoozedMove || null);
            const lastCaseMove = (savedNextMoves || []).find(m => m && !m.done && typeof m.source === 'string' && m.source.startsWith('Case ·')) || null;
            const showMove = !!lastCaseMove && lastCaseMove.id !== snoozedMove;
            const stillOnIt = () => {
                if (!lastCaseMove) return;
                setSnoozedMove(lastCaseMove.id);
                homeSession.write({ snoozedMove: lastCaseMove.id });
            };

            // ---- 4. Today's decision --------------------------------------------
            const [pickerOpen, setPickerOpen] = useState(false);
            // Stable for the same reason as the world sheet's: it is the picker's onEscape.
            const closePicker = useCallback(() => setPickerOpen(false), []);
            const openToday = () => { if (scenario) launchCase(scenario.id, 'home'); };
            const openPicker = () => {
                Telemetry.track('ui_open', { what: 'picker' });
                setPickerOpen(true);
            };
            const talkItThrough = () => {
                const opening = revisitOpening(workedToday);
                if (!opening) return;
                setMentorOpening(opening);
                setCurrentView('ci-mentor');
            };
            const addNote = () => {
                setJournalFocus(today.lastAttemptId);
                setCurrentView('profile');
            };
            const canTalk = doneToday && mentorEnabled && !today.discussed && !!workedToday;
            const canNote = doneToday && !today.reflected && !impersonating && today.lastAttemptId != null;
            const nextUp = doneToday ? nextUpLine(scenario) : null;
            const typeChip = scenario ? decisionTypeLabel(scenario.decisionType) : null;
            const industryChip = scenario ? industryLabel(scenario.industry) : null;

            // ---- 5. Your decision profile -----------------------------------------
            const profile = record ? record.profile : null;
            const decisions = profile && profile.totals && typeof profile.totals.decisions === 'number' ? profile.totals.decisions : null;
            const strongest = profile ? flaggedTypesLine(profile.strengths, 'Strongest so far') : null;
            const hardest = profile ? flaggedTypesLine(profile.struggles, 'Hardest so far') : null;
            const unlockLine = profile ? nearestUnlockLine(profile) : null;
            // The introduction, for someone who worked cases before a2: shown on
            // their first visit this session until they work a case in a2 (every
            // a2 lock-in stores how sure they were, so no confidence anywhere in
            // the record means they have not). Read once, at mount, so it stays
            // put for this visit and is gone on the next.
            const [introSeen] = useState(() => !!session.profileIntroSeen);
            const showIntro = !introSeen && decisions != null && decisions >= 1 && history.length > 0 && history.every(h => !h || h.confidence == null);
            useEffect(() => { if (showIntro) homeSession.write({ profileIntroSeen: true }); }, [showIntro]);

            const kicker = { fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', fontWeight: 600 };
            const onNavyButton = { minHeight: '42px', padding: '9px 16px', fontSize: '13.5px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', opacity: 1, background: 'transparent', color: 'var(--text-on-navy)', border: '1px solid rgba(255,255,255,0.32)' };
            const navyChip = { fontSize: '11px', fontWeight: 600, color: 'var(--text-on-navy-muted)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: '999px', padding: '2px 9px' };

            if (homeLoading) {
                return (
                    <div className="ed-pagein" data-testid="home-view" aria-busy="true" aria-label="Preparing your briefing"
                        style={{ padding: '26px 0 32px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                        <div className="ed-skel" style={{ width: '150px', height: '13px', borderRadius: '6px', background: 'var(--track)' }}></div>
                        <div className="ed-skel" style={{ width: '250px', height: '30px', borderRadius: '8px', background: 'var(--track)', animationDelay: '0.1s' }}></div>
                        <div className="ed-skel" style={{ height: '110px', borderRadius: '14px', background: '#f0ece3', animationDelay: '0.2s' }}></div>
                        <div className="ed-skel" style={{ height: '190px', borderRadius: '16px', background: 'var(--border-card)', animationDelay: '0.3s' }}></div>
                        <div className="ed-skel" style={{ height: '80px', borderRadius: '14px', background: '#f0ece3', animationDelay: '0.4s' }}></div>
                        <div style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-meta)' }}>Preparing your briefing…</div>
                    </div>
                );
            }

            return (
                <div className="ed-pagein" data-testid="home-view" style={{ padding: '26px 0 32px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {offline && (
                        <div role="status" style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-input)', borderRadius: '12px', padding: '13px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                            <div>
                                <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--navy)', marginBottom: '2px' }}>You're offline</div>
                                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Today's case file is available. Molly and Ask a CEO will reconnect automatically.</div>
                            </div>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--text-faint)', flexShrink: 0 }}></div>
                        </div>
                    )}

                    <section data-testid="welcome-section">
                        <div style={{ fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 600, marginBottom: '6px' }}>{todayLine}</div>
                        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '30px', color: 'var(--navy)', fontWeight: 600, margin: 0 }}>{greeting}, {firstName}.</h1>
                    </section>

                    {/* 1. A CEO answered */}
                    {answered && (
                        <div onClick={() => setCurrentView('ceo-wisdom')} onKeyDown={btnKey(() => setCurrentView('ceo-wisdom'))}
                            role="button" tabIndex={0} data-testid="home-ceo-answered" className="ed-hover-gold-border"
                            style={{ background: 'var(--white)', border: '1px solid var(--gold)', borderRadius: '14px', padding: '14px 18px', cursor: 'pointer', fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.45 }}>
                            A CEO answered your question{answeredTitle ? ` about "${answeredTitle}"` : ''} →
                        </div>
                    )}

                    {/* 2. Your world — the game is the front door.
                        docs/design/06 §"Skipping": the six rounds replace the
                        world sheet as the first-run step, and the short sheet
                        stays for a member who will not play, so the pick and
                        Molly keep working either way. It was built and then
                        never put in front of anybody: the card offered the
                        three-fieldset form and nothing else.

                        So the card leads with the rounds and keeps the form as
                        the quick way through, named for what it actually does
                        rather than as a lesser option. */}
                    {showWorldCard && (
                        <div data-testid="home-world-card" style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '16px 18px' }}>
                            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--navy)', marginBottom: '4px' }}>Make the cases about your world</div>
                            <p style={{ margin: '0 0 12px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                                {mentorEnabled
                                    ? 'Six short rounds — how you decide, your Monday, the call you are proudest of. Molly reads them back as a few lines in your own words, and they pick your cases from then on.'
                                    : 'Six short rounds about how you decide. They become a few lines in your own words, and those pick your cases from then on.'}
                            </p>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <button type="button" onClick={() => setCurrentView('who-you-are')} className="ed-btn ed-btn-navy" data-testid="home-world-play"
                                    style={{ minHeight: '42px', padding: '9px 16px', fontSize: '13.5px' }}>Start the rounds</button>
                                <button type="button" onClick={() => setWorldSheetOpen('home')} className="ed-btn ed-btn-quiet" data-testid="home-world-open"
                                    style={{ minHeight: '42px', padding: '9px 16px', fontSize: '13.5px' }}>Just pick my industries</button>
                                <button type="button" onClick={dismissWorldCard} className="ed-btn ed-btn-quiet" data-testid="home-world-dismiss"
                                    style={{ minHeight: '42px', padding: '9px 16px', fontSize: '13.5px', border: 'none', background: 'transparent', color: 'var(--text-tertiary)' }}>Not now</button>
                            </div>
                        </div>
                    )}

                    {/* 3. From your last case */}
                    {showMove && (
                        <div data-testid="home-last-move" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderTop: '3px solid var(--gold)', borderRadius: '12px', padding: '14px 18px' }}>
                            <div style={{ ...kicker, marginBottom: '4px' }}>From your last case</div>
                            <p style={{ margin: '0 0 6px', fontSize: '14px', color: 'var(--navy)', lineHeight: 1.55, fontWeight: 500 }}>{lastCaseMove.text}</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-body)' }}>Did you do it?</span>
                                {/* Not a write an admin viewing as the member makes for them. */}
                                {!impersonating && (
                                    <button type="button" onClick={() => toggleNextMoveDone(lastCaseMove.id)} className="ed-btn ed-btn-navy" data-testid="home-move-done"
                                        style={{ minHeight: '36px', padding: '6px 14px', fontSize: '13px' }}>Done</button>
                                )}
                                <button type="button" onClick={stillOnIt} className="ed-btn ed-btn-quiet" data-testid="home-move-later"
                                    style={{ minHeight: '36px', padding: '6px 14px', fontSize: '13px' }}>Still on it</button>
                            </div>
                        </div>
                    )}

                    {/* 4. Today's decision — navy hero card.
                        When there is no case, SAY so. The card simply vanished:
                        the app's primary action disappeared from the home screen
                        with nothing in its place and no explanation, which reads
                        as a broken page rather than an empty library. */}
                    {!scenario && !doneToday && (
                        <div data-testid="no-case" style={{ background: 'var(--cream-dark)', border: '1px dashed var(--border-input)', borderRadius: '16px', padding: '24px', textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', color: 'var(--navy)', fontWeight: 600, marginBottom: '6px' }}>
                                No case to open right now.
                            </div>
                            <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                The library did not load, or every situation in it is awaiting review.
                                {canSeeLibrary(currentUser) ? ' The interviews and the moments are still here.' : ' The moments are still here.'}
                            </div>
                            <div
                                onClick={() => setCurrentView(canSeeLibrary(currentUser) ? 'interviews' : 'moments')}
                                onKeyDown={btnKey(() => setCurrentView(canSeeLibrary(currentUser) ? 'interviews' : 'moments'))}
                                role="button" tabIndex={0}
                                style={{ marginTop: '14px', display: 'inline-block', color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer', padding: '6px 10px' }}>
                                {canSeeLibrary(currentUser) ? 'Go to the interviews →' : 'Go to the moments →'}
                            </div>
                        </div>
                    )}
                    {doneToday && (
                        <section data-testid="feature-card-simulator" aria-labelledby="ed-home-done-title"
                            style={{ background: 'var(--navy)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--e2-navy)' }}>
                            <div style={{ fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700, marginBottom: '12px' }}>Today's decision</div>
                            <h2 id="ed-home-done-title" style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', lineHeight: 1.35, color: 'var(--text-on-navy)', fontWeight: 600, margin: '0 0 6px' }}>Today's practice is done</h2>
                            {workedTodayTitle && (
                                <p data-testid="home-worked-today" style={{ fontSize: '14px', color: 'var(--text-on-navy-muted)', lineHeight: 1.55, margin: '0 0 8px' }}>
                                    You worked "{workedTodayTitle}".
                                </p>
                            )}
                            {stateLine && <div data-testid="home-today-state" style={{ fontSize: '12.5px', color: 'var(--gold)', fontWeight: 600, margin: '0 0 16px' }}>{stateLine}</div>}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {canTalk && (
                                    <button type="button" onClick={talkItThrough} className="ed-btn ed-btn-gold" data-testid="home-talk"
                                        style={{ minHeight: '42px', padding: '9px 16px', fontSize: '13.5px' }}>Talk it through</button>
                                )}
                                {canNote && (
                                    <button type="button" onClick={addNote} data-testid="home-add-note"
                                        className={canTalk ? undefined : 'ed-btn ed-btn-gold'}
                                        style={canTalk ? onNavyButton : { minHeight: '42px', padding: '9px 16px', fontSize: '13.5px' }}>Add a note</button>
                                )}
                                {scenario && (
                                    <button type="button" onClick={openToday} data-testid="home-another-case"
                                        className={canTalk || canNote ? undefined : 'ed-btn ed-btn-gold'}
                                        style={canTalk || canNote ? onNavyButton : { minHeight: '42px', padding: '9px 16px', fontSize: '13.5px' }}>Another case</button>
                                )}
                            </div>
                            {nextUp && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '14px' }}>
                                    <span data-testid="home-next-up" style={{ fontSize: '12.5px', color: 'var(--text-on-navy-dim)' }}>{nextUp}</span>
                                    {heroSpot && <span data-testid="spotbad-badge-home" style={{ ...SPOTBAD_CHIP, marginBottom: 0 }}>Spot the bad call</span>}
                                </div>
                            )}
                        </section>
                    )}
                    {scenario && !doneToday && (
                        <div>
                            <div
                                onClick={openToday}
                                onKeyDown={btnKey(openToday)}
                                role="button" tabIndex={0} data-testid="feature-card-simulator"
                                className="ed-hover-navy-lift"
                                style={{ background: 'var(--navy)', borderRadius: '16px', padding: '24px', cursor: 'pointer', boxShadow: 'var(--e2-navy)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700 }}>Today's decision</span>
                                </div>
                                {heroSpot && (
                                    <div data-testid="spotbad-badge-home" style={SPOTBAD_CHIP}>
                                        Spot the bad call
                                    </div>
                                )}
                                {(industryChip || typeChip) && (
                                    <div data-testid="home-case-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' }}>
                                        {industryChip && <span style={navyChip}>{industryChip}</span>}
                                        {typeChip && <span style={navyChip}>{typeChip}</span>}
                                    </div>
                                )}
                                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', lineHeight: 1.35, color: 'var(--text-on-navy)', fontWeight: 600, margin: '0 0 6px' }}>{scenario.title}</h2>
                                {whyLine && (
                                    <div data-testid="home-why" style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gold)', margin: '0 0 6px' }}>{whyLine}</div>
                                )}
                                <p style={{ fontSize: '13.5px', color: 'var(--text-on-navy-muted)', lineHeight: 1.55, margin: '0 0 12px' }}>
                                    {scenario.timeContext} {heroSpot ? 'Three real decisions are on the desk — spot the one that backfired.' : 'The chief executive is revealed only after you commit.'}
                                </p>
                                {stateLine && <div data-testid="home-today-state" style={{ fontSize: '12px', color: 'var(--text-on-navy-dim)', margin: '0 0 14px' }}>{stateLine}</div>}
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'var(--gold)', color: 'var(--navy)', fontWeight: 700, fontSize: '13.5px', padding: '11px 18px', borderRadius: '10px' }}>Open the case file →</div>
                            </div>
                            <div style={{ textAlign: 'right', marginTop: '6px' }}>
                                <span onClick={openPicker} onKeyDown={btnKey(openPicker)} role="button" tabIndex={0} className="ed-link" data-testid="home-different-case"
                                    style={{ fontSize: '13px', fontWeight: 600, padding: '8px 4px', display: 'inline-block' }}>Different case</span>
                            </div>
                        </div>
                    )}
                    {pickerOpen && (
                        <DecisionPicker
                            scenarios={eligible}
                            completedIds={(completedSimulations || []).map(s => s && s.id)}
                            world={world}
                            onPick={(id) => { setPickerOpen(false); launchCase(id, 'picker'); }}
                            onClose={closePicker} />
                    )}

                    {/* 5. Your decision profile — the index and its standing, and what the record says so far */}
                    <div data-testid="power-index-card" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '18px 20px' }}>
                        <div style={{ ...kicker, color: 'var(--gold-darker)', marginBottom: '10px' }}>Your decision profile</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px' }}>
                            <div>
                                <div style={{ ...kicker, marginBottom: '4px' }}>Decision index</div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                    <span data-testid="power-index-score" style={{ fontFamily: 'var(--font-heading)', fontSize: '30px', color: 'var(--navy)', fontWeight: 600 }}>{powerIndex}</span>
                                    {weekDelta > 0 && (
                                        <span style={{ fontSize: '12.5px', color: 'var(--success-ink)', fontWeight: 600 }}>▲ {weekDelta} this week</span>
                                    )}
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ ...kicker, marginBottom: '4px' }}>Standing</div>
                                <div data-testid="tier-badge" style={{ fontSize: '14.5px', color: 'var(--navy)', fontWeight: 600 }}>{tierInfo.label}</div>
                                {/* A band name on its own is a word, not a position: "Executive
                                    Track" says nothing about how many bands there are or what
                                    put them in this one. Both halves come from what is already
                                    on screen — no new claim, and nothing to fetch. */}
                                <div data-testid="tier-band" style={{ fontSize: '11.5px', color: 'var(--text-tertiary)', marginTop: '2px', lineHeight: 1.45 }}>
                                    {`band ${tierBand} of ${scoreRanges.length}`}
                                    {decisions === 0
                                        ? ' · nothing on record yet'
                                        : `${weekDelta > 0 ? ` · ▲${weekDelta} this week` : ''}, from ${decisions} ${decisions === 1 ? 'decision' : 'decisions'}`}
                                </div>
                            </div>
                        </div>
                        {profile && (
                            <div data-testid="home-record" style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid var(--hairline)', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.5 }}>
                                <div style={{ fontWeight: 600, color: 'var(--navy)' }}>
                                    {decisions === 0 ? 'No decisions on record yet — today\'s case is the first.' : `${decisions} ${decisions === 1 ? 'decision' : 'decisions'} on record`}
                                </div>
                                {strongest && <div>{strongest}</div>}
                                {hardest && <div>{hardest}</div>}
                                {unlockLine && <div style={{ color: 'var(--text-tertiary)' }}>{unlockLine}</div>}
                            </div>
                        )}
                        {showIntro && (
                            <div onClick={() => setCurrentView('profile')} onKeyDown={btnKey(() => setCurrentView('profile'))}
                                role="button" tabIndex={0} className="ed-link" data-testid="home-profile-intro"
                                style={{ marginTop: '10px', fontSize: '13px', fontWeight: 700, lineHeight: 1.45 }}>
                                Your decision profile is built from the {decisions === 1 ? 'case' : `${decisions} cases`} you've already worked →
                            </div>
                        )}
                        <div className="ed-link" role="button" tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); setCurrentView('profile'); }}
                            onKeyDown={btnKey(() => setCurrentView('profile'))}
                            style={{ fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', marginTop: '6px', display: 'inline-flex', alignItems: 'center', minHeight: '40px' }}>Your decision profile →</div>
                    </div>

                    {/* 6. Molly and Ask a CEO — the two ways to take a case further */}
                    <section data-testid="feature-cards-section" style={{ display: 'grid', gridTemplateColumns: 'var(--ed-home-grid, 1fr)', gap: '14px' }}>
                        {mentorEnabled && (
                            <div
                                onClick={() => setCurrentView('ci-mentor')}
                                onKeyDown={btnKey(() => setCurrentView('ci-mentor'))}
                                role="button" tabIndex={0} className="ed-hover-gold-border" data-testid="feature-card-ci-mentor"
                                style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '18px 20px', cursor: 'pointer' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '8px' }}>
                                    <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600 }}>M</div>
                                    <span style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)' }}>Talk a decision through with Molly</span>
                                </div>
                                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>A case you've worked, or a call on your own desk. Molly asks what you'd weigh, grounded in the CEO library.</p>
                            </div>
                        )}
                        <div
                            onClick={() => setCurrentView('ceo-wisdom')}
                            onKeyDown={btnKey(() => setCurrentView('ceo-wisdom'))}
                            role="button" tabIndex={0} className="ed-hover-gold-border" data-testid="feature-card-ceo-wisdom"
                            style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '18px 20px', cursor: 'pointer' }}>
                            <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)', marginBottom: '8px' }}>Ask a real CEO</div>
                            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>Take a case you've worked — or a decision in front of you — to a CEO who has faced one like it. They answer in their own words.</p>
                        </div>
                    </section>

                    {/* 7. Corpus stat row — counted from the library that is loaded,
                        not asserted. It used to read "180 CEO interviews · 1,240
                        documented decisions · 96 industries" as fixed text. The
                        first happened to be about right (181); the second was out
                        by a factor of eight (156); the third measured nothing the
                        product stores. Marketing copy in the position of an
                        inventory is still a number the user will believe.
                        "CEO interviews" then counted the CEOs named in cases and
                        moments (~170), not the library (1,288 on 15 Oct 2026). It
                        is now the library's size, rounded down to 50 (Marco). */}
                    <div style={{ display: 'flex', gap: '22px', padding: '4px 4px 0', fontSize: '12.5px', color: 'var(--text-tertiary)', flexWrap: 'wrap' }}>
                        {librarySize && <div data-testid="home-library-size"><strong style={{ color: 'var(--navy)' }}>{librarySize.toLocaleString()}+</strong> CEO interviews</div>}
                        <div><strong style={{ color: 'var(--navy)' }}>{corpus.decisions.toLocaleString()}</strong> documented decisions</div>
                        <div><strong style={{ color: 'var(--navy)' }}>{corpus.companies}</strong> companies</div>
                    </div>
                </div>
            );
        };

        // ============================================
        // CI MENTOR VIEW COMPONENT
        // ============================================
        // ============================================
        // AUDIO CLIP CARD — custom play chrome + mandatory transcript expander
        // ============================================
        const AudioClipCard = ({ title, meta, src: clipSrc, fullSrc = null, transcript, track, startS: clipStart = null, endS: clipEnd = null }) => {
            // "Whole interview" (Marco, Oct 2026: "a button to click to listen to
            // the WHOLE thing"). `fullSrc` is the full recording: for a timed clip
            // the same file, for a curated clip the CEO's full interview when the
            // worker found one. Pressed, the card becomes that recording, from
            // the start, with no cited window.
            const [whole, setWhole] = useState(false);
            const src = whole && fullSrc ? fullSrc : clipSrc;
            const startS = whole ? null : clipStart;
            const endS = whole ? null : clipEnd;
            const audioRef = useRef(null);
            const [playing, setPlaying] = useState(false);
            const [duration, setDuration] = useState(null);
            const [transcriptOpen, setTranscriptOpen] = useState(false);
            // Timed citation (docs/MOLLY_GROUNDING.md): `src` is the FULL
            // interview and startS/endS bound the cited moment. Playback seeks
            // to startS and auto-stops at endS; "Keep listening" lifts the stop
            // so the user can hear the rest of the conversation.
            const [stopped, setStopped] = useState(false);
            // A clip that will not load used to animate as if it were playing:
            // `el.play()` rejects, `.catch(() => {})` swallows it, and `playing`
            // had already been set to true. The user watched a pause button and
            // a progress bar sit there over silence.
            const [failed, setFailed] = useState(false);
            // Position drives the scrubber and the readout. `bounded` is the
            // same fact as boundedRef, in state, because the transport has to
            // re-render when "Keep listening" opens the clip up to the whole
            // interview.
            const [pos, setPos] = useState(0);
            const [bounded, setBounded] = useState(endS != null);
            const boundedRef = useRef(endS != null);
            const seekedRef = useRef(false);
            const barRef = useRef(null);

            // Optional listen telemetry: `track` = { source: 'mentor' | 'askCeo' }.
            // Same measured-listening tracker the reveal player uses, so a clip
            // Molly offered reports audio_play / audio_80 / audio_listen with the
            // same maths — only the `source` says where it was heard.
            const trackerRef = useRef(null);
            const tracker = () => {
                if (!track) return null;
                if (!trackerRef.current) trackerRef.current = makeListenTracker({ kind: track.kind || 'short', source: track.source, runId: null, entityId: track.entityId ?? null, clip: track.clip || null });
                return trackerRef.current;
            };
            const tk = (fn) => (e) => { const t = tracker(); if (t && t[fn]) t[fn](e.target); };

            const toggle = () => {
                const el = audioRef.current;
                if (!el) return;
                if (playing) { el.pause(); setPlaying(false); }
                else {
                    if (startS != null && !seekedRef.current) {
                        try { el.currentTime = startS; } catch (e) { /* metadata not ready — the seek retries below */ }
                        seekedRef.current = true;
                    } else if (boundedRef.current && endS != null && el.currentTime >= endS - 0.25) {
                        // Play at the end of the cited moment means play it
                        // again, not stop again on the first frame.
                        try { el.currentTime = startS != null ? startS : 0; setPos(startS != null ? startS : 0); } catch (e) { /* best effort */ }
                    }
                    setStopped(false);
                    setFailed(false);
                    el.play().then(() => setPlaying(true)).catch(() => {
                        // Autoplay policy, a 404 on the R2 key, a decode error —
                        // whatever it is, it did not play, and the UI must not
                        // pretend otherwise.
                        setPlaying(false);
                        setFailed(true);
                    });
                }
            };

            const playWhole = () => {
                const el = audioRef.current;
                boundedRef.current = false;
                setBounded(false);
                setStopped(false);
                setFailed(false);
                seekedRef.current = true;
                setPos(0);
                // Same file: its length is already known and will not be read
                // again (no new loadedmetadata). Another file: it will be.
                setDuration(el && fullSrc === clipSrc && isFinite(el.duration) && el.duration > 0 ? el.duration : null);
                setWhole(true);
                // Same file: rewind and go. Another file: the new src loads, and
                // the effect below starts it.
                if (el && fullSrc === clipSrc) {
                    try { el.currentTime = 0; } catch (e) { /* best effort */ }
                    el.play().then(() => setPlaying(true)).catch(() => { setPlaying(false); setFailed(true); });
                }
            };
            useEffect(() => {
                const el = audioRef.current;
                if (!whole || !el || fullSrc === clipSrc) return;
                el.load();
                el.play().then(() => setPlaying(true)).catch(() => { setPlaying(false); setFailed(true); });
            }, [whole]);

            const keepListening = () => {
                boundedRef.current = false;
                setBounded(false);
                setStopped(false);
                const el = audioRef.current;
                if (el) { el.play().catch(() => {}); setPlaying(true); }
            };

            useEffect(() => () => {
                if (audioRef.current) audioRef.current.pause();
                if (trackerRef.current) trackerRef.current.finish();
            }, []);

            // As far as the reader is concerned the cited moment IS the clip:
            // position, length and the scrubber are all measured inside
            // [startS, endS]. "Keep listening" lifts the end and the player
            // becomes the whole interview.
            const winStart = startS != null ? startS : 0;
            const winEnd = bounded && endS != null ? endS : (duration != null ? duration : (endS != null ? endS : 0));
            const winLen = Math.max(0, winEnd - winStart);
            const elapsed = Math.max(0, Math.min(winLen, pos - winStart));
            const frac = winLen > 0 ? elapsed / winLen : 0;

            const seekTo = (fraction) => {
                const el = audioRef.current;
                if (!el || !winLen) return;
                const t = winStart + Math.max(0, Math.min(1, fraction)) * winLen;
                try { el.currentTime = t; } catch (e) { return; }
                // A hand-placed position outranks the jump-to-the-moment seek.
                seekedRef.current = true;
                setPos(t);
            };
            const seekFromEvent = (e) => {
                const box = barRef.current;
                if (!box) return;
                const r = box.getBoundingClientRect();
                if (r.width > 0) seekTo((e.clientX - r.left) / r.width);
            };

            // The length is shown in the pill beside the player, so the meta
            // line only has to say WHERE in the interview the moment sits.
            const metaWithLength = [
                meta,
                whole ? 'The whole interview' : startS != null
                    ? `${fmtTime(startS)}–${endS != null ? fmtTime(endS) : '…'} of the interview`
                    : null
            ].filter(Boolean).join(' · ');

            return (
                <div style={{ marginTop: '13px', padding: '12px 14px', background: 'var(--cream-dark)', borderRadius: '10px' }}>
                    {/* Same chrome as the Simulator's excerpt player: a round
                        play button that keeps its shape, the length in a pill,
                        and a seekable bar with an elapsed / total readout. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                            onClick={toggle}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                            role="button" tabIndex={0} aria-label={playing ? 'Pause audio clip' : 'Play audio clip'}
                            style={{ width: '42px', minWidth: '42px', height: '42px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0, cursor: 'pointer' }}>
                            {playing ? '❚❚' : '▶'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--navy)' }}>{title}</div>
                            <div style={{ fontSize: '11.5px', color: 'var(--text-tertiary)' }}>
                                {metaWithLength}
                                {transcript && !whole && (
                                    <>
                                        {metaWithLength ? ' · ' : ''}
                                        <a href="#" className="ed-link" style={{ textDecoration: 'underline' }}
                                            onClick={(e) => { e.preventDefault(); setTranscriptOpen(o => !o); }}>
                                            {transcriptOpen ? 'Hide transcript' : 'Transcript'}
                                        </a>
                                    </>
                                )}
                                {fullSrc && !whole && (
                                    <>
                                        {metaWithLength || transcript ? ' · ' : ''}
                                        <a href="#" className="ed-link" data-testid="clip-whole-interview" style={{ textDecoration: 'underline' }}
                                            onClick={(e) => { e.preventDefault(); playWhole(); }}>
                                            Whole interview
                                        </a>
                                    </>
                                )}
                            </div>
                        </div>
                        {/* Length up front, so it's obvious how long this runs */}
                        {src && winLen > 0 && (
                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: 'var(--gold-darker)', background: 'rgba(212,182,104,0.18)', border: '1px solid rgba(212,182,104,0.55)', borderRadius: '999px', padding: '3px 10px' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                                {fmtTime(winLen)}
                            </div>
                        )}
                    </div>
                    {src && (
                        <div style={{ marginTop: '11px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div
                                ref={barRef}
                                onClick={seekFromEvent}
                                role="slider" aria-label="Seek within the clip"
                                aria-valuemin={0} aria-valuemax={Math.round(winLen)} aria-valuenow={Math.round(elapsed)}
                                aria-valuetext={winLen ? `${fmtTime(elapsed)} of ${fmtTime(winLen)}` : undefined}
                                tabIndex={winLen ? 0 : -1}
                                onKeyDown={(e) => {
                                    if (!winLen) return;
                                    if (e.key === 'ArrowRight') { e.preventDefault(); seekTo((elapsed + 5) / winLen); }
                                    else if (e.key === 'ArrowLeft') { e.preventDefault(); seekTo((elapsed - 5) / winLen); }
                                }}
                                style={{ flex: 1, minWidth: 0, height: '16px', display: 'flex', alignItems: 'center', cursor: winLen ? 'pointer' : 'default' }}>
                                <div style={{ width: '100%', height: '4px', background: 'rgba(11,20,38,0.16)', borderRadius: '999px', position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${frac * 100}%`, background: 'var(--gold-dark)', borderRadius: '999px' }}></div>
                                    <div style={{ position: 'absolute', top: '50%', left: `${frac * 100}%`, transform: 'translate(-50%, -50%)', width: '11px', height: '11px', borderRadius: '50%', background: 'var(--gold-dark)', boxShadow: '0 0 0 3px var(--cream-dark)' }}></div>
                                </div>
                            </div>
                            <div style={{ flexShrink: 0, fontSize: '11.5px', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums', minWidth: '72px', textAlign: 'right' }}>
                                {winLen ? `${fmtTime(elapsed)} / ${fmtTime(winLen)}` : 'Loading…'}
                            </div>
                        </div>
                    )}
                    {stopped && (
                        <div style={{ marginTop: '9px', paddingTop: '9px', borderTop: '1px solid var(--border-card)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '11.5px', color: 'var(--text-tertiary)' }}>End of the cited moment.</span>
                            <span
                                onClick={keepListening}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); keepListening(); } }}
                                role="button" tabIndex={0}
                                style={{ fontSize: '11.5px', color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>
                                Keep listening →
                            </span>
                        </div>
                    )}
                    {transcript && transcriptOpen && !whole && (
                        <p style={{ margin: '10px 0 0', fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.65, borderTop: '1px solid var(--border-card)', paddingTop: '10px' }}>
                            "{transcript}"
                        </p>
                    )}
                    {src && <audio ref={audioRef} src={src} preload="metadata"
                        onLoadedMetadata={(e) => { setDuration(isFinite(e.target.duration) && e.target.duration > 0 ? e.target.duration : null); tk('onLoadedMetadata')(e); }}
                        onPlay={(e) => {
                            // A seek requested before metadata loaded lands here.
                            if (startS != null && seekedRef.current && e.target.currentTime < startS - 1) {
                                try { e.target.currentTime = startS; } catch (err) { /* best effort */ }
                            }
                            tk('onPlay')(e);
                        }}
                        onPause={(e) => {
                            // Paused from anywhere — its own button, the end of the
                            // cited moment, or another player starting — the button says so.
                            setPlaying(false);
                            tk('onPause')(e);
                        }}
                        onTimeUpdate={(e) => {
                            setPos(e.target.currentTime);
                            if (boundedRef.current && endS != null && e.target.currentTime >= endS) {
                                e.target.pause(); setPlaying(false); setStopped(true);
                            }
                            tk('onTimeUpdate')(e);
                        }}
                        onSeeked={(e) => { setPos(e.target.currentTime); tk('onSeeked')(e); }}
                        onEnded={(e) => { setPlaying(false); tk('onEnded')(e); }}
                        onError={() => { setPlaying(false); setFailed(true); }}
                        style={{ display: 'none' }} />}
                    {failed && (
                        <div role="status" style={{ marginTop: '8px', fontSize: '12.5px', color: 'var(--text-meta)' }}>
                            This clip would not play. The transcript below is the same words.
                        </div>
                    )}
                </div>
            );
        };

        /**
         * A CEO's own words, when Molly quotes them (Marco, Oct 2026: "display the
         * quote in a QUOTE box with the person's name … to help identify that this
         * is real"). The words are the LIBRARY's, matched to the reply by the
         * worker (resolveQuotes) — never the model's version of them — and the
         * recording is under them when there is one, so the member can hear the
         * CEO say it.
         */
        const MollyQuote = ({ quote: q, id, heardBelow = false }) => {
            if (!q || !q.quote) return null;
            const who = `${q.ceo}${q.company ? `, ${q.company}` : ''}`;
            return (
                <figure id={id} data-testid="molly-quote" style={{ margin: '13px 0 0', padding: '14px 16px 12px', background: 'var(--cream)', border: '1px solid var(--border-card)', borderLeft: '3px solid var(--gold)', borderRadius: '10px' }}>
                    <blockquote style={{ margin: 0, fontFamily: 'var(--serif)', fontSize: '15.5px', lineHeight: 1.5, color: 'var(--navy)' }}>
                        <span aria-hidden="true" style={{ color: 'var(--gold-dark)', fontSize: '22px', lineHeight: 0, verticalAlign: '-6px', marginRight: '3px' }}>“</span>{q.quote}<span aria-hidden="true" style={{ color: 'var(--gold-dark)' }}>”</span>
                    </blockquote>
                    <figcaption style={{ marginTop: '8px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                        — <strong style={{ color: 'var(--navy)' }}>{who}</strong>
                        <span style={{ color: 'var(--text-tertiary)' }}> · in their own words, from the {BRAND.name} library{q.audioFile ? ' — hear them say it' : heardBelow ? ' — hear them below' : ''}</span>
                    </figcaption>
                    {/* Their recording of the line; failing that, their whole interview —
                        unless a moment of theirs is right below, which offers it too. */}
                    {(q.audioFile || (q.fullAudioFile && !heardBelow)) && (
                        <AudioClipCard
                            title={q.audioFile ? `Hear ${q.ceo}` : `The whole interview with ${q.ceo}`}
                            meta={q.audioFile && q.startS == null && q.kind === 'insight' ? 'Their recording of this line' : null}
                            src={fixAudioPath(q.audioFile || q.fullAudioFile)}
                            fullSrc={q.audioFile && q.fullAudioFile ? fixAudioPath(q.fullAudioFile) : null}
                            transcript={null}
                            startS={q.startS ?? null}
                            endS={q.endS ?? null}
                            track={{ source: 'mentor', kind: q.startS != null ? 'full' : 'short', clip: { key: q.audioFile, ceo: q.ceo, why: 'quote' } }}
                        />
                    )}
                </figure>
            );
        };

        /**
         * What sits behind the CI Mentor nav entry for a user whose account has
         * it switched off (users.mentor_enabled, migration 0009).
         *
         * The entry itself deliberately STAYS in the nav. Removing it would make
         * a pilot user's app quietly different from the one they were shown, and
         * leave them wondering what else is missing; this way the feature is
         * visibly on its way rather than absent. It is also why the gate lives at
         * the view and not on the buttons — Home cards, the simulator hand-off
         * and the sidebar all route through here, so there is exactly one place
         * that can be wrong.
         */
        const MentorComingSoonView = () => {
            const { database, setCurrentView } = useApp();
            const name = (database.ciMentor && database.ciMentor.agent && database.ciMentor.agent.name) || 'Molly';
            return (
                <div className="ed-pagein" data-testid="ci-mentor-coming-soon" style={{ padding: '26px 0 32px' }}>
                    <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                        CI Mentor
                    </h2>
                    <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
                        Coming soon.
                    </p>
                    <div style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '22px 20px' }}>
                        <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '10px' }}>
                            Not ready yet
                        </div>
                        <div style={{ fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.65 }}>
                            {name} works a challenge the way the CEOs in the library worked theirs —
                            clarify, stress-test, pattern-match, then land on an action plan. She is
                            still being trained on the interviews, so she is not open on your account
                            yet. Everything else is: run a decision, read the moments, go through the
                            full interviews.
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '16px' }}>
                        <div onClick={() => setCurrentView('simulator')} onKeyDown={btnKey(() => setCurrentView('simulator'))}
                            role="button" tabIndex={0}
                            style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', fontWeight: 600 }}>
                            Run a decision →
                        </div>
                        <div onClick={() => setCurrentView('home')} onKeyDown={btnKey(() => setCurrentView('home'))}
                            role="button" tabIndex={0}
                            style={{ fontSize: '13.5px', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>
                            Back to home
                        </div>
                    </div>
                </div>
            );
        };

        /**
         * The case a stored Molly conversation carries on with, as caseRef holds
         * it ({ scenarioId, choice }), or null. The row says which case it began
         * from (`scenarioId`, null for none), so a conversation from days ago —
         * or one this screen never held — gets its case back. Which option the
         * member picked only the thread on screen knows, and only for that same
         * row and case; otherwise it is null. A worker that does not send the
         * key at all (undefined) leaves the thread's own record, for that row.
         */
        const caseOfConversation = (row, thread) => {
            const data = row && typeof row === 'object' ? row : {};
            const own = thread && data.id != null && thread.conversationId === data.id && thread.scenarioId ? thread : null;
            const id = Number(data.scenarioId);
            if (data.scenarioId != null && Number.isInteger(id) && id > 0) {
                return { scenarioId: id, choice: own && Number(own.scenarioId) === id ? own.choice || null : null };
            }
            return own && data.scenarioId === undefined ? { scenarioId: own.scenarioId, choice: own.choice || null } : null;
        };

        /**
         * Molly's thread once conversation `id` is deleted (Your conversations).
         * Her thread may still be writing into that row: it lets go of the row
         * and keeps the case, so her next turn opens a new row (on the case,
         * which is recorded again) instead of posting into one that is gone.
         */
        const threadAfterDelete = (thread, id) => {
            if (!thread || thread.conversationId !== id) return thread;
            return thread.scenarioId ? { ...thread, conversationId: null } : null;
        };

        /**
         * Molly. As a page (Profile → Sessions with Molly), or EMBEDDED in the
         * simulator's last phase, where she opens on the case just played and
         * the exchange stays with the situation instead of jumping the user to
         * a fresh chat with a greeting. `opening` is her first line — a
         * question about the decision, composed client-side so the moment
         * does not wait on a model; `onFirstReply` fires once, when the user
         * answers it.
         */
        /**
         * Molly's AI-at-work lessons (docs/design/06): the index comes from
         * GET /api/lessons (Edify's own scripts live in the worker); what the
         * member did with them lives under `userProfile` — `lessons` (started
         * and done, per id) and `promptLibrary` (the prompts they kept, with
         * the brackets empty). Both ride the autosave and are theirs alone.
         */
        const PROMPT_LIBRARY_MAX = 30;
        const lessonStateOf = (userProfile) => (userProfile && userProfile.lessons && typeof userProfile.lessons === 'object' && !Array.isArray(userProfile.lessons)) ? userProfile.lessons : {};
        const promptLibraryOf = (userProfile) => (userProfile && Array.isArray(userProfile.promptLibrary))
            ? userProfile.promptLibrary.filter(x => x && typeof x === 'object' && typeof x.text === 'string' && typeof x.title === 'string')
            : [];
        const withLessonMark = (userProfile, id, field, now = new Date()) => {
            const lessons = lessonStateOf(userProfile);
            const prior = lessons[id] && typeof lessons[id] === 'object' ? lessons[id] : {};
            if (prior[field]) return userProfile;
            return { ...(userProfile || {}), lessons: { ...lessons, [id]: { ...prior, [field]: now.toISOString() } } };
        };
        const withKeptPrompt = (userProfile, item, now = new Date()) => {
            const kept = promptLibraryOf(userProfile).filter(x => !(x.lessonId && x.lessonId === item.lessonId && x.title === item.title));
            const entry = { id: `k${now.getTime().toString(36)}`, lessonId: item.lessonId || null, title: String(item.title).slice(0, 80), text: String(item.text).slice(0, 4000), savedAt: now.toISOString() };
            return { ...(userProfile || {}), promptLibrary: [entry, ...kept].slice(0, PROMPT_LIBRARY_MAX) };
        };
        const withoutKeptPrompt = (userProfile, id) => ({ ...(userProfile || {}), promptLibrary: promptLibraryOf(userProfile).filter(x => x.id !== id) });

        // Talk · Learn, on Molly's page and on the lessons list. Page mode only.
        const MollyTabs = ({ active }) => {
            const { setCurrentView } = useApp();
            const tab = (key, label, view) => (
                <div key={key} onClick={() => { if (active !== key) setCurrentView(view); }} onKeyDown={btnKey(() => { if (active !== key) setCurrentView(view); })}
                    role="tab" tabIndex={0} aria-selected={active === key} data-testid={`molly-tab-${key}`}
                    style={{ flex: 1, minHeight: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '9px', fontSize: '13.5px', fontWeight: 700, cursor: active === key ? 'default' : 'pointer',
                        background: active === key ? 'var(--white)' : 'transparent', color: active === key ? 'var(--navy)' : 'var(--text-secondary)', boxShadow: active === key ? '0 1px 2px rgba(0,0,0,0.06)' : 'none' }}>{label}</div>
            );
            return (
                <div role="tablist" aria-label="Molly" style={{ display: 'flex', gap: '6px', padding: '4px', borderRadius: '12px', background: 'var(--cream-dark)', marginBottom: '12px' }}>
                    {tab('talk', 'Talk', 'ci-mentor')}
                    {tab('learn', 'Learn: AI at work', 'molly-learn')}
                </div>
            );
        };

        const MollyLearnView = () => {
            const { setCurrentView, setMentorOpening, userProfile, setUserProfile, currentUser, authToken } = useApp();
            const impersonating = !!(currentUser && currentUser.impersonatedBy);
            const [lessons, setLessons] = useState(null);   // null loading · [] failed
            useEffect(() => { Telemetry.track('ui_open', { what: 'learn' }); }, []);
            useEffect(() => {
                if (!API_URL || !authToken) { setLessons([]); return; }
                let off = false;
                fetch(`${API_URL}/api/lessons`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => (r.ok ? r.json() : null))
                    .then(res => { if (!off) setLessons(res && res.success && res.data && Array.isArray(res.data.lessons) ? res.data.lessons : []); })
                    .catch(() => { if (!off) setLessons([]); });
                return () => { off = true; };
            }, [authToken]);
            const state = lessonStateOf(userProfile);
            const kept = promptLibraryOf(userProfile);
            const done = (lessons || []).filter(l => state[l.id] && state[l.id].doneAt).length;
            const start = (l) => {
                if (impersonating) return;
                setUserProfile(withLessonMark(userProfile, l.id, 'startedAt'));
                setMentorOpening({ text: l.opening, lessonId: l.id, lessonTitle: l.title, keep: l.keep, stepCount: l.stepCount });
                setCurrentView('ci-mentor');
            };
            const kicker = { fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 };
            return (
                <div className="ed-pagein" data-testid="molly-learn-view" style={{ padding: '26px 0 32px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <MollyTabs active="learn" />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={kicker}>AI at work</div>
                        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', color: 'var(--navy)', fontWeight: 600, margin: 0, lineHeight: 1.25 }}>Use AI in your business. Safely, and on your terms.</h2>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>Short lessons with Molly. Each one ends with something you keep: a rule, a template, a prompt. Your real figures never leave your machine; Molly teaches you to describe their shape instead.</p>
                    </div>
                    {lessons && lessons.length > 0 && (
                        <div style={{ background: 'var(--gold-transparent)', border: '1px solid var(--gold-lighter)', borderRadius: '12px', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
                                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{done} of {lessons.length} done</span>
                                <span onClick={() => setCurrentView('prompt-library')} onKeyDown={btnKey(() => setCurrentView('prompt-library'))} role="button" tabIndex={0} data-testid="learn-library"
                                    style={{ color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer', minHeight: '32px', display: 'inline-flex', alignItems: 'center' }}>{kept.length} prompt{kept.length === 1 ? '' : 's'} in your library →</span>
                            </div>
                            <span style={{ width: '100%', height: '8px', borderRadius: '4px', background: 'var(--cream-dark)', overflow: 'hidden', display: 'block' }}><span style={{ display: 'block', width: `${Math.round(done / lessons.length * 100)}%`, height: '100%', background: 'var(--gold-dark)' }} /></span>
                        </div>
                    )}
                    {lessons === null && <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)' }}>Loading the lessons…</div>}
                    {lessons && lessons.length === 0 && <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)' }}>The lessons could not be loaded. They need a connection and a signed-in account.</div>}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {(lessons || []).map((l, i) => {
                            const st = state[l.id] || {};
                            const status = st.doneAt ? 'done' : st.startedAt ? 'started' : 'todo';
                            return (
                                <div key={l.id} onClick={() => start(l)} onKeyDown={btnKey(() => start(l))} role="button" tabIndex={0} data-testid={`lesson-${l.id}`}
                                    className="ed-hover-gold-border"
                                    style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '12px 14px', borderRadius: '14px', background: 'var(--white)', border: `1.5px solid ${status === 'started' ? 'var(--navy)' : 'var(--border-card)'}`, cursor: impersonating ? 'default' : 'pointer' }}>
                                    <span aria-hidden="true" style={{ flex: 'none', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, boxSizing: 'border-box',
                                        background: status === 'done' ? 'var(--success-ink)' : status === 'started' ? 'var(--navy)' : 'transparent', color: status === 'todo' ? 'var(--text-secondary)' : 'var(--white)', border: status === 'todo' ? '1.5px solid var(--border-card)' : 'none' }}>{status === 'done' ? '✓' : i + 1}</span>
                                    <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                        <span style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}><span style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)' }}>{l.title}</span><span style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{l.minutes} min</span></span>
                                        <span style={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>You keep: {l.keep && l.keep.title ? l.keep.title.toLowerCase() : 'a prompt'}{status === 'done' ? ' · done' : status === 'started' ? ' · in progress' : ''}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    <div style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', lineHeight: 1.5 }}>{impersonating ? 'Viewing as this member: the lessons are theirs to take.' : 'Molly teaches the method, not a vendor. The prompts work in whichever AI tool your company has approved, and lesson 1 is how to find out what that is.'}</div>
                </div>
            );
        };

        const PromptLibraryView = () => {
            const { setCurrentView, userProfile, setUserProfile, currentUser, showToast } = useApp();
            const impersonating = !!(currentUser && currentUser.impersonatedBy);
            const kept = promptLibraryOf(userProfile);
            const copy = (text) => {
                if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(text).then(() => { if (showToast) showToast('Copied. Fill the brackets on your own machine.', 'success'); }).catch(() => {});
            };
            return (
                <div className="ed-pagein" data-testid="prompt-library-view" style={{ padding: '26px 0 32px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div onClick={() => setCurrentView('molly-learn')} onKeyDown={btnKey(() => setCurrentView('molly-learn'))} role="button" tabIndex={0} style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', fontWeight: 600, alignSelf: 'flex-start' }}>← AI at work</div>
                    <div>
                        <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 }}>Your prompt library</div>
                        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', color: 'var(--navy)', fontWeight: 600, margin: '4px 0 6px' }}>What you kept.</h2>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>Stored with the brackets empty. {BRAND.name} never sees a figure; you fill them in the tool your company has approved.</p>
                    </div>
                    {kept.length === 0 && <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)' }}>Nothing kept yet. Each lesson with Molly ends with something to keep here.</div>}
                    {kept.map(k => (
                        <div key={k.id} data-testid="kept-prompt" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}><span style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)' }}>{k.title}</span><span style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{isFinite(Date.parse(k.savedAt)) ? new Date(k.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span></div>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12.5px', lineHeight: 1.5, color: 'var(--navy)', background: 'var(--cream-dark)', borderRadius: '10px', padding: '10px 12px' }}>{k.text}</pre>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button type="button" onClick={() => copy(k.text)} className="ed-btn ed-btn-quiet" style={{ padding: '9px 14px', fontSize: '13px', minHeight: '40px' }}>Copy</button>
                                {!impersonating && <button type="button" onClick={() => setUserProfile(withoutKeptPrompt(userProfile, k.id))} className="ed-btn ed-btn-quiet" style={{ padding: '9px 14px', fontSize: '13px', minHeight: '40px', color: 'var(--text-secondary)' }}>Remove</button>}
                            </div>
                        </div>
                    ))}
                </div>
            );
        };

        // Conversations the member asked Molly to remember (migration 0029).
        // Module scope, not view state: Molly's thread outlives the view (it is
        // App state) and so must the mark on it. Filled from the server's
        // `remembered` whenever a session is put back.
        const REMEMBERED_CONVOS = new Set();

        const CIMentorView = ({ embedded = false, opening = null, onFirstReply = null, onUserTurn = null } = {}) => {
            const { database, addCISession, ciSeed, setCiSeed, setCeoSeed, setCurrentView, authToken, handleSessionExpired, savedNextMoves, addSavedNextMove, requestFeedback, showToast,
                mentorMsgs: messages, setMentorMsgs: setMessages, mentorStep: currentStep, setMentorStep: setCurrentStep,
                mentorTyping: isTyping, setMentorTyping: setIsTyping, mentorLogged, setMentorLogged,
                mentorRated: ratedTurns, setMentorRated: setRatedTurns,
                mentorFresh, setMentorFresh,
                mentorResumedFrom: resumedFrom, setMentorResumedFrom: setResumedFrom,
                mentorResume, setMentorResume, setConvosBackTo,
                mentorThread, setMentorThread, refreshDecisionProfile, userProfile, setUserProfile, mentorEnabled } = useApp();
            const [inputValue, setInputValue] = useState('');
            // The two session controls live behind "More" (see the masthead below).
            const [sessionMenu, setSessionMenu] = useState(false);
            // Re-render after the Remember toggle; the truth is REMEMBERED_CONVOS.
            const [, setRememberTick] = useState(0);
            const messagesEndRef = useRef(null);
            const threadRef = useRef(null);
            const textareaRef = useRef(null);
            const botTimerRef = useRef(null);
            // The persisted conversation this session is writing into. A ref, not
            // state, so it survives re-renders without triggering them and two
            // rapid turns cannot race into creating two conversations.
            const conversationIdRef = useRef(null);
            // The lesson this thread is, when it is one (docs/design/06):
            // { id, title, keep, stepCount }. Mirrored on mentorThread like the
            // case, so a remount keeps teaching the same lesson.
            const lessonRef = useRef(null);
            // The thread lives in memory. A reload — or a laptop that slept —
            // emptied the screen while the transcript sat safely in D1, and the
            // comment on persistTurn claimed the opposite. Opening Molly now
            // walks back into the conversation that was interrupted, rather
            // than greeting someone who is mid-sentence.
            // Long enough to cover a night; short enough that yesterday's
            // problem is a new session, not an interrupted one.
            const INTERRUPTED_MS = 36 * 3600 * 1000;

            /**
             * The conversation that was interrupted, loaded and ready — or null.
             * Every failure path is a null on purpose: the greeting is a
             * perfectly good place to start, and none of this is worth an error.
             */
            const findInterrupted = async () => {
                try {
                    const list = await fetch(`${API_URL}/api/conversations?limit=1&kind=mentor`, {
                        headers: { Authorization: `Bearer ${authToken}` }
                    }).then(r => r.json());
                    const c = list && list.success ? (list.data.items || [])[0] : null;
                    if (!c || !c.lastActiveAt) return null;
                    const iso = c.lastActiveAt.includes('T') ? c.lastActiveAt : c.lastActiveAt.replace(' ', 'T') + 'Z';
                    const t = Date.parse(iso);
                    if (!isFinite(t) || Date.now() - t > INTERRUPTED_MS) return null;
                    const full = await fetch(`${API_URL}/api/conversations/${c.id}`, {
                        headers: { Authorization: `Bearer ${authToken}` }
                    }).then(r => r.json());
                    if (!full || !full.success || !(full.data.messages || []).length) return null;
                    return full.data;
                } catch (e) {
                    return null;
                }
            };

            /**
             * Put a stored conversation back on the screen AND back on the ref,
             * so the next turn appends to that row instead of opening a second
             * one about the same problem.
             */
            const putBack = (data) => {
                const msgs = (data.messages || []).map(m => ({
                    type: m.role === 'bot' ? 'bot' : 'user',
                    text: m.text,
                    ...(m.sources && m.sources.length ? storedSourcesOf(m.sources) : {}),
                    timestamp: new Date()
                }));
                // A greeting queued on mount would land on top of the thread.
                if (botTimerRef.current) { clearTimeout(botTimerRef.current); botTimerRef.current = null; }
                setIsTyping(false);
                conversationIdRef.current = data.id;
                if (data.remembered) REMEMBERED_CONVOS.add(data.id); else REMEMBERED_CONVOS.delete(data.id);
                // The case goes with its own conversation and no other (see
                // caseOfConversation): picking a different one out of Past
                // sessions must not carry on sending Molly the case the screen
                // was about a moment ago.
                caseRef.current = caseOfConversation(data, mentorThread);
                setMentorThread(threadNow());
                setMessages(msgs);
                // The thumbs are not restored: they were keyed to the live turn
                // numbers of the session that gave them, and a rebuilt
                // transcript does not have the greeting those counted from.
                setRatedTurns({});
                const at = stepTitles.indexOf(data.stageReached);
                setCurrentStep(at >= 0 ? at : 0);
                // Already counted as a session on the day it was worked.
                setMentorLogged(true);
                setMentorFresh(false);
                setResumedFrom({ id: data.id, title: data.title || null, at: msgs.length });
                Telemetry.track('ui_open', { what: 'mentor_resume' }, { entityType: 'conversation', entityId: data.id });
            };

            /**
             * Start again on a different problem.
             *
             * Dropping conversationIdRef is what makes the next turn open a
             * FRESH row in D1 rather than appending to the old one — the
             * previous session stays where it is, readable in Your
             * conversations, instead of being overwritten by an unrelated
             * challenge.
             */
            const startNewSession = () => {
                conversationIdRef.current = null;
                lessonRef.current = null;
                setMessages([]);
                setCurrentStep(0);
                setIsTyping(false);
                setMentorLogged(false);
                setRatedTurns({});
                setResumedFrom(null);
                // Remembered, so leaving Molly and coming back does not walk
                // them into the conversation they just put down.
                setMentorFresh(true);
                setInputValue('');
                Telemetry.track('ui_open', { what: 'mentor_new_session' });
            };

            // "Please remember this conversation" (tester feedback, 30 Sep
            // 2026). Only a saved mentor session can be remembered: the id is
            // what the worker reads back, and a lesson is not Molly's to carry.
            const isRemembered = !!conversationIdRef.current && REMEMBERED_CONVOS.has(conversationIdRef.current);
            const toggleRemember = async () => {
                const id = conversationIdRef.current;
                if (!id || !authToken) return;
                const on = !REMEMBERED_CONVOS.has(id);
                try {
                    const res = await fetch(`${API_URL}/api/conversations/${encodeURIComponent(id)}/remember`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({ remember: on })
                    });
                    if (res.status === 401) { handleSessionExpired(); return; }
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok || !data.success) throw new Error((data && data.error) || 'failed');
                    if (on) REMEMBERED_CONVOS.add(id); else REMEMBERED_CONVOS.delete(id);
                    setRememberTick(t => t + 1);
                    Telemetry.track('ui_open', { what: on ? 'mentor_remember' : 'mentor_forget' }, { entityType: 'conversation', entityId: id });
                    showToast(on
                        ? 'Molly will remember this conversation in your future sessions.'
                        : 'Forgotten — Molly will no longer bring this conversation up.', 'success');
                } catch (e) {
                    showToast('That did not save. Try again in a moment.', 'error');
                }
            };

            // Say where the thread went. It leaving the screen is the whole
            // reason Past sessions exists, and silence reads as lost. Only when
            // there is something to reopen — persistence can fail offline.
            const pressNewSession = () => {
                const kept = !!conversationIdRef.current;
                startNewSession();
                // A different problem is not about the case either, and a view
                // mounted after this must not walk back into the row just put down.
                caseRef.current = null;
                setMentorThread(null);
                if (kept) showToast('Saved — reopen it under Past sessions.', 'success');
            };

            const mentor = database.ciMentor;
            const stepTitles = (mentor.processSteps && mentor.processSteps.length === 4)
                ? mentor.processSteps.map(s => s.title)
                : ['Clarify', 'Stress-Test', 'Pattern-Match', 'Action Plan'];
            // Recorded against each stored turn so an archived conversation shows
            // how far it got, per the archive spec's stage_reached.
            const MENTOR_STAGES = stepTitles;
            const hour = new Date().getHours();
            const greetingText = `${hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'}. ${mentor.agent.greeting}`;

            // Initial greeting on mount. If the user arrived via the Decision
            // Simulator handoff (ciSeed set), greet and then auto-send their typed
            // company issue as the first turn so Molly responds immediately.
            //
            // The thread on screen is App state and survives a remount; the two
            // refs did not. So "Keep going with Molly" showed the case's exchange
            // on her page, then wrote the next turn into a SECOND row with no case
            // behind it. mentorThread mirrors both refs (every assignment below is
            // followed by setMentorThread), and a mount that finds a thread on
            // screen takes them back first — unless it was handed an opening,
            // which starts a thread of its own.
            useEffect(() => {
                const resuming = messages.length > 0;
                if (!opening && resuming && mentorThread) { conversationIdRef.current = mentorThread.conversationId; caseRef.current = mentorThread.scenarioId ? { scenarioId: mentorThread.scenarioId, choice: mentorThread.choice } : null; lessonRef.current = mentorThread.lesson || null; }
                if (opening && opening.text) {
                    // In the situation: a new case is a new conversation, whatever
                    // was said last week. No greeting — Molly is already in the
                    // room; her first line is about the call just made.
                    conversationIdRef.current = null;
                    setMessages([]);
                    setCurrentStep(0);
                    setMentorLogged(false);
                    setRatedTurns({});
                    setResumedFrom(null);
                    setMentorFresh(false);
                    setInputValue('');
                    setIsTyping(false);
                    if (opening.scenarioId) caseRef.current = { scenarioId: opening.scenarioId, choice: opening.choice || null };
                    // A lesson is its own thread: no case behind it, the script in front.
                    lessonRef.current = opening.lessonId ? { id: opening.lessonId, title: opening.lessonTitle || '', keep: opening.keep || null, stepCount: opening.stepCount || null } : null;
                    if (opening.lessonId) caseRef.current = null;
                    setMentorThread(threadNow());
                    const t = setTimeout(() => addBotMessage(opening.text, { fromCase: true }), 300);
                    return () => clearTimeout(t);
                }
                if (mentorResume && mentorResume.id) {
                    // Picked out of Your conversations by hand. Same road back
                    // as an interrupted one — it is already loaded, so it skips
                    // the lookup.
                    const r = mentorResume;
                    setMentorResume(null);
                    putBack({ id: r.id, title: r.title || null, stageReached: r.stage, messages: r.messages || [], scenarioId: r.scenarioId });
                    return;
                }
                if (ciSeed) {
                    // Seed may be a plain string (legacy) or { text, fromSim } — the
                    // "from today's simulation" kicker only applies to sim handoffs.
                    const seed = typeof ciSeed === 'string' ? { text: ciSeed, fromSim: true } : ciSeed;
                    if (seed.scenarioId) { caseRef.current = { scenarioId: seed.scenarioId, choice: seed.choice || null }; setMentorThread(threadNow()); }
                    setCiSeed(null); // consume once
                    // A seed arriving mid-conversation continues the thread; only a
                    // fresh thread gets the greeting (and its lead-in delay).
                    const t1 = resuming ? null : setTimeout(() => addBotMessage(greetingText), 400);
                    const t2 = setTimeout(() => {
                        addUserMessage(seed.text, seed.fromSim);
                        setTimeout(() => processUserInput(seed.text), 500);
                    }, resuming ? 300 : 2600);
                    return () => { if (t1) clearTimeout(t1); clearTimeout(t2); };
                }
                if (resuming) return; // resumed session — don't re-greet

                let cancelled = false;
                let timer = null;
                const greet = () => { timer = setTimeout(() => addBotMessage(greetingText), 500); };

                // Nothing to look up: embedded, signed out, or a session the
                // user deliberately ended with New session.
                if (embedded || mentorFresh || !API_URL || !authToken) {
                    greet();
                    return () => { if (timer) clearTimeout(timer); };
                }

                // Look before greeting. "Good morning" landing on the screen
                // and then being wiped by the restored thread is worse than a
                // moment of the typing indicator, which is what this shows.
                setIsTyping(true);
                findInterrupted().then(data => {
                    if (cancelled) return;
                    if (data) putBack(data);
                    else greet();   // addBotMessage keeps the indicator running
                });
                return () => {
                    cancelled = true;
                    if (timer) clearTimeout(timer);
                    // Leaving during the lookup must not strand the composer:
                    // the typing flag is app state and outlives this view.
                    setIsTyping(false);
                };
            }, []);

            // The greeting's typing delay must not outlive the view — and since the
            // typing flag is shared app state now, cancelling it must also clear the
            // flag or the composer stays disabled forever.
            useEffect(() => () => {
                if (botTimerRef.current) {
                    clearTimeout(botTimerRef.current);
                    botTimerRef.current = null;
                    setIsTyping(false);
                }
                // Leaving the view cancels a turn in flight; otherwise its
                // response lands on an unmounted component and the typing flag
                // (which is app state) stays set.
                if (abortRef.current) abortRef.current.abort('unmount');
            }, []);

            // Follow the thread. A reply is read from its FIRST line: landing
            // on its last one (tester feedback, Sep 2026) left the member
            // scrolling back up through every answer to find where it began.
            // So a new reply brings its top into view; the member's own turn
            // and the typing dots still go to the bottom, where they are.
            // Embedded in a case, the thread scrolls ITSELF: scrollIntoView
            // scrolls every scrollable ancestor too, so each message dragged
            // the case page under a member who had scrolled up to reread the
            // brief.
            // "↑ Start of the answer" (Marco, Oct 2026: "can it scroll BACK up to the
            // Start of the Answer"). A reply opens at its first line already; once
            // the member has read on past it — into the clips, the next move — the
            // pill takes them back to where the latest answer begins.
            const [backToAnswer, setBackToAnswer] = useState(null);
            const latestAnswerIdx = (() => { for (let i = messages.length - 1; i > 0; i--) if (messages[i].type === 'bot') return i; return -1; })();
            useEffect(() => {
                const box = threadRef.current;
                if (!box || latestAnswerIdx < 1) { setBackToAnswer(null); return; }
                const check = () => {
                    const el = box.querySelector(`[data-msg-idx="${latestAnswerIdx}"]`);
                    if (!el) { setBackToAnswer(null); return; }
                    const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top;
                    setBackToAnswer(top < -120 ? latestAnswerIdx : null);
                };
                check();
                box.addEventListener('scroll', check, { passive: true });
                return () => box.removeEventListener('scroll', check);
            }, [latestAnswerIdx]);
            const goToAnswerStart = () => {
                const box = threadRef.current;
                const el = box && box.querySelector(`[data-msg-idx="${latestAnswerIdx}"]`);
                if (!el) return;
                box.scrollTo({ top: box.scrollTop + el.getBoundingClientRect().top - box.getBoundingClientRect().top - 8, behavior: scrollBehavior() });
            };

            useEffect(() => {
                const last = messages[messages.length - 1];
                const box = threadRef.current;
                const replyEl = !isTyping && last && last.type === 'bot' && messages.length > 1 && box
                    ? box.querySelector(`[data-msg-idx="${messages.length - 1}"]`) : null;
                if (replyEl) {
                    if (embedded) {
                        box.scrollTop += replyEl.getBoundingClientRect().top - box.getBoundingClientRect().top - 8;
                    } else {
                        replyEl.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
                    }
                    return;
                }
                if (embedded) {
                    const threadEl = threadRef.current;
                    if (threadEl) threadEl.scrollTop = threadEl.scrollHeight;
                    return;
                }
                messagesEndRef.current?.scrollIntoView({ behavior: scrollBehavior() });
            }, [messages, isTyping]);

            // Add bot message with typing simulation (greeting only — real replies
            // arrive when the network does)
            const addBotMessage = (text, extra = {}) => {
                setIsTyping(true);
                const delay = 1000 + Math.random() * 1000;
                botTimerRef.current = setTimeout(() => {
                    botTimerRef.current = null;
                    setMessages(prev => [...prev, { type: 'bot', text, ...extra, timestamp: new Date() }]);
                    setIsTyping(false);
                }, delay);
            };

            const addUserMessage = (text, seeded = false) => {
                setMessages(prev => [...prev, { type: 'user', text, seeded, timestamp: new Date() }]);
            };

            const handleSend = () => {
                if (!inputValue.trim() || isTyping) return; // no concurrent LLM calls
                // The simulator counts the handoff as taken when the user answers
                // Molly's opening — once, on the first reply.
                const userTurns = messages.filter(m => m.type === 'user').length;
                if (onFirstReply && userTurns === 0) onFirstReply();
                if (onUserTurn) onUserTurn(userTurns + 1);
                addUserMessage(inputValue);
                const userInput = inputValue;
                setInputValue('');
                if (textareaRef.current) textareaRef.current.style.height = '46px';
                setTimeout(() => { processUserInput(userInput); }, 400);
            };

            // 👍 / 👎 per bot turn, keyed by message index for this session. The
            // event carries the conversation id (null when persistence failed —
            // the rating still counts) and the stage the rail was on.
            // The case this session came from, kept for the whole conversation:
            // turn four is still about the situation they just worked.
            const caseRef = useRef(null);
            // Both refs as the App keeps them (mentorThread), or null when there
            // is neither a row nor a case to carry on.
            const threadNow = () => (conversationIdRef.current || caseRef.current || lessonRef.current)
                ? { conversationId: conversationIdRef.current || null, scenarioId: caseRef.current ? caseRef.current.scenarioId : null, choice: caseRef.current ? caseRef.current.choice || null : null, lesson: lessonRef.current || null }
                : null;
            // A 👎 opens a panel: what was wrong, optionally why in their own
            // words, and the tick that decides whether anyone at Edify may read
            // the conversation. See docs/MOLLY_REVIEW.md — the tick is the only
            // thing that opens that door, and it is revocable.
            const [reportFor, setReportFor] = useState(null);

            // The report row, written to the user's OWN conversation. The
            // `mentor_rated` event still fires unchanged beside it, because
            // Pulse's arithmetic is built on that event.
            const sendRating = async (index, rating, extra = {}) => {
                const id = conversationIdRef.current;
                if (!id || !API_URL || !authToken) return;   // nothing persisted to attach it to
                try {
                    await fetch(`${API_URL}/api/conversations/${id}/ratings`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({
                            turn: index,
                            rating,
                            stage: stepTitles[Math.min(currentStep, 3)] || null,
                            ...extra
                        })
                    });
                } catch (e) { /* the event below is still the measurement */ }
            };

            const rateMentorTurn = (index, rating) => {
                setRatedTurns(prev => (prev[index] === rating ? prev : { ...prev, [index]: rating }));
                if (ratedTurns[index] === rating) return;
                Telemetry.track('mentor_rated', {
                    conversationId: conversationIdRef.current || null,
                    turn: index,
                    rating,
                    stage: stepTitles[Math.min(currentStep, 3)] || null,
                    // Where the rating was given: inside a case's handoff, or on her page.
                    place: embedded ? 'case' : 'page'
                });
                // A 👍 records itself and closes any open report panel. A 👎
                // opens the panel — the row is written when they finish it, or
                // straight away if they dismiss it, so a report is never lost.
                if (rating === 'up') { setReportFor(null); sendRating(index, 'up'); }
                else setReportFor({ index });
            };

            const submitReport = async ({ index, reason, note, share }) => {
                await sendRating(index, 'down', { reason: reason || null, note: note || null, share: !!share });
                setReportFor(null);
                showToast(share ? 'Thank you — the team can read this one.' : 'Thank you.', 'success');
            };

            // Persist a completed exchange to D1 so the conversation survives a
            // reload and can be reopened later (and, once old, archived to R2).
            // Best-effort: the chat must never break because persistence failed,
            // so every step swallows its error and the UI is unaffected.
            const persistTurn = async (userText, botText, sources, stage) => {
                if (!API_URL || !authToken) return;
                try {
                    let id = conversationIdRef.current;
                    // Whether this turn opens a row for a case: the one that marks
                    // the case "talked through" in the member's record.
                    let openedOnCase = false;
                    // A row this turn did not open can be gone: deleted from Your
                    // conversations (or by an admin) while the thread stayed on
                    // screen. Its messages 404, and writing every later turn into
                    // it would lose them all without a word — so the thread lets
                    // go of it and this turn opens a fresh row, once.
                    const inherited = !!id;
                    if (!id) {
                        id = (crypto.randomUUID ? crypto.randomUUID() : `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
                        conversationIdRef.current = id;
                        setMentorThread(threadNow());
                        openedOnCase = !!caseRef.current;
                        // The case rides on the row by id (migration 0027), never
                        // as prose; a worker that predates it ignores the key.
                        const created = await fetch(`${API_URL}/api/conversations`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                            body: JSON.stringify({ id, title: userText, ...(caseRef.current ? { scenarioId: caseRef.current.scenarioId } : {}), ...(lessonRef.current ? { kind: 'lesson', title: lessonRef.current.title || userText } : {}) })
                        });
                        if (!created.ok) { conversationIdRef.current = null; setMentorThread(threadNow()); return; }
                    }
                    const post = (role, text, extra) => fetch(`${API_URL}/api/conversations/${id}/messages`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({ role, text, stage, ...extra })
                    });
                    const sent = await post('user', userText, {});
                    if (sent.status === 404) {
                        conversationIdRef.current = null;
                        setMentorThread(threadNow());
                        if (inherited) return persistTurn(userText, botText, sources, stage);
                        return;
                    }
                    const replied = await post('bot', botText, sources && sources.length ? { sources } : {});
                    if (replied.status === 404) { conversationIdRef.current = null; setMentorThread(threadNow()); return; }
                    // Home and the journal read "talked through" from the server.
                    if (openedOnCase) refreshDecisionProfile();
                } catch (e) {
                    // Offline or the endpoint is unavailable — the live conversation
                    // is unaffected; this turn simply isn't archived.
                }
            };

            // Call the real LLM mentor (Phase 2 — Workers AI, proxied by the Worker).
            // A hung model call used to freeze the composer for good: no timeout,
            // no abort, no way back. This gives the turn a deadline and the user
            // a Stop.
            const abortRef = useRef(null);
            const MENTOR_TIMEOUT_MS = 35000;

            const stopMentor = () => {
                if (abortRef.current) abortRef.current.abort('user');
            };

            const requestMentorReply = async (latestInput) => {
                const history = messages
                    .filter(m => m.type === 'user' || m.type === 'bot')
                    .map(m => ({ role: m.type === 'bot' ? 'assistant' : 'user', content: m.text }));
                // On retry the kept failed turn is already the trailing entry —
                // don't send the same user message twice.
                const last = history[history.length - 1];
                if (!last || last.role !== 'user' || last.content !== latestInput) {
                    history.push({ role: 'user', content: latestInput });
                }
                const controller = new AbortController();
                abortRef.current = controller;
                const timer = setTimeout(() => controller.abort('timeout'), MENTOR_TIMEOUT_MS);
                try {
                    const res = await fetch(`${API_URL}/api/chat`, {
                        method: 'POST',
                        signal: controller.signal,
                        headers: {
                            'Content-Type': 'application/json',
                            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
                        },
                        body: JSON.stringify({
                            usage: lessonRef.current ? 'lesson' : 'ciMentor',
                            messages: history,
                            // So Molly's memory does not read this session back
                            // to her a second time (mollyMemory.ts).
                            ...(conversationIdRef.current ? { conversationId: conversationIdRef.current } : {}),
                            // An id and the option they picked — the worker
                            // resolves the rest from its own library. A lesson
                            // sends its id the same way: the script is the worker's.
                            ...(lessonRef.current ? { context: { lessonId: lessonRef.current.id } } : caseRef.current ? { context: caseRef.current } : {})
                        })
                    });
                    if (res.status === 401) { handleSessionExpired(); return { error: 'Your session expired.' }; }
                    const data = await res.json().catch(() => ({}));
                    if (res.ok && data.success && data.data && data.data.text) {
                        // `stage` is the worker's validated read of what THIS
                        // reply did (null on an older worker, or when the model
                        // said nothing usable — then the rail falls back to
                        // counting turns, as it always did).
                        return {
                            text: data.data.text, sources: data.data.sources || [],
                            // The CEO's own words when the reply quotes them (worker: resolveQuotes).
                            quotes: data.data.quotes || [],
                            stage: data.data.stage || null,
                            // false = the library matched nothing, so this reply is
                            // Molly reasoning rather than what CEOs actually did.
                            grounded: data.data.grounded !== false
                        };
                    }
                    // The worker says when a retry cannot help (a refusal, a
                    // misconfigured model): honour that over the status code, so
                    // the card offers "try again" only when trying again can work.
                    return { error: data.error || 'Molly is unavailable right now.', retriable: data.retriable === false ? false : (res.status >= 500 || res.status === 429) };
                } catch (e) {
                    // Four different endings, and they must not read the same.
                    // 'unmount' especially: treating it as a user Stop deleted
                    // the user's question from a thread that lives in APP state
                    // and survives the unmount — so stepping away mid-reply lost
                    // the turn, and the composer it was "handed back" to had
                    // already gone.
                    if (controller.signal.aborted) {
                        if (controller.signal.reason === 'timeout') {
                            return { error: 'That took too long — Molly did not answer within 35 seconds.', retriable: true };
                        }
                        return controller.signal.reason === 'unmount' ? { discarded: true } : { stopped: true };
                    }
                    return { error: 'The connection dropped mid-thought.', retriable: true };
                } finally {
                    clearTimeout(timer);
                    abortRef.current = null;
                }
            };

            // Process a user turn. The step rail advances one step per successful
            // Molly reply; a failed turn renders a retriable error card instead.
            const processUserInput = async (input) => {
                setIsTyping(true);
                const reply = await requestMentorReply(input);
                setIsTyping(false);
                if (reply.discarded) {
                    // The view unmounted mid-flight. The thread is APP state and
                    // survives, so the user's turn stays exactly where it is —
                    // removing it (as a Stop does) deleted their question from a
                    // conversation they were coming back to.
                    return;
                }
                if (reply.stopped) {
                    // Stopped by the user, deliberately: drop the pending turn
                    // from the thread and hand their words back to the composer,
                    // unsent. Safe here — the view is still mounted.
                    setMessages(prev => {
                        const last = prev[prev.length - 1];
                        return last && last.type === 'user' && last.text === input ? prev.slice(0, -1) : prev;
                    });
                    setInputValue(input);
                    return;
                }
                if (reply.text) {
                    setMessages(prev => [...prev, { type: 'bot', text: reply.text, sources: reply.sources, quotes: reply.quotes, grounded: reply.grounded, timestamp: new Date() }]);
                    // The rail follows the CONVERSATION now, not the turn count:
                    // Molly names the stage her reply actually did and the worker
                    // validates it. It only ever moves forward — a reply that
                    // circles back to clarifying should not rewind a rail the
                    // user has already watched advance.
                    const said = reply.stage ? MENTOR_STAGES.indexOf(reply.stage) : -1;
                    const step = said >= 0
                        ? Math.max(currentStep, Math.min(said, 3))
                        : Math.min(currentStep + 1, 3);
                    setCurrentStep(step);
                    if (!mentorLogged) { setMentorLogged(true); addCISession({ completedSteps: 1 }); }
                    // The session is "done" when the rail reaches its last stage —
                    // there is no explicit end, so this is the moment to ask. Guarded
                    // on the arrival, not the state, so later turns don't re-ask.
                    if (step === 3 && currentStep < 3) {
                        const firstUser = messages.find(m => m.type === 'user');
                        requestFeedback('mentor', {
                            type: 'mentorSession',
                            id: conversationIdRef.current || null,
                            label: feedbackLabel((firstUser && firstUser.text) || input),
                            extra: { view: 'ci-mentor', stage: MENTOR_STAGES[step] || null, turns: messages.length }
                        });
                    }
                    // Persist the exchange. Both turns are written only after a
                    // successful reply, so a failed turn never leaves a dangling
                    // user message in the stored transcript.
                    persistTurn(input, stripClipMarkers(reply.text), [...(reply.sources || []), ...(reply.quotes || []).map(q => ({ ...q, why: 'quote' }))], MENTOR_STAGES[step] || null);
                } else {
                    setMessages(prev => [...prev, {
                        type: 'error', text: reply.error, retryInput: input,
                        // A 403 (the mentor is closed to this account) or a 400
                        // will fail identically forever; offering "Try again"
                        // for those spends the user's daily allowance on a wall.
                        retriable: reply.retriable !== false, timestamp: new Date()
                    }]);
                }
            };

            // Retry a failed turn: drop the error card, re-send the same input.
            const retryMentor = (input) => {
                if (isTyping) return; // same guard as handleSend — no concurrent calls
                setMessages(prev => prev.filter(m => m.type !== 'error'));
                processUserInput(input);
            };

            // Split a trailing "Action: ..." line out of a reply so it can render
            // as the saveable "Your next move" card.
            // The model is told to write plain prose, and mostly does — but an 8B
            // still emits `**Action:**` often enough that a strict match loses
            // the "Your next move" card silently, which is the one part of the
            // reply the user is meant to keep. Tolerate the asterisks here and
            // strip them from what is shown.
            const stripMd = (t) => String(t || '')
                // A belt for the worker's braces: if a `Stage:` line survives
                // (an older worker, or a shape its regex missed) it must not
                // reach the screen as prose — and while it sits at the end it
                // also stops `Action:` being last, which silently removes the
                // "Your next move" card.
                .replace(/(?:^|\n)[ \t]*[*_]{0,2}[ \t]*Stage[ \t]*[*_]{0,2}[ \t]*:[^\n]*/gi, '')
                .replace(/\*\*(.+?)\*\*/g, '$1')
                .replace(/(^|\s)\*(\S[^*]*?)\*(?=\s|$|[.,;:!?])/g, '$1$2')
                .replace(/^#{1,6}\s+/gm, '')
                .replace(/^\s*[-*•]\s+/gm, '');
            const splitAction = (text) => {
                const clean = stripMd(text);
                const m = clean.match(/(?:^|\n)\s*Action:\s*([^\n]+?)\s*$/i);
                if (!m) return { main: clean, action: null };
                return { main: clean.replace(m[0], '').trim(), action: m[1].trim() };
            };

            // Molly cites clips as [C1]… (the worker guarantees every surviving
            // marker maps to a source). Render each as an inline "hear the CEO"
            // chip at the exact point in the argument, scrolling its player into
            // view — the citation is part of the advice, not an appendix.
            const CLIP_MARKER_RE = /\[C\d{1,2}\]/g;
            const stripClipMarkers = (text) => String(text || '').replace(CLIP_MARKER_RE, '').replace(/ {2,}/g, ' ').replace(/ +([.,;:!?])/g, '$1');
            const renderWithClipChips = (text, sources, msgIndex) => {
                const bits = String(text || '').split(/(\[C\d{1,2}\])/g);
                if (bits.length === 1) return text;
                return bits.map((bit, i) => {
                    const m = bit.match(/^\[(C\d{1,2})\]$/);
                    if (!m) return bit;
                    const srcIdx = (sources || []).findIndex(x => x.marker === m[1]);
                    if (srcIdx < 0) return '';
                    const src = sources[srcIdx];
                    return (
                        <span key={`chip-${i}`}
                            onClick={() => {
                                const el = document.getElementById(`mentor-clip-${msgIndex}-${srcIdx}`);
                                if (el) { el.scrollIntoView({ behavior: scrollBehavior(), block: 'center' }); el.classList.remove('ed-clip-flash'); void el.offsetWidth; el.classList.add('ed-clip-flash'); }
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.target.click(); } }}
                            role="button" tabIndex={0} aria-label={`Play the clip of ${src.ceo}`}
                            data-testid="mentor-clip-chip"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', margin: '0 2px', padding: '1px 8px', borderRadius: '999px', background: 'var(--cream-dark)', border: '1px solid var(--gold)', color: 'var(--gold-darker)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', verticalAlign: 'baseline', whiteSpace: 'nowrap' }}>
                            ▶ {src.ceo}
                        </span>
                    );
                });
            };

            const handleKeyDown = (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                }
            };

            const handleTextareaChange = (e) => {
                setInputValue(e.target.value);
                e.target.style.height = 'auto';
                const newHeight = Math.min(Math.max(e.target.scrollHeight, 46), 160);
                e.target.style.height = newHeight + 'px';
            };

            // "Ask a CEO": carry the current challenge into the Ask-a-CEO view.
            // A session that began in a case carries the CASE instead, as the
            // same attachment the case's own exit sends (§7.6) — its title, the
            // member's call and the CEO's, and nothing said here. The question
            // box starts empty, because what they told Molly is not a question
            // they chose to put to a person.
            const askACeo = () => {
                const c = caseRef.current;
                const scenarios = (database.decisionSimulator && database.decisionSimulator.scenarios) || [];
                const scenario = c && c.scenarioId ? scenarios.find(s => s && s.id === c.scenarioId) : null;
                if (scenario) {
                    setCeoSeed({ text: '', attachment: caseAttachmentFor(scenario, c.choice) });
                } else {
                    const challenge = messages.find(m => m.type === 'user')?.text || '';
                    if (challenge) setCeoSeed(challenge);
                }
                setCurrentView('ceo-wisdom');
            };

            const botReplies = messages.filter(m => m.type === 'bot').length;
            const stepPct = (currentStep / 3 * 75 + 12);

            return (
                <div className={embedded ? '' : 'ed-pagein'} data-testid="ci-mentor-view" data-embedded={embedded ? 'true' : undefined}
                    style={embedded
                        ? { display: 'flex', flexDirection: 'column', minHeight: 0 }
                        : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', paddingTop: '18px' }}>

                    {/* Masthead. Embedded, it is an identity line and nothing to
                        press: New session and Close belong to the page, and the
                        simulator around this owns where the user goes next. */}
                    {embedded && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 0 10px', borderBottom: '1px solid var(--border-card)' }}>
                            <div data-testid="bot-avatar" style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '16px', fontWeight: 600 }}>M</div>
                            <div>
                                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', color: 'var(--navy)', fontWeight: 600, lineHeight: 1.2 }}>Molly</div>
                                <div style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 600 }}>CI Mentor · on this case</div>
                            </div>
                        </div>
                    )}
                    {!embedded && mentorEnabled && <MollyTabs active={lessonRef.current ? 'learn' : 'talk'} />}
                    {/* The masthead was one row holding an avatar, a name, a subtitle
                        and THREE text actions. At 393px that leaves about 120px for
                        the subtitle, so a lesson called "What never leaves your
                        company" wrapped four times — and it was set in 11px letter-
                        spaced capitals, the least readable setting in the system.

                        So: a bar, then the context. The bar carries who you are
                        talking to and the one control people reach for (Close). The
                        two session controls are occasional, so they moved behind
                        More. A lesson then gets a line of its own, at a size meant
                        for reading, in sentence case. */}
                    {!embedded && <div style={{ paddingBottom: '12px', borderBottom: '1px solid var(--border-card)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                            <div data-testid="bot-avatar" style={{ flex: 'none', width: '40px', height: '40px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '19px', fontWeight: 600 }}>M</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', color: 'var(--navy)', fontWeight: 600, lineHeight: 1.2 }}>Molly</div>
                                <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)' }} data-testid="mentor-subtitle">{lessonRef.current ? 'Learning together' : 'In session'}</div>
                            </div>
                            <button type="button" onClick={() => setSessionMenu(true)} data-testid="mentor-session-menu"
                                aria-label="Session options" aria-haspopup="dialog"
                                style={{ flex: 'none', minWidth: '40px', minHeight: '40px', borderRadius: '10px', border: '1px solid var(--border-input)', background: 'var(--white)', color: 'var(--navy)', cursor: 'pointer', fontSize: '15px', fontWeight: 700, lineHeight: 1 }}>···</button>
                            <button type="button" onClick={() => setCurrentView('home')} data-testid="mentor-close" aria-label="Close Molly"
                                style={{ flex: 'none', minWidth: '40px', minHeight: '40px', borderRadius: '10px', border: '1px solid var(--border-input)', background: 'var(--white)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '17px', lineHeight: 1 }}>×</button>
                        </div>
                        {lessonRef.current && (
                            <div data-testid="mentor-lesson-title" style={{ marginTop: '10px' }}>
                                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', lineHeight: 1.3, color: 'var(--navy)', fontWeight: 600 }}>{lessonRef.current.title}</div>
                                {lessonRef.current.stepCount > 0 && (
                                    <div style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                        {`${lessonRef.current.stepCount} ${lessonRef.current.stepCount === 1 ? 'step' : 'steps'}`}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>}

                    {/* The thread lives in App state so it survives navigation —
                        deliberate — but nothing ever reset it except signing out.
                        A second, unrelated challenge had to be typed into the
                        conversation about the first, which is also the history
                        sent to the model, so the reply was answering both.

                        Everything said here is kept, and the only door to it used
                        to be four items down a menu on the Profile page. A session
                        that ends with "New session" has to say where it went. */}
                    {sessionMenu && (
                        <ModalPortal onEscape={() => setSessionMenu(false)}>
                            <div onClick={() => setSessionMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                                <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Session options" data-testid="mentor-session-sheet" className="ed-pagein"
                                    style={{ width: '100%', maxWidth: '520px', background: 'var(--white)', borderRadius: '20px 20px 0 0', boxShadow: 'var(--shadow-modal)', padding: '10px 0 18px' }}>
                                    {messages.length > 0 && (
                                        <button type="button" autoFocus data-testid="mentor-new-session"
                                            onClick={() => { setSessionMenu(false); pressNewSession(); }}
                                            className="ed-menu-row"
                                            style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: '14px 22px', fontSize: '15px', fontWeight: 700, color: 'var(--gold-darker)', minHeight: '52px' }}>
                                            Start a new session
                                        </button>
                                    )}
                                    {conversationIdRef.current && !lessonRef.current && (
                                        <button type="button" data-testid="mentor-remember"
                                            onClick={() => { setSessionMenu(false); toggleRemember(); }}
                                            className="ed-menu-row"
                                            style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: '14px 22px', fontSize: '15px', color: 'var(--navy)', minHeight: '52px' }}>
                                            {isRemembered ? 'Forget this conversation' : 'Remember this conversation'}
                                            <span style={{ display: 'block', fontSize: '12.5px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                                {isRemembered ? 'Molly stops bringing it into future sessions.' : 'Molly brings what you said here into future sessions.'}
                                            </span>
                                        </button>
                                    )}
                                    <button type="button" data-testid="mentor-past-sessions"
                                        onClick={() => { setSessionMenu(false); setConvosBackTo('ci-mentor'); setCurrentView('conversations'); }}
                                        className="ed-menu-row"
                                        style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: '14px 22px', fontSize: '15px', color: 'var(--navy)', minHeight: '52px' }}>
                                        Past sessions
                                    </button>
                                    <button type="button" onClick={() => setSessionMenu(false)}
                                        className="ed-menu-row"
                                        style={{ width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: '14px 22px', fontSize: '15px', color: 'var(--text-secondary)', minHeight: '52px' }}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </ModalPortal>
                    )}

                    {/* Step rail — position, not badges. Not in the situation: the
                        case has its own stepper and this is one exchange, not a
                        four-stage session (yet). */}
                    {!embedded && !lessonRef.current && <div data-testid="process-steps" style={{ padding: '13px 0 11px', borderBottom: '1px solid var(--border-card)', background: 'var(--cream)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            {stepTitles.map((label, i) => (
                                <span key={label} data-testid={`process-step-${i + 1}`} style={{
                                    fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase',
                                    fontWeight: i === currentStep ? 700 : 500,
                                    color: i < currentStep ? 'var(--text-tertiary)' : i === currentStep ? 'var(--navy)' : 'var(--text-faint)'
                                }}>{label}</span>
                            ))}
                        </div>
                        <div style={{ height: '2px', background: 'var(--track)', borderRadius: '2px', position: 'relative' }}>
                            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${stepPct}%`, background: 'linear-gradient(90deg, var(--gold-dark), var(--gold))', borderRadius: '2px', transition: 'width 0.32s var(--page-ease)' }}></div>
                            <div style={{ position: 'absolute', left: `calc(${stepPct}% - 4px)`, top: '-3px', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--gold)', boxShadow: '0 0 0 3px var(--cream)', transition: 'left 0.32s var(--page-ease)' }}></div>
                        </div>
                    </div>}

                    {/* Says what just happened, because the screen filling with
                        an old conversation needs an explanation and a way out.
                        Gone as soon as they add anything of their own. */}
                    {!embedded && resumedFrom && messages.length <= resumedFrom.at && (
                        <div data-testid="mentor-resume-last"
                            style={{ marginTop: '12px', background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '11px 14px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', marginBottom: '2px' }}>Picked up where you left off</div>
                                <div style={{ fontSize: '13px', color: 'var(--navy)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {resumedFrom.title || 'Your last session'}
                                </div>
                            </div>
                            <span
                                onClick={pressNewSession} onKeyDown={btnKey(pressNewSession)}
                                role="button" tabIndex={0} data-testid="mentor-resume-fresh"
                                style={{ flexShrink: 0, fontSize: '12.5px', color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>
                                Different problem →
                            </span>
                        </div>
                    )}

                    {/* Thread */}
                    {/* What was wrong, and who may read it. Opened by a 👎. */}
                    {reportFor && (
                        <MentorReportPanel
                            onCancel={() => { sendRating(reportFor.index, 'down'); setReportFor(null); }}
                            onSubmit={(payload) => submitReport({ index: reportFor.index, ...payload })}
                        />
                    )}

                    {/* role="log" + aria-live: a screen-reader user used to send a
                        message and hear nothing at all for up to 35 seconds, then
                        have the page silently change under them. The simulator's
                        result banner got this right; the chat — the product's core
                        interaction — did not. */}
                    <div data-testid="chat-container" role="log" aria-live="polite" aria-relevant="additions"
                        aria-label="Conversation with Molly" ref={threadRef}
                        style={{ flex: embedded ? '0 1 auto' : 1, minHeight: 0, maxHeight: embedded ? '48vh' : undefined, overflowY: 'auto', padding: '16px 2px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {/* A row of no height that sticks to the top of the thread, so
                            the pill appearing never moves the conversation under it. */}
                        {backToAnswer != null && (
                            <div style={{ position: 'sticky', top: 0, height: 0, zIndex: 3, display: 'flex', justifyContent: 'center', overflow: 'visible' }}>
                                <button type="button" data-testid="back-to-answer" onClick={goToAnswerStart}
                                    style={{ marginTop: '6px', padding: '7px 14px', border: '1px solid var(--border-card)', borderRadius: '999px', background: 'var(--white)', boxShadow: 'var(--e2, 0 2px 8px rgba(11,20,38,0.12))', color: 'var(--navy)', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                                    ↑ Start of Molly's answer
                                </button>
                            </div>
                        )}
                        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-meta)', letterSpacing: '0.05em' }}>
                            Illustrative guidance modeled on executive thinking — not a specific CEO speaking.
                        </div>
                        {/* In view, not only in the menu: a memory the member
                            has to hunt for is one they never set. Offered once
                            the session is saved and Molly has answered. */}
                        {!embedded && !lessonRef.current && conversationIdRef.current && (isRemembered || messages.some(m => m.type === 'user')) && (
                            isRemembered ? (
                                <div data-testid="mentor-remembered" style={{ textAlign: 'center', fontSize: '11.5px', color: 'var(--gold-darker)', fontWeight: 600 }}>
                                    Molly will remember this conversation ·{' '}
                                    <span role="button" tabIndex={0} onClick={toggleRemember} onKeyDown={btnKey(toggleRemember)}
                                        style={{ textDecoration: 'underline', cursor: 'pointer' }}>Forget</span>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center' }}>
                                    <span role="button" tabIndex={0} data-testid="mentor-remember-inline" onClick={toggleRemember} onKeyDown={btnKey(toggleRemember)}
                                        style={{ fontSize: '11.5px', color: 'var(--gold-darker)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
                                        Remember this conversation
                                    </span>
                                </div>
                            )
                        )}

                        {messages.map((msg, index) => {
                            if (msg.type === 'user') {
                                return (
                                    <div key={index} data-testid="user-message" style={{ alignSelf: 'flex-end', maxWidth: '88%' }}>
                                        {msg.seeded && (
                                            <div style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', textAlign: 'right', marginBottom: '4px' }}>
                                                Your challenge · from today's simulation
                                            </div>
                                        )}
                                        <div style={{ background: 'var(--navy)', color: 'var(--text-on-navy)', padding: '13px 16px', borderRadius: '14px 14px 4px 14px', fontSize: '14px', lineHeight: 1.55, whiteSpace: 'pre-line' }}>{msg.text}</div>
                                    </div>
                                );
                            }
                            if (msg.type === 'error') {
                                const errText = (msg.text || '').trim().replace(/([^.!?])$/, '$1.');
                                return (
                                    <div key={index} data-testid="mentor-error" style={{ alignSelf: 'flex-start', maxWidth: '88%', background: 'var(--cream)', border: '1px dashed var(--border-input)', borderRadius: '4px 14px 14px 14px', padding: '13px 16px' }}>
                                        <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--error-ink)', marginBottom: '3px' }}>Molly couldn't reply</div>
                                        <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                            {errText} Your message is kept.{' '}
                                            {msg.retriable !== false ? (
                                                <span
                                                    onClick={() => retryMentor(msg.retryInput)}
                                                    onKeyDown={btnKey(() => retryMentor(msg.retryInput))}
                                                    role="button" tabIndex={0}
                                                    style={{ color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>Try again</span>
                                            ) : (
                                                <span>Trying again will not help with this one.</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            }
                            const parsed = splitAction(msg.text);
                            const moveSaved = parsed.action && savedNextMoves.some(m => m.text === parsed.action);
                            const rated = ratedTurns[index] || null;
                            return (
                                <div key={index} data-testid="bot-message" data-msg-idx={index} data-from-case={msg.fromCase ? 'true' : undefined} style={{ maxWidth: '94%', alignSelf: 'flex-start', width: '100%' }}>
                                    {msg.fromCase && (
                                        <div style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', marginBottom: '4px' }}>
                                            Molly · about the call you just made
                                        </div>
                                    )}
                                    <div style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '4px 14px 14px 14px', padding: '15px 16px', boxShadow: 'var(--e1)' }}>
                                        <p style={{ margin: 0, fontSize: '14px', color: '#2b2f3a', lineHeight: 1.65, whiteSpace: 'pre-line' }}>{renderWithClipChips(parsed.main, msg.sources, index)}</p>
                                        {msg.grounded === false && (
                                            // Honesty about the source: the library had nothing
                                            // on this, so the reply is reasoning, not evidence.
                                            <div data-testid="mentor-ungrounded" style={{ marginTop: '10px', paddingTop: '9px', borderTop: '1px dashed var(--border-card)', fontSize: '11.5px', color: 'var(--text-tertiary)', display: 'flex', gap: '7px', alignItems: 'flex-start' }}>
                                                <span aria-hidden="true">◇</span>
                                                <span>The CEO library has nothing on this one — that answer is reasoning, not something a CEO in the library did.</span>
                                            </div>
                                        )}
                                        {Array.isArray(msg.quotes) && msg.quotes.map((q, qi) => {
                                            // The clip chip for this recording scrolls to the box.
                                            const at = (msg.sources || []).findIndex(src => sameRecording(q, src));
                                            return <MollyQuote key={`quote-${qi}`} quote={q} heardBelow={(msg.sources || []).some(src => src.audioFile && sameCeo(src.ceo, q.ceo))} id={at >= 0 ? `mentor-clip-${index}-${at}` : `mentor-quote-${index}-${qi}`} />;
                                        })}
                                        {msg.sources && msg.sources.map((src, i) => (msg.quotes || []).some(q => sameRecording(q, src)) ? null : (
                                            <div key={`src-${i}`} id={`mentor-clip-${index}-${i}`}>
                                                <AudioClipCard
                                                    title={`${src.ceo}${src.company ? ' · ' + src.company : ''}`}
                                                    meta={[src.startS == null && src.topic ? `On ${src.topic}` : null, src.why === 'library' ? 'From the library' : src.cited ? 'Cited by Molly' : src.why === 'named' ? (src.startS == null && !src.transcript ? 'Named by Molly · the whole interview' : 'Named by Molly') : null].filter(Boolean).join(' · ') || null}
                                                    src={fixAudioPath(src.audioFile)}
                                                    fullSrc={wholeOf(src) ? fixAudioPath(wholeOf(src)) : null}
                                                    transcript={src.transcript || src.audioTranscript || null}
                                                    startS={src.startS ?? null}
                                                    endS={src.endS ?? null}
                                                    track={{ source: 'mentor', kind: src.startS != null ? 'full' : 'short', clip: { key: src.audioFile, ceo: src.ceo, why: src.why || (src.cited ? 'cited' : 'named') } }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {/* Stated sentiment on the reply itself — the only place the
                                        Mentor is rated. One event per press (docs/PULSE.md); a
                                        second press on the other glyph simply records the change.
                                        Not on the opening: that line is composed here, not by the
                                        model, and Pulse's arithmetic is about the model's replies. */}
                                    {/* Only on replies this session produced.
                                        A rating is stored against its TURN
                                        NUMBER, and that number counts positions
                                        in the array on screen — which a resumed
                                        conversation renumbers, because the
                                        greeting that was there when it was first
                                        worked is not part of what was saved. A
                                        thumb on a restored reply would file a
                                        report against a turn the reviewer would
                                        count differently, and could land on the
                                        same (conversation, turn) as a rating
                                        already given. The turn a reply was is
                                        not recoverable from the transcript, so
                                        the honest thing is not to offer it. */}
                                    {!msg.fromCase && index >= (resumedFrom ? resumedFrom.at : 0) && <div data-testid="mentor-rate" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginTop: '5px', paddingRight: '2px' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', height: '22px', fontSize: '10.5px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-meta)', marginRight: '4px' }}>
                                            {rated ? 'Thanks' : 'Helpful?'}
                                        </span>
                                        {/* Drawn thumbs, not emoji: an emoji glyph carries its own
                                            font metrics and rode several pixels above the label no
                                            matter what the flex row asked for. An SVG in a fixed
                                            box sits on the line with the word. */}
                                        {[['up', 'Helpful'], ['down', 'Not helpful']].map(([val, label]) => (
                                            <span key={val}
                                                onClick={() => rateMentorTurn(index, val)}
                                                onKeyDown={btnKey(() => rateMentorTurn(index, val))}
                                                role="button" tabIndex={0} aria-label={label} aria-pressed={rated === val}
                                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    width: '26px', height: '22px', borderRadius: '8px', cursor: 'pointer',
                                                    color: rated === val ? 'var(--gold-darker)' : 'var(--text-tertiary)',
                                                    border: `1px solid ${rated === val ? 'var(--gold)' : 'transparent'}`,
                                                    background: rated === val ? 'var(--cream-dark)' : 'transparent',
                                                    opacity: rated && rated !== val ? 0.45 : 1 }}>
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    {val === 'up'
                                                        ? <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                                                        : <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />}
                                                </svg>
                                            </span>
                                        ))}
                                    </div>}
                                    {parsed.action && (
                                        <div data-testid="mentor-action" style={{ marginTop: '10px', background: 'var(--white)', border: '1px solid var(--border-card)', borderTop: '3px solid var(--gold)', borderRadius: '10px', padding: '13px 16px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                                <span style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 }}>Your next move</span>
                                                <span
                                                    onClick={() => { if (!moveSaved) addSavedNextMove(parsed.action, 'Molly · ' + stepTitles[Math.min(currentStep, 3)]); }}
                                                    // Space too: it is what everyone presses on
                                                    // something that looks like a button, and
                                                    // role="button" promises it works.
                                                    onKeyDown={btnKey(() => { if (!moveSaved) addSavedNextMove(parsed.action, 'Molly · ' + stepTitles[Math.min(currentStep, 3)]); })}
                                                    role="button" tabIndex={0} aria-label={moveSaved ? 'Saved to your next moves' : 'Save this next move'}
                                                    style={{ fontSize: '11.5px', color: 'var(--gold-darker)', fontWeight: 600, cursor: moveSaved ? 'default' : 'pointer' }}>
                                                    {moveSaved ? 'Saved ✓' : 'Save ↓'}
                                                </span>
                                            </div>
                                            <p style={{ margin: 0, fontSize: '14px', color: 'var(--navy)', lineHeight: 1.55, fontWeight: 500 }}>{parsed.action}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Typing indicator, with a way out of it */}
                        {isTyping && (
                            <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div data-testid="typing-indicator" role="status" aria-label="Molly is thinking"
                                    style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '4px 14px 14px 14px', padding: '14px 18px', display: 'flex', gap: '5px' }}>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--gold-dark)', animation: 'edPulse 1.2s infinite' }}></div>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--gold-dark)', animation: 'edPulse 1.2s 0.2s infinite' }}></div>
                                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--gold-dark)', animation: 'edPulse 1.2s 0.4s infinite' }}></div>
                                </div>
                                <span
                                    onClick={stopMentor}
                                    onKeyDown={btnKey(stopMentor)}
                                    role="button" tabIndex={0} data-testid="mentor-stop"
                                    style={{ fontSize: '12px', color: 'var(--text-tertiary)', cursor: 'pointer', textDecoration: 'underline' }}>
                                    Stop
                                </span>
                            </div>
                        )}

                        {/* Escalation strip → Ask a CEO (carries the challenge, or the
                            case). Never inside a case: the fromCase opening counts as
                            a reply, so it appeared after one answer and walked the
                            member out of the run past its own exits — which have an
                            "Ask a real CEO about this" of their own. */}
                        {/* The thing this lesson leaves you with (docs/design/06):
                            authored, never generated, brackets for the figures you
                            fill in on your own machine. Kept with the brackets empty. */}
                        {lessonRef.current && lessonRef.current.keep && !embedded && (() => {
                            const lesson = lessonRef.current;
                            const already = promptLibraryOf(userProfile).some(k => k.lessonId === lesson.id && k.title === lesson.keep.title);
                            const doneAt = (lessonStateOf(userProfile)[lesson.id] || {}).doneAt;
                            const copyKeep = () => {
                                if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(lesson.keep.text).then(() => { if (showToast) showToast('Copied. Fill the brackets on your own machine.', 'success'); }).catch(() => {});
                            };
                            return (
                                <div data-testid="lesson-keep" style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 }}>What you keep · {lesson.keep.title}</span>
                                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>figures in brackets</span>
                                    </div>
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px', lineHeight: 1.5, color: 'var(--navy)', maxHeight: '220px', overflowY: 'auto' }}>{lesson.keep.text}</pre>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        <button type="button" onClick={copyKeep} className="ed-btn ed-btn-quiet" style={{ padding: '9px 14px', fontSize: '13px', minHeight: '40px' }}>Copy</button>
                                        <button type="button" onClick={() => { setUserProfile(withKeptPrompt(userProfile, { lessonId: lesson.id, title: lesson.keep.title, text: lesson.keep.text })); if (showToast) showToast('Kept, brackets empty. It is in your prompt library.', 'success'); }} disabled={already} data-testid="lesson-keep-save" className="ed-btn ed-btn-navy" style={{ padding: '9px 14px', fontSize: '13px', minHeight: '40px' }}>{already ? 'In your library ✓' : 'Save to my library'}</button>
                                        <button type="button" onClick={() => { setUserProfile(withLessonMark(userProfile, lesson.id, 'doneAt')); if (showToast) showToast('Lesson marked done.', 'success'); }} disabled={!!doneAt} data-testid="lesson-done" className="ed-btn ed-btn-quiet" style={{ padding: '9px 14px', fontSize: '13px', minHeight: '40px' }}>{doneAt ? 'Done ✓' : 'Mark lesson done'}</button>
                                    </div>
                                    <div style={{ fontSize: '11.5px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{BRAND.name} stores this with the brackets empty and never sees a figure. Before you run it anywhere: ranges not ledger lines, no names, and the tool your company approved with training off.</div>
                                </div>
                            );
                        })()}
                        {botReplies >= 2 && !isTyping && !embedded && !lessonRef.current && (
                            <div
                                onClick={askACeo}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); askACeo(); } }}
                                role="button" tabIndex={0} data-testid="ask-a-ceo-btn"
                                className="ed-hover-gold-border"
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 15px', background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '10px', cursor: 'pointer' }}>
                                <span style={{ fontSize: '13px', color: 'var(--text-body)' }}>Take this challenge further</span>
                                <span style={{ fontSize: '13px', color: 'var(--gold-darker)', fontWeight: 700 }}>Ask a CEO →</span>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Composer */}
                    <div data-testid="chat-input-area" style={{ padding: '12px 0 16px', borderTop: '1px solid var(--border-card)', display: 'flex', gap: '10px', alignItems: 'flex-end', background: 'var(--cream)' }}>
                        <textarea
                            ref={textareaRef}
                            value={inputValue}
                            onChange={handleTextareaChange}
                            onKeyDown={handleKeyDown}
                            placeholder={embedded && messages.filter(m => m.type === 'user').length === 0 ? 'Answer Molly…' : 'Reply to Molly…'}
                            data-testid="chat-input"
                            rows={1}
                            style={{ flex: 1, border: '1px solid var(--border-input)', borderRadius: '12px', padding: '13px 15px', fontSize: '14.5px', color: 'var(--navy)', background: 'var(--white)', resize: 'none', lineHeight: 1.5, outline: 'none', minHeight: '46px', maxHeight: '160px', overflow: 'auto', fontFamily: 'inherit' }}
                        />
                        <button
                            onClick={handleSend}
                            disabled={!inputValue.trim() || isTyping}
                            data-testid="send-button"
                            aria-label="Send message"
                            className="ed-hover-navy"
                            style={{ width: '46px', height: '46px', border: 'none', borderRadius: '12px', background: (!inputValue.trim() || isTyping) ? 'var(--border-card)' : 'var(--navy)', color: (!inputValue.trim() || isTyping) ? 'var(--text-meta)' : 'var(--gold)', fontSize: '17px', cursor: (!inputValue.trim() || isTyping) ? 'default' : 'pointer', flexShrink: 0 }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 2 11 13"></path><path d="M22 2 15 22 11 13 2 9z"></path></svg>
                        </button>
                    </div>
                </div>
            );
        };

        // ============================================
        // "Spot the bad call" badge chip — shared by the Home hero and the
        // simulator intro so the mode mark can't drift between surfaces.
        const SPOTBAD_CHIP = { display: 'inline-flex', alignItems: 'center', background: 'var(--claret)', border: '1px solid rgba(217, 160, 168, 0.45)', color: 'var(--text-on-claret)', fontSize: '10.5px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, padding: '5px 11px', borderRadius: '999px', marginBottom: '10px' };

        // ============================================
        // CELEBRATION — fires when the user matches the CEO
        // ============================================
        // Hand-rolled on a canvas rather than pulling in a confetti library:
        // the app is bundled and self-hosted (no CDN), and this is ~40 lines.
        // Purely decorative, so it is aria-hidden and pointer-events:none, and
        // it does not render at all under prefers-reduced-motion — the result
        // is always communicated in text regardless.
        const Celebration = ({ spot }) => {
            const ref = useRef(null);

            useEffect(() => {
                if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
                const canvas = ref.current;
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                const dpr = window.devicePixelRatio || 1;
                const W = canvas.clientWidth, H = canvas.clientHeight;
                canvas.width = W * dpr; canvas.height = H * dpr;
                ctx.scale(dpr, dpr);

                const palette = spot
                    ? ['#d9a0a8', '#722f37', '#f5f2ed', '#8a4750']
                    : ['#d4b668', '#1a2744', '#f0e4c4', '#b89b4f', '#ffffff'];
                // Two side bursts, the way a stage cannon fires inward.
                const parts = [];
                for (const [ox, dir] of [[0.12, 1], [0.88, -1]]) {
                    for (let i = 0; i < 55; i++) {
                        const a = (Math.random() * 0.9 + 0.15) * Math.PI;
                        const v = 7 + Math.random() * 9;
                        parts.push({
                            x: W * ox, y: H * 0.42 + (Math.random() - 0.5) * 40,
                            vx: Math.cos(a) * v * dir, vy: -Math.abs(Math.sin(a) * v) - 2,
                            w: 5 + Math.random() * 6, h: 3 + Math.random() * 5,
                            rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.35,
                            c: palette[(Math.random() * palette.length) | 0],
                        });
                    }
                }

                let raf, frame = 0;
                const tick = () => {
                    frame++;
                    ctx.clearRect(0, 0, W, H);
                    let alive = false;
                    for (const p of parts) {
                        p.vy += 0.32;                 // gravity
                        p.vx *= 0.992;                // drag
                        p.x += p.vx; p.y += p.vy; p.rot += p.vr;
                        if (p.y > H + 40) continue;
                        alive = true;
                        ctx.save();
                        ctx.globalAlpha = Math.max(0, 1 - frame / 150);
                        ctx.translate(p.x, p.y); ctx.rotate(p.rot);
                        ctx.fillStyle = p.c;
                        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                        ctx.restore();
                    }
                    if (alive && frame < 150) raf = requestAnimationFrame(tick);
                    else ctx.clearRect(0, 0, W, H);
                };
                raf = requestAnimationFrame(tick);
                return () => cancelAnimationFrame(raf);
            }, [spot]);

            return (
                <canvas
                    ref={ref}
                    aria-hidden="true"
                    data-testid="celebration"
                    style={{
                        position: 'fixed', inset: 0, width: '100%', height: '100%',
                        pointerEvents: 'none', zIndex: 'var(--z-toast)'
                    }}
                />
            );
        };

        // ============================================
        // DECISION PRACTICE — THE ONE-SCREEN RUN (docs/DECISION_PRACTICE.md §7.4)
        // ============================================
        // The case, the call, the CEO, the takeaway and Molly on one page, each
        // section staying where it is as the next appears. Every piece of it
        // lives HERE, at module scope, and is handed what it needs as props:
        // SimulatorView re-renders once a second while the clock runs, and a
        // component defined inside it would be a new type on each of those
        // renders — React would remount it, so the note field would drop focus
        // mid-word, the CEO's clip would stop and Molly's thread would reset.

        // The soft clock: it runs from the case opening to the lock-in, can be
        // paused or extended, and at zero only changes colour. Nothing is
        // submitted, scrolled or focused on the member's behalf.
        const SIM_CLOCK_SECONDS = 60;
        // The three lock-in buttons, and the confidence stored with the attempt.
        const CONFIDENCE_LEVELS = [[1, 'Guessing'], [3, 'Leaning'], [5, 'Sure']];
        const REFLECTION_NOTE_MAX = 1000;
        // Which step a phase is, for the run's sim_step bookkeeping. The four
        // names are whitelisted by the console's reports (events.ts STEPS); in
        // a2 they are boundaries on one page rather than four pages.
        const SIM_PHASE_STEP = { situation: 1, decision: 2, reveal: 3, lesson: 4 };

        const ceoLastName = (n) => ((n || '').trim().split(' ').pop()) || 'The CEO';
        // "Title: description" → both halves; an option without a colon is all
        // description.
        const optionParts = (option) => {
            const text = String((option && option.text) || '');
            const parts = text.split(':');
            return parts.length > 1
                ? { title: parts[0].trim(), desc: parts.slice(1).join(':').trim() }
                : { title: null, desc: text };
        };
        // An option's short name: the half before the colon.
        const optionLabelOf = (scenario, id) => {
            const o = scenario && id ? (scenario.options || []).find(x => x && x.id === id) : null;
            return o ? String(o.text || '').split(':')[0].trim() : null;
        };

        /**
         * The case a member takes into Ask a CEO (§7.6): what the case was,
         * their call and the CEO's. Never how sure they were, a second thought,
         * their note, the score, the index, or anything said to Molly — this is
         * shown to an advisor. caseAttachmentText renders it for the question.
         */
        const caseAttachmentFor = (scenario, chosenId) => {
            if (!scenario) return null;
            return {
                scenarioId: scenario.id,
                title: scenario.title || '',
                industry: scenario.industry || null,
                decisionType: scenario.decisionType || null,
                situation: scenario.situation || '',
                scenarioType: isSpotBad(scenario) ? 'spotBad' : 'match',
                chosen: chosenId || null,
                chosenText: optionLabelOf(scenario, chosenId),
                ceo: scenario.ceo || null,
                company: scenario.company || null,
                ceoChoice: scenario.actualChoice || null,
                ceoChoiceText: optionLabelOf(scenario, scenario.actualChoice)
            };
        };

        /**
         * The attachment as the advisor reads it (§7.6), and as the member sees
         * it before sending — one text, so the preview is exactly what travels
         * as the question's `context`. Four lines. The situation is cut on a word
         * so a long brief cannot crowd out the two calls, and the whole stays
         * inside the 4,000 characters `context` takes. Only the fields named here
         * are read: whatever else rides on the object — a note, how sure they
         * were — has no way into it.
         */
        const CASE_ATTACHMENT_SITUATION_MAX = 700;
        const caseAttachmentText = (a) => {
            if (!a) return '';
            // One line per fact: a line break inside a brief or an option would
            // read as the start of the next line.
            const one = (x) => String(x == null ? '' : x).replace(/\s+/g, ' ').trim();
            const cutAtWord = (s, max) => {
                if (s.length <= max) return s;
                const head = s.slice(0, max + 1);
                const at = head.lastIndexOf(' ');
                return (at > 0 ? head.slice(0, at) : s.slice(0, max)).replace(/[\s,;:.—–-]+$/, '') + '…';
            };
            const spot = a.scenarioType === 'spotBad';
            const tags = [industryLabel(a.industry), decisionTypeLabel(a.decisionType)].filter(Boolean).join(' · ');
            const call = (id, text) => [one(id), one(text)].filter(Boolean).join(' — ');
            const who = [one(a.ceo), one(a.company)].filter(Boolean).join(', ');
            const situation = one(a.situation);
            const lines = [`From ${BRAND.nameWithArticle} case: "${one(a.title)}"${tags ? ` (${tags})` : ''}`];
            if (situation) lines.push(`The situation: ${cutAtWord(situation, CASE_ATTACHMENT_SITUATION_MAX)}`);
            if (a.chosen) lines.push(`${spot ? 'The call I flagged as the bad one' : 'My call'}: ${call(a.chosen, a.chosenText)}`);
            if (a.ceoChoice) lines.push(`${spot ? 'The bad call' : "The CEO's call"}${who ? ` (${who})` : ''}: ${call(a.ceoChoice, a.ceoChoiceText)}`);
            return lines.join('\n').slice(0, 4000);
        };

        /**
         * Molly's first line when a member comes back to a case they worked —
         * the journal's and Home's "Talk it through" (§7.7). Composed here, like
         * the handoff's opening and shaped like it ({ text, scenarioId, choice }),
         * so her page opens on the case by id. Days later a letter means nothing
         * on its own, so each call carries its short name.
         */
        const revisitOpening = (attempt) => {
            if (!attempt || !attempt.scenarioId) return null;
            // `close` ends the short name as an aside mid-sentence: B, "Cut", as…
            const name = (id, close = '') => {
                const o = (attempt.options || []).find(x => x && x.id === id);
                const label = o ? String(o.text || '').split(':')[0].trim() : '';
                return label ? `${id}, "${label}"${close}` : String(id);
            };
            const t = Date.parse(attempt.createdAt || '');
            const when = isFinite(t) ? ` on ${new Date(t).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}` : '';
            const who = attempt.ceo ? ceoLastName(attempt.ceo) : 'the CEO';
            const mine = attempt.chosen ? name(attempt.chosen) : null;
            const theirs = attempt.correctChoice ? name(attempt.correctChoice) : null;
            const same = !!mine && attempt.chosen === attempt.correctChoice;
            let call;
            if (!mine) call = '';
            else if (attempt.scenarioType === 'spotBad') {
                const flagged = name(attempt.chosen, ',');
                call = same
                    ? ` and flagged ${flagged} as the bad call — it was the one that went wrong`
                    : ` and flagged ${flagged} as the bad call${theirs ? `; the one that went wrong was ${theirs}` : ''}`;
            } else {
                call = same
                    ? ` and chose ${mine} — the same call ${who} made`
                    : ` and chose ${mine}${theirs ? `; ${who} chose ${theirs}` : ''}`;
            }
            return {
                text: `You worked "${attempt.title || 'this case'}"${when}${call}. Looking back, what would you want to test about your call?`,
                scenarioId: attempt.scenarioId,
                choice: attempt.chosen || null
            };
        };

        // The next read a member's record will open (§6 `unlocks`), in words:
        // counts, never a percentage, and nothing it has not measured.
        const PRACTICE_UNLOCK_LINES = {
            strengths: (k) => `${k} more ${k === 1 ? 'case' : 'cases'} to your first read on the decision types you call best and find hardest`,
            passedOver: (k) => `${k} more ${k === 1 ? 'call' : 'calls'} that differ from the CEO's to open "When you differed"`,
            confidence: (k, u) => `"Sure vs. right" opens at ${u.need} calls marked Sure and ${u.need} marked Guessing — ${u.have} of ${u.need} so far`,
            trend: (k) => `${k} more ${k === 1 ? 'case' : 'cases'} to open "Earlier vs. recent cases"`,
            secondThoughts: (k) => `${k} more second ${k === 1 ? 'thought' : 'thoughts'} to open "After the reveal"`
        };
        const practiceUnlockLine = (u) => {
            const line = u && PRACTICE_UNLOCK_LINES[u.key];
            if (!line || typeof u.need !== 'number' || typeof u.have !== 'number' || u.have >= u.need) return null;
            return line(u.need - u.have, u);
        };

        // The unlock to name as the next step — Home's profile card and the
        // Takeaway's "Your record" share it, so they never disagree. Nearest by
        // what is left, but not a read the member cannot yet move: a second
        // thought is asked, and a differed call counted, only after a match call
        // differs from the CEO's, so a member with none of either (new, or who
        // always matches) is not told to collect them — unless nothing else is
        // left to open.
        const nearestUnlock = (unlocks) => {
            const open = (Array.isArray(unlocks) ? unlocks : []).filter(u => practiceUnlockLine(u));
            const movable = open.filter(u => !((u.key === 'secondThoughts' || u.key === 'passedOver') && u.have === 0));
            return (movable.length ? movable : open).reduce((best, u) => (!best || (u.need - u.have) < (best.need - best.have) ? u : best), null);
        };

        /**
         * What this case added to the member's record, for the Takeaway —
         * read from the REFRESHED decision profile, never from the local blob
         * (a replay overwrites `correct` there). → { line, unlock }, or null
         * while the record does not yet include this attempt.
         */
        const caseRecordFor = (decisionProfile, scenario, attemptId) => {
            if (!decisionProfile || !decisionProfile.profile || attemptId == null || !scenario) return null;
            const history = Array.isArray(decisionProfile.history) ? decisionProfile.history : [];
            const row = history.find(h => h && Number(h.attemptId) === Number(attemptId));
            if (!row) return null;
            const profile = decisionProfile.profile;
            let line = null;
            if (!row.counted) {
                line = 'A replay: your record counts the first time you work a case.';
            } else {
                const type = decisionTypeLabel(scenario.decisionType);
                const typeRow = type && Array.isArray(profile.byType) ? profile.byType.find(r => r && r.key === scenario.decisionType) : null;
                const decisions = profile.totals && typeof profile.totals.decisions === 'number' ? profile.totals.decisions : null;
                if (typeRow) line = `${type}: ${typeRow.called} of ${typeRow.n} so far`;
                else if (decisions != null) line = `${decisions} ${decisions === 1 ? 'decision' : 'decisions'} on your record so far`;
            }
            const nearest = nearestUnlock(profile.unlocks);
            return { line, unlock: nearest ? practiceUnlockLine(nearest) : null };
        };

        const RUN_KICKER = { fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 };
        const RUN_CARD = { background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '16px 18px' };

        // The clock, in the Decide header (two columns) or the sticky bar (one).
        const SimClock = ({ timer, timerTotal, paused, onPause, onExtend, spot }) => {
            const out = timer <= 0;
            const low = !out && timer <= 10;
            const frac = Math.max(0, Math.min(1, timerTotal > 0 ? timer / timerTotal : 0));
            const quiet = { border: '1px solid var(--border-input)', background: 'var(--white)', borderRadius: '8px', padding: '5px 10px', fontSize: '12px', fontWeight: 600, color: 'var(--navy)', cursor: 'pointer', minHeight: '34px', opacity: 1 };
            return (
                <div data-testid="sim-clock" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <div role="timer" aria-label={out ? "Time's up" : `${timer} seconds remaining`} style={{ width: '32px', height: '32px', flexShrink: 0 }}>
                        <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%' }} aria-hidden="true">
                            <circle cx="18" cy="18" r="15" fill="none" stroke="var(--border-card)" strokeWidth="3.5" />
                            <circle cx="18" cy="18" r="15" fill="none"
                                stroke={out || low ? 'var(--warning-dark)' : (spot ? 'var(--claret)' : 'var(--gold)')}
                                strokeWidth="3.5" strokeLinecap="round" strokeDasharray="94.25"
                                strokeDashoffset={94.25 * (1 - frac)} transform="rotate(-90 18 18)"
                                style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.3s' }} />
                        </svg>
                    </div>
                    {out ? (
                        <span data-testid="sim-clock-out" style={{ fontSize: '12.5px', fontWeight: 600, lineHeight: 1.35, color: 'var(--navy)', background: 'var(--warning-surface)', border: '1px solid var(--warning-border)', borderRadius: '8px', padding: '5px 9px' }}>
                            Time's up — make your call when you're ready
                        </span>
                    ) : (
                        <>
                            <span aria-hidden="true" style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', fontWeight: 700, color: low ? 'var(--gold-darker)' : 'var(--navy)', fontVariantNumeric: 'tabular-nums', minWidth: '38px' }}>
                                {Math.floor(timer / 60)}:{String(timer % 60).padStart(2, '0')}
                            </span>
                            <button type="button" onClick={onPause} className="ed-btn-quiet" style={quiet} data-testid="timer-pause-btn">{paused ? 'Resume' : 'Pause'}</button>
                            <button type="button" onClick={onExtend} className="ed-btn-quiet" style={quiet} data-testid="timer-extend-btn" aria-label="Add 30 seconds">+30s</button>
                        </>
                    )}
                </div>
            );
        };

        // "Case ▴": back up to the brief, on each section after the lock-in (one column only).
        const CaseChip = ({ onClick }) => (!onClick ? null : (
            <button type="button" onClick={onClick} data-testid="case-chip" aria-label="Back to the case"
                style={{ flexShrink: 0, border: '1px solid var(--border-input)', background: 'var(--white)', borderRadius: '999px', padding: '4px 11px', fontSize: '12px', fontWeight: 600, color: 'var(--navy)', cursor: 'pointer', minHeight: '30px', opacity: 1 }}>
                Case ▴
            </button>
        ));

        // A section heading the page can land on: tabIndex -1, so it takes
        // focus when its section appears without joining the tab order.
        const RunSectionHead = ({ id, kicker, title, headingRef, spot, onCaseChip }) => (
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
                <div style={{ minWidth: 0 }}>
                    {kicker && <div style={{ ...RUN_KICKER, color: spot ? 'var(--claret)' : 'var(--gold-darker)', marginBottom: '4px' }}>{kicker}</div>}
                    <h2 id={id} ref={headingRef} tabIndex={-1} style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', lineHeight: 1.25, color: 'var(--navy)', fontWeight: 600, margin: 0, scrollMarginTop: '16px', outline: 'none' }}>
                        {title}
                    </h2>
                </div>
                <CaseChip onClick={onCaseChip} />
            </div>
        );

        /**
         * The run's rail: Decide · The CEO · Takeaway · (Molly), each a jump to
         * its section once the section is on the page. Done is read from the
         * phase — Decide once locked in, The CEO once the Takeaway was reached,
         * the Takeaway once it was read to the end — and Molly from a turn the
         * member actually took.
         */
        const PracticeRail = ({ phase, spot, mentorEnabled, mollyTalked, onJump }) => {
            const at = (...phases) => phases.includes(phase);
            const steps = [
                { id: 'decide', label: 'Decide', done: at('reveal', 'lesson', 'handoff'), open: true },
                { id: 'ceo', label: 'The CEO', done: at('lesson', 'handoff'), open: at('reveal', 'lesson', 'handoff') },
                { id: 'takeaway', label: 'Takeaway', done: phase === 'handoff', open: at('reveal', 'lesson', 'handoff') }
            ];
            if (mentorEnabled) steps.push({ id: 'molly', label: 'Molly', done: !!mollyTalked, open: phase === 'handoff' });
            const current = steps.find(s => s.open && !s.done);
            const accent = spot ? 'var(--claret)' : 'var(--gold-dark)';
            return (
                <nav data-testid="practice-rail" aria-label="This case" style={{ marginBottom: '18px' }}>
                    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {steps.map(s => {
                            const isCurrent = !!current && current.id === s.id;
                            return (
                                <li key={s.id}>
                                    <button type="button" data-testid={`rail-${s.id}`} disabled={!s.open} onClick={() => onJump(s.id)}
                                        aria-current={isCurrent ? 'step' : undefined}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '5px', minHeight: '32px', padding: '4px 12px',
                                            borderRadius: '999px', fontSize: '12.5px', opacity: 1,
                                            fontWeight: isCurrent ? 700 : 600, cursor: s.open ? 'pointer' : 'default',
                                            background: isCurrent ? 'var(--white)' : 'transparent',
                                            border: `1.5px solid ${isCurrent ? accent : s.done ? 'var(--border-input)' : 'var(--border-card)'}`,
                                            color: s.open ? 'var(--navy)' : 'var(--text-meta)'
                                        }}>
                                        {s.done && <span aria-hidden="true" style={{ color: 'var(--success-ink)' }}>✓</span>}
                                        {s.label}
                                        {s.done && <span className="sr-only"> completed</span>}
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                </nav>
            );
        };

        /**
         * The case file and the brief. Every exhibit string is a React text
         * node — no markup, no links (§3). The panel says where its contents
         * came from: drafted by a model from the brief, not the company's papers.
         * `wide`: the sticky left column, everything open. Otherwise the phone
         * order — the facts and the stakes, the brief, then who is affected and
         * what is pulling behind a row that opens in place.
         */
        const CaseFile = ({ scenario, spot, wide, detailsOpen, onToggleDetails, briefRef, onNotForMe }) => {
            const ex = scenario.exhibits && typeof scenario.exhibits === 'object' ? scenario.exhibits : null;
            const facts = ex && Array.isArray(ex.facts) ? ex.facts.filter(f => f && f.value && f.label) : [];
            const stakeholders = ex && Array.isArray(ex.stakeholders) ? ex.stakeholders.filter(p => p && p.who && p.stake) : [];
            const tension = ex && ex.tension && ex.tension.left && ex.tension.right ? ex.tension : null;
            const stakes = ex && typeof ex.stakes === 'string' && ex.stakes.trim() ? ex.stakes : null;
            const hasGlance = facts.length > 0 || !!stakes;
            const hasDetails = !!tension || stakeholders.length > 0;
            const tags = [industryLabel(scenario.industry), decisionTypeLabel(scenario.decisionType)].filter(Boolean);
            const panel = { background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '14px 16px', marginTop: '12px' };
            const aiLabel = (
                <div style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', fontWeight: 600, marginBottom: '10px' }}>
                    Case file · summarised from the brief with AI
                </div>
            );
            const detailsLabel = tension && stakeholders.length ? 'Who\'s affected · what\'s pulling' : stakeholders.length ? 'Who\'s affected' : 'What\'s pulling';
            const small = { fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 700, marginBottom: '6px' };
            const details = (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {tension && (
                        <div>
                            <div style={small}>What's pulling</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)', gap: '8px', alignItems: 'center' }}>
                                <div style={{ background: 'var(--cream)', borderRadius: '10px', padding: '9px 11px', fontSize: '13px', color: 'var(--navy)', lineHeight: 1.45 }}>{tension.left}</div>
                                <span aria-hidden="true" style={{ color: 'var(--text-meta)', fontWeight: 700 }}>↔</span>
                                <span className="sr-only">against</span>
                                <div style={{ background: 'var(--cream)', borderRadius: '10px', padding: '9px 11px', fontSize: '13px', color: 'var(--navy)', lineHeight: 1.45 }}>{tension.right}</div>
                            </div>
                        </div>
                    )}
                    {stakeholders.length > 0 && (
                        <div>
                            <div style={small}>Who's affected</div>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {stakeholders.map((p, i) => (
                                    <li key={i} style={{ fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.5 }}>
                                        <strong style={{ color: 'var(--navy)' }}>{p.who}</strong> — {p.stake}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            );
            return (
                <section key="case" data-testid="case-file" aria-labelledby="ed-case-title">
                    <div style={{ ...RUN_KICKER, color: spot ? 'var(--claret)' : 'var(--gold-darker)', marginBottom: '6px' }}>
                        {[`Case № ${scenario.id}`, ...tags].join(' · ')}
                    </div>
                    <h2 id="ed-case-title" ref={briefRef} tabIndex={-1} style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', lineHeight: 1.25, color: 'var(--navy)', fontWeight: 600, margin: 0, scrollMarginTop: '16px', outline: 'none' }}>
                        {scenario.title}
                    </h2>
                    {/* Bad Call mode banner — the mode must be unmissable while reading */}
                    {spot && (
                        <div data-testid="spotbad-mode-banner" style={{ background: 'var(--claret-deep)', borderRadius: '12px', padding: '12px 16px', marginTop: '12px' }}>
                            <div style={{ fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--claret-rose)', marginBottom: '3px' }}>The bad call</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-on-claret)', lineHeight: 1.55 }}>One of the decisions ahead was the costly one. Read like an auditor.</div>
                        </div>
                    )}
                    {hasGlance && (
                        <div data-testid="case-file-glance" style={panel}>
                            {aiLabel}
                            {facts.length > 0 && (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 14px' }}>
                                    {facts.map((f, i) => (
                                        <div key={i} style={{ minWidth: 0 }}>
                                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '19px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.2, overflowWrap: 'anywhere' }}>{f.value}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.35 }}>{f.label}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {stakes && (
                                <div style={{ marginTop: facts.length ? '12px' : 0, paddingTop: facts.length ? '10px' : 0, borderTop: facts.length ? '1px solid var(--hairline)' : 'none', fontSize: '13.5px', color: 'var(--navy)', lineHeight: 1.5 }}>
                                    <span style={{ fontWeight: 700 }}>At stake: </span>{stakes}
                                </div>
                            )}
                        </div>
                    )}
                    <div data-testid="case-brief" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderTop: spot ? '3px solid var(--claret)' : '1px solid var(--border-card)', borderRadius: '14px', padding: '18px 20px', marginTop: '12px' }}>
                        <p style={{ fontSize: '14.5px', color: 'var(--text-body)', lineHeight: 1.7, margin: 0 }}>{scenario.situation}</p>
                        {scenario.timeContext && (
                            <p style={{ fontSize: '14.5px', color: 'var(--text-body)', lineHeight: 1.7, margin: '12px 0 0' }}>{scenario.timeContext}</p>
                        )}
                    </div>
                    {hasDetails && (wide ? (
                        <div data-testid="case-file-details" style={panel}>
                            {!hasGlance && aiLabel}
                            {details}
                        </div>
                    ) : (
                        <div style={{ ...panel, padding: 0 }}>
                            <button type="button" onClick={onToggleDetails} aria-expanded={!!detailsOpen} data-testid="case-file-details-toggle"
                                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', background: 'transparent', border: 'none', padding: '13px 16px', fontSize: '13.5px', fontWeight: 600, color: 'var(--navy)', cursor: 'pointer', textAlign: 'left', minHeight: '46px', opacity: 1 }}>
                                <span>{detailsLabel}</span>
                                <span aria-hidden="true">{detailsOpen ? '▾' : '▸'}</span>
                            </button>
                            {detailsOpen && (
                                <div data-testid="case-file-details" style={{ padding: '0 16px 14px' }}>
                                    {aiLabel}
                                    {details}
                                </div>
                            )}
                        </div>
                    ))}
                    {onNotForMe && (
                        <div onClick={onNotForMe} onKeyDown={btnKey(onNotForMe)} role="button" tabIndex={0} data-testid="not-for-me"
                            className="ed-link" style={{ marginTop: '10px', fontSize: '13px', fontWeight: 600, padding: '6px 2px', display: 'inline-block' }}>
                            Not for me — another case
                        </div>
                    )}
                </section>
            );
        };

        /**
         * ① Decide: the options and the lock-in. Selecting is not deciding —
         * the lock-in is, with how sure the member is (Guessing / Leaning /
         * Sure). After the lock-in the list stays, read-only, with the member's
         * call and the CEO's marked in words as well as colour.
         */
        const DecidePanel = ({ scenario, spot, phase, selected, confidence, onSelect, onLockIn, headingRef, optionsRef, lockRowRef, clock }) => {
            const locked = phase !== 'situation' && phase !== 'decision';
            const options = scenario.options || [];
            const accent = spot ? 'var(--claret)' : 'var(--gold-dark)';
            const ceoCall = spot ? 'The bad call' : `${ceoLastName(scenario.ceo)}'s call`;
            const sureLabel = (CONFIDENCE_LEVELS.find(([level]) => level === confidence) || [])[1] || null;
            return (
                <section key="decide" data-testid="sim-decide" aria-labelledby="ed-decision-question">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                        <div style={{ ...RUN_KICKER, color: spot ? 'var(--claret)' : 'var(--gold-darker)' }}>Decide</div>
                        {clock}
                    </div>
                    <h2 id="ed-decision-question" ref={headingRef} tabIndex={-1} style={{ fontFamily: 'var(--font-heading)', fontSize: '21px', color: 'var(--navy)', margin: '0 0 6px', fontWeight: 600, scrollMarginTop: '16px', outline: 'none' }}>
                        {spot ? 'Which of these was the wrong call?' : 'Can you match the path the CEO chose?'}
                    </h2>
                    {/* Say the task plainly under the heading: in Bad Call mode the
                        pick is deliberately the WRONG one, which is the opposite
                        instinct from every other case. */}
                    {!locked && (
                        <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 14px' }}>
                            {spot
                                ? 'Pick the decision that backfired — you are choosing the wrong call on purpose.'
                                : 'Pick the path you believe they actually took. It is often not the obvious one.'}
                        </p>
                    )}
                    {locked && selected && (
                        <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 14px' }}>
                            You locked in {selected}{sureLabel ? ` · ${sureLabel}` : ''}.
                        </p>
                    )}
                    <div ref={optionsRef} role={locked ? 'list' : 'radiogroup'} aria-labelledby={locked ? undefined : 'ed-decision-question'}
                        style={{ display: 'flex', flexDirection: 'column', gap: '10px', scrollMarginTop: '16px' }}>
                        {options.map((option, optIndex) => {
                            const { title: optTitle, desc: optDesc } = optionParts(option);
                            const mine = selected === option.id;
                            const theirs = locked && option.id === scenario.actualChoice;
                            const marks = locked ? [mine ? 'Your call' : null, theirs ? ceoCall : null].filter(Boolean) : [];
                            const border = theirs ? `2px solid ${accent}` : mine ? `2px solid ${locked ? 'var(--navy)' : accent}` : '1px solid var(--border-card)';
                            const thick = theirs || mine;
                            const body = (
                                <>
                                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, background: mine ? (spot ? 'var(--claret)' : 'var(--gold)') : 'var(--cream-dark)', color: mine ? (spot ? 'var(--text-on-claret)' : 'var(--navy)') : 'var(--text-tertiary)' }}>{option.id}</div>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        {marks.length > 0 && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '5px' }}>
                                                {marks.map(m => (
                                                    <span key={m} style={{ fontSize: '10.5px', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, borderRadius: '999px', padding: '2px 8px', background: m === 'Your call' ? 'var(--navy)' : (spot ? 'var(--claret)' : 'var(--gold)'), color: m === 'Your call' ? 'var(--text-on-navy)' : (spot ? 'var(--text-on-claret)' : 'var(--navy)') }}>{m}</span>
                                                ))}
                                            </div>
                                        )}
                                        {optTitle && <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)', marginBottom: '3px' }}>{optTitle}</div>}
                                        <div style={{ fontSize: optTitle ? '13px' : '14px', color: optTitle ? 'var(--text-secondary)' : 'var(--navy)', lineHeight: 1.55 }}>{optDesc}</div>
                                    </div>
                                </>
                            );
                            const style = {
                                background: 'var(--white)', border, borderRadius: '14px', padding: thick ? '13px 15px' : '14px 16px',
                                display: 'flex', gap: '12px', alignItems: 'flex-start', transition: 'border-color 0.15s ease'
                            };
                            if (locked) {
                                return <div key={option.id} role="listitem" data-testid={`option-${option.id}`} style={style}>{body}</div>;
                            }
                            return (
                                <div key={option.id}
                                    onClick={() => onSelect(option.id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(option.id); }
                                        else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                                            e.preventDefault();
                                            const nextIx = (optIndex + 1) % options.length;
                                            onSelect(options[nextIx].id);
                                            e.currentTarget.parentElement.children[nextIx].focus();
                                        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                                            e.preventDefault();
                                            const prevIx = (optIndex - 1 + options.length) % options.length;
                                            onSelect(options[prevIx].id);
                                            e.currentTarget.parentElement.children[prevIx].focus();
                                        }
                                    }}
                                    role="radio" aria-checked={mine} tabIndex={0}
                                    className={spot ? 'ed-hover-option-claret' : 'ed-hover-option'} data-testid={`option-${option.id}`}
                                    style={{ ...style, cursor: 'pointer' }}>
                                    {body}
                                </div>
                            );
                        })}
                    </div>
                    {!locked && selected && (
                        <div ref={lockRowRef} role="group" aria-labelledby="ed-lock-in-label" data-testid="submit-decision-btn"
                            tabIndex={-1} style={{ marginTop: '16px', scrollMarginBottom: '120px', outline: 'none' }}>
                            <div id="ed-lock-in-label" style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)', marginBottom: '9px' }}>
                                {spot ? `Lock in ${selected} as the wrong call — how sure are you?` : `Lock in ${selected} — how sure are you?`}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
                                {CONFIDENCE_LEVELS.map(([level, label]) => (
                                    <button key={level} type="button" onClick={() => onLockIn(level)} data-testid={`confidence-${level}`}
                                        className={'ed-btn ' + (spot ? 'ed-btn-claret' : 'ed-btn-navy')} style={{ padding: '12px 6px', fontSize: '14px' }}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            );
        };

        /**
         * ② The CEO, appended on the lock-in: the result in words, the two calls
         * side by side, the CEO in their own voice (`children` — the card and
         * its <audio> stay in SimulatorView, which owns the run's listen
         * trackers), how other members chose, and — only when a match call
         * differed — the second thought. No score number anywhere.
         */
        const CeoSection = ({ scenario, spot, selected, headingRef, onCaseChip, choices, second, children }) => {
            const matched = selected === scenario.actualChoice;
            const chosen = (scenario.options || []).find(o => o.id === selected);
            const ceoLast = ceoLastName(scenario.ceo);
            const label = (id) => optionLabelOf(scenario, id);
            const chip = { padding: '8px 14px', borderRadius: '999px', fontSize: '13.5px', fontWeight: 600, cursor: 'pointer', minHeight: '38px', opacity: 1 };
            return (
                <section key="ceo" data-testid="sim-ceo" aria-labelledby="ed-sim-ceo" style={{ marginTop: '30px' }}>
                    {/* Getting it right is the moment worth coming back for */}
                    {matched && <Celebration spot={spot} />}
                    <RunSectionHead id="ed-sim-ceo" title="The CEO" headingRef={headingRef} onCaseChip={onCaseChip} />
                    {/* Result — communicated in text, never color alone */}
                    <div aria-live="polite" style={{
                        borderRadius: '14px', padding: '16px 20px', marginBottom: '12px',
                        background: matched ? 'var(--success-surface)' : (spot ? 'var(--claret-surface)' : 'var(--warning-surface)'),
                        border: `1px solid ${matched ? 'var(--success-border)' : (spot ? 'var(--claret-border)' : 'var(--warning-border)')}`
                    }}>
                        <div style={{ ...RUN_KICKER, color: matched ? 'var(--success-ink)' : (spot ? 'var(--claret)' : 'var(--gold-darker)'), marginBottom: '5px' }}>
                            {spot
                                ? (matched ? 'You caught it' : 'That one actually worked')
                                : (matched ? 'You called the transformative move' : 'The counter-intuitive call')}
                        </div>
                        <div style={{ fontSize: '15.5px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1.5 }}>
                            {/* Never gender the CEO in generated copy — much of the
                                library is women, so it is always "they". */}
                            {spot
                                ? (matched
                                    ? `You flagged ${selected} — the call that cost ${scenario.company || scenario.ceo} dearly${scenario.year ? ' in ' + scenario.year : ''}.`
                                    : `You flagged ${selected}${chosen ? ' — ' + chosen.text.split(':')[0].trim().toLowerCase() : ''} — a fair suspicion, but it paid off. The costly call was ${scenario.actualChoice}.`)
                                : (matched
                                    ? `You chose ${selected} — the same call ${scenario.ceo} made${scenario.year ? ' in ' + scenario.year : ''}.`
                                    // "the one most executives pick" was a claim about a
                                    // population this product has never measured — it is
                                    // fixed text on the not-matched branch, so it was said
                                    // about whichever option the user happened to choose.
                                    : `You chose ${selected}${chosen ? ' — ' + chosen.text.split(':')[0].trim().toLowerCase() : ''} — a defensible path. ${scenario.ceo} went with ${scenario.actualChoice}. Here's what they saw that others didn't.`)}
                        </div>
                    </div>
                    {/* The two calls side by side, in words */}
                    <div data-testid="call-compare" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px', marginBottom: '14px' }}>
                        {[['Your call', selected], [spot ? 'The bad call' : `${ceoLast}'s call`, scenario.actualChoice]].map(([who, id]) => (
                            <div key={who} style={{ ...RUN_CARD, padding: '11px 13px' }}>
                                <div style={{ ...RUN_KICKER, fontSize: '10.5px', color: 'var(--text-secondary)', marginBottom: '3px' }}>{who}</div>
                                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.35 }}>{id}{label(id) ? ` — ${label(id)}` : ''}</div>
                            </div>
                        ))}
                    </div>
                    {children}
                    {/* How other members chose — asked for only after this call is
                        in (the worker refuses anyone who has not worked the case),
                        shares rounded to 10, nothing at all when it could not be had. */}
                    {choices && (
                        <div data-testid="member-choices" style={{ ...RUN_CARD, marginTop: '14px' }}>
                            <div style={{ ...RUN_KICKER, color: 'var(--text-secondary)', marginBottom: '10px' }}>How other members chose</div>
                            {choices.shares ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                                    {(scenario.options || []).map(o => {
                                        const pct = Math.max(0, Math.min(100, Number(choices.shares[o.id]) || 0));
                                        return (
                                            <div key={o.id}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '13px', color: 'var(--navy)', marginBottom: '4px' }}>
                                                    <span style={{ minWidth: 0 }}>
                                                        <strong>{o.id}</strong>{label(o.id) ? ` — ${label(o.id)}` : ''}
                                                        {o.id === selected && <span style={{ color: 'var(--text-secondary)' }}> · your call</span>}
                                                    </span>
                                                    <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{pct}%</span>
                                                </div>
                                                <div aria-hidden="true" style={{ height: '6px', borderRadius: '999px', background: 'var(--cream-dark)', overflow: 'hidden' }}>
                                                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: '999px', background: o.id === scenario.actualChoice ? (spot ? 'var(--claret)' : 'var(--gold)') : 'var(--navy-lighter)' }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <div style={{ fontSize: '11.5px', color: 'var(--text-meta)' }}>Other members' first attempts, up to yesterday · rounded to the nearest 10%</div>
                                </div>
                            ) : (
                                <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.55 }}>Fewer than 10 other members had worked this case by yesterday</p>
                            )}
                        </div>
                    )}
                    {/* Second thought — only when a match call differed. The member's
                        answer is theirs to keep in the journal; it never changes the score. */}
                    {second && (
                        <div data-testid="second-thought" style={{ ...RUN_CARD, marginTop: '14px' }}>
                            <div id="ed-second-thought" style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.45, marginBottom: '10px' }}>
                                Knowing what {ceoLast} did, would you still choose {selected}?
                            </div>
                            <div role="group" aria-labelledby="ed-second-thought" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {second.options.map(id => {
                                    const on = second.value === id;
                                    return (
                                        <button key={id} type="button" data-testid={`second-thought-${id}`} aria-pressed={on}
                                            disabled={second.disabled} onClick={() => second.onChoose(id)}
                                            style={{ ...chip, cursor: second.disabled ? 'default' : 'pointer', border: `1.5px solid ${on ? 'var(--navy)' : 'var(--border-input)'}`, background: on ? 'var(--navy)' : 'var(--white)', color: on ? 'var(--text-on-navy)' : (second.disabled ? 'var(--text-meta)' : 'var(--navy)') }}>
                                            {id === selected ? `Still ${id}` : `Switch to ${id}`}
                                        </button>
                                    );
                                })}
                            </div>
                            <div role="status" aria-live="polite" style={{ marginTop: '8px', fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                                {second.blocked
                                    || (second.status === 'saved' ? (second.value === selected ? `Kept in your journal: you'd still choose ${selected}.` : `Kept in your journal: you'd switch to ${second.value}.`)
                                        : second.status === 'error' ? 'That didn\'t save. Try again.' : '')}
                            </div>
                        </div>
                    )}
                </section>
            );
        };

        /**
         * ③ The Takeaway, directly below The CEO: what happened, the move to
         * take away (and keep), the index, and what this case added to the
         * member's record. `children` is the private note. `endRef` marks its
         * bottom — seeing it completes the case.
         */
        const TakeawaySection = ({ scenario, spot, headingRef, endRef, onCaseChip, powerIndex, powerIndexChange, wasReplay, streak, moveText, moveSaved, onSaveMove, record, onOpenProfile, children }) => {
            // "The result" is what actually happened: the factoid, with the lesson
            // as the fallback for rows that carry one but no factoid.
            const resultText = scenario.factoid || scenario.lesson;
            return (
                <section key="takeaway" data-testid="sim-takeaway" aria-labelledby="ed-sim-takeaway" style={{ marginTop: '30px' }}>
                    <RunSectionHead id="ed-sim-takeaway" title="Takeaway" headingRef={headingRef} onCaseChip={onCaseChip} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {resultText && (
                            <div data-testid="lesson-part-a" style={{ background: spot ? 'var(--claret-surface)' : 'var(--warning-surface)', border: `1px solid ${spot ? 'var(--claret-border)' : 'var(--warning-border)'}`, borderRadius: '14px', padding: '16px 18px' }}>
                                <div style={{ ...RUN_KICKER, color: spot ? 'var(--claret)' : 'var(--gold-darker)', marginBottom: '6px' }}>The result</div>
                                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.65 }}>{resultText}</p>
                            </div>
                        )}
                        {moveText && (
                            <div data-testid="lesson-part-b" style={{ background: 'var(--navy)', borderRadius: '14px', padding: '16px 18px' }}>
                                <div style={{ ...RUN_KICKER, color: spot ? 'var(--claret-rose)' : 'var(--gold)', marginBottom: '6px' }}>Your move</div>
                                <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#e2e6ee', lineHeight: 1.65 }}>{moveText}</p>
                                <button type="button" onClick={onSaveMove} disabled={moveSaved} data-testid="save-case-move"
                                    className={'ed-btn ' + (spot ? 'ed-btn-claret' : 'ed-btn-gold')} style={{ minHeight: '40px', padding: '9px 16px', fontSize: '13.5px' }}>
                                    {moveSaved ? 'Saved to my moves ✓' : 'Save to my moves'}
                                </button>
                            </div>
                        )}
                        <div style={{ ...RUN_CARD, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                            <div>
                                <div style={{ fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', fontWeight: 600 }}>Decision index</div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '7px' }}>
                                    <span style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', fontWeight: 600, color: 'var(--navy)' }}>{powerIndex}</span>
                                    {/* A replay earns nothing, and says so rather than
                                        showing a ▲ 0 that reads like a rounding error. */}
                                    {wasReplay ? (
                                        <span style={{ fontSize: '12px', color: 'var(--text-meta)', fontWeight: 600 }}>unchanged</span>
                                    ) : (
                                        <span style={{ fontSize: '12px', color: powerIndexChange >= 0 ? 'var(--success-ink)' : 'var(--error-accent)', fontWeight: 600 }}>
                                            {powerIndexChange >= 0 ? '▲' : '▼'} {Math.abs(powerIndexChange)}
                                        </span>
                                    )}
                                </div>
                                {wasReplay && (
                                    <div style={{ fontSize: '11.5px', color: 'var(--text-meta)', marginTop: '3px' }}>
                                        You have worked this case before, so it does not move your index.
                                    </div>
                                )}
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', fontWeight: 600 }}>Practice</div>
                                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--navy)' }}>Day {streak}</div>
                            </div>
                        </div>
                        <div data-testid="your-record" style={RUN_CARD}>
                            <div style={{ ...RUN_KICKER, color: 'var(--text-secondary)', marginBottom: '6px' }}>Your record</div>
                            {record && record.line && <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1.45 }}>{record.line}</div>}
                            {record && record.unlock && <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: '4px' }}>{record.unlock}</div>}
                            <div onClick={onOpenProfile} onKeyDown={btnKey(onOpenProfile)} role="button" tabIndex={0} data-testid="open-decision-profile"
                                className="ed-link" style={{ display: 'inline-block', marginTop: '8px', fontSize: '13px', fontWeight: 600 }}>
                                Your decision profile →
                            </div>
                        </div>
                        {children}
                    </div>
                    <div ref={endRef} data-testid="takeaway-end" aria-hidden="true" style={{ height: '1px' }} />
                </section>
            );
        };

        /**
         * The member's private note on the case. Saved against the attempt, so
         * it waits for the attempt's id; the sentence under the field is the
         * whole privacy promise and is what the worker enforces (notes are
         * refused to impersonation and never returned to the console).
         */
        const ReflectNote = ({ value, saved, status, blocked, onChange, onSave }) => {
            const dirty = value.trim() !== (saved || '');
            return (
                <div data-testid="reflect-note" style={RUN_CARD}>
                    <label htmlFor="ed-reflect-note" style={{ display: 'block', fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.45 }}>
                        Where would a call like this show up for you?
                    </label>
                    <div style={{ fontSize: '12px', color: 'var(--text-meta)', margin: '2px 0 8px' }}>Optional</div>
                    <textarea id="ed-reflect-note" data-testid="reflect-note-input" className="ed-input" rows={3}
                        maxLength={REFLECTION_NOTE_MAX} value={value} disabled={!!blocked}
                        onChange={(e) => onChange(e.target.value)} aria-describedby="ed-reflect-privacy"
                        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontSize: '14px', lineHeight: 1.55 }} />
                    <p id="ed-reflect-privacy" style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        Private to you. It isn't sent to Molly or the CEO mentors, and {BRAND.name} staff can't read it.
                    </p>
                    {blocked ? (
                        <p role="status" data-testid="reflect-note-blocked" style={{ margin: '8px 0 0', fontSize: '12.5px', color: 'var(--text-body)', lineHeight: 1.5 }}>{blocked}</p>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                            <button type="button" onClick={onSave} disabled={!dirty || status === 'saving'} data-testid="reflect-note-save"
                                className="ed-btn ed-btn-navy" style={{ minHeight: '40px', padding: '9px 16px', fontSize: '13.5px' }}>
                                {status === 'saving' ? 'Saving…' : 'Save to my journal'}
                            </button>
                            <span role="status" aria-live="polite" style={{ fontSize: '12.5px', color: status === 'error' ? 'var(--error-ink)' : 'var(--success-ink)' }}>
                                {status === 'saved' && !dirty ? 'Saved to your journal.' : status === 'error' ? 'That didn\'t save. Try again.' : ''}
                            </span>
                            <span style={{ marginLeft: 'auto', fontSize: '11.5px', color: 'var(--text-meta)', fontVariantNumeric: 'tabular-nums' }}>{value.length} / {REFLECTION_NOTE_MAX}</span>
                        </div>
                    )}
                </div>
            );
        };

        /**
         * Choose a decision: every eligible case, filterable, as a sheet. "For
         * you" is the member's world — their industries and focus areas, best
         * fit first — and is offered only once they have saved one. A row
         * never shows the CEO, the company or the year: those are the reveal.
         */
        // The words a member reaches for when they look for a leadership
        // challenge (tester feedback, 30 Sep 2026: search by "ethics, employee
        // conflict, crisis management", not by industry alone). Matched as well
        // as the topic's own label, so "conflict" finds People & culture and
        // "ethics" finds Ethics & stakeholders. Keyed by the canonical
        // decision-type slugs; the labels themselves stay PRACTICE_VOCAB's.
        const TOPIC_WORDS = {
            crisis: 'crisis management emergency survival turnaround recall scandal disaster risk',
            strategy: 'strategy growth expansion competition market positioning pivot',
            innovation: 'innovation product technology disruption launch r&d',
            people: 'people culture team employees employee conflict hiring firing layoffs talent succession morale',
            capital: 'capital deals finance funding investors acquisition merger m&a debt valuation',
            customers: 'customers brand marketing reputation loyalty pricing',
            operations: 'operations execution supply chain efficiency quality scale',
            integrity: 'ethics ethical integrity values stakeholders trust governance compliance honesty',
            leadership: 'leadership leading yourself self personal resilience communication decision-making'
        };

        const DecisionPicker = ({ scenarios, completedIds, world, onPick, onClose }) => {
            // decisionProfile comes from context rather than a prop: the picker is
            // rendered from two places (Home and the simulator) and the nudge below
            // wants the same record both times.
            const { decisionProfile } = useApp();
            const worldSet = !!(world && world.set);
            const [scope, setScope] = useState(worldSet ? 'mine' : 'all');
            const [industry, setIndustry] = useState('');
            const [type, setType] = useState('');
            const [stage, setStage] = useState('');
            const [hideWorked, setHideWorked] = useState(false);
            const [search, setSearch] = useState('');
            const done = new Set(completedIds || []);
            const fit = (s) => {
                if (!worldSet) return 4;
                const inIndustry = s.industry != null && world.industries.includes(s.industry);
                const inFocus = s.decisionType != null && world.focusTypes.includes(s.decisionType);
                return inIndustry && inFocus ? 1 : inIndustry ? 2 : inFocus ? 3 : 4;
            };
            // What the text box searches: the title and the three tags. NOT the CEO,
            // the company, or the YEAR — all three are sealed until the reveal, and
            // a box that matched them would hand the answer to anyone who typed a
            // name or an era, turning the list into a lookup from "who" to "which
            // case". practice-simulator.test.mjs greps this component for those
            // three fields for exactly that reason, and caught the year when the
            // search was first written — so do not name them here either, even in
            // a comment: the guard is a text search, and it is right to be.
            const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
            const hay = (s) => [s.title, industryLabel(s.industry), decisionTypeLabel(s.decisionType), stageLabel(s.companyStage)]
                .filter(Boolean).join(' ').toLowerCase();
            const matches = (s) => { const h = hay(s) + ' ' + (TOPIC_WORDS[s.decisionType] || ''); return terms.every(w => h.includes(w)); };

            // How many cases each leadership topic would show under the OTHER
            // filters, so a chip never promises a list that turns out empty.
            const topicCounts = {};
            for (const s of (scenarios || [])) {
                if (scope === 'mine' && fit(s) >= 4) continue;
                if (industry && s.industry !== industry) continue;
                if (stage && s.companyStage !== stage) continue;
                if (hideWorked && done.has(s.id)) continue;
                if (!matches(s)) continue;
                if (s.decisionType) topicCounts[s.decisionType] = (topicCounts[s.decisionType] || 0) + 1;
            }
            const shown = (scenarios || [])
                .filter(s => scope !== 'mine' || fit(s) < 4)
                .filter(s => !industry || s.industry === industry)
                .filter(s => !type || s.decisionType === type)
                .filter(s => !stage || s.companyStage === stage)
                .filter(s => !hideWorked || !done.has(s.id))
                .filter(matches)
                .sort((a, b) => (scope === 'mine' ? fit(a) - fit(b) : 0) || a.id - b.id);

            // "This might be a good one to try." Picked from `shown`, so the nudge
            // can never contradict the filters the member just set — narrow to one
            // industry and the recommendation is from that industry or absent.
            // Three signals, all already in the record: the decision types their
            // profile says they read WORST (profile.struggles, which the worker only
            // fills for a type whose interval clears their own average — so it is a
            // measured weakness, not a small-sample wobble), their world, and
            // whether they have worked it. A case has to earn 3 points to be shown
            // at all; below that there is no reason worth printing.
            const profile = decisionProfile && decisionProfile.profile ? decisionProfile.profile : null;
            const struggleKeys = new Set((profile && Array.isArray(profile.struggles) ? profile.struggles : [])
                .filter(r => r && r.key).map(r => r.key));
            const recommended = (() => {
                if (!shown.length) return null;
                const scoreOf = (s) => {
                    let n = 0;
                    if (s.decisionType != null && struggleKeys.has(s.decisionType)) n += 4;
                    if (worldSet && s.industry != null && world.industries.includes(s.industry)) n += 2;
                    if (worldSet && s.decisionType != null && world.focusTypes.includes(s.decisionType)) n += 1;
                    if (!done.has(s.id)) n += 2;
                    return n;
                };
                let best = null, bestScore = 0;
                for (const s of shown) {
                    const n = scoreOf(s);
                    if (n > bestScore) { best = s; bestScore = n; }
                }
                if (!best || bestScore < 3) return null;
                const why = [];
                if (best.decisionType != null && struggleKeys.has(best.decisionType)) {
                    why.push(`${decisionTypeLabel(best.decisionType)} is where you have differed most`);
                }
                if (worldSet && best.industry != null && world.industries.includes(best.industry)) why.push('your industry');
                if (worldSet && !why.length && best.decisionType != null && world.focusTypes.includes(best.decisionType)) why.push('your focus');
                if (!done.has(best.id)) why.push('never worked');
                return why.length ? { scenario: best, why } : null;
            })();
            const rest = recommended ? shown.filter(s => s.id !== recommended.scenario.id) : shown;
            const select = { fontSize: '13px', padding: '8px 10px', minHeight: '38px', flex: '1 1 130px', minWidth: 0 };
            const tab = (on) => ({ padding: '6px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', minHeight: '34px', opacity: 1, border: `1.5px solid ${on ? 'var(--navy)' : 'var(--border-input)'}`, background: on ? 'var(--navy)' : 'var(--white)', color: on ? 'var(--text-on-navy)' : 'var(--navy)' });
            const pill = { flexShrink: 0, fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700, borderRadius: '999px', padding: '3px 9px' };
            return (
                <ModalPortal onEscape={onClose}>
                    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                        <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ed-picker-title"
                            data-testid="decision-picker" className="ed-pagein"
                            style={{ width: '100%', maxWidth: '520px', maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--white)', borderRadius: '20px 20px 0 0', boxShadow: 'var(--shadow-modal)' }}>
                            <div style={{ padding: '20px 22px 12px', borderBottom: '1px solid var(--hairline)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px' }}>
                                    <h3 id="ed-picker-title" style={{ fontFamily: 'var(--font-heading)', fontSize: '19px', color: 'var(--navy)', fontWeight: 600, margin: 0 }}>Choose a decision</h3>
                                    <div onClick={onClose} onKeyDown={btnKey(onClose)} role="button" tabIndex={0} className="ed-link" style={{ fontSize: '13.5px', cursor: 'pointer' }}>Close</div>
                                </div>
                                {worldSet && (
                                    <div role="group" aria-label="Which cases" style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                                        <button type="button" aria-pressed={scope === 'mine'} onClick={() => setScope('mine')} style={tab(scope === 'mine')} data-testid="picker-for-you">For you</button>
                                        <button type="button" aria-pressed={scope === 'all'} onClick={() => setScope('all')} style={tab(scope === 'all')} data-testid="picker-all">All</button>
                                    </div>
                                )}
                                <div style={{ position: 'relative', marginBottom: '8px' }}>
                                    <span aria-hidden="true" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-meta)', fontSize: '13px' }}>⌕</span>
                                    <input className="ed-input" type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                                        aria-label="Search cases by title or tag" placeholder="Search a topic — ethics, conflict, crisis — or a title…"
                                        data-testid="picker-search"
                                        /* 16px is not a taste: below it, focusing an input makes iOS
                                           Safari zoom the whole view and leave it zoomed, which is the
                                           clearest tell that an app is a web page. */
                                        style={{ width: '100%', boxSizing: 'border-box', fontSize: '16px', minHeight: '44px', padding: '10px 34px 10px 30px' }} />
                                    {search && (
                                        <button type="button" onClick={() => setSearch('')} aria-label="Clear the search" data-testid="picker-search-clear"
                                            style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', width: '32px', height: '32px', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '15px', lineHeight: 1 }}>×</button>
                                    )}
                                </div>
                                {/* Leadership topic first, as chips — it is how an adult
                                    learner looks for the problem in front of them, so it
                                    leads; industry and stage are the refinements. */}
                                <div style={{ fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-tertiary)', fontWeight: 700, margin: '2px 0 6px' }}>Leadership topic</div>
                                <div role="radiogroup" aria-label="Leadership topic" data-testid="picker-type"
                                    style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '6px', WebkitOverflowScrolling: 'touch' }}>
                                    {[['', 'All topics'], ...PRACTICE_VOCAB.decisionTypes].map(([slug, label]) => {
                                        const on = type === slug;
                                        const n = slug ? (topicCounts[slug] || 0) : null;
                                        return (
                                            <button key={slug || 'all'} type="button" role="radio" aria-checked={on}
                                                onClick={() => setType(slug)} data-testid={slug ? `picker-topic-${slug}` : 'picker-topic-all'}
                                                disabled={!on && n === 0}
                                                style={{
                                                    flexShrink: 0, whiteSpace: 'nowrap', padding: '6px 12px', borderRadius: '999px', fontSize: '12.5px', minHeight: '34px',
                                                    fontWeight: on ? 700 : 500, cursor: !on && n === 0 ? 'default' : 'pointer', opacity: !on && n === 0 ? 0.45 : 1,
                                                    border: `1.5px solid ${on ? 'var(--navy)' : 'var(--border-input)'}`,
                                                    background: on ? 'var(--navy)' : 'var(--white)', color: on ? 'var(--text-on-navy)' : 'var(--navy)', fontFamily: 'inherit'
                                                }}>
                                                {label}{n != null ? ` · ${n}` : ''}
                                            </button>
                                        );
                                    })}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    <select className="ed-input" aria-label="Industry" value={industry} onChange={(e) => setIndustry(e.target.value)} style={select} data-testid="picker-industry">
                                        <option value="">Any industry</option>
                                        {PRACTICE_VOCAB.industries.map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
                                    </select>
                                    <select className="ed-input" aria-label="Company stage" value={stage} onChange={(e) => setStage(e.target.value)} style={select} data-testid="picker-stage">
                                        <option value="">Any stage</option>
                                        {PRACTICE_VOCAB.stages.map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
                                    </select>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-body)', cursor: 'pointer' }}>
                                        <input type="checkbox" checked={hideWorked} onChange={(e) => setHideWorked(e.target.checked)} data-testid="picker-hide-worked" />
                                        Hide cases I've worked
                                    </label>
                                    <span data-testid="picker-count" aria-live="polite" style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                                        {`${shown.length} of ${(scenarios || []).length} ${shown.length === 1 ? 'case' : 'cases'}`}
                                    </span>
                                </div>
                            </div>
                            <div style={{ overflowY: 'auto', padding: '6px 0 20px' }}>
                                {shown.length === 0 && (
                                    <div style={{ padding: '28px 22px', textAlign: 'center', fontSize: '13.5px', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
                                        {terms.length
                                            ? `Nothing matches “${search.trim()}”. The search reads titles and tags — a CEO's name is sealed until the reveal.`
                                            : scope === 'mine' ? 'Nothing from your world matches these filters yet — try All.' : 'No case matches these filters.'}
                                    </div>
                                )}
                                {recommended && (
                                    <div style={{ padding: '4px 14px 10px' }}>
                                        <button type="button" onClick={() => onPick(recommended.scenario.id)} data-testid="picker-recommended"
                                            className="ed-hover-gold-border"
                                            style={{ width: '100%', textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font-body)', background: 'var(--gold-transparent)', border: '1.5px solid var(--gold)', borderRadius: '14px', padding: '13px 15px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                            <span style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 }}>★ Try this one</span>
                                            <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.3 }}>{recommended.scenario.title}</span>
                                            <span data-testid="picker-recommended-why" style={{ fontSize: '12.5px', color: 'var(--text-body)', lineHeight: 1.45 }}>{recommended.why.join(' · ')}</span>
                                            <span style={{ fontSize: '11.5px', color: 'var(--text-meta)' }}>
                                                {[industryLabel(recommended.scenario.industry), decisionTypeLabel(recommended.scenario.decisionType), stageLabel(recommended.scenario.companyStage)].filter(Boolean).join(' · ') || 'Not tagged yet'}
                                            </span>
                                        </button>
                                    </div>
                                )}
                                {rest.map(s => (
                                    <div key={s.id}
                                        onClick={() => onPick(s.id)}
                                        onKeyDown={btnKey(() => onPick(s.id))}
                                        role="button" tabIndex={0}
                                        className="ed-menu-row" data-testid={`picker-case-${s.id}`}
                                        style={{ padding: '12px 22px', display: 'flex', gap: '10px', alignItems: 'center', cursor: 'pointer' }}>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1.35 }}>{s.title}</div>
                                            <div style={{ fontSize: '11.5px', color: 'var(--text-meta)', marginTop: '2px' }}>
                                                {[industryLabel(s.industry), decisionTypeLabel(s.decisionType), stageLabel(s.companyStage)].filter(Boolean).join(' · ') || 'Not tagged yet'}
                                            </div>
                                        </div>
                                        {isSpotBad(s) && <span style={{ ...pill, color: 'var(--text-on-claret)', background: 'var(--claret)' }}>Bad Call</span>}
                                        {done.has(s.id) && <span style={{ ...pill, color: 'var(--success-ink)', background: 'var(--success-surface)', border: '1px solid var(--success-border)' }}>✓ Worked</span>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </ModalPortal>
            );
        };

        // ---- The research (docs/RESEARCH_PACKAGE.md §2 "App") --------------------
        // Real exhibits for a case — the numbers going in, the timeline up to the
        // day, document excerpts, the market context — and, once the call is
        // locked in, what happened next. Researched off-site from public sources
        // and approved by a person in the console before any member sees it. The
        // worker sends it only for a case marked `hasResearch`, and `after` only
        // to a member with an attempt on the case: the consequences are a reveal.
        // Every string renders as a React text node; a link is drawn only for an
        // https URL, and an image only from a research key on our own API. A case
        // whose research is missing, or did not load, is the case as it always
        // was — nothing on the page says so. Like every piece above, these live at
        // module scope (SimulatorView re-renders every second).

        const RESEARCH_LABEL = `Researched with AI from public sources · checked by ${BRAND.name}`;
        const RESEARCH_FIGURE_KINDS = ['line', 'bar', 'share', 'kpi'];
        const RESEARCH_SOURCE_KINDS = { 'edify-transcript': `${BRAND.name} interview`, filing: 'Filing', company: 'Company document', press: 'Press release', news: 'News', book: 'Book', academic: 'Academic', dataset: 'Dataset', interview: 'Interview', other: 'Source' };
        const RESEARCH_DOCUMENT_KINDS = { quote: 'Quote', press: 'Press release', filing: 'Filing', letter: 'Letter', news: 'News', memo: 'Memo' };
        const RESEARCH_GRAPHIC_KINDS = { 'org-chart': 'Org chart', map: 'Map', diagram: 'Diagram' };
        // Series and parts in order: navy, gold, then quieter tones. The first five
        // are at least 3:1 on white (a line or bar has at most three series); a
        // sixth share part leans on its white edge and its legend row. Colour is
        // never the only difference: a line's series differ by marker shape and
        // stroke too (RESEARCH_SERIES_STROKES), and a share part past the sixth
        // by its fill (researchPartFill).
        const RESEARCH_COLORS = ['var(--navy)', 'var(--gold-darker)', 'var(--text-tertiary)', 'var(--claret-light)', 'var(--navy-lighter)', 'var(--gold)'];
        // A line series' stroke, by its index: solid, long dashes, dots. Its
        // marker follows the same index (circle, square, diamond), and the
        // legend draws both, so series 2 and 3 — near-identical in lightness —
        // are told apart without colour.
        const RESEARCH_SERIES_STROKES = [{ dash: null, cap: 'round' }, { dash: '8 4', cap: 'butt' }, { dash: '0.1 4.4', cap: 'round' }];
        // What the Decide sources say under a redacted list while the call is open.
        const RESEARCH_SOURCES_CLOSED = 'Titles and links open after you decide.';
        const RESEARCH_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        // A chart is drawn at the width its column measures, so its text is its
        // real size on a phone and in the wide case column alike. About 6.4px a
        // character at the 10.5–11px the axes use.
        const RESEARCH_MIN_WIDTH = 240;
        const RESEARCH_MAX_WIDTH = 720;
        const RESEARCH_CHAR_PX = 6.4;

        const researchIsObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
        // Text for a text node: a trimmed string or a finite number, else ''. A
        // field that arrives as an object must never reach JSX as a child.
        const researchText = (v) => (typeof v === 'string' ? v.trim() : (typeof v === 'number' && isFinite(v) ? String(v) : ''));
        // A citation of our own interview is stored under the name the product had
        // when the research was written ("<name> interview transcript", publisher
        // "<name>" — the research contract, docs/RESEARCH_PACKAGE.md). Members read it
        // under today's name. Only that one kind of source: a third party's title
        // is never rewritten.
        const BRAND_FORMER = (BRAND.formerNames || []).length
            ? new RegExp('\\b(?:' + BRAND.formerNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')\\b', 'g') : null;
        const researchOwnText = (s, v) => {
            const t = researchText(v);
            return s && s.kind === 'edify-transcript' && BRAND_FORMER ? t.replace(BRAND_FORMER, BRAND.name) : t;
        };
        // A label from one of the maps above — own keys only, so a kind such as
        // "constructor" is the fallback, never a function rendered as a child.
        const researchKindLabel = (map, kind, fallback) =>
            (typeof kind === 'string' && Object.prototype.hasOwnProperty.call(map, kind) ? map[kind] : fallback);

        /**
         * GET /api/scenarios/:id/research, made safe to render → { before, after
         * | null, sources, researchedAt, coverage }, or null. Lists keep only
         * objects, `after` is null unless the worker sent it, and a status other
         * than approved — a draft in front of a staff member's own session — is
         * nothing: this is the member's screen.
         */
        const researchViewOf = (x) => {
            if (!researchIsObject(x)) return null;
            if (x.status != null && x.status !== 'approved') return null;
            const list = (v) => (Array.isArray(v) ? v.filter(researchIsObject) : []);
            const b = researchIsObject(x.before) ? x.before : {};
            const before = { figures: list(b.figures), timeline: list(b.timeline), documents: list(b.documents), context: list(b.context), graphics: list(b.graphics) };
            let after = null;
            if (researchIsObject(x.after)) {
                const a = x.after;
                const outcome = researchIsObject(a.outcome) && researchText(a.outcome.text) ? a.outcome : null;
                after = { outcome, figures: list(a.figures), timeline: list(a.timeline), documents: list(a.documents), graphics: list(a.graphics) };
            }
            const seen = new Set();
            // A source the worker sent redacted (before the member's lock-in) is
            // cut to what it may show here, whatever else arrived beside the flag.
            const sources = list(x.sources).filter(s => typeof s.id === 'string' && s.id && !seen.has(s.id) && !!seen.add(s.id))
                .map(s => (s.redacted === true ? { id: s.id, kind: s.kind, published: s.published, redacted: true } : s));
            return { before, after, sources, researchedAt: researchText(x.researchedAt) || null, coverage: researchText(x.coverage) || null };
        };

        const researchHasContent = (part) => researchIsObject(part)
            && (!!part.outcome || ['figures', 'timeline', 'documents', 'context', 'graphics'].some(k => Array.isArray(part[k]) && part[k].length > 0));

        /**
         * The sources one part cites, in the stored order, numbered from 1 for
         * that part → { list, numbers: Map(id → n) }. Numbered per part, so the
         * Decide marks keep their numbers when the refetch after the lock-in
         * brings the sources of `after` along with it.
         */
        const researchPartSources = (sources, part) => {
            const cited = new Set();
            const walk = (v) => {
                if (Array.isArray(v)) v.forEach(walk);
                else if (researchIsObject(v)) {
                    for (const k of Object.keys(v)) {
                        if (k === 'sourceIds' && Array.isArray(v[k])) v[k].forEach(id => { if (typeof id === 'string') cited.add(id); });
                        else walk(v[k]);
                    }
                }
            };
            walk(part);
            const list = (Array.isArray(sources) ? sources : []).filter(s => researchIsObject(s) && cited.has(s.id));
            const numbers = new Map(list.map((s, i) => [s.id, i + 1]));
            return { list, numbers };
        };
        // An item's marks: the numbers of the sources it cites, once each, in order.
        const researchMarks = (ids, numbers) => [...new Set((Array.isArray(ids) ? ids : [])
            .map(id => (numbers instanceof Map ? numbers.get(id) : undefined))
            .filter(n => typeof n === 'number'))].sort((a, b) => a - b);

        // A source the member cannot open yet: every source on the Decide part
        // until the call is locked in, and any the worker sent redacted — it
        // sends { id, kind, published, redacted: true } until the member has an
        // attempt on the case, and the full source only on the refetch after.
        const researchSourceRedacted = (s, revealed) => !revealed || !researchIsObject(s) || s.redacted === true;
        // What a redacted source shows: "Source 1 · News · 1987". Never a title,
        // a publisher or a URL, which can name the company. The catch-all kind
        // ("other" → "Source") would only repeat the word, so it is left out.
        const researchSourceLabel = (s, n) => {
            const o = researchIsObject(s) ? s : {};
            const kind = researchKindLabel(RESEARCH_SOURCE_KINDS, o.kind, '');
            return [`Source ${n}`, kind === RESEARCH_SOURCE_KINDS.other ? '' : kind, researchDate(o.published)].filter(Boolean).join(' · ');
        };

        // A citation is a link only when it is plainly https to a named host —
        // nothing else (javascript:, data:, a bare path, credentials in the URL)
        // becomes an href.
        const researchSafeUrl = (v) => {
            if (typeof v !== 'string') return null;
            const url = v.trim();
            return url.length <= 2000
                && /^https:\/\/[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}(?::\d{1,5})?(?:[/?#][^\s<>"\\]*)?$/.test(url)
                ? url : null;
        };

        // A graphic is served by our API from R2, under this case's own research
        // keys (images.ts `research/<scenarioId>/…`) — never from a URL in the data.
        const researchImageSrc = (key, scenarioId) => {
            if (typeof key !== 'string' || !API_URL || key.includes('..')) return null;
            const m = key.match(/^research\/(\d+)\/[A-Za-z0-9][A-Za-z0-9._-]*\.(?:svg|png)$/);
            return m && Number(m[1]) === Number(scenarioId) ? `${API_URL}/api/images/${key}` : null;
        };

        // "1987-02-11" → "11 Feb 1987", "1985-06" → "Jun 1985", "1987" stays;
        // anything else ("Q3 1980") is shown as written.
        const researchDate = (v) => {
            const s = researchText(v);
            const m = s.match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/);
            if (!m) return s;
            const month = m[2] ? RESEARCH_MONTHS[Number(m[2]) - 1] : null;
            if (m[2] && !month) return s;
            return [m[3] ? String(Number(m[3])) : null, month, m[1]].filter(Boolean).join(' ');
        };

        // 60000 → "60,000", 12.4 → "12.4", -3.25 → "−3.25": two decimals at most —
        // except a value two decimals would round to nothing, which keeps two
        // significant figures (0.004 → "0.004", 0.00123 → "0.0012"): a sourced
        // value that is not zero never reads as 0.
        const researchNumber = (v) => {
            if (typeof v !== 'number' || !isFinite(v)) return '';
            const abs = Math.abs(v);
            if (abs > 0 && Math.round(abs * 100) === 0) {
                const dp = 1 - Math.floor(Math.log10(abs));
                return (v < 0 ? '−' : '') + (dp <= 20 ? abs.toFixed(dp).replace(/0+$/, '') : abs.toPrecision(2));
            }
            const [int, dec] = (Math.round(abs * 100) / 100).toFixed(2).split('.');
            const frac = dec.replace(/0+$/, '');
            const body = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (frac ? '.' + frac : '');
            return (v < 0 && body !== '0' ? '−' : '') + body;
        };
        // A value with its unit: "51%", "51% of spend", "$12.4M", "60,000 sq ft".
        const researchValue = (v, unit) => {
            const n = researchNumber(v);
            if (!n) return '';
            const u = researchText(unit);
            if (!u) return n;
            if (u[0] === '%') return n + u;
            const cur = u.match(/^([$£€¥])\s*(.*)$/);
            if (cur) {
                const neg = n[0] === '−';
                const rest = cur[2];
                return `${neg ? '−' : ''}${cur[1]}${neg ? n.slice(1) : n}${!rest ? '' : /^[A-Za-z]{1,3}$/.test(rest) ? rest : ' ' + rest}`;
            }
            return `${n} ${u}`;
        };
        // An axis's labels, compact and read off the axis as a whole: one unit
        // for every tick (k, M or B, by the axis's larger end: 60000 → "60k",
        // 2500000 → "2.5M") and as many decimals as its step needs, so two ticks
        // never read the same — 2.5M, 2.5025M, 2.505M, never 2.5M, 2.5M, 2.51M —
        // and a fine axis is never all "0". Trailing zeros are dropped.
        const researchTickLabels = (scale) => {
            const ticks = researchIsObject(scale) && Array.isArray(scale.ticks) ? scale.ticks : [];
            const big = Math.max(0, ...ticks.filter(v => typeof v === 'number' && isFinite(v)).map(Math.abs));
            const [div, suffix] = big >= 1e9 ? [1e9, 'B'] : big >= 1e6 ? [1e6, 'M'] : big >= 1e4 ? [1e3, 'k'] : [1, ''];
            const step = typeof scale.step === 'number' && isFinite(scale.step) && scale.step > 0 ? scale.step / div : 1;
            const dp = Math.min(12, (step.toFixed(12).replace(/\.?0+$/, '').split('.')[1] || '').length);
            return ticks.map(v => {
                if (typeof v !== 'number' || !isFinite(v)) return '';
                const [int, dec] = (Math.abs(v) / div).toFixed(dp).split('.');
                const frac = (dec || '').replace(/0+$/, '');
                const body = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (frac ? '.' + frac : '');
                return (v < 0 && /[1-9]/.test(body) ? '−' : '') + body + (body === '0' ? '' : suffix);
            });
        };

        // A readable axis over [lo, hi]: steps of 1, 2, 2.5 or 5 × 10ⁿ, about
        // `count` of them → { min, max, step, ticks }.
        const researchNiceScale = (lo, hi, count = 4) => {
            let a = Number(lo), b = Number(hi);
            if (!isFinite(a) || !isFinite(b)) { a = 0; b = 1; }
            if (a > b) [a, b] = [b, a];
            if (a === b) { if (a > 0) a = 0; else if (a < 0) b = 0; else b = 1; }
            const clean = (n) => Number(n.toFixed(10));
            const raw = (b - a) / count;
            const mag = Math.pow(10, Math.floor(Math.log10(raw)));
            const norm = raw / mag;
            const step = clean((norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag);
            const min = clean(Math.floor(clean(a / step)) * step);
            const max = clean(Math.ceil(clean(b / step)) * step);
            const ticks = [];
            for (let i = 0; min + i * step <= max + step / 2 && i < 60; i++) ticks.push(clean(min + i * step));
            return { min, max, step, ticks };
        };
        // The axis a figure's values get. A bar always starts at zero (its length
        // is the value); a line starts at zero when its low is already within
        // half its high, and otherwise spans its own range rather than flattening
        // a real change into a line along the top.
        const researchDomain = (values, kind) => {
            const ys = (Array.isArray(values) ? values : []).filter(v => typeof v === 'number' && isFinite(v));
            if (!ys.length) return researchNiceScale(0, 1);
            let lo = Math.min(...ys), hi = Math.max(...ys);
            if (kind === 'bar' || (lo >= 0 && lo <= hi / 2)) { lo = Math.min(0, lo); hi = Math.max(0, hi); }
            return researchNiceScale(lo, hi);
        };
        const researchLinear = (d0, d1, r0, r1) => (v) => (d1 === d0 ? (r0 + r1) / 2 : r0 + (v - d0) * (r1 - r0) / (d1 - d0));
        const researchRound = (n) => Math.round(n * 10) / 10;

        // A figure's series, normalised: at most 3 (share and kpi: 1), at most 24
        // points (kpi: 2), each { x, y | null, estimate }.
        const researchSeries = (figure) => {
            if (!researchIsObject(figure) || !Array.isArray(figure.series)) return [];
            const one = figure.kind === 'share' || figure.kind === 'kpi';
            return figure.series.filter(researchIsObject).slice(0, one ? 1 : 3).map(s => ({
                name: researchText(s.name),
                points: (Array.isArray(s.points) ? s.points : []).filter(researchIsObject)
                    .map(p => ({ x: researchText(p.x), y: typeof p.y === 'number' && isFinite(p.y) ? p.y : null, estimate: p.estimate === true }))
                    .filter(p => p.x)
                    .slice(0, figure.kind === 'kpi' ? 2 : 24)
            }));
        };
        // The x labels across every series, in first-seen order. A label one
        // series repeats is that many categories — two "A competitor" bars are
        // two bars, never one with the second value lost (researchAligned).
        const researchCategories = (series) => {
            const out = [], have = new Map();
            (Array.isArray(series) ? series : []).forEach(s => {
                const seen = new Map();
                (s && Array.isArray(s.points) ? s.points : []).forEach(p => {
                    if (!p || !p.x) return;
                    const n = (seen.get(p.x) || 0) + 1;
                    seen.set(p.x, n);
                    if (n > (have.get(p.x) || 0)) { out.push(p.x); have.set(p.x, n); }
                });
            });
            return out;
        };
        // One series' point for each category, in the categories' order, or
        // null where it has none: the n-th category of a label is the series'
        // n-th point of that label.
        const researchAligned = (s, cats) => {
            const points = s && Array.isArray(s.points) ? s.points : [];
            const used = new Map();
            return (Array.isArray(cats) ? cats : []).map(c => {
                const n = used.get(c) || 0;
                used.set(c, n + 1);
                return points.filter(q => q && q.x === c)[n] || null;
            });
        };

        // A line's x as a point in time, in years — the middle of the period it
        // names, so a year, a half, a quarter, a month and a day each sit where
        // they belong: "1987" 1987.5, "H1 1987" 1987.25, "Q3 1987" 1987.625,
        // "1987-02" / "Feb 1987" the middle of February, "1987-02-11" that day.
        // Quarters and halves read either way round, as the validator reads
        // them (research.ts dateOfX). Anything else ("FY1987", "Competitor A")
        // is null. Years 1600–2100, as the validator's.
        const researchTimeOf = (x) => {
            const s = researchText(x);
            const year = (y) => (Number(y) >= 1600 && Number(y) <= 2100 ? Number(y) : null);
            let m = s.match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/);
            if (m) {
                const y = year(m[1]);
                if (y === null) return null;
                if (!m[2]) return y + 0.5;
                const mo = Number(m[2]);
                if (mo < 1 || mo > 12) return null;
                if (!m[3]) return y + (mo - 0.5) / 12;
                const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
                const d = Number(m[3]);
                return d < 1 || d > days ? null : y + (mo - 1) / 12 + (d - 0.5) / days / 12;
            }
            m = s.match(/^(?:q([1-4])[\s/-]*(\d{4})|(\d{4})[\s/-]*q([1-4]))$/i);
            if (m) { const y = year(m[2] || m[3]); return y === null ? null : y + (Number(m[1] || m[4]) - 0.5) / 4; }
            m = s.match(/^(?:h([12])[\s/-]*(\d{4})|(\d{4})[\s/-]*h([12]))$/i);
            if (m) { const y = year(m[2] || m[3]); return y === null ? null : y + (Number(m[1] || m[4]) - 0.5) / 2; }
            m = s.match(/^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.?[\s/-]*(\d{4})$/i);
            if (m) {
                const y = year(m[2]);
                return y === null ? null : y + (RESEARCH_MONTHS.findIndex(n => n.toLowerCase() === m[1].toLowerCase()) + 0.5) / 12;
            }
            return null;
        };
        // A line's x axis → { cats, times }. When every x reads as a time, the
        // series are merged into one axis in time order (a series that starts
        // later or skips a year never runs back on itself) and `times` places
        // each on a true time scale, so a five-year gap is five times a one-year
        // one. Otherwise the labels keep first-seen order, evenly spaced, and
        // `times` is null.
        const researchLineAxis = (series) => {
            const cats = researchCategories(series);
            const times = cats.map(researchTimeOf);
            if (cats.length < 2 || times.some(t => t === null) || Math.min(...times) === Math.max(...times)) return { cats, times: null };
            const order = cats.map((c, i) => i).sort((a, b) => times[a] - times[b] || a - b);
            return { cats: order.map(i => cats[i]), times: order.map(i => times[i]) };
        };

        // A line through pixel points → { record, estimate } path data, each run
        // of neighbouring segments one continuous path so a series' dash pattern
        // flows through its points. A segment touching an estimate is the
        // estimate path (drawn faded); a missing value breaks the line.
        const researchLinePath = (points) => {
            const out = { record: [], estimate: [] };
            let last = null, lastEnd = null;
            for (let i = 1; i < (points || []).length; i++) {
                const a = points[i - 1], b = points[i];
                if (!a || !b || a.y == null || b.y == null) { last = null; continue; }
                const which = a.estimate || b.estimate ? 'estimate' : 'record';
                const to = `L${researchRound(b.x)} ${researchRound(b.y)}`;
                if (which === last && lastEnd === a) out[which].push(to);
                else out[which].push(`M${researchRound(a.x)} ${researchRound(a.y)}${to}`);
                last = which;
                lastEnd = b;
            }
            return { record: out.record.join(''), estimate: out.estimate.join('') };
        };

        // The gap, in px, kept between two x labels.
        const RESEARCH_LABEL_GAP = 4;

        /**
         * A line figure laid out at `width` px: y ticks with gridlines, x labels
         * thinned from the last one back so the most recent period is always
         * named, and each series' points and paths. Times sit on a true time
         * scale (researchLineAxis); anything else is spaced as categories.
         */
        const researchLineLayout = (series, width) => {
            const w = Math.max(RESEARCH_MIN_WIDTH, Math.round(width) || 0);
            const { cats, times } = researchLineAxis(series);
            const scale = researchDomain(series.flatMap(s => s.points.map(p => p.y)), 'line');
            const top = 10, plot = 150, bottom = 26;
            const tickLabels = researchTickLabels(scale);
            const textW = (label) => label.length * RESEARCH_CHAR_PX;
            // Room at each end for the first and last x label to sit centred on
            // its point (up to a sixth of the width), so the labels keep one
            // rhythm to the edges instead of the end ones shifting into their
            // neighbours.
            const reserve = (label) => (cats.length > 1 && label ? Math.min(Math.ceil(textW(label) / 2) + 2, Math.round(w / 6)) : 0);
            const left = Math.max(Math.ceil(Math.max(1, ...tickLabels.map(t => t.length)) * RESEARCH_CHAR_PX) + 8, reserve(cats[0]));
            const right = Math.max(14, reserve(cats[cats.length - 1]));
            const span = w - left - right;
            const xAt = times
                ? (i) => left + (times[i] - times[0]) / (times[times.length - 1] - times[0]) * span
                : (i) => (cats.length > 1 ? left + i * span / (cats.length - 1) : left + span / 2);
            const yAt = researchLinear(scale.min, scale.max, top + plot, top);
            // Evenly spaced points (categories, or a time series with no gaps)
            // name every n-th, counted from the last; unevenly spaced times try
            // each label in turn. Either way the pass below has the last word.
            const gaps = cats.slice(1).map((c, i) => xAt(i + 1) - xAt(i));
            const even = gaps.length > 0 && gaps.every(g => Math.abs(g - gaps[0]) < 0.5);
            const every = even ? Math.max(1, Math.ceil((Math.max(1, ...cats.map(c => c.length)) * RESEARCH_CHAR_PX + 8) / gaps[0])) : 1;
            const labels = cats.map((label, i) => {
                const x = xAt(i), half = textW(label) / 2;
                return { label, x: researchRound(x), shown: (cats.length - 1 - i) % every === 0, anchor: x + half > w ? 'end' : x - half < 0 ? 'start' : 'middle' };
            });
            // A label near an edge is anchored to its end or its start, which
            // moves its text by half its width: from the last label back, one
            // whose text as drawn runs into the label kept after it is hidden.
            let keptLeft = Infinity;
            for (let i = labels.length - 1; i >= 0; i--) {
                const l = labels[i];
                if (!l.shown) continue;
                const a = l.anchor === 'end' ? l.x - textW(l.label) : l.anchor === 'start' ? l.x : l.x - textW(l.label) / 2;
                if (a + textW(l.label) + RESEARCH_LABEL_GAP > keptLeft) l.shown = false;
                else keptLeft = a;
            }
            const lines = series.map((s, j) => {
                const at = researchAligned(s, cats);
                const points = cats.map((c, i) => {
                    const p = at[i];
                    return p ? { x: researchRound(xAt(i)), y: p.y == null ? null : researchRound(yAt(p.y)), value: p.y, estimate: p.estimate, label: c } : null;
                }).filter(Boolean);
                return { name: s.name, color: j, points, ...researchLinePath(points) };
            });
            return {
                width: w, height: top + plot + bottom, left, right, top, plotBottom: top + plot, timeScale: !!times,
                ticks: scale.ticks.map((v, i) => ({ v, label: tickLabels[i], y: researchRound(yAt(v)) })),
                labels, lines
            };
        };

        /**
         * A bar figure laid out at `width` px, as horizontal bars — a category's
         * name on its own line and its bars (one per series) beneath, so a long
         * name never runs into a bar on a phone. Values past the bar's end.
         */
        const researchBarLayout = (series, width) => {
            const w = Math.max(RESEARCH_MIN_WIDTH, Math.round(width) || 0);
            const cats = researchCategories(series);
            const values = series.flatMap(s => s.points.map(p => p.y)).filter(v => v != null);
            const scale = researchDomain(values, 'bar');
            const room = Math.max(1, ...values.map(v => researchNumber(v).length)) * RESEARCH_CHAR_PX + 6;
            const hasNeg = values.some(v => v < 0);
            const left = hasNeg ? room : 0, right = room;
            const xAt = researchLinear(scale.min, scale.max, left, w - right);
            const zero = xAt(0);
            const n = Math.max(1, series.length);
            const barH = n > 1 ? 10 : 14, gap = 3, labelH = 17, groupGap = 12, top = 2;
            const groupH = labelH + n * barH + (n - 1) * gap;
            const aligned = series.map(s => researchAligned(s, cats));
            const groups = cats.map((label, i) => {
                const y0 = top + i * (groupH + groupGap);
                const bars = series.map((s, j) => {
                    const p = aligned[j][i];
                    if (!p || p.y == null) return null;
                    const end = xAt(p.y);
                    const y = y0 + labelH + j * (barH + gap);
                    return {
                        series: j, value: p.y, estimate: p.estimate,
                        x: researchRound(Math.min(zero, end)), y: researchRound(y), w: researchRound(Math.abs(end - zero)), h: barH,
                        text: researchNumber(p.y), tx: researchRound(p.y < 0 ? end - 4 : end + 4), ty: researchRound(y + barH - 2), anchor: p.y < 0 ? 'end' : 'start'
                    };
                }).filter(Boolean);
                return { label, y: researchRound(y0 + 12), bars };
            });
            return { width: w, height: top + cats.length * (groupH + groupGap) - groupGap + 4, zero: researchRound(zero), hasNeg, groups };
        };

        // A share figure's parts, normalised to a whole: { label, value, pct,
        // start, estimate }. What is drawn adds up to 100 even when the record
        // sums to 98; what is written is the value as sourced. A negative or
        // missing value is no part of the bar.
        const researchShareParts = (points) => {
            const pts = (Array.isArray(points) ? points : []).filter(p => p && typeof p === 'object');
            const sizes = pts.map(p => (typeof p.y === 'number' && isFinite(p.y) && p.y > 0 ? p.y : 0));
            const sum = sizes.reduce((a, b) => a + b, 0);
            let start = 0;
            return pts.map((p, i) => {
                const pct = sum > 0 ? sizes[i] / sum * 100 : 0;
                const part = { label: researchText(p.x), value: typeof p.y === 'number' && isFinite(p.y) ? p.y : null, pct, start, estimate: p.estimate === true };
                start += pct;
                return part;
            });
        };
        // A share part's fill → { color, style }: the palette colour by its
        // index, and from the seventh part on a pattern in that colour — dots,
        // then stripes, then a grid — so a colour that comes back is never the
        // same fill as an earlier part, in the bar or in its legend row (a share
        // figure holds up to 24 parts; the palette has six colours).
        const RESEARCH_PART_STYLES = ['solid', 'dots', 'stripes', 'grid'];
        const researchPartFill = (i) => {
            const n = Math.max(0, Math.floor(Number(i)) || 0);
            return { color: n % RESEARCH_COLORS.length, style: RESEARCH_PART_STYLES[Math.floor(n / RESEARCH_COLORS.length) % RESEARCH_PART_STYLES.length] };
        };

        // Measures the element a chart draws into (see RESEARCH_CHAR_PX).
        const useResearchWidth = (ref) => {
            const [width, setWidth] = useState(320);
            useEffect(() => {
                const el = ref.current;
                if (!el) return;
                const measure = () => {
                    const w = Math.round(el.getBoundingClientRect().width);
                    if (w > 0) setWidth(Math.min(RESEARCH_MAX_WIDTH, Math.max(RESEARCH_MIN_WIDTH, w)));
                };
                measure();
                if (typeof ResizeObserver === 'undefined') {
                    window.addEventListener('resize', measure);
                    return () => window.removeEventListener('resize', measure);
                }
                const ro = new ResizeObserver(measure);
                ro.observe(el);
                return () => ro.disconnect();
            }, []);
            return width;
        };

        const RESEARCH_SMALL_LABEL = { fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-secondary)', fontWeight: 600 };
        const RESEARCH_KICKER = { ...RUN_KICKER, fontSize: '10.5px', color: 'var(--text-secondary)', marginBottom: '6px' };
        const RESEARCH_HATCH_SWATCH = { display: 'inline-block', width: '12px', height: '10px', borderRadius: '2px', border: '1px solid var(--navy)', background: 'repeating-linear-gradient(45deg, var(--navy) 0 1.5px, var(--white) 1.5px 4px)', flexShrink: 0 };

        // One hatch per palette colour, for the estimates in one chart. Ids are
        // the chart's own, so two charts on the page never share a pattern.
        const researchHatches = (uid) => (
            <defs>
                {RESEARCH_COLORS.map((c, i) => (
                    <pattern key={i} id={`${uid}-h${i}`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <rect width="5" height="5" fill="var(--white)" />
                        <rect width="2" height="5" fill={c} />
                    </pattern>
                ))}
            </defs>
        );
        // The patterns a share part past the sixth is filled with (researchPartFill),
        // one per colour and style, ids the chart's own.
        const researchPartPatterns = (uid) => (
            <defs>
                {RESEARCH_COLORS.map((c, i) => (
                    <React.Fragment key={i}>
                        <pattern id={`${uid}-dots${i}`} width="5" height="5" patternUnits="userSpaceOnUse">
                            <rect width="5" height="5" fill="var(--white)" />
                            <circle cx="2.5" cy="2.5" r="1.6" fill={c} />
                        </pattern>
                        <pattern id={`${uid}-stripes${i}`} width="5" height="5" patternUnits="userSpaceOnUse">
                            <rect width="5" height="5" fill="var(--white)" />
                            <rect width="5" height="2.5" fill={c} />
                        </pattern>
                        <pattern id={`${uid}-grid${i}`} width="5" height="5" patternUnits="userSpaceOnUse">
                            <rect width="5" height="5" fill={c} />
                            <rect x="1.5" y="1.5" width="2" height="2" fill="var(--white)" />
                        </pattern>
                    </React.Fragment>
                ))}
            </defs>
        );
        // A share part's legend swatch, filled as its part is drawn.
        const researchPartSwatch = (fill, estimate) => {
            const c = RESEARCH_COLORS[fill.color];
            const box = { display: 'inline-block', width: '12px', height: '10px', borderRadius: '2px', flexShrink: 0 };
            if (estimate) return { ...RESEARCH_HATCH_SWATCH, border: `1px solid ${c}`, background: `repeating-linear-gradient(45deg, ${c} 0 1.5px, var(--white) 1.5px 4px)` };
            if (fill.style === 'dots') return { ...box, border: `1px solid ${c}`, background: `radial-gradient(circle, ${c} 1.2px, var(--white) 1.5px) 0 0 / 4px 4px` };
            if (fill.style === 'stripes') return { ...box, border: `1px solid ${c}`, background: `repeating-linear-gradient(0deg, ${c} 0 2px, var(--white) 2px 4px)` };
            if (fill.style === 'grid') return { ...box, border: `1px solid ${c}`, background: `radial-gradient(circle, var(--white) 1px, ${c} 1.3px) 0 0 / 4px 4px` };
            return { ...box, background: c };
        };
        // A line series' point: circle, square or diamond, so the series differ by
        // more than colour. An estimate is hatched.
        const researchMarker = (shape, x, y, color, fill, key) => {
            if (shape === 1) return <rect key={key} x={x - 3.6} y={y - 3.6} width="7.2" height="7.2" fill={fill} stroke={color} strokeWidth="1.6" />;
            if (shape === 2) return <path key={key} d={`M${x} ${y - 4.6}L${x + 4.6} ${y}L${x} ${y + 4.6}L${x - 4.6} ${y}Z`} fill={fill} stroke={color} strokeWidth="1.6" />;
            return <circle key={key} cx={x} cy={y} r="3.8" fill={fill} stroke={color} strokeWidth="1.6" />;
        };
        // A line series' stroke props (RESEARCH_SERIES_STROKES), for the chart
        // and its legend alike.
        const researchStrokeProps = (j) => {
            const st = RESEARCH_SERIES_STROKES[j % RESEARCH_SERIES_STROKES.length];
            return st.dash ? { strokeDasharray: st.dash, strokeLinecap: st.cap } : { strokeLinecap: st.cap };
        };

        // Numbered superscripts for the sources an item cites.
        const SourceMarks = ({ ids, numbers }) => {
            const marks = researchMarks(ids, numbers);
            if (!marks.length) return null;
            return (
                <sup data-testid="source-marks" style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-secondary)', marginLeft: '3px', lineHeight: 0, whiteSpace: 'nowrap' }}>
                    <span className="sr-only">{marks.length === 1 ? ' source ' : ' sources '}</span>
                    {marks.join(', ')}
                </sup>
            );
        };

        /**
         * A chart drawn from the data: line, bar, share or kpi, with its title,
         * the unit, a hatch for every estimate and a legend line that says so,
         * and — for the drawn kinds whose values live only in the picture — the
         * same numbers as a visually hidden table for a screen reader.
         */
        const ResearchFigure = ({ figure, numbers }) => {
            const bodyRef = useRef(null);
            const width = useResearchWidth(bodyRef);
            const uid = 'edr' + React.useId().replace(/[^A-Za-z0-9]/g, '');
            const kind = researchIsObject(figure) ? figure.kind : null;
            const series = researchSeries(figure);
            if (!RESEARCH_FIGURE_KINDS.includes(kind) || !series.some(s => s.points.some(p => p.y != null))) return null;
            const title = researchText(figure.title);
            const unit = researchText(figure.unit);
            const note = researchText(figure.note);
            const drawn = kind === 'line' || kind === 'bar';
            const meta = [researchText(figure.subtitle), drawn ? unit : ''].filter(Boolean).join(' · ');
            const estimated = series.some(s => s.points.some(p => p.estimate));
            const hatch = (i) => `url(#${uid}-h${i % RESEARCH_COLORS.length})`;
            const color = (i) => RESEARCH_COLORS[i % RESEARCH_COLORS.length];
            const axisText = { fontSize: '10.5', fill: 'var(--text-secondary)' };
            let body = null;
            if (kind === 'line') {
                const L = researchLineLayout(series, width);
                body = (
                    <svg viewBox={`0 0 ${L.width} ${L.height}`} width="100%" aria-hidden="true" focusable="false" style={{ display: 'block', height: 'auto' }}>
                        {researchHatches(uid)}
                        {L.ticks.map(t => (
                            <g key={`t${t.v}`}>
                                <line x1={L.left} x2={L.width - L.right} y1={t.y} y2={t.y} stroke="var(--track)" strokeWidth="1" />
                                <text x={L.left - 6} y={t.y + 3.5} textAnchor="end" {...axisText}>{t.label}</text>
                            </g>
                        ))}
                        {L.labels.map((l, i) => l.shown && (
                            <text key={`x${i}`} x={l.x} y={L.plotBottom + 18} textAnchor={l.anchor} {...axisText}>{l.label}</text>
                        ))}
                        {L.lines.map(line => (
                            <g key={`s${line.color}`}>
                                {line.record && <path d={line.record} fill="none" stroke={color(line.color)} strokeWidth="2.2" {...researchStrokeProps(line.color)} />}
                                {line.estimate && <path d={line.estimate} fill="none" stroke={color(line.color)} strokeOpacity="0.45" strokeWidth="2.2" {...researchStrokeProps(line.color)} />}
                                {line.points.filter(p => p.y != null).map((p, k) => researchMarker(line.color, p.x, p.y, color(line.color), p.estimate ? hatch(line.color) : color(line.color), `p${k}`))}
                            </g>
                        ))}
                    </svg>
                );
            } else if (kind === 'bar') {
                const B = researchBarLayout(series, width);
                body = (
                    <svg viewBox={`0 0 ${B.width} ${B.height}`} width="100%" aria-hidden="true" focusable="false" style={{ display: 'block', height: 'auto' }}>
                        {researchHatches(uid)}
                        {B.hasNeg && <line x1={B.zero} x2={B.zero} y1="0" y2={B.height} stroke="var(--border-input)" strokeWidth="1" />}
                        {B.groups.map((g, i) => (
                            <g key={`c${i}`}>
                                <text x="0" y={g.y} fontSize="11" fontWeight="600" fill="var(--navy)">{g.label}</text>
                                {g.bars.map(bar => (
                                    <g key={`b${bar.series}`}>
                                        <rect x={bar.x} y={bar.y} width={bar.w} height={bar.h} rx="2"
                                            fill={bar.estimate ? hatch(bar.series) : color(bar.series)} stroke={bar.estimate ? color(bar.series) : 'none'} strokeWidth="1" />
                                        <text x={bar.tx} y={bar.ty} textAnchor={bar.anchor} {...axisText} fill="var(--text-body)">{bar.text}</text>
                                    </g>
                                ))}
                            </g>
                        ))}
                    </svg>
                );
            } else if (kind === 'share') {
                const parts = researchShareParts(series[0].points);
                const H = 26;
                body = (
                    <>
                        <svg viewBox={`0 0 ${width} ${H}`} width="100%" aria-hidden="true" focusable="false" style={{ display: 'block', height: 'auto' }}>
                            {researchHatches(uid)}
                            {parts.length > RESEARCH_COLORS.length && researchPartPatterns(uid)}
                            {parts.map((p, i) => {
                                const fill = researchPartFill(i);
                                return p.pct > 0 && (
                                    <rect key={`p${i}`} x={researchRound(p.start / 100 * width)} y="0" width={researchRound(p.pct / 100 * width)} height={H}
                                        fill={p.estimate ? hatch(fill.color) : fill.style === 'solid' ? color(fill.color) : `url(#${uid}-${fill.style}${fill.color})`} stroke="var(--white)" strokeWidth="1.5" />
                                );
                            })}
                        </svg>
                        <ul style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            {parts.map((p, i) => (
                                <li key={`l${i}`} style={{ display: 'flex', alignItems: 'baseline', gap: '8px', fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.4 }}>
                                    <span aria-hidden="true" style={researchPartSwatch(researchPartFill(i), p.estimate)} />
                                    <span style={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>{p.label}</span>
                                    <span style={{ fontWeight: 700, color: 'var(--navy)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                                        {researchValue(p.value, unit || '%')}{p.estimate ? ' · estimate' : ''}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </>
                );
            } else {
                const [head, compare] = series[0].points;
                if (!head || head.y == null) return null;
                body = (
                    <div>
                        <div style={{ fontFamily: 'var(--font-heading)', fontSize: '30px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.15, overflowWrap: 'anywhere' }}>
                            {researchValue(head.y, unit)}
                        </div>
                        <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                            {head.x}{head.estimate ? ' · estimate' : ''}
                        </div>
                        {compare && compare.y != null && (
                            <div style={{ fontSize: '13px', color: 'var(--text-body)', marginTop: '6px' }}>
                                vs {researchValue(compare.y, unit)} · {compare.x}{compare.estimate ? ' · estimate' : ''}
                            </div>
                        )}
                    </div>
                );
            }
            // The table's rows are the chart's categories in the chart's order —
            // a line's in time order — each series' point found as the chart
            // finds it, so a repeated label is a row of its own.
            const cats = kind === 'line' ? researchLineAxis(series).cats : kind === 'bar' ? researchCategories(series) : [];
            const cells = series.map(s => researchAligned(s, cats));
            return (
                <div data-testid="research-figure" data-kind={kind} style={{ ...RUN_CARD, minWidth: 0 }}>
                    {title && (
                        <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.35 }}>
                            {title}<SourceMarks ids={figure.sourceIds} numbers={numbers} />
                        </div>
                    )}
                    {meta && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>{meta}</div>}
                    <div ref={bodyRef} style={{ marginTop: '10px', minWidth: 0 }}>{body}</div>
                    {drawn && (series.length > 1 || estimated) && (
                        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {series.length > 1 && series.map((s, j) => (
                                <li key={`k${j}`} data-testid="research-legend-series" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    {kind === 'line' ? (
                                        // The series as the chart draws it: its stroke and its marker.
                                        <svg aria-hidden="true" focusable="false" width="34" height="10" viewBox="0 0 34 10" style={{ flexShrink: 0, overflow: 'visible' }}>
                                            <line x1="0" x2="34" y1="5" y2="5" stroke={color(j)} strokeWidth="2.2" {...researchStrokeProps(j)} />
                                            {researchMarker(j, 17, 5, color(j), color(j), 'm')}
                                        </svg>
                                    ) : (
                                        <span aria-hidden="true" style={{ display: 'inline-block', width: '12px', height: '10px', borderRadius: '2px', background: color(j) }} />
                                    )}
                                    {s.name}
                                </li>
                            ))}
                            {estimated && (
                                <li style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                    {kind === 'line' && <span aria-hidden="true" style={{ display: 'inline-block', width: '14px', height: '2px', background: 'var(--navy)', opacity: 0.45, flexShrink: 0 }} />}
                                    <span aria-hidden="true" style={RESEARCH_HATCH_SWATCH} />
                                    {kind === 'line' ? 'Hatched point, faded line: estimate' : 'Hatched: estimate'}
                                </li>
                            )}
                        </ul>
                    )}
                    {note && <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{note}</p>}
                    {/* The numbers the picture holds, for a screen reader. The class
                        is on a wrapper: a table grows to fit its cells whatever its
                        width, and nothing clips it — on a phone the hidden table
                        pushed <main> sideways. */}
                    {drawn && (
                        <div className="sr-only">
                            <table data-testid="research-figure-table">
                                <caption>{title}{unit ? ` (${unit})` : ''}</caption>
                                <thead>
                                    <tr>
                                        <th scope="col">{kind === 'line' ? 'When' : 'Category'}</th>
                                        {series.map((s, j) => <th key={`h${j}`} scope="col">{s.name || `Series ${j + 1}`}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {cats.map((c, i) => (
                                        <tr key={`r${i}`}>
                                            <th scope="row">{c}</th>
                                            {series.map((s, j) => {
                                                const p = cells[j][i];
                                                return <td key={`d${j}`}>{p && p.y != null ? `${researchValue(p.y, unit)}${p.estimate ? ' (estimate)' : ''}` : '—'}</td>;
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            );
        };

        // Dated moments: "Up to the decision" before, "What followed" after.
        const ResearchTimeline = ({ items, numbers, title }) => {
            const rows = (items || []).map(t => ({ when: researchDate(t.when), what: researchText(t.what), ids: t.sourceIds })).filter(r => r.what);
            if (!rows.length) return null;
            return (
                <div data-testid="research-timeline" style={{ ...RUN_CARD, minWidth: 0 }}>
                    <div style={RESEARCH_KICKER}>{title}</div>
                    <ol style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}>
                        {rows.map((r, i) => (
                            <li key={i} style={{ position: 'relative', marginLeft: '5px', padding: i === rows.length - 1 ? '0 0 0 16px' : '0 0 12px 16px', borderLeft: `2px solid ${i === rows.length - 1 ? 'transparent' : 'var(--border-card)'}` }}>
                                <span aria-hidden="true" style={{ position: 'absolute', left: '-7px', top: '2px', width: '12px', height: '12px', borderRadius: '50%', background: 'var(--white)', border: '2px solid var(--gold-dark)', boxSizing: 'border-box' }} />
                                {r.when && <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.03em' }}>{r.when}</div>}
                                <div style={{ fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                                    {r.what}<SourceMarks ids={r.ids} numbers={numbers} />
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            );
        };

        // A real excerpt on paper: what it is, verbatim in quotes, who and when.
        const ResearchDocument = ({ doc, numbers }) => {
            const excerpt = researchText(doc && doc.excerpt);
            if (!excerpt) return null;
            const title = researchText(doc.title);
            const byline = [researchText(doc.from), researchDate(doc.date)].filter(Boolean).join(' · ');
            return (
                <figure data-testid="research-document" style={{ margin: 0, minWidth: 0, background: 'var(--cream)', border: '1px solid var(--border-card)', borderLeft: '3px solid var(--gold-dark)', borderRadius: '6px', padding: '14px 16px', boxShadow: 'var(--e1)' }}>
                    <div style={RESEARCH_KICKER}>{researchKindLabel(RESEARCH_DOCUMENT_KINDS, doc.kind, 'Document')}</div>
                    {title && <div style={{ fontFamily: 'var(--font-heading)', fontSize: '15.5px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1.3, marginBottom: '8px' }}>{title}</div>}
                    <blockquote style={{ margin: 0, fontFamily: 'var(--font-heading)', fontSize: '15px', fontStyle: 'italic', lineHeight: 1.6, color: 'var(--navy)', overflowWrap: 'anywhere' }}>
                        “{excerpt}”
                    </blockquote>
                    <figcaption style={{ marginTop: '9px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                        {byline ? `— ${byline}` : ''}<SourceMarks ids={doc.sourceIds} numbers={numbers} />
                    </figcaption>
                </figure>
            );
        };

        // The lines the brief lacks: the market, the competitors, the regulation.
        const ResearchContext = ({ items, numbers }) => {
            const rows = (items || []).map(c => ({ label: researchText(c.label), text: researchText(c.text), ids: c.sourceIds })).filter(r => r.text);
            if (!rows.length) return null;
            return (
                <div data-testid="research-context" style={{ ...RUN_CARD, minWidth: 0 }}>
                    <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {rows.map((r, i) => (
                            <div key={i}>
                                {r.label && <dt style={{ ...RESEARCH_KICKER, marginBottom: '2px' }}>{r.label}</dt>}
                                <dd style={{ margin: 0, fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.55, overflowWrap: 'anywhere' }}>
                                    {r.text}<SourceMarks ids={r.ids} numbers={numbers} />
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            );
        };

        // An org chart, a map or a diagram Cowork drew — served by our API only,
        // and gone quietly if it does not load.
        const ResearchGraphic = ({ graphic, numbers, scenarioId }) => {
            const [failed, setFailed] = useState(false);
            const src = graphic ? researchImageSrc(graphic.key, scenarioId) : null;
            const alt = researchText(graphic && graphic.alt);
            if (!src || !alt || failed) return null;
            const caption = researchText(graphic.caption);
            return (
                <figure data-testid="research-graphic" style={{ ...RUN_CARD, margin: 0, minWidth: 0 }}>
                    <div style={RESEARCH_KICKER}>{researchKindLabel(RESEARCH_GRAPHIC_KINDS, graphic.kind, 'Graphic')}</div>
                    <img src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)}
                        style={{ display: 'block', width: '100%', maxWidth: '100%', height: 'auto', borderRadius: '8px' }} />
                    <figcaption style={{ marginTop: '8px', fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.45 }}>
                        {caption}<SourceMarks ids={graphic.sourceIds} numbers={numbers} />
                    </figcaption>
                </figure>
            );
        };

        /**
         * The part's sources, behind a disclosure, numbered as the marks are.
         * Until the call is locked in (`revealed`), and for as long as the worker
         * still sends a source redacted (the refetch after the lock-in has not
         * landed), a source is "Source 1 · News · 1987" and never a link: a
         * title, publisher, URL or the line it supports can name the company, and
         * the case is anonymous until the reveal. One line under the list says
         * when they open. After it, each is a link — https only, a new tab, no
         * opener, no referrer — with where in the source to look and the line it
         * backs.
         */
        const SourcesList = ({ list, revealed, id, closedLine = RESEARCH_SOURCES_CLOSED }) => {
            const [open, setOpen] = useState(false);
            if (!list || !list.length) return null;
            const line = { fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.5, overflowWrap: 'anywhere' };
            const anyRedacted = list.some(s => researchSourceRedacted(s, revealed));
            return (
                <div data-testid="research-sources" style={{ ...RUN_CARD, padding: 0, minWidth: 0 }}>
                    <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls={id} data-testid="research-sources-toggle"
                        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', background: 'transparent', border: 'none', padding: '12px 16px', fontSize: '13.5px', fontWeight: 600, color: 'var(--navy)', cursor: 'pointer', textAlign: 'left', minHeight: '44px', opacity: 1 }}>
                        <span>Sources ({list.length})</span>
                        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
                    </button>
                    {open && (
                        <div id={id} style={{ padding: '0 16px 14px' }}>
                            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {list.map((s, i) => {
                                    if (researchSourceRedacted(s, revealed)) {
                                        return <li key={s.id} data-testid="research-source" style={line}>{researchSourceLabel(s, i + 1)}</li>;
                                    }
                                    const kind = researchKindLabel(RESEARCH_SOURCE_KINDS, s.kind, 'Source');
                                    const published = researchDate(s.published);
                                    const n = <span style={{ fontWeight: 700, color: 'var(--navy)', marginRight: '6px', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>;
                                    const title = researchOwnText(s, s.title) || kind;
                                    const url = researchSafeUrl(s.url);
                                    const meta = [kind, researchOwnText(s, s.publisher), researchText(s.author), published, researchText(s.locator)].filter(Boolean).join(' · ');
                                    const supports = researchText(s.supports);
                                    return (
                                        <li key={s.id} data-testid="research-source" style={{ fontSize: '13px', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                                            {n}
                                            {url ? (
                                                <a href={url} target="_blank" rel="noopener noreferrer" className="ed-link" style={{ fontWeight: 600 }}>
                                                    {title}<span className="sr-only"> (opens in a new tab)</span>
                                                </a>
                                            ) : (
                                                <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{title}</span>
                                            )}
                                            {meta && <div style={line}>{meta}</div>}
                                            {supports && <div style={{ ...line, fontStyle: 'italic' }}>“{supports}”</div>}
                                        </li>
                                    );
                                })}
                            </ol>
                            {anyRedacted && closedLine && (
                                <p data-testid="research-sources-redacted" style={{ ...line, margin: '10px 0 0' }}>{closedLine}</p>
                            )}
                        </div>
                    )}
                </div>
            );
        };

        /**
         * One part of a case's research. `before`, under the case file while
         * deciding: the charts, the timeline up to the decision, the documents,
         * the context, any graphic. `after`, under the CEO card once the call is
         * in: "What happened next" — the outcome, then its charts, timeline,
         * documents and graphics. Each part numbers its own sources.
         * `closedLine`: what the sources say while any is still closed — by
         * default that they open after the decision; once the call is in, why
         * they have not (SimulatorView's sourcesClosedLine).
         */
        const ResearchPart = ({ research, which, scenarioId, revealed, closedLine }) => {
            const part = research ? research[which] : null;
            if (!researchHasContent(part)) return null;
            const after = which === 'after';
            const { list, numbers } = researchPartSources(research.sources, part);
            const labelId = `ed-research-${which}`;
            const outcome = after && part.outcome ? researchText(part.outcome.text) : '';
            return (
                <section data-testid={`research-${which}`} aria-labelledby={labelId}
                    style={{ marginTop: after ? '16px' : '12px', display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
                    {after ? (
                        <div>
                            <h3 id={labelId} style={{ fontFamily: 'var(--font-heading)', fontSize: '19px', lineHeight: 1.3, color: 'var(--navy)', fontWeight: 600, margin: '0 0 3px' }}>What happened next</h3>
                            <div style={RESEARCH_SMALL_LABEL}>{RESEARCH_LABEL}</div>
                        </div>
                    ) : (
                        <div id={labelId} style={{ ...RESEARCH_SMALL_LABEL, marginTop: '4px' }}>{RESEARCH_LABEL}</div>
                    )}
                    {outcome && (
                        <div data-testid="research-outcome" style={{ ...RUN_CARD, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.65, overflowWrap: 'anywhere' }}>
                                {outcome}<SourceMarks ids={part.outcome.sourceIds} numbers={numbers} />
                            </p>
                        </div>
                    )}
                    {part.figures.map((f, i) => <ResearchFigure key={`f${i}`} figure={f} numbers={numbers} />)}
                    <ResearchTimeline items={part.timeline} numbers={numbers} title={after ? 'What followed' : 'Up to the decision'} />
                    {part.documents.map((d, i) => <ResearchDocument key={`d${i}`} doc={d} numbers={numbers} />)}
                    {!after && <ResearchContext items={part.context} numbers={numbers} />}
                    {part.graphics.map((g, i) => <ResearchGraphic key={`g${i}`} graphic={g} numbers={numbers} scenarioId={scenarioId} />)}
                    <SourcesList list={list} revealed={revealed} id={`${labelId}-sources`} closedLine={closedLine} />
                </section>
            );
        };

        // ============================================
        // DECISION SIMULATOR VIEW
        // ============================================
        const SimulatorView = () => {
            const { database, powerIndex, setPowerIndex, streak, setStreak, showToast, addCompletedSimulation, completedSimulations, setCurrentView, authToken, requestFeedback,
                world, decisionProfile, refreshDecisionProfile, simLaunch, setSimLaunch,
                mentorEnabled, currentUser, isDesktop, setCeoSeed, savedNextMoves, addSavedNextMove, setWorldSheetOpen, setSimulatorRun, setPracticePhase, tabIntro } = useApp();
            const [phase, setPhase] = useState('intro'); // intro, situation, decision, reveal, lesson, handoff
            // Molly's first line for the handoff, fixed when the case completes so
            // the embedded view reads one stable object on mount.
            const [handoffOpening, setHandoffOpening] = useState(null);
            // sim_handoff(to=mentor) once per run — on the first answer to Molly,
            // or on continuing into a full session, whichever comes first.
            const handoffFiredRef = useRef(false);
            // The case's feedback prompt, built at completion and asked on the
            // way OUT of the handoff (see askPendingFeedback).
            const pendingFeedbackRef = useRef(null);
            // What happened in the handoff: how many times Molly was answered in
            // the case, and whether the handoff is still open (so leaving it is
            // recorded once, whichever way out is taken).
            const mollyTurnsRef = useRef(0);
            const handoffOpenRef = useRef(false);
            const [currentScenario, setCurrentScenario] = useState(null);
            const [selectedOption, setSelectedOption] = useState(null);
            const [timer, setTimer] = useState(SIM_CLOCK_SECONDS);
            const [timerTotal, setTimerTotal] = useState(SIM_CLOCK_SECONDS);
            const [paused, setPaused] = useState(false);
            const [score, setScore] = useState(null);
            const [powerIndexChange, setPowerIndexChange] = useState(0);
            // True when this attempt does not move the index: the case has been
            // worked before. The server has always answered `counted: false`
            // for these; the reveal simply never said so.
            const [wasReplay, setWasReplay] = useState(false);
            const [transcriptOpen, setTranscriptOpen] = useState(false);
            const [showFullInterview, setShowFullInterview] = useState(false);
            const [excerptPlaying, setExcerptPlaying] = useState(false);
            const [excerptDuration, setExcerptDuration] = useState(null); // seconds, once metadata loads
            const [excerptTime, setExcerptTime] = useState(0);            // playback position
            const excerptRef = useRef(null);
            const timerRef = useRef(null);
            // When the case was opened. elapsedMs runs from here to the lock-in,
            // less the time the clock was paused (§4).
            const decisionStartRef = useRef(null);
            const pausedAtRef = useRef(null);
            const pausedMsRef = useRef(0);

            // ---- the one screen (§7.4) --------------------------------------
            // The run wrapper is keyed by its runId, so a new case is a fresh
            // mount (and the only time the page-in animation plays).
            const [runKey, setRunKey] = useState(null);
            const [confidence, setConfidence] = useState(null);
            // The attempt row POST /api/simulations created: 'none' before the
            // lock-in, 'pending', 'saved' (id null on a worker behind 0027),
            // 'failed', or 'readonly' (an admin viewing as the member).
            const [attempt, setAttempt] = useState({ status: 'none', id: null });
            const [choices, setChoices] = useState(null);
            // The case's research, once it has loaded (§2 of RESEARCH_PACKAGE):
            // `after` arrives with the refetch once this attempt is on record.
            const [research, setResearch] = useState(null);
            // The refetch after the lock-in: 'idle' until it is asked, 'loading',
            // then 'done' whatever came back — so sources still closed after it
            // say they could not be opened rather than that they are opening.
            const [researchReveal, setResearchReveal] = useState('idle');
            const [secondChoice, setSecondChoice] = useState(null);
            const [secondStatus, setSecondStatus] = useState('idle');
            const [noteText, setNoteText] = useState('');
            const [noteSaved, setNoteSaved] = useState(null);
            const [noteStatus, setNoteStatus] = useState('idle');
            const noteSentRef = useRef(null);
            // A turn taken with Molly in the case, for the rail (the ref below
            // is bookkeeping and does not re-render).
            const [mollyTalked, setMollyTalked] = useState(false);
            const [detailsOpen, setDetailsOpen] = useState(false);
            const [pickerOpen, setPickerOpen] = useState(false);
            // Stable, because it is the picker's onEscape: ModalPortal re-runs its
            // focus effect when that changes, and every App re-render (the
            // notification poll, a toast) would throw focus back to Close.
            const closePicker = useCallback(() => setPickerOpen(false), []);
            // One column: the options or the lock-in row on screen. The sticky
            // clock bar steps aside then — it sits over the lock-in buttons.
            const [callInView, setCallInView] = useState(false);
            // Case file beside the decision — only when the run is measured
            // wide enough to hold both, not whenever the window is "desktop".
            const [twoCol, setTwoCol] = useState(false);
            const runWrapRef = useRef(null);
            const briefRef = useRef(null);
            const decideHeadingRef = useRef(null);
            const optionsRef = useRef(null);
            const lockRowRef = useRef(null);
            const ceoHeadingRef = useRef(null);
            const takeawayHeadingRef = useRef(null);
            const takeawayEndRef = useRef(null);
            const mollyHeadingRef = useRef(null);
            // The latest render's handlers, for observers created renders ago.
            const latestRef = useRef({});

            // ---- usage telemetry for the run ---------------------------------
            // One record per simulation run, in a ref: it is bookkeeping, not
            // something to render. Every sim_* and audio_* event carries its runId,
            // so the console can ask "on THIS case, did they play the clip?" as a
            // GROUP BY rather than a guess from timestamps. docs/EVENTS_CONTRACT.md
            // is the list of events and what each prop means.
            const runRef = useRef(null);
            const runMeta = (r) => ({ runId: r.runId, entityType: 'scenario', entityId: r.scenarioId });
            const stepsSum = (r) => Object.values(r.steps).reduce((a, b) => a + b, 0);
            // Close the step the run is on and record how long it was open. Called
            // at each transition site — the only place the exit reason is known.
            const leaveStep = (exit) => {
                const r = runRef.current;
                if (!r || !r.step) return;
                const durationMs = Date.now() - r.stepStartedAt;
                r.steps[r.step] = (r.steps[r.step] || 0) + durationMs;
                Telemetry.track('sim_step', { step: r.step, durationMs, exit }, runMeta(r));
                r.step = null;
            };
            // The clips are summarised when the run is LEFT — an exit pressed, a
            // run abandoned, a new run started, this view going, the page going —
            // and not when the CEO section is scrolled past or the bottom of the
            // Takeaway completes the case: the clip keeps playing, and keeps
            // being measured, while the member reads on below it. A summary sent
            // early would cut listenedMs short, and the clip's audio_80 could
            // still follow it.
            // Silent for a run that never pressed play — that silence is how the
            // console sees a skipped clip.
            const finishAudio = () => {
                const r = runRef.current;
                if (!r) return;
                r.trackers.short.finish();
                r.trackers.full.finish();
            };
            // The run ended without a completion — another was started, the user
            // tapped a different tab (this view unmounts), the page was left. In
            // this variant that is the common way out. Idempotent, and a completed
            // run is left alone.
            const abandonRun = () => {
                const r = runRef.current;
                if (!r || r.completed || r.abandoned) return;
                const step = r.step;
                const durationMs = step ? Date.now() - r.stepStartedAt : 0;
                leaveStep('abandon');
                finishAudio();
                r.abandoned = true;
                Telemetry.track('sim_abandoned', { step, durationMs, totalMs: stepsSum(r) }, runMeta(r));
            };
            const beginRun = (scenario, source, replay) => {
                abandonRun();
                setWasReplay(false);
                // abandonRun leaves a COMPLETED run alone, clips included, and a
                // new case can start from one without an exit being pressed (a
                // launch from elsewhere, the ?id= link). Idempotent.
                finishAudio();
                const runId = uuid();
                runRef.current = {
                    runId, scenarioId: scenario.id, source, startedAt: Date.now(),
                    step: null, stepStartedAt: 0, steps: {},
                    completed: false, abandoned: false,
                    trackers: {
                        short: makeListenTracker({ kind: 'short', source: 'reveal', runId, entityId: scenario.id, clip: { key: scenario.audioFile, ceo: scenario.ceo } }),
                        full: makeListenTracker({ kind: 'full', source: 'reveal', runId, entityId: scenario.id, clip: { key: scenario.audioFileFull || scenario.audioFile, ceo: scenario.ceo } })
                    }
                };
                Telemetry.track('sim_started', {
                    scenarioId: scenario.id,
                    scenarioType: isSpotBad(scenario) ? 'spotBad' : 'match',
                    source,
                    replay: !!replay,
                    // Their Nth run, lifetime: completed simulations come from the
                    // synced profile, so history from before telemetry existed
                    // counts. Makes the learning curve a plain GROUP BY.
                    runIndex: (completedSimulations || []).length + 1
                }, runMeta(runRef.current));
            };
            // Is this still the run a request was made for? A member can be on
            // the next case before a slow answer about the last one lands.
            const isRun = (runId) => !!runRef.current && runRef.current.runId === runId;

            // A case with no options can't be decided — never serve one, or the
            // decision phase becomes a dead end the user can't get past.
            const scenarios = database.decisionSimulator.scenarios.filter(
                s => s.active !== false && Array.isArray(s.options) && s.options.length > 0
            );

            // Next case: shared deterministic selector (module-scope pickPracticeCase)
            // so the Home hero and the Simulator always agree on today's case.
            const pickToday = () => pickPracticeCase(scenarios, completedSimulations?.map(s => s.id) || [], world, practiceOptsOf(decisionProfile));
            const pickNextScenario = (specificId = null) => {
                if (specificId) {
                    const specific = scenarios.find(s => s.id === parseInt(specificId));
                    if (specific) return specific;
                }
                return pickToday().scenario;
            };

            // The case previewed on the landing. Derived every render (the
            // selector is deterministic) so it can't disagree with Home or replay a
            // case that the profile hydrate has since marked completed.
            // `previewPick` carries why it was picked, for whyLineFor.
            const previewPick = pickToday();
            const previewScenario = previewPick.scenario;
            // Once every case is played, pickPracticeCase rotates back through the
            // library — so the "next" case can be one already completed.
            const nextIsReplay = !!previewScenario &&
                (completedSimulations || []).some(s => s.id === previewScenario.id);

            // `source` is how the case was reached — today's card, a deep link,
            // the next-case exit, the picker, Home or the journal — so the console
            // can see which entry points actually get used.
            const startSimulation = (specificId = null, source = 'today') => {
                const next = specificId ? pickNextScenario(specificId) : (previewScenario || pickNextScenario());
                if (!next) return;
                beginRun(next, source, (completedSimulations || []).some(s => s.id === next.id));
                setRunKey(runRef.current.runId);
                setSimulatorRun(true);
                setPickerOpen(false);
                setCurrentScenario(next);
                setPhase('situation');
                setTimer(SIM_CLOCK_SECONDS);
                setTimerTotal(SIM_CLOCK_SECONDS);
                setPaused(false);
                decisionStartRef.current = Date.now();
                pausedAtRef.current = null;
                pausedMsRef.current = 0;
                setSelectedOption(null);
                setScore(null);
                setPowerIndexChange(0);
                setTranscriptOpen(false);
                setShowFullInterview(false);
                setExcerptPlaying(false);
                setExcerptDuration(null);
                setExcerptTime(0);
                setConfidence(null);
                setAttempt({ status: 'none', id: null });
                setChoices(null);
                setResearch(null);
                setResearchReveal('idle');
                setSecondChoice(null);
                setSecondStatus('idle');
                setNoteText('');
                setNoteSaved(null);
                setNoteStatus('idle');
                noteSentRef.current = null;
                setMollyTalked(false);
                setDetailsOpen(false);
                if (next.hasResearch) loadResearch(next.id, runRef.current.runId);
            };

            // Choosing an option ends the reading (`situation`); the page does
            // not change and the clock keeps running to the lock-in.
            const selectOption = (id) => {
                if (phase !== 'situation' && phase !== 'decision') return;
                setSelectedOption(id);
                if (phase === 'situation') { leaveStep('forward'); setPhase('decision'); }
            };

            const togglePause = () => {
                if (paused) {
                    if (pausedAtRef.current) pausedMsRef.current += Date.now() - pausedAtRef.current;
                    pausedAtRef.current = null;
                } else {
                    pausedAtRef.current = Date.now();
                }
                setPaused(!paused);
            };
            const extendClock = () => { setTimer(t => t + 30); setTimerTotal(t => t + 30); };

            // The tab's introduction (TabIntroModal) covers the case the first
            // time the member arrives, and a case opened from Home arrives with
            // its clock already running. So the clock waits for it: paused the
            // same way the Pause button pauses it — the pause is taken out of
            // elapsedMs too — and resumed when the dialog goes. Only the pause
            // this took is given back; a member who paused by hand keeps theirs.
            const introPausedRef = useRef(false);
            useEffect(() => {
                const running = phase === 'situation' || phase === 'decision';
                if (tabIntro === 'simulator') {
                    if (running && !paused) {
                        introPausedRef.current = true;
                        pausedAtRef.current = Date.now();
                        setPaused(true);
                    }
                } else if (introPausedRef.current) {
                    introPausedRef.current = false;
                    if (paused) {
                        if (pausedAtRef.current) pausedMsRef.current += Date.now() - pausedAtRef.current;
                        pausedAtRef.current = null;
                        setPaused(false);
                    }
                }
            }, [tabIntro, phase, paused]);

            // How other members chose (§4) — asked for once this member's call is
            // in, because the worker refuses anyone who has not worked the case.
            // A refusal or a failure shows nothing.
            const loadChoices = (scenarioId, runId) => {
                if (!API_URL || !authToken) return;
                fetch(`${API_URL}/api/scenarios/${scenarioId}/choices`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => (r.ok ? r.json() : null))
                    .then(body => {
                        const data = body && body.success && body.data ? body.data : body;
                        if (data && typeof data.total === 'number' && isRun(runId)) setChoices(data);
                    })
                    .catch(() => { /* nothing to show */ });
            };

            // The case's research — only for a case marked `hasResearch`, once
            // when it opens and once more when the attempt is on record, because
            // the worker sends `after` only to a member who has decided. Loading
            // and failure are silent: the case works without it. A slower answer
            // never takes `after` back off the page. `reveal`: this is the
            // refetch after the lock-in, whose end researchReveal records.
            const loadResearch = (scenarioId, runId, reveal = false) => {
                if (!API_URL || !authToken) return;
                const settle = () => { if (reveal && isRun(runId)) setResearchReveal('done'); };
                if (reveal) setResearchReveal('loading');
                fetch(`${API_URL}/api/scenarios/${scenarioId}/research`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => (r.ok ? r.json() : null))
                    .then(body => {
                        const view = researchViewOf(body && body.success && body.data ? body.data : body);
                        if (view && isRun(runId)) setResearch(prev => (prev && prev.after && !view.after ? prev : view));
                        settle();
                    })
                    .catch(() => { /* the case as it always was */ settle(); });
            };

            // `level`: how sure the member said they were — the lock-in button.
            const submitDecision = (level) => {
                if (!selectedOption || phase !== 'decision') return;
                if (timerRef.current) clearInterval(timerRef.current);
                const sure = CONFIDENCE_LEVELS.some(([l]) => l === level) ? level : null;

                const correct = selectedOption === currentScenario.actualChoice;
                // Deterministic score (no Math.random): correctness + decisiveness, where a
                // faster decision scores higher. Same choice + same timing => same score.
                // The time is the case's: opened → locked in, less any pause.
                const pausedMs = pausedMsRef.current + (pausedAtRef.current ? Date.now() - pausedAtRef.current : 0);
                const elapsed = decisionStartRef.current ? Math.max(0, Date.now() - decisionStartRef.current - pausedMs) / 1000 : 0;
                const decisiveness = Math.max(0, Math.min(1, 1 - elapsed / 30));
                const newScore = correct
                    ? Math.round(85 + 15 * decisiveness)
                    : 55;
                setScore(newScore);
                setConfidence(sure);

                {
                    const r = runRef.current;
                    if (r) {
                        Telemetry.track('sim_decision_locked', {
                            choice: selectedOption,
                            correct,
                            decisionMs: r.step === 'decision' ? Date.now() - r.stepStartedAt : Math.round(elapsed * 1000),
                            clockRemaining: null   // B's run clock; this variant's clock is soft and never ends a step
                        }, runMeta(r));
                    }
                    leaveStep('forward');
                    if (r) Telemetry.track('sim_confidence', { level: sure }, runMeta(r));
                }

                // A case already worked earns nothing — the server says so, and
                // the app knows it before the reveal renders. Guessing +3 here
                // and being corrected a second later was the visible half of
                // the same bug.
                const alreadyWorked = (completedSimulations || []).some(s => s && s.id === currentScenario.id);
                const change = alreadyWorked ? 0 : (correct ? 3 : -1);
                setPowerIndexChange(change);
                setWasReplay(alreadyWorked);

                addCompletedSimulation({
                    id: currentScenario.id,
                    score: newScore,
                    correct: correct,
                    pointsEarned: change,
                    date: new Date().toISOString()
                });

                if (!alreadyWorked) {
                    if (correct) setPowerIndex(prev => Math.min(100, prev + 3));
                    else setPowerIndex(prev => Math.max(0, prev - 1));
                }
                // NO optimistic streak bump. A streak is consecutive DAYS, and
                // this fires once per DECISION — four cases in an afternoon and
                // the modal read "4 consecutive days. Strong momentum — habits
                // form in the second week." The server owns it (migration 0017)
                // and the reconcile below applies the real value.
                setPhase('reveal');

                // The numbers above are an optimistic local guess so the reveal is
                // instant and the app still works offline. The server is the real
                // authority (it owns the answer key and the running index, and it
                // refuses to score the same case twice) — reconcile with it.
                if (API_URL && authToken) {
                    const runId = runRef.current ? runRef.current.runId : null;
                    const scenarioId = currentScenario.id;
                    const researched = !!currentScenario.hasResearch;
                    setAttempt({ status: 'pending', id: null });
                    fetch(`${API_URL}/api/simulations`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({
                            scenarioId,
                            choice: selectedOption,
                            elapsedMs: Math.round(elapsed * 1000),
                            confidence: sure,
                            runId
                        })
                    })
                        .then(r => r.json().catch(() => null).then(res => ({ status: r.status, res })))
                        .then(({ status, res }) => {
                            if (!res || !res.success || !res.data) {
                                // 403 is the impersonation gate: every write is refused.
                                if (isRun(runId)) setAttempt({ status: status === 403 ? 'readonly' : 'failed', id: null });
                                return;
                            }
                            setPowerIndex(res.data.powerIndex);
                            setStreak(res.data.streak);
                            if (isRun(runId)) {
                                setPowerIndexChange(res.data.pointsEarned);
                                setScore(res.data.score);
                                setAttempt({ status: 'saved', id: res.data.attemptId == null ? null : Number(res.data.attemptId) });
                                // `counted` was read off this response and thrown
                                // away. It is the server's answer to "does this
                                // attempt move anything", and it is the only
                                // authority on it — the client's guess above is
                                // based on local history that may be incomplete.
                                if (res.data.counted === false) setWasReplay(true);
                            }
                            // The attempt is in the log: today and the record read it there.
                            refreshDecisionProfile();
                            // …and the worker now sends what happened next.
                            if (researched && isRun(runId)) loadResearch(scenarioId, runId, true);
                        })
                        .catch(() => { if (isRun(runId)) setAttempt({ status: 'failed', id: null }); })
                        .then(() => { if (isRun(runId)) loadChoices(scenarioId, runId); });
                } else {
                    setAttempt({ status: 'failed', id: null });
                }
            };

            // PUT /api/simulations/:attemptId/reflection → { status, res }. The
            // token is passed in: the unmount path's closure is the first render's.
            const putReflection = (attemptId, body, token) =>
                fetch(`${API_URL}/api/simulations/${attemptId}/reflection`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify(body)
                }).then(r => r.json().catch(() => null).then(res => ({ status: r.status, res })));

            // "Knowing what the CEO did, would you still choose B?" — kept in the
            // member's journal; the score never moves.
            const chooseSecondThought = (id) => {
                const scenario = currentScenario;
                if (!scenario || attempt.id == null || !API_URL || !authToken || secondStatus === 'saving') return;
                const runId = runRef.current ? runRef.current.runId : null;
                const meta = runRef.current ? runMeta(runRef.current) : undefined;
                const original = selectedOption;
                setSecondChoice(id);
                setSecondStatus('saving');
                putReflection(attempt.id, { secondChoice: id }, authToken)
                    .then(({ status, res }) => {
                        if (!res || !res.success) {
                            if (isRun(runId)) setSecondStatus(status === 403 ? 'readonly' : 'error');
                            return;
                        }
                        Telemetry.track('sim_reflected', { kind: 'second', changed: id !== original, towardCeo: id === scenario.actualChoice }, meta);
                        if (isRun(runId)) setSecondStatus('saved');
                        refreshDecisionProfile();
                    })
                    .catch(() => { if (isRun(runId)) setSecondStatus('error'); });
            };

            // The note, as a snapshot of what is on screen — so the exits and the
            // unmount path send the same thing. The text never rides on an event.
            const noteDraft = () => {
                const runId = runRef.current ? runRef.current.runId : null;
                return {
                    attemptId: attempt.id, text: noteText, saved: noteSaved, token: authToken,
                    meta: runRef.current ? runMeta(runRef.current) : undefined,
                    refresh: refreshDecisionProfile,
                    settle: (status, note) => {
                        if (!isRun(runId)) return;
                        setNoteStatus(status);
                        if (status === 'saved') setNoteSaved(note);
                    }
                };
            };
            const sendNote = (draft) => {
                if (!draft || draft.attemptId == null || !API_URL || !draft.token) return false;
                const note = String(draft.text || '').trim().slice(0, REFLECTION_NOTE_MAX);
                if (note === (draft.saved || '') || note === noteSentRef.current) return false;
                noteSentRef.current = note;
                const settle = draft.settle || (() => {});
                putReflection(draft.attemptId, { note: note || null }, draft.token)
                    .then(({ status, res }) => {
                        if (!res || !res.success) {
                            noteSentRef.current = null;
                            settle(status === 403 ? 'readonly' : 'error', null);
                            return;
                        }
                        if (note) Telemetry.track('sim_reflected', { kind: 'note', changed: null, towardCeo: null }, draft.meta);
                        settle('saved', note);
                        if (draft.refresh) draft.refresh();
                    })
                    .catch(() => { noteSentRef.current = null; settle('error', null); });
                return true;
            };
            const saveNote = () => { if (sendNote(noteDraft())) setNoteStatus('saving'); };
            const noteDraftRef = useRef(null);
            noteDraftRef.current = { ...noteDraft(), settle: null };

            // What the member did after the lock-in, for the case's feedback row
            // (§7.4): how sure they said they were, whether a second thought kept
            // or switched the call, whether a note was written — never the note.
            // Merged into the pending prompt whenever it changes, because the
            // prompt can be asked by the unmount path, which reads only refs.
            const reflectionRef = useRef(null);
            reflectionRef.current = {
                confidence,
                secondThought: secondStatus === 'saved' && secondChoice ? (secondChoice === selectedOption ? 'kept' : 'switched') : null,
                noted: !!noteSaved || (attempt.id != null && !!noteText.trim())
            };
            const stampFeedbackExtra = () => {
                const ctx = pendingFeedbackRef.current;
                if (!ctx) return;
                pendingFeedbackRef.current = { ...ctx, extra: { ...(ctx.extra || {}), ...reflectionRef.current } };
            };

            const finishSimulation = () => {
                setPhase('intro');
                setCurrentScenario(null);
            };

            // End of the case. The handoff that follows is the start of a different
            // journey, so this is the last moment the answers are still about THIS
            // scenario — which is exactly what the feedback records. It is BUILT
            // here and ASKED when the handoff is left: Molly's question about the
            // call is the moment, and a ratings dialog opening on top of it was
            // the first thing a new member saw after their first case.
            const completeSimulation = () => {
                leaveStep('forward');
                {
                    const r = runRef.current;
                    if (r && !r.completed) {
                        r.completed = true;
                        Telemetry.track('sim_completed', {
                            correct: !!(currentScenario && selectedOption === currentScenario.actualChoice),
                            score,
                            totalMs: stepsSum(r),
                            steps: { ...r.steps }
                        }, runMeta(r));
                    }
                }
                setHandoffOpening(mollyOpening(currentScenario, selectedOption));
                handoffFiredRef.current = false;
                mollyTurnsRef.current = 0;
                handoffOpenRef.current = true;
                setPhase('handoff');
                if (!currentScenario) return;
                // The run id rides along twice: on the context (so the feedback_*
                // events carry run_id and the console can join a rating to the
                // run that earned it) and inside `extra` (so the feedback row
                // itself knows its run). Contract: docs/PULSE.md.
                const feedbackRunId = runRef.current ? runRef.current.runId : null;
                // This completion is COUNTED now (1st, 4th, 7th…), so closing the
                // tab during the handoff cannot un-count it; the prompt itself is
                // built only when it is due and asked on the way out.
                pendingFeedbackRef.current = !bumpFeedbackCounter('simulator') ? null : {
                    type: 'scenario',
                    id: currentScenario.id,
                    label: feedbackLabel(currentScenario.title),
                    runId: feedbackRunId,
                    extra: {
                        view: 'simulator',
                        ceo: currentScenario.ceo || null,
                        company: currentScenario.company || null,
                        scenarioType: isSpotBad(currentScenario) ? 'spotBad' : 'match',
                        choice: selectedOption,
                        correct: selectedOption === currentScenario.actualChoice,
                        score,
                        runId: feedbackRunId
                    }
                };
            };

            // Every exit from the handoff — on to Molly's page, the next case,
            // home, or any other tab — asks the case's feedback once, and the
            // response records how the handoff went: the way out, and whether
            // (and how much) Molly was answered in the case. The completion was
            // counted at completeSimulation; only the session rule applies here.
            const askPendingFeedback = (exit) => {
                const ctx = pendingFeedbackRef.current;
                if (!ctx) return;
                pendingFeedbackRef.current = null;
                const turns = mollyTurnsRef.current;
                requestFeedback('simulator', {
                    ...ctx,
                    extra: { ...(ctx.extra || {}), exit: exit || null, mollyAnswered: turns > 0, mollyTurns: turns }
                }, { counted: true });
            };

            // The handoff is left once per run, whichever way: on to Molly's page
            // (`mentor`), the next case, home, `ceo` (Ask a real CEO), or `away` —
            // another tab, or the page itself closing. sim_handoff_left is the
            // measure of the handoff as a whole, independent of the feedback
            // cadence: was Molly answered in the case, and how much was said. On
            // pagehide nothing is asked — a prompt cannot be seen on a page that
            // is going.
            const leaveHandoff = (exit, { ask = true } = {}) => {
                if (!handoffOpenRef.current) return;
                handoffOpenRef.current = false;
                const turns = mollyTurnsRef.current;
                if (runRef.current) Telemetry.track('sim_handoff_left', { exit, answered: turns > 0, turns }, runMeta(runRef.current));
                if (ask) askPendingFeedback(exit);
            };

            // Each answer to Molly inside the case, counted (CIMentorView reports
            // the running total).
            const noteMollyTurn = (n) => { mollyTurnsRef.current = n; if (n > 0) setMollyTalked(true); };

            /**
             * Molly's first line after a case: a question about the decision
             * just made. Not a greeting, not a form asking for a different
             * problem. Composed here, deterministically — the signature moment
             * must not wait seven seconds on a model — and the worker sees it
             * as the assistant's first turn, grounded on the case by id
             * (docs/MOLLY_GROUNDING.md), so her second line follows from it.
             */
            const mollyOpening = (scenario, chosenId) => {
                if (!scenario) return null;
                const who = lastName(scenario.ceo);
                const label = (id) => {
                    const o = (scenario.options || []).find(x => x.id === id);
                    return o ? String(o.text || '').split(':')[0].trim() : null;
                };
                const mine = label(chosenId), theirs = label(scenario.actualChoice);
                const matched = !!chosenId && chosenId === scenario.actualChoice;
                let text;
                if (isSpotBad(scenario)) {
                    text = matched
                        ? `You spotted the call that went wrong${mine ? ` — "${mine}"` : ''}. Most people don't. What gave it away — and where in your own business might the same move be sitting unnoticed?`
                        : `The one that went wrong was${theirs ? ` "${theirs}"` : ' a different option'}${mine ? `; you picked "${mine}"` : ''}. No score for this — it is the more useful outcome. What made the bad call look reasonable to you?`;
                } else {
                    text = matched
                        ? `You made the same call ${who} did${mine ? ` — "${mine}"` : ''}. Before we agree with ourselves: what would have had to be true for that to be the wrong move?`
                        : `You went with${mine ? ` "${mine}"` : ' a different option'}; ${who} went with${theirs ? ` "${theirs}"` : ' another'}. That is not a scorecard — it is a difference in what each of you was protecting. What were you protecting?`;
                }
                return { text, scenarioId: scenario.id, choice: chosenId || null };
            };

            // `via`: 'answer' = they answered Molly's question in the case;
            // 'continue' = they went to her page without answering first.
            const markHandoffTaken = (via) => {
                if (handoffFiredRef.current) return;
                handoffFiredRef.current = true;
                if (runRef.current) Telemetry.track('sim_handoff', { to: 'mentor', via }, runMeta(runRef.current));
            };

            // The exchange started here can carry on in Molly's own page: the
            // thread is app state, so the page resumes it rather than greeting.
            const handleContinueWithMolly = () => {
                markHandoffTaken('continue');
                leaveHandoff('mentor');
                finishSimulation();
                setCurrentView('ci-mentor');
            };

            // "Not today". sim_handoff is once per run: `home` means the handoff
            // was NOT taken, so it is not sent when Molly was already answered —
            // sim_handoff_left (exit:'home') still records the way out.
            const handleSkipHandoff = () => {
                if (runRef.current && !handoffFiredRef.current) Telemetry.track('sim_handoff', { to: 'home', via: 'skip' }, runMeta(runRef.current));
                leaveHandoff('home');
                finishSimulation();
                setCurrentView('home');
            };

            // Straight into the next case without bouncing through home.
            // startSimulation resets every phase-local piece of state, so this
            // is a clean restart rather than a continuation.
            const handleNextCase = () => {
                if (runRef.current) Telemetry.track('sim_next', { from: 'handoff' }, runMeta(runRef.current));
                leaveHandoff('next');
                startSimulation(null, 'next');
                window.scrollTo({ top: 0, behavior: 'auto' });
            };

            // "Done for today". With Molly on it is the skip it always was. With
            // her off there was no handoff offered, so there is no sim_handoff to
            // send — the way out is still recorded and the feedback still asked.
            const handleDoneForToday = () => {
                if (mentorEnabled) { handleSkipHandoff(); return; }
                leaveHandoff('home');
                finishSimulation();
                setCurrentView('home');
            };

            // "Ask a real CEO about this": the case goes with them as an
            // attachment (§7.6) and the question box starts empty. No feedback
            // prompt on this exit — it would cover the form they chose.
            const handleAskCeo = () => {
                leaveHandoff('ceo', { ask: false });
                setCeoSeed({ text: '', attachment: caseAttachmentFor(currentScenario, selectedOption) });
                setCurrentView('ceo-wisdom');
            };

            // Every way out of a run that is PRESSED on the page (§7.4). A note
            // typed and not yet saved is saved first. A Takeaway whose bottom was
            // never scrolled to completes now — completeSimulation, unchanged, is
            // the lesson → handoff boundary, crossed once per run (the run's own
            // flag, not the phase, which lags a render behind the observer) — and
            // the clips are summarised. Then the exit's own handler runs, as it
            // always has.
            const leaveRun = (exit) => {
                sendNote(noteDraft());
                if (runRef.current && !runRef.current.completed) completeSimulation();
                finishAudio();
                stampFeedbackExtra();
                exit();
            };

            const openPicker = (entityId = null) => {
                Telemetry.track('ui_open', { what: 'picker' }, entityId == null ? undefined : { entityType: 'scenario', entityId });
                setPickerOpen(true);
            };
            // "Not for me — another case": the run is abandoned (it was never
            // decided) and the picker opens on the landing.
            const handleNotForMe = () => {
                const id = currentScenario ? currentScenario.id : null;
                abandonRun();
                finishSimulation();
                openPicker(id);
            };

            useEffect(() => {
                return () => {
                    if (timerRef.current) clearInterval(timerRef.current);
                    if (excerptRef.current) excerptRef.current.pause();
                };
            }, []);

            // A run still open when this view unmounts (any other tab) or the page
            // is left (close, reload, navigate) was abandoned. A tab merely going
            // to the background is not — Telemetry.onPageHide fires only on
            // pagehide.
            useEffect(() => {
                const off = Telemetry.onPageHide(abandonRun);
                const offHandoff = Telemetry.onPageHide(() => leaveHandoff('away', { ask: false }));
                return () => { off(); offHandoff(); abandonRun(); leaveHandoff('away'); };
            }, []);

            // The clips of a COMPLETED run are summarised when it is left without
            // an exit being pressed (abandonRun covers the rest), and a note left
            // typed but unsaved is saved on the way out. Both idempotent. Refs
            // only: this closure is the first render's.
            useEffect(() => {
                const off = Telemetry.onPageHide(finishAudio);
                return () => { off(); finishAudio(); sendNote(noteDraftRef.current); };
            }, []);

            // The App widens its content column while a run is open (§7.4).
            useEffect(() => { setSimulatorRun(phase !== 'intro'); setPracticePhase(phase === 'intro' ? null : phase); }, [phase]);
            useEffect(() => () => { setSimulatorRun(false); setPracticePhase(null); }, []);

            // Step entry follows the phase. Exit is recorded at each transition
            // site (leaveStep), which is the only place the reason is known; the
            // guard here is a safety net for a transition that forgot to.
            useEffect(() => {
                const r = runRef.current;
                if (!r || !SIM_PHASE_STEP[phase]) return;
                if (r.step && r.step !== phase) leaveStep('forward');
                if (r.step !== phase) { r.step = phase; r.stepStartedAt = Date.now(); }
            }, [phase]);

            // Auto-start with specific scenario from URL param
            useEffect(() => {
                const params = new URLSearchParams(window.location.search);
                const scenarioId = params.get('id');
                if (scenarioId && phase === 'intro') {
                    startSimulation(scenarioId, 'deeplink');
                    window.history.replaceState({}, '', window.location.pathname);
                }
            }, []);

            // A case launched from elsewhere — Home, the picker, the journal —
            // via launchCase(id, source). Consumed once, and cleared before the
            // run starts so a re-render can't start it twice. Keyed on the
            // launch rather than on mount: a launch made while this view is
            // already open must still start.
            useEffect(() => {
                if (!simLaunch) return;
                const { id, source } = simLaunch;
                setSimLaunch(null);
                startSimulation(id, source);
            }, [simLaunch]);

            // The soft clock — pausable (WCAG 2.2.1) and extendable. It runs from
            // the case opening to the lock-in and only counts down; what happens
            // at zero is in the effect below, so that step's exit is recorded as
            // a timeout rather than a tap.
            useEffect(() => {
                if ((phase === 'situation' || phase === 'decision') && !paused) {
                    timerRef.current = setInterval(() => {
                        setTimer(prev => {
                            if (prev <= 1) {
                                clearInterval(timerRef.current);
                                return 0;
                            }
                            return prev - 1;
                        });
                    }, 1000);
                }
                return () => {
                    if (timerRef.current) clearInterval(timerRef.current);
                };
            }, [phase, paused]);

            // Time out before any option was chosen closes the reading step as a
            // timeout. Nothing moves on the page: the clock turns amber and says
            // to make the call when ready.
            useEffect(() => {
                if (phase === 'situation' && timer === 0) {
                    leaveStep('timeout');
                    setPhase('decision');
                }
            }, [timer, phase]);

            // Keep the pending feedback prompt's `extra` current (see reflectionRef).
            useEffect(() => { stampFeedbackExtra(); }, [phase, confidence, secondStatus, secondChoice, noteSaved, noteText, attempt]);

            // Measure the run: two columns only when it is at least 880px wide.
            const hasRun = phase !== 'intro' && !!currentScenario;
            useEffect(() => {
                const el = runWrapRef.current;
                if (!hasRun || !el) { setTwoCol(false); return; }
                const measure = () => setTwoCol(el.getBoundingClientRect().width >= 880);
                measure();
                if (typeof ResizeObserver === 'undefined') {
                    window.addEventListener('resize', measure);
                    return () => window.removeEventListener('resize', measure);
                }
                const ro = new ResizeObserver(measure);
                ro.observe(el);
                return () => ro.disconnect();
            }, [runKey, hasRun]);

            // A new run starts at its top. <main> is the scroll container, not
            // the window; focus goes to the case title for a keyboard or
            // screen-reader user whose button just unmounted.
            useEffect(() => {
                const wrap = runWrapRef.current;
                if (!runKey || !wrap) return;
                const main = wrap.closest ? wrap.closest('main') : null;
                if (main) main.scrollTop = 0;
                if (briefRef.current) briefRef.current.focus({ preventScroll: true });
            }, [runKey]);

            // The CEO appears on the lock-in and is scrolled to.
            useEffect(() => {
                if (phase !== 'reveal') return;
                const h = ceoHeadingRef.current;
                if (!h) return;
                h.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
                h.focus({ preventScroll: true });
            }, [phase]);

            // The page's two scroll boundaries (§7.4; sim_step in the contract):
            // the Takeaway heading in view ends `reveal`, and the bottom of the
            // Takeaway in view ends `lesson` — completion, which brings Molly in.
            // Observed against the viewport, so <main> scrolling them in counts.
            // Completion does not close the clips (see finishAudio).
            latestRef.current = { completeSimulation };
            useEffect(() => {
                const target = phase === 'reveal' ? takeawayHeadingRef.current : phase === 'lesson' ? takeawayEndRef.current : null;
                if (!target) return;
                let crossed = false;
                const cross = () => {
                    if (crossed) return;
                    crossed = true;
                    if (phase === 'reveal') { leaveStep('forward'); setPhase('lesson'); }
                    else if (runRef.current && !runRef.current.completed) { latestRef.current.completeSimulation(); }
                };
                if (typeof IntersectionObserver === 'undefined') { cross(); return; }
                const io = new IntersectionObserver((entries) => {
                    if (entries.some(e => e.isIntersecting)) { io.disconnect(); cross(); }
                }, { threshold: phase === 'reveal' ? 0.6 : 0 });
                io.observe(target);
                return () => { crossed = true; io.disconnect(); };
            }, [phase, runKey]);

            // One column, while the clock runs: is the options list or the
            // lock-in row on screen? The sticky bar rides above the bottom nav
            // and, with the lock-in row at the bottom of the viewport, covered
            // its three buttons — so it steps aside while the call itself is in
            // view, and is back (with its jump) once the member scrolls away.
            const deciding = !twoCol && (phase === 'situation' || phase === 'decision');
            const lockRowShown = !!selectedOption;
            useEffect(() => {
                const targets = deciding ? [optionsRef.current, lockRowRef.current].filter(Boolean) : [];
                if (!targets.length || typeof IntersectionObserver === 'undefined') { setCallInView(false); return; }
                const onScreen = new Set();
                const io = new IntersectionObserver((entries) => {
                    for (const e of entries) { if (e.isIntersecting) onScreen.add(e.target); else onScreen.delete(e.target); }
                    setCallInView(onScreen.size > 0);
                }, { threshold: 0 });
                targets.forEach(t => io.observe(t));
                return () => io.disconnect();
            }, [deciding, lockRowShown, runKey]);

            const toggleExcerpt = () => {
                const el = excerptRef.current;
                if (!el) return;
                if (excerptPlaying) { el.pause(); setExcerptPlaying(false); }
                else { el.play().catch(() => {}); setExcerptPlaying(true); }
            };

            // Click/drag along the progress track to scrub the excerpt.
            const seekExcerpt = (e) => {
                const el = excerptRef.current;
                if (!el || !excerptDuration) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                el.currentTime = frac * excerptDuration;
                setExcerptTime(el.currentTime);
            };
            const excerptFrac = excerptDuration ? Math.max(0, Math.min(1, excerptTime / excerptDuration)) : 0;

            const kicker = { fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 600 };
            const goldKicker = { fontSize: '11px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700 };
            const initials = (n) => (n || '?').split(' ').map(w => w[0]).join('').slice(0, 2);
            const lastName = (n) => ((n || '').trim().split(' ').pop()) || 'The CEO';
            const spot = isSpotBad(currentScenario);        // active run's mode
            const introSpot = isSpotBad(previewScenario);   // intro previews before start
            // The intro's two slots that nothing used to fill: the case's industry
            // and the stakes line of its case file.
            const introSector = previewScenario ? industryLabel(previewScenario.industry) : null;
            const introAtStake = (previewScenario && previewScenario.exhibits && previewScenario.exhibits.stakes) || null;
            const whyLine = whyLineFor(previewPick, world);
            const impersonating = !!(currentUser && currentUser.impersonatedBy);
            // "The result" is the factoid, falling back to the lesson; the CEO
            // card repeats the factoid only when the Takeaway shows the lesson.
            const resultText = currentScenario && (currentScenario.factoid || currentScenario.lesson);
            // Contract: a transcript expander accompanies every audio clip. When a
            // scenario lacks audioTranscript, the CEO's actual-decision quote is the
            // faithful text alternative for the same excerpt.
            const excerptTranscript = currentScenario && (currentScenario.audioTranscript || currentScenario.actualDecision);
            // The run's listen trackers, for the <audio> handlers below.
            const tr = runRef.current ? runRef.current.trackers : null;
            const toggleTranscript = () => {
                if (!transcriptOpen && runRef.current) Telemetry.track('sim_transcript_opened', { kind: 'short' }, runMeta(runRef.current));
                setTranscriptOpen(o => !o);
            };

            const locked = phase === 'reveal' || phase === 'lesson' || phase === 'handoff';
            const clockRunning = phase === 'situation' || phase === 'decision';
            const scrollToHeading = (el, block = 'start') => {
                if (!el) return;
                el.scrollIntoView({ block, behavior: scrollBehavior() });
                if (el.tabIndex === -1) el.focus({ preventScroll: true });
            };
            const jumpTo = (id) => scrollToHeading(
                id === 'decide' ? (twoCol ? decideHeadingRef.current : briefRef.current)
                    : id === 'ceo' ? ceoHeadingRef.current
                    : id === 'takeaway' ? takeawayHeadingRef.current
                    : id === 'molly' ? mollyHeadingRef.current : null
            );
            // One column: a chip on each later section scrolls back to the brief.
            const caseChip = twoCol ? null : () => scrollToHeading(briefRef.current);
            const moveText = currentScenario && (currentScenario.actionItem || (currentScenario.lesson ? `Based on ${currentScenario.ceo}'s approach: ${currentScenario.lesson}` : null));
            const moveSaved = !!moveText && (savedNextMoves || []).some(m => m && m.text === moveText);
            const readOnlyLine = 'Read-only while viewing as this member.';
            // What the Decide sources say while any is still closed. Before the
            // lock-in, that they open after it; after, why they have not — never
            // a promise the page will not keep (the refetch only brings them
            // once the attempt is on record).
            const sourcesClosedLine = !locked ? RESEARCH_SOURCES_CLOSED
                : attempt.status === 'failed' ? 'This decision wasn\'t saved, so titles and links stay closed.'
                : attempt.status === 'readonly' ? 'Read-only while viewing as this member, so titles and links stay closed.'
                : researchReveal === 'done' ? 'Titles and links couldn\'t be opened just now.'
                : 'Opening titles and links…';
            const noteBlocked = attempt.status === 'failed' ? 'This decision wasn\'t saved, so a note can\'t be either.'
                : attempt.status === 'readonly' || noteStatus === 'readonly' ? readOnlyLine
                : attempt.status !== 'saved' ? 'One moment — saving your decision first.'
                : attempt.id == null ? 'Notes can\'t be saved on this decision yet.'
                : null;
            // Asked only when a MATCH call differed from the CEO's.
            const askSecond = locked && !!currentScenario && !spot && !!selectedOption && selectedOption !== currentScenario.actualChoice;
            const second = !askSecond ? null : {
                options: [selectedOption, currentScenario.actualChoice,
                    ...currentScenario.options.map(o => o.id).filter(id => id !== selectedOption && id !== currentScenario.actualChoice)],
                value: secondChoice,
                status: secondStatus,
                disabled: attempt.status !== 'saved' || attempt.id == null || secondStatus === 'saving' || secondStatus === 'readonly',
                blocked: attempt.status === 'failed' ? 'This decision wasn\'t saved, so a second thought can\'t be either.'
                    : attempt.status === 'readonly' || secondStatus === 'readonly' ? readOnlyLine : null,
                onChoose: chooseSecondThought
            };
            const record = attempt.status === 'failed' ? { line: 'This decision wasn\'t saved, so your record hasn\'t changed.' }
                : attempt.status === 'readonly' ? { line: readOnlyLine }
                : attempt.status === 'saved' && attempt.id == null ? null
                : decisionProfile === null ? { line: 'Your record couldn\'t be loaded just now.' }
                : (caseRecordFor(decisionProfile, currentScenario, attempt.id) || { line: 'Adding this case to your record…' });

            return (
                <div data-testid="simulator-view" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* A changing aria-label on role="timer" is not
                        reliably announced, and announcing every second
                        would be unusable anyway. So: a polite region
                        that speaks only at the thresholds that change
                        what someone would do — and at zero, which moves
                        nothing on the page and says so. */}
                    <span className="sr-only" role="status" aria-live="polite">
                        {!hasRun || !clockRunning ? ''
                            : timer === 10 ? 'Ten seconds remaining.'
                            : timer === 5 ? 'Five seconds remaining.'
                            : timer === 0 ? "Time's up — make your call when you're ready."
                            : ''}
                    </span>

                    {phase === 'intro' && (
                        <div key="intro" className="ed-pagein" data-testid="simulator-intro" style={{ padding: '26px 0 32px' }}>
                            {/* Members have called this the Decision Simulator since long before the
                                case vocabulary arrived, so the screen they open says so. It is
                                the only place that does: Home, the picker and the day's stops
                                still speak of the case, which is the thing INSIDE the simulator. */}
                            <div data-testid="simulator-kicker" style={{ ...kicker, ...(introSpot ? { color: 'var(--claret)' } : {}), marginBottom: '14px' }}>{introSpot ? 'Decision Simulator · The bad call' : 'Decision Simulator · Today\'s decision'}</div>
                            <div style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--e2-light)' }}>
                                <div style={{ background: introSpot ? 'var(--claret-deep)' : 'var(--navy)', padding: '22px 24px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
                                        <span style={{ ...goldKicker, ...(introSpot ? { color: 'var(--claret-rose)' } : {}) }}>Case № {previewScenario ? previewScenario.id : '—'}</span>
                                        {previewScenario && (introSector || previewScenario.year) && (
                                            <span style={{ fontSize: '12px', color: introSpot ? 'var(--claret-rose)' : 'var(--text-on-navy-dim)' }}>
                                                {[introSector, previewScenario.year].filter(Boolean).join(' · ')}
                                            </span>
                                        )}
                                    </div>
                                    {introSpot && (
                                        <div data-testid="spotbad-badge" style={SPOTBAD_CHIP}>
                                            Spot the bad call
                                        </div>
                                    )}
                                    <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', lineHeight: 1.3, color: introSpot ? 'var(--text-on-claret)' : 'var(--text-on-navy)', fontWeight: 600, margin: 0 }}>
                                        {previewScenario ? previewScenario.title : 'No case available'}
                                    </h1>
                                </div>
                                <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {previewScenario && whyLine && (
                                        <div data-testid="simulator-why" style={{ fontSize: '13.5px', fontWeight: 600, color: introSpot ? 'var(--claret)' : 'var(--gold-darker)' }}>
                                            {whyLine}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                                        <div>
                                            <div style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', marginBottom: '3px' }}>Chief executive</div>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: introSpot ? 'var(--claret)' : 'var(--gold-darker)' }}>Sealed until the reveal</div>
                                        </div>
                                        <div>
                                            {/* For Bad Call cases the mode line always wins over the stakes —
                                                the format is the thing the reader must not miss */}
                                            <div style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', marginBottom: '3px' }}>
                                                {introAtStake && !introSpot ? 'At stake' : 'Format'}
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: introSpot ? 'var(--claret)' : 'var(--navy)' }}>
                                                {introSpot ? 'Three decisions · one bad call' : (introAtStake || 'One decision · three paths')}
                                            </div>
                                        </div>
                                    </div>
                                    <p style={{ fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.65, margin: 0 }}>
                                        {introSpot
                                            ? 'You will read the situation exactly as the chief executive saw it. Then three real decisions from that desk go in front of you — two made history for the right reasons. Find the one that didn\'t.'
                                            : 'The brief, a case file summarised from it with AI, and the paths beside them. Make your call — and only then learn who sat in the chair, and hear in their own voice what they actually did.'}
                                    </p>
                                    <button onClick={() => startSimulation()} disabled={!previewScenario} className={'ed-btn ' + (introSpot ? 'ed-btn-claret' : 'ed-btn-gold')} data-testid="start-simulation-btn">
                                        Open the case file
                                    </button>
                                    <button type="button" onClick={() => openPicker()} className="ed-btn ed-btn-quiet" data-testid="open-picker-btn">
                                        Choose a different decision
                                    </button>
                                    {!impersonating && (
                                        <div onClick={() => setWorldSheetOpen('simulator')} onKeyDown={btnKey(() => setWorldSheetOpen('simulator'))}
                                            role="button" tabIndex={0} data-testid="tune-world" className="ed-link"
                                            style={{ alignSelf: 'center', fontSize: '13.5px', fontWeight: 600, padding: '4px 2px' }}>
                                            Tune to your world
                                        </div>
                                    )}
                                    <div style={{ fontSize: '12px', color: 'var(--text-meta)', textAlign: 'center' }}>The clock is soft: pause it, add time, or let it run out.</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {pickerOpen && (
                        <DecisionPicker
                            scenarios={scenarios}
                            completedIds={(completedSimulations || []).map(s => s && s.id)}
                            world={world}
                            onPick={(id) => startSimulation(id, 'picker')}
                            onClose={closePicker} />
                    )}

                    {hasRun && (
                        <div key={runKey || 'run'} ref={runWrapRef} className="ed-pagein" data-testid="simulator-run" style={{ padding: '18px 0 32px' }}>
                            <PracticeRail phase={phase} spot={spot} mentorEnabled={mentorEnabled} mollyTalked={mollyTalked} onJump={jumpTo} />
                            <div style={twoCol ? { display: 'flex', alignItems: 'flex-start', gap: '28px' } : undefined}>
                                <div data-testid="sim-case-column" style={twoCol
                                    ? { flex: '0 0 55%', minWidth: 0, position: 'sticky', top: '16px', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', paddingRight: '4px', boxSizing: 'border-box' }
                                    : undefined}>
                                    <CaseFile scenario={currentScenario} spot={spot} wide={twoCol}
                                        detailsOpen={detailsOpen} onToggleDetails={() => setDetailsOpen(o => !o)}
                                        briefRef={briefRef} onNotForMe={locked ? null : handleNotForMe} />
                                    {/* Under the case file: the record as of the decision.
                                        Its sources open fully once the call is locked in. */}
                                    <ResearchPart research={research} which="before" scenarioId={currentScenario.id} revealed={locked} closedLine={sourcesClosedLine} />
                                </div>
                                <div data-testid="sim-call-column" style={twoCol ? { flex: '1 1 0%', minWidth: 0 } : { marginTop: '24px' }}>
                                    <DecidePanel scenario={currentScenario} spot={spot} phase={phase}
                                        selected={selectedOption} confidence={confidence}
                                        onSelect={selectOption} onLockIn={submitDecision}
                                        headingRef={decideHeadingRef} optionsRef={optionsRef} lockRowRef={lockRowRef}
                                        clock={twoCol && clockRunning
                                            ? <SimClock timer={timer} timerTotal={timerTotal} paused={paused} onPause={togglePause} onExtend={extendClock} spot={spot} />
                                            : null} />

                                    {locked && (
                                        <CeoSection scenario={currentScenario} spot={spot} selected={selectedOption}
                                            headingRef={ceoHeadingRef} onCaseChip={caseChip} choices={choices} second={second}>
                                            {/* The chair you were sitting in — stays navy in both modes (the
                                                house brand is the constant); claret kickers carry the mode */}
                                            <div style={{ background: 'var(--navy)', borderRadius: '16px', padding: '24px', boxShadow: 'var(--e3)' }}>
                                                <div style={{ ...goldKicker, ...(spot ? { color: 'var(--claret-rose)' } : {}), marginBottom: '8px' }}>{spot ? 'The desk where it happened' : 'The chair you were sitting in'}</div>
                                                {/* The COMPANY is the recognition hook — people know the brand
                                                    long before the executive's name. It gets the full width and
                                                    the display face; the CEO is the credit line beneath it.
                                                    Falls back to the CEO as headline when there is no company. */}
                                                <div style={{ paddingBottom: '18px', marginBottom: '16px', borderBottom: `1px solid ${spot ? 'rgba(217, 160, 168, 0.28)' : 'rgba(212, 182, 104, 0.25)'}` }}>
                                                    <div data-testid="reveal-company" style={{
                                                        fontFamily: 'var(--font-heading)', fontSize: '34px', lineHeight: 1.08,
                                                        color: spot ? 'var(--claret-rose)' : 'var(--gold)', fontWeight: 700,
                                                        letterSpacing: '-0.01em', margin: '0 0 10px', overflowWrap: 'anywhere'
                                                    }}>
                                                        {currentScenario.company || currentScenario.ceo}
                                                    </div>
                                                    {currentScenario.company && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                                                            <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: spot ? 'linear-gradient(135deg, var(--claret), var(--claret-dark))' : 'linear-gradient(135deg, var(--gold), var(--gold-dark))', color: spot ? 'var(--text-on-claret)' : 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>
                                                                {initials(currentScenario.ceo)}
                                                            </div>
                                                            <div style={{ minWidth: 0 }}>
                                                                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-on-navy)', lineHeight: 1.25 }}>
                                                                    {currentScenario.ceo}
                                                                </div>
                                                                <div style={{ fontSize: '12.5px', color: 'var(--text-on-navy-muted)', lineHeight: 1.35 }}>
                                                                    {[currentScenario.ceoTitle, currentScenario.year].filter(Boolean).join(' · ')}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ ...goldKicker, ...(spot ? { color: 'var(--claret-rose)' } : {}), marginBottom: '10px' }}>{spot ? 'How the bad call played out' : 'What they actually did'}</div>
                                                <p style={{ fontFamily: 'var(--font-heading)', fontSize: '19px', lineHeight: 1.5, color: 'var(--text-on-navy)', margin: '0 0 16px' }}>
                                                    {currentScenario.actualDecision}
                                                </p>
                                                {/* The factoid is the Takeaway's "The result", so it only
                                                    appears here on the rare rows where that shows the lesson
                                                    instead — otherwise the member would read it twice. */}
                                                {currentScenario.factoid && resultText !== currentScenario.factoid && (
                                                    <p style={{ fontSize: '13.5px', color: 'var(--text-on-navy-muted)', lineHeight: 1.65, margin: '0 0 18px' }}>{currentScenario.factoid}</p>
                                                )}

                                                {/* Audio excerpt — custom chrome + mandatory transcript expander.
                                                    Gated on real audio/transcript material: when a scenario has
                                                    neither, actualDecision is already printed just above and an
                                                    expander would duplicate it verbatim. */}
                                                {(currentScenario.audioFile || currentScenario.audioTranscript) && (
                                                    <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(212,182,104,0.35)', borderRadius: '12px', padding: '14px 16px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            {currentScenario.audioFile && (
                                                                <div
                                                                    onClick={toggleExcerpt}
                                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExcerpt(); } }}
                                                                    role="button" tabIndex={0} aria-label={excerptPlaying ? 'Pause excerpt' : 'Play excerpt'}
                                                                    style={{ width: '42px', height: '42px', borderRadius: '50%', background: 'var(--gold)', color: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', cursor: 'pointer', flexShrink: 0 }}>
                                                                    {excerptPlaying ? '❚❚' : '▶'}
                                                                </div>
                                                            )}
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-on-navy)' }}>{currentScenario.ceo}, in their own words</div>
                                                                <div style={{ fontSize: '11.5px', color: 'var(--text-on-navy-dim)' }}>{currentScenario.audioFile ? 'The decision, from the interview' : 'The decision, from the record'}</div>
                                                            </div>
                                                            {/* Total length is shown up front so it's obvious how long the clip is */}
                                                            {currentScenario.audioFile && excerptDuration != null && (
                                                                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 700, color: 'var(--gold)', background: 'rgba(212,182,104,0.14)', border: '1px solid rgba(212,182,104,0.35)', borderRadius: '999px', padding: '3px 10px' }}>
                                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                                                                    {fmtTime(excerptDuration)}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Seekable progress bar with an elapsed / total readout */}
                                                        {currentScenario.audioFile && (
                                                            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <div
                                                                    onClick={seekExcerpt}
                                                                    role="slider" aria-label="Seek within the excerpt"
                                                                    aria-valuemin={0} aria-valuemax={Math.round(excerptDuration || 0)} aria-valuenow={Math.round(excerptTime)}
                                                                    aria-valuetext={excerptDuration ? `${fmtTime(excerptTime)} of ${fmtTime(excerptDuration)}` : undefined}
                                                                    tabIndex={excerptDuration ? 0 : -1}
                                                                    onKeyDown={(e) => {
                                                                        const el = excerptRef.current;
                                                                        if (!el || !excerptDuration) return;
                                                                        if (e.key === 'ArrowRight') { e.preventDefault(); el.currentTime = Math.min(excerptDuration, el.currentTime + 5); setExcerptTime(el.currentTime); }
                                                                        else if (e.key === 'ArrowLeft') { e.preventDefault(); el.currentTime = Math.max(0, el.currentTime - 5); setExcerptTime(el.currentTime); }
                                                                    }}
                                                                    style={{ flex: 1, height: '16px', display: 'flex', alignItems: 'center', cursor: excerptDuration ? 'pointer' : 'default' }}>
                                                                    <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.18)', borderRadius: '999px', position: 'relative' }}>
                                                                        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${excerptFrac * 100}%`, background: 'var(--gold)', borderRadius: '999px' }}></div>
                                                                        <div style={{ position: 'absolute', top: '50%', left: `${excerptFrac * 100}%`, transform: 'translate(-50%, -50%)', width: '11px', height: '11px', borderRadius: '50%', background: 'var(--gold)', boxShadow: '0 0 0 3px rgba(26,39,68,0.9)' }}></div>
                                                                    </div>
                                                                </div>
                                                                <div style={{ flexShrink: 0, fontSize: '11.5px', color: 'var(--text-on-navy-dim)', fontVariantNumeric: 'tabular-nums', minWidth: '72px', textAlign: 'right' }}>
                                                                    {excerptDuration != null ? `${fmtTime(excerptTime)} / ${fmtTime(excerptDuration)}` : 'Loading…'}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {excerptTranscript && (
                                                            <>
                                                                <div
                                                                    onClick={toggleTranscript}
                                                                    onKeyDown={btnKey(toggleTranscript)}
                                                                    role="button" tabIndex={0} aria-expanded={transcriptOpen}
                                                                    style={{ marginTop: '10px', fontSize: '12px', color: spot ? 'var(--claret-rose)' : 'var(--gold)', cursor: 'pointer', fontWeight: 600 }}>
                                                                    {transcriptOpen
                                                                        ? (currentScenario.audioFile ? 'Hide transcript ↑' : 'Hide the full account ↑')
                                                                        : (currentScenario.audioFile ? 'Read transcript ↓' : 'Read the full account ↓')}
                                                                </div>
                                                                {transcriptOpen && (
                                                                    <p style={{ margin: '10px 0 0', fontSize: '13px', color: '#c8cdd8', lineHeight: 1.65, borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: '10px' }}>
                                                                        "{excerptTranscript}"
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                        {currentScenario.audioFile && (
                                                            <audio
                                                                ref={excerptRef}
                                                                src={currentScenario.audioFile}
                                                                preload="metadata"
                                                                onLoadedMetadata={(e) => { setExcerptDuration(isFinite(e.target.duration) && e.target.duration > 0 ? e.target.duration : null); if (tr) tr.short.onLoadedMetadata(e.target); }}
                                                                onTimeUpdate={(e) => { setExcerptTime(e.target.currentTime); if (tr) tr.short.onTimeUpdate(e.target); }}
                                                                onPlay={(e) => { if (tr) tr.short.onPlay(e.target); }}
                                                                onPause={(e) => { if (tr) tr.short.onPause(e.target); }}
                                                                onSeeked={(e) => { if (tr) tr.short.onSeeked(e.target); }}
                                                                onEnded={(e) => { setExcerptPlaying(false); setExcerptTime(0); if (tr) tr.short.onEnded(e.target); }}
                                                                style={{ display: 'none' }}
                                                            />
                                                        )}
                                                    </div>
                                                )}

                                                {/* Full interview */}
                                                {currentScenario.audioFileFull && (
                                                    <div style={{ marginTop: '12px' }}>
                                                        <button
                                                            onClick={() => {
                                                                if (!showFullInterview && excerptRef.current) {
                                                                    excerptRef.current.pause();
                                                                    setExcerptPlaying(false);
                                                                }
                                                                setShowFullInterview(v => !v);
                                                            }}
                                                            className="ed-btn ed-btn-gold"
                                                            aria-expanded={showFullInterview}
                                                            style={{ width: '100%', fontSize: '14px', padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '9px' }}>
                                                            <span style={{ fontSize: '12px' }}>▶</span> Listen to the full interview clip
                                                        </button>
                                                        {showFullInterview && (
                                                            <audio src={currentScenario.audioFileFull} controls autoPlay style={{ width: '100%', marginTop: '10px', borderRadius: '10px' }}
                                                                onLoadedMetadata={(e) => { if (tr) tr.full.onLoadedMetadata(e.target); }}
                                                                onPlay={(e) => { if (tr) tr.full.onPlay(e.target); }}
                                                                onPause={(e) => { if (tr) tr.full.onPause(e.target); }}
                                                                onSeeked={(e) => { if (tr) tr.full.onSeeked(e.target); }}
                                                                onTimeUpdate={(e) => { if (tr) tr.full.onTimeUpdate(e.target); }}
                                                                onEnded={(e) => { if (tr) tr.full.onEnded(e.target); }} />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {/* After the CEO card: what happened next — sent by the
                                                worker only once this member has an attempt on the case. */}
                                            <ResearchPart research={research} which="after" scenarioId={currentScenario.id} revealed />
                                        </CeoSection>
                                    )}

                                    {locked && (
                                        <TakeawaySection scenario={currentScenario} spot={spot}
                                            headingRef={takeawayHeadingRef} endRef={takeawayEndRef} onCaseChip={caseChip}
                                            powerIndex={powerIndex} powerIndexChange={powerIndexChange} wasReplay={wasReplay} streak={streak}
                                            moveText={moveText} moveSaved={moveSaved}
                                            onSaveMove={() => { if (moveText && !moveSaved) addSavedNextMove(moveText, `Case · ${currentScenario.title}`); }}
                                            record={record}
                                            onOpenProfile={() => leaveRun(() => setCurrentView('profile'))}>
                                            <ReflectNote value={noteText} saved={noteSaved} status={noteStatus} blocked={noteBlocked}
                                                onChange={(v) => { setNoteText(v); if (noteStatus === 'error') setNoteStatus('idle'); }}
                                                onSave={saveNote} />
                                        </TakeawaySection>
                                    )}

                                    {/* Signature moment: Molly in the situation. The case does not
                                        end and hand the user to a chat page — Molly opens on the
                                        call they just made, right here, and the exchange stays
                                        with the case. Her page is where it can CONTINUE. Only for
                                        a member Molly is on for: otherwise no section at all. */}
                                    {mentorEnabled && phase === 'handoff' && currentScenario && (
                                        <section key="molly" data-testid="sim-molly" aria-labelledby="ed-sim-molly" style={{ marginTop: '30px' }}>
                                            <RunSectionHead id="ed-sim-molly" kicker="Molly · optional" headingRef={mollyHeadingRef} onCaseChip={caseChip}
                                                title={`${lastName(currentScenario.ceo)} had this decision. Talk it through.`} />
                                            <p style={{ fontSize: '14.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 14px' }}>
                                                Molly — your CI Mentor — has a question about the call you just made. Answer it here; the conversation stays with the case.
                                            </p>
                                            <div data-testid="handoff-molly" style={{ background: 'var(--cream)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '0 16px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                                <CIMentorView embedded opening={handoffOpening} onFirstReply={() => markHandoffTaken('answer')} onUserTurn={noteMollyTurn} />
                                            </div>
                                        </section>
                                    )}

                                    {/* The ways out. Naming the next case is the pull — "Run
                                        another" is a chore, a titled case is a hook — and
                                        pickPracticeCase rotates once everything is played, so it
                                        is labelled honestly as a replay when it is one. */}
                                    {locked && (
                                        <div key="exits" data-testid="sim-exits" style={{ marginTop: '28px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {previewScenario && (
                                                <button type="button" onClick={() => leaveRun(handleNextCase)} data-testid="next-case-cta"
                                                    aria-label={`${nextIsReplay ? 'Play again' : 'Next case'}: ${previewScenario.title}`}
                                                    className="ed-hover-gold-border"
                                                    style={{
                                                        width: '100%', padding: '14px 16px', cursor: 'pointer', textAlign: 'left', font: 'inherit', opacity: 1,
                                                        background: 'var(--white)', border: '1px solid var(--border-card)',
                                                        borderRadius: '12px', display: 'flex', alignItems: 'center',
                                                        justifyContent: 'space-between', gap: '12px'
                                                    }}>
                                                    <span style={{ minWidth: 0 }}>
                                                        <span style={{ display: 'block', fontSize: '10.5px', letterSpacing: '0.14em', textTransform: 'uppercase', color: isSpotBad(previewScenario) ? 'var(--claret)' : 'var(--gold-darker)', fontWeight: 700, marginBottom: '3px' }}>
                                                            {nextIsReplay ? 'Play again' : 'Next case'}{isSpotBad(previewScenario) ? ' · The bad call' : ''}
                                                        </span>
                                                        <span style={{ display: 'block', fontFamily: 'var(--font-heading)', fontSize: '16px', color: 'var(--navy)', fontWeight: 600, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {previewScenario.title}
                                                        </span>
                                                    </span>
                                                    <span aria-hidden="true" style={{ flexShrink: 0, fontSize: '15px', fontWeight: 700, color: isSpotBad(previewScenario) ? 'var(--claret)' : 'var(--gold-darker)' }}>→</span>
                                                </button>
                                            )}
                                            <button type="button" onClick={() => leaveRun(handleDoneForToday)} className="ed-btn ed-btn-quiet" data-testid="issue-skip">
                                                Done for today
                                            </button>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '4px 16px' }}>
                                                {mentorEnabled && phase === 'handoff' && (
                                                    <div
                                                        onClick={() => leaveRun(handleContinueWithMolly)}
                                                        onKeyDown={btnKey(() => leaveRun(handleContinueWithMolly))}
                                                        role="button" tabIndex={0} data-testid="handoff-continue" className="ed-link"
                                                        style={{ fontSize: '13.5px', fontWeight: 600, padding: '6px 2px' }}>
                                                        Keep going with Molly in a full session →
                                                    </div>
                                                )}
                                                <div
                                                    onClick={() => leaveRun(handleAskCeo)}
                                                    onKeyDown={btnKey(() => leaveRun(handleAskCeo))}
                                                    role="button" tabIndex={0} data-testid="ask-ceo-exit" className="ed-link"
                                                    style={{ fontSize: '13.5px', fontWeight: 600, padding: '6px 2px' }}>
                                                    Ask a real CEO about this
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* One column: the clock and the way to the call ride above
                                the bottom nav until the lock-in — hidden, not removed,
                                while the options or the lock-in row are on screen (it
                                would cover them), so the page does not change height
                                under the member's thumb. */}
                            {!twoCol && clockRunning && (
                                <div data-testid="sim-sticky-bar" aria-hidden={callInView ? 'true' : undefined} style={{
                                    visibility: callInView ? 'hidden' : 'visible', pointerEvents: callInView ? 'none' : 'auto',
                                    position: 'sticky', bottom: isDesktop ? '12px' : 'calc(74px + env(safe-area-inset-bottom, 0px))',
                                    zIndex: 'var(--z-sticky)', marginTop: '18px',
                                    background: 'rgba(255, 255, 255, 0.97)', border: '1px solid var(--border-card)', borderRadius: '14px',
                                    boxShadow: 'var(--e2-light)', padding: '8px 8px 8px 10px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'
                                }}>
                                    <SimClock timer={timer} timerTotal={timerTotal} paused={paused} onPause={togglePause} onExtend={extendClock} spot={spot} />
                                    <button type="button" data-testid="sim-sticky-jump"
                                        onClick={() => (selectedOption ? scrollToHeading(lockRowRef.current, 'center') : scrollToHeading(decideHeadingRef.current))}
                                        className={'ed-btn ' + (spot ? 'ed-btn-claret' : 'ed-btn-navy')}
                                        style={{ minHeight: '40px', padding: '8px 13px', fontSize: '13.5px', flexShrink: 0 }}>
                                        {selectedOption ? `Lock in ${selectedOption} ↓` : 'Your call ↓'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        };

        // ============================================
        // TRANSFORMATIVE MOMENTS VIEW
        // ============================================
        /**
         * `playbookOnly` is the Pivot Playbook.
         *
         * Saving a moment toasted "Moment saved to your Pivot Playbook!" and
         * flipped the button to "Saved to Playbook" — and then the list was
         * written to localStorage, synced into the profile blob, and rendered
         * NOWHERE. No view, no Profile row, no count. The user filed something
         * away, was told twice that it was filed, and could never get it back.
         * The data and the ids were always there; this is the screen.
         */
        const MomentsView = ({ playbookOnly = false }) => {
            const { database, showToast, addSavedMoment, savedMoments, setCurrentView } = useApp();
            const [selectedMoment, setSelectedMoment] = useState(null);
            const [view, setView] = useState('list'); // list, detail, questions
            const [expandedQuestion, setExpandedQuestion] = useState(null);
            const [answers, setAnswers] = useState(() =>
                storage.get(STORAGE_KEYS.CEO_ANSWERS, {})
            );

            const savedIds = (savedMoments || []).map(m => (m && typeof m === 'object') ? m.id : m);
            const allMoments = database.transformativeMoments.moments.filter(m => m.active !== false);
            const moments = playbookOnly ? allMoments.filter(m => savedIds.includes(m.id)) : allMoments;
            const questions = database.transformativeMoments.tenKeyQuestions;

            // Calculate progress based on answered questions
            const questionsProgress = Object.keys(answers).filter(key => answers[key]?.trim()).length;

            // Save answers to localStorage whenever they change
            useEffect(() => {
                storage.set(STORAGE_KEYS.CEO_ANSWERS, answers);
            }, [answers]);

            // Auto-select moment from URL param
            useEffect(() => {
                const params = new URLSearchParams(window.location.search);
                const momentId = params.get('id');
                if (momentId && !selectedMoment) {
                    const moment = moments.find(m => m.id === parseInt(momentId));
                    if (moment) {
                        setSelectedMoment(moment);
                        setView('detail');
                        // Clear URL params after use
                        window.history.replaceState({}, '', window.location.pathname);
                    }
                }
            }, [moments]);

            const handleAnswerChange = (questionIndex, value) => {
                setAnswers(prev => ({
                    ...prev,
                    [questionIndex]: value
                }));
            };

            const handleQuestionClick = (index) => {
                setExpandedQuestion(expandedQuestion === index ? null : index);
            };

            const handlePrint = () => {
                window.print();
            };

            const handleShare = async () => {
                const questionsText = questions.map((q, i) =>
                    `${i + 1}. ${q}\n   Answer: ${answers[i] || '(Not answered)'}`
                ).join('\n\n');

                const shareText = `The 10 Key CEO Questions\n\n${questionsText}`;

                if (navigator.share) {
                    try {
                        await navigator.share({
                            title: 'The 10 Key CEO Questions',
                            text: shareText
                        });
                    } catch (e) {
                        if (e.name !== 'AbortError') {
                            copyToClipboard(shareText);
                        }
                    }
                } else {
                    copyToClipboard(shareText);
                }
            };

            const copyToClipboard = async (text) => {
                try {
                    await navigator.clipboard.writeText(text);
                    showToast('Copied to clipboard!', 'success');
                } catch (e) {
                    showToast('Unable to share', 'error');
                }
            };

            const handleMomentSelect = (moment) => {
                setSelectedMoment(moment);
                setView('detail');
            };

            const handleBack = () => {
                setView('list');
                setSelectedMoment(null);
            };

            // savedMoments holds ids (addSavedMoment/removeSavedMoment dedupe and
            // filter on ids, and the array is synced into the profile blob).
            const savedMomentIds = savedIds;

            const handleSaveMoment = () => {
                if (!savedMomentIds.includes(selectedMoment.id)) {
                    addSavedMoment(selectedMoment.id);
                    showToast('Moment saved to your Pivot Playbook!', 'success');
                } else {
                    showToast('Already saved to your Playbook', 'info');
                }
            };

            const isMomentSaved = selectedMoment && savedMomentIds.includes(selectedMoment.id);

            return (
                <div className="view-transition moments-view" data-testid="moments-view">
                    {view === 'list' && (
                        <div className="moments-list-view fade-in" data-testid="moments-list">
                            {playbookOnly && (
                                <div
                                    onClick={() => setCurrentView('profile')}
                                    onKeyDown={btnKey(() => setCurrentView('profile'))}
                                    role="button" tabIndex={0}
                                    style={{ fontSize: '13px', color: 'var(--text-tertiary)', cursor: 'pointer', marginBottom: '12px' }}>← Profile</div>
                            )}
                            <div className="moments-header">
                                <h1 className="moments-title">{playbookOnly ? 'My Pivot Playbook' : 'Transformative CEO Moments'}</h1>
                                <p className="moments-subtitle">
                                    {playbookOnly ? 'The moments you kept.' : 'The decisions that created CEO legends'}
                                </p>
                            </div>

                            {playbookOnly && moments.length === 0 && (
                                <div style={{ background: 'var(--cream-dark)', border: '1px dashed var(--border-input)', borderRadius: '14px', padding: '26px 22px', textAlign: 'center' }}>
                                    <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--navy)', fontWeight: 600, marginBottom: '5px' }}>Nothing saved yet.</div>
                                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                                        Open a moment worth keeping and tap <strong style={{ color: 'var(--gold-darker)' }}>Save to My Pivot Playbook</strong> — it will wait for you here.
                                    </div>
                                    <div
                                        onClick={() => setCurrentView('moments')}
                                        onKeyDown={btnKey(() => setCurrentView('moments'))}
                                        role="button" tabIndex={0}
                                        style={{ marginTop: '14px', color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>
                                        Browse the moments →
                                    </div>
                                </div>
                            )}

                            {/* The library's own framing — the definition, the ten
                                questions, "Featured Moments" — belongs to browsing,
                                not to the shelf of what you kept. */}
                            {!playbookOnly && (
                                <>
                                    <div className="definition-card">
                                        <span className="definition-label">Transformative CEO:</span>
                                        <p className="definition-text">
                                            "{database.transformativeMoments.definition}"
                                        </p>
                                    </div>

                                    <button
                                        onClick={() => setView('questions')}
                                        className="questions-cta-btn"
                                        data-testid="questions-cta"
                                    >
                                        <i className="fa-solid fa-clipboard-list questions-icon"></i>
                                        <div className="questions-content">
                                            <span className="questions-title">The 10 Key CEO Questions</span>
                                            <span className="questions-desc">Build your business overview</span>
                                        </div>
                                        <i className="fa-solid fa-chevron-right questions-arrow"></i>
                                    </button>

                                    <h2 className="section-header">Featured Moments</h2>
                                </>
                            )}

                            <div className="moments-list">
                                {moments.map((moment, index) => (
                                    <button
                                        key={moment.id}
                                        onClick={() => handleMomentSelect(moment)}
                                        className="moment-card slide-up card-interactive"
                                        style={{ animationDelay: `${index * 0.08}s` }}
                                        data-testid={`moment-card-${moment.id}`}
                                    >
                                        <div className="moment-meta">
                                            {/* The moments table has no `year` column and the
                                                API mapping never set one, so every moment loaded
                                                from the server rendered this as an empty navy
                                                pill. Only the embedded fallback rows have a year. */}
                                            {moment.year ? <span className="moment-year">{moment.year}</span> : null}
                                            <span className="moment-type">{moment.pivotType}</span>
                                        </div>
                                        <h3 className="moment-title">{moment.title}</h3>
                                        <div className="moment-ceo">
                                            <span className="moment-ceo-name">{moment.ceo}, {moment.ceoTitle}, {moment.company}</span>
                                        </div>
                                        <p className="moment-preview">
                                            {moment.moment.substring(0, 100)}...
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {view === 'detail' && selectedMoment && (
                        <div className="moment-detail-view fade-in" data-testid="moment-detail">
                            <button onClick={handleBack} className="back-button">
                                <i className="fa-solid fa-arrow-left"></i> Back to Moments
                            </button>

                            <div className="detail-header">
                                <span className="detail-year">{selectedMoment.year}</span>
                                <h1 className="detail-title">{selectedMoment.title}</h1>
                                <div className="detail-ceo">
                                    <span className="detail-ceo-name">{selectedMoment.ceo}, {selectedMoment.ceoTitle}, {selectedMoment.company}</span>
                                </div>
                            </div>

                            <div className="moment-section">
                                <span className="section-label">The Moment</span>
                                <p className="moment-quote">"{selectedMoment.moment}"</p>
                            </div>

                            <div className="context-section">
                                <span className="section-label">Context</span>
                                <p className="context-desc">{selectedMoment.context}</p>
                            </div>

                            <div className="decision-section">
                                <span className="section-label">The Key Decision</span>
                                <div className="key-decision-box">
                                    <span className="pivot-badge">{selectedMoment.pivotType}</span>
                                    <p className="key-decision-text">{selectedMoment.keyDecision}</p>
                                </div>
                            </div>

                            <div className="outcome-section">
                                <span className="section-label">The Outcome</span>
                                <p className="outcome-text">{selectedMoment.outcome}</p>
                            </div>

                            <div className="lesson-section">
                                <span className="lesson-badge">
                                    <i className="fa-solid fa-lightbulb"></i> CEO Lesson
                                </span>
                                <p className="lesson-quote">"{selectedMoment.lesson}"</p>
                            </div>

                            {selectedMoment.audioFile && (
                                <div className="audio-section" style={{ marginTop: 'var(--space-4)', padding: 'var(--space-4)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)' }}>
                                    <span className="section-label" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
                                        <i className="fa-solid fa-headphones"></i> Hear It From the CEO
                                    </span>
                                    {selectedMoment.audioTranscript && (
                                        <p style={{ fontSize: 'var(--text-sm)', fontStyle: 'italic', color: 'var(--text-2)', marginBottom: 'var(--space-3)' }}>
                                            "{selectedMoment.audioTranscript}"
                                        </p>
                                    )}
                                    <audio
                                        src={selectedMoment.audioFile}
                                        style={{ width: '100%', borderRadius: 'var(--radius-md)' }}
                                        controls
                                    />
                                </div>
                            )}

                            <button
                                onClick={handleSaveMoment}
                                className={`btn-primary save-moment-btn ${isMomentSaved ? 'saved' : ''}`}
                                data-testid="save-moment-btn"
                            >
                                <i className={`fa-${isMomentSaved ? 'solid' : 'regular'} fa-bookmark`}></i>
                                {isMomentSaved ? 'Saved to Playbook' : 'Save to My Pivot Playbook'}
                            </button>
                        </div>
                    )}

                    {view === 'questions' && (
                        <div className="questions-view fade-in" data-testid="questions-view">
                            <button onClick={() => setView('list')} className="back-button">
                                <i className="fa-solid fa-arrow-left"></i> Back to Moments
                            </button>

                            <div className="questions-header">
                                <h1 className="questions-main-title">The 10 Key CEO Questions</h1>
                                <p className="questions-intro">
                                    Create an overview of your business to unlock personalized pivot recommendations
                                </p>
                            </div>

                            {/* Toolbar */}
                            <div className="questions-toolbar">
                                <button onClick={handlePrint} className="toolbar-btn">
                                    <i className="fa-solid fa-print"></i>
                                    Print
                                </button>
                                <button onClick={handleShare} className="toolbar-btn">
                                    <i className="fa-solid fa-share-nodes"></i>
                                    Share
                                </button>
                            </div>

                            <div className="questions-progress-bar">
                                <div
                                    className="questions-progress-fill"
                                    style={{ width: `${(questionsProgress / 10) * 100}%` }}
                                ></div>
                            </div>
                            <span className="questions-progress-text">{questionsProgress} of 10 answered</span>

                            <div className="questions-list">
                                {questions.map((question, index) => (
                                    <div
                                        key={index}
                                        className={`question-item ${answers[index]?.trim() ? 'complete' : ''}`}
                                        data-testid={`question-${index}`}
                                    >
                                        <div
                                            onClick={() => handleQuestionClick(index)}
                                            className="question-clickable"
                                        >
                                            <span className="question-number">{index + 1}</span>
                                            <p className="question-text">{question}</p>
                                            {answers[index]?.trim() ? (
                                                <i className="fa-solid fa-check check-mark"></i>
                                            ) : (
                                                <i className={`fa-solid fa-chevron-${expandedQuestion === index ? 'up' : 'down'} expand-icon`}></i>
                                            )}
                                        </div>
                                        {expandedQuestion === index && (
                                            <div className="answer-section">
                                                <textarea
                                                    value={answers[index] || ''}
                                                    onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                    placeholder="Type your answer here..."
                                                    className="answer-textarea"
                                                    rows={4}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            );
        };

        // ============================================
        // CEO WISDOM VIEW
        // ============================================
        /**
         * "3 hours ago" / "in 4 hours".
         *
         * The screen used to say a flat "usually within a day" and nothing else,
         * so a question thirty hours past our promise read the same as one asked
         * five minutes ago. Both columns were already on the wire.
         */
        const humanStamp = (iso) => {
            if (!iso) return null;
            const t = Date.parse(String(iso).indexOf('T') === -1 ? String(iso).replace(' ', 'T') + 'Z' : iso);
            return isFinite(t) ? t : null;
        };
        const humanSpan = (mins) => {
            const m = Math.abs(Math.round(mins));
            if (m < 1) return 'a moment';
            if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
            const h = Math.round(m / 60);
            if (m < 60 * 36) return `${h} hour${h === 1 ? '' : 's'}`;
            const d = Math.round(m / 1440);
            return `${d} day${d === 1 ? '' : 's'}`;
        };
        const humanAgo = (iso) => {
            const t = humanStamp(iso);
            if (t == null) return 'recently';
            return `${humanSpan((Date.now() - t) / 60000)} ago`;
        };
        const humanUntil = (iso) => {
            const t = humanStamp(iso);
            if (t == null) return 'soon';
            return t <= Date.now() ? 'any time now' : `within ${humanSpan((t - Date.now()) / 60000)}`;
        };

        const CEOWisdomView = () => {
            const { showToast, ceoSeed, setCeoSeed, authToken, setCurrentView, setFabSuppressed, markNotificationsSeen,
                world, refreshDecisionProfile, shareLevel, saveShareLevel, currentUser } = useApp();
            // Viewing as a member is read-only; the sharing level is theirs.
            const viewingAs = !!(currentUser && currentUser.impersonatedBy);
            // What an advisor will see, as the member chose it (src/sharing.ts).
            // Unchosen reads as the default, exactly as the worker reads it.
            const share = shareLevelOf(shareLevel || 'anonymized');
            const [changingShare, setChangingShare] = useState(false);
            const pickShare = async (id) => {
                if (id === share.id) { setChangingShare(false); return; }
                const ok = await saveShareLevel(id);
                if (ok) { setChangingShare(false); showToast(`Saved — advisors will see: ${shareLevelOf(id).label.toLowerCase()}.`, 'success'); }
                else showToast('That did not save. Try again in a moment.', 'error');
            };
            // landing | compose. There is no AI step any more: "Ask a CEO" IS the
            // human lane (docs/CEO_MENTORS.md, 6 Sep 2026). The screen used to
            // answer from the library first and offer a real CEO only afterwards,
            // which put the whole point of the lane behind a second button most
            // people never pressed. Molly is the instant conversation; she is
            // linked from here, not stood in front of the queue.
            const [view, setView] = useState('landing');
            // Arriving here IS reading them: this is the screen every
            // notification in this lane points at.
            useEffect(() => { markNotificationsSeen(); }, [markNotificationsSeen]);
            const [questionText, setQuestionText] = useState('');
            const [askSeeded, setAskSeeded] = useState(false);
            // The seed's text came from Molly (so the box says where it came
            // from); a case arrives with an empty box and an attachment instead.
            const [carriedFromMolly, setCarriedFromMolly] = useState(false);
            // A case the member brought with them (§7.6), shown before sending
            // exactly as it will travel, and theirs to remove.
            const [attachment, setAttachment] = useState(null);
            const questionRef = useRef(null);
            const [sending, setSending] = useState(false);
            const [humanQuestions, setHumanQuestions] = useState(null);
            const [humanLoadFailed, setHumanLoadFailed] = useState(false);

            // The chips ARE the roster's stated expertise, so a chosen topic always
            // matches at least one advisor — routing's strongest content signal.
            const [ceoTopics, setCeoTopics] = useState([]);
            const [topic, setTopic] = useState(null);
            // How many CEOs could actually take a question. Null until we know:
            // with nobody on the roster the compose is not offered, or a question
            // goes into a queue nobody is watching with the clock running.
            const [ceoRosterSize, setCeoRosterSize] = useState(null);
            // The roster could not be read. Still null, and the landing treats it
            // as it always has; this only tells a waiting seed to stop waiting.
            const [rosterLoadFailed, setRosterLoadFailed] = useState(false);

            useEffect(() => {
                if (!API_URL || !authToken) return;
                let cancelled = false;
                fetch(`${API_URL}/api/ceo-topics`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => r.json())
                    .then(res => {
                        if (cancelled) return;
                        if (!res || !res.success) { setRosterLoadFailed(true); return; }
                        setCeoTopics(res.data.topics || []);
                        setCeoRosterSize(Number(res.data.mentors) || 0);
                    })
                    .catch(() => { /* leave it null: offer nothing rather than a door into an empty room */ if (!cancelled) setRosterLoadFailed(true); });
                return () => { cancelled = true; };
            }, [authToken]);

            // A question that is being replied to or withdrawn.
            const [qBusy, setQBusy] = useState(null);      // id currently posting
            const [qReply, setQReply] = useState({});      // id -> draft text

            const loadHumanQuestions = useCallback(async () => {
                if (!API_URL || !authToken) return;
                try {
                    const res = await fetch(`${API_URL}/api/ceo-questions/mine`, {
                        headers: { Authorization: `Bearer ${authToken}` }
                    }).then(r => r.json());
                    if (!res || !res.success) throw new Error((res && res.error) || 'could not load');
                    setHumanQuestions(res.data.items || []);
                    setHumanLoadFailed(false);
                } catch (e) {
                    // An empty list and a failed request are different facts and
                    // have to look it: somebody waiting on a real person must not
                    // see NOTHING where their question should be.
                    setHumanLoadFailed(true);
                }
            }, [authToken]);

            useEffect(() => { loadHumanQuestions(); }, [loadHumanQuestions]);

            // Three questions to a human a day (the server's ASK_HUMAN_DAILY_LIMIT,
            // by UTC day; a question withdrawn within a minute is refunded). The
            // count here is a courtesy from what is already loaded — the server
            // is the authority and says so in words when it refuses.
            const MAX_QUESTIONS_PER_DAY = 3;
            const utcDay = (iso) => {
                const t = humanStamp(iso);
                return t == null ? null : new Date(t).toISOString().slice(0, 10);
            };
            const todayUtc = new Date().toISOString().slice(0, 10);
            const askedToday = (humanQuestions || []).filter(q => q.status !== 'cancelled' && utcDay(q.createdAt) === todayUtc).length;
            const questionsRemaining = Math.max(0, MAX_QUESTIONS_PER_DAY - askedToday);

            // Hide the Molly FAB while composing — it would cover the send button.
            useEffect(() => {
                setFabSuppressed(view !== 'landing');
                return () => setFabSuppressed(false);
            }, [view, setFabSuppressed]);

            // Arriving from the CI Mentor's "Ask a CEO" affordance: the challenge
            // they were working comes with them, into the box. A seed is a string
            // (Molly's first user turn) or { text, attachment } (a case, §7.6).
            //
            // It used to jump straight to the compose, so somebody arriving from
            // a case with no questions left today — or with nobody on the roster
            // — was handed a form that could not send. A seed now waits on the
            // landing for the two facts the landing's own button is checked
            // against, and opens the compose only if that button would.
            const [arrival, setArrival] = useState(null);
            // A case that came with an arrival the landing had to stop:
            // { title, why: 'roster' | 'allowance' }. Said, not dropped silently.
            const [droppedCase, setDroppedCase] = useState(null);
            useEffect(() => {
                if (ceoSeed) {
                    const seed = typeof ceoSeed === 'string' ? { text: ceoSeed, attachment: null } : ceoSeed;
                    setArrival(seed);
                    setCeoSeed(null);
                }
            }, []);
            useEffect(() => {
                if (!arrival) return;
                const unknowable = !API_URL || !authToken;
                const rosterKnown = unknowable || ceoRosterSize !== null || rosterLoadFailed;
                const countKnown = unknowable || humanQuestions !== null || humanLoadFailed;
                if (!rosterKnown || !countKnown) return;
                setArrival(null);
                // The member brought a case to ask about; it goes nowhere from
                // here, and they are told so.
                if (arrival.attachment && (ceoRosterSize === 0 || questionsRemaining <= 0)) {
                    setDroppedCase({ title: arrival.attachment.title || null, why: ceoRosterSize === 0 ? 'roster' : 'allowance' });
                }
                // The landing is where they are, and it says which of the two it is.
                if (ceoRosterSize === 0 || questionsRemaining <= 0) return;
                setQuestionText(arrival.text || '');
                setCarriedFromMolly(!!arrival.text);
                setAttachment(arrival.attachment || null);
                setAskSeeded(true);
                setView('compose');
            }, [arrival, ceoRosterSize, rosterLoadFailed, humanQuestions, humanLoadFailed, questionsRemaining]);

            const handleStartQuestion = () => {
                if (questionsRemaining <= 0) return;
                setArrival(null);
                setDroppedCase(null);
                setQuestionText('');
                setTopic(null);
                setAskSeeded(false);
                setCarriedFromMolly(false);
                setAttachment(null);
                setView('compose');
            };

            // The advisors are a page of this screen, as compose is. It opens at
            // its top with focus on its heading: <main> scrolls, not the window,
            // and the card that opened it has just unmounted.
            const advisorsHeadingRef = useRef(null);
            const openAdvisors = () => {
                Telemetry.track('ui_open', { what: 'ceo_advisors' });
                setView('advisors');
            };
            useEffect(() => {
                if (view !== 'advisors') return;
                const heading = advisorsHeadingRef.current;
                if (!heading) return;
                const main = heading.closest ? heading.closest('main') : null;
                if (main) main.scrollTop = 0;
                heading.focus({ preventScroll: true });
            }, [view]);

            // What travels with the question besides its words (§7.6). The
            // industry only once the member has told us their world — a legacy
            // value nobody chose is not an answer to give an advisor — and the
            // case only while it is still attached. Each is undefined otherwise,
            // which JSON leaves out of the body altogether.
            const worldIndustryLabel = (world && world.set && industryLabel(world.industries[0])) || undefined;
            const attachmentContext = attachment ? caseAttachmentText(attachment) : undefined;
            const attachmentScenarioId = attachment && attachment.scenarioId ? attachment.scenarioId : undefined;
            const removeAttachment = () => {
                setAttachment(null);
                if (questionRef.current) questionRef.current.focus();
            };

            /**
             * The ask. Straight to the queue: routed to an advisor who is on and
             * knows the subject when there is one, otherwise open for the next
             * one to claim. The server says which, and the toast repeats it
             * honestly rather than implying a person is already reading.
             */
            const submitToCeo = async () => {
                const question = questionText.trim();
                // The same two bounds the server enforces, so the button never
                // sends something that is going to come back refused.
                if (question.length < ASK_MIN_CHARS || question.length > ASK_MAX_CHARS || sending) return;
                setSending(true);
                try {
                    const res = await fetch(`${API_URL}/api/ceo-questions`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({ question, topic, industry: worldIndustryLabel, context: attachmentContext, scenarioId: attachmentScenarioId })
                    }).then(r => r.json());
                    if (!res || !res.success) throw new Error((res && res.error) || 'That did not send');
                    Telemetry.track('ceo_question_asked', { routed: !!res.data.routed, topic: topic || null, seeded: askSeeded, attached: !!attachmentContext });
                    showToast(res.data.routed
                        ? 'Sent — a CEO has it now.'
                        : 'Sent — it is in the queue for the next CEO on.', 'success');
                    setQuestionText('');
                    setTopic(null);
                    setAskSeeded(false);
                    setCarriedFromMolly(false);
                    setAttachment(null);
                    setView('landing');
                    loadHumanQuestions();
                    // The journal marks a case "Asked a CEO" from the server's record.
                    if (attachmentScenarioId) refreshDecisionProfile();
                } catch (err) {
                    showToast(err.message, 'error');
                } finally {
                    setSending(false);
                }
            };

            /**
             * Replying to a real CEO. The server allows two follow-ups
             * (ASKER_FOLLOWUPS) and says how many are left, so this never offers
             * a box the API will refuse.
             */
            const sendHumanReply = async (id) => {
                const text = String(qReply[id] || '').trim();
                if (!text || qBusy) return;
                setQBusy(id);
                try {
                    const res = await fetch(`${API_URL}/api/ceo-questions/${id}/messages`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({ text })
                    }).then(r => r.json());
                    if (!res || !res.success) throw new Error((res && res.error) || 'That did not send');
                    setQReply(prev => ({ ...prev, [id]: '' }));
                    Telemetry.track('ceo_question_replied', { questionId: id });
                    showToast('Sent. They will see it next time they are in.', 'success');
                    loadHumanQuestions();
                } catch (err) {
                    showToast(err.message, 'error');
                } finally {
                    setQBusy(null);
                }
            };

            /**
             * Taking a question back. Two windows, both decided by the SERVER and
             * reported as `canCancel` — a minute after asking, and after 48 hours
             * with nothing back. In between a CEO may be part-way through it.
             */
            const cancelHumanQuestion = async (id) => {
                if (qBusy) return;
                setQBusy(id);
                try {
                    const res = await fetch(`${API_URL}/api/ceo-questions/${id}/cancel`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${authToken}` }
                    }).then(r => r.json());
                    if (!res || !res.success) throw new Error((res && res.error) || 'That did not work');
                    Telemetry.track('ceo_question_cancelled', { questionId: id });
                    showToast(res.data.allowanceRefunded
                        ? 'Withdrawn. That one has not cost you a question today.'
                        : 'Withdrawn.', 'success');
                    loadHumanQuestions();
                } catch (err) {
                    showToast(err.message, 'error');
                } finally {
                    setQBusy(null);
                }
            };

            const kicker = { fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 600 };
            const backLink = (
                <div
                    onClick={() => setView('landing')}
                    onKeyDown={btnKey(() => setView('landing'))}
                    role="button" tabIndex={0}
                    style={{ fontSize: '13px', color: 'var(--text-tertiary)', cursor: 'pointer', marginBottom: '14px' }}>
                    ← Ask a CEO
                </div>
            );
            const mollyLink = (label) => (
                <span
                    onClick={() => setCurrentView('ci-mentor')}
                    onKeyDown={btnKey(() => setCurrentView('ci-mentor'))}
                    role="button" tabIndex={0} data-testid="ask-molly-instead"
                    style={{ color: 'var(--gold-darker)', fontWeight: 600, cursor: 'pointer' }}>{label}</span>
            );

            // ── Compose ──
            if (view === 'compose') {
                const asked = questionText.trim().length;
                const tooLong = questionText.length > ASK_MAX_CHARS;
                const disabled = asked < ASK_MIN_CHARS || tooLong || sending;
                return (
                    <div key="compose" className="ed-pagein" data-testid="ceo-wisdom-view" style={{ padding: '26px 0 32px' }}>
                        {backLink}
                        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '23px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>What would you put to a CEO?</h2>
                        <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: '0 0 16px' }}>
                            A former CEO on our roster reads it and answers it themselves, usually within a day. You will have two follow-ups once the answer arrives.
                        </p>
                        {/* The case, as the advisor will read it — the preview IS the
                            text that is sent, not a summary of it. */}
                        {attachment && (
                            <div data-testid="ask-attachment" style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderLeft: '3px solid var(--gold)', borderRadius: '12px', padding: '13px 15px', marginBottom: '14px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '7px' }}>
                                    <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--navy)', lineHeight: 1.45, minWidth: 0 }}>
                                        Attached: "{attachment.title}" — your call and the CEO's call
                                    </span>
                                    <span
                                        onClick={removeAttachment}
                                        onKeyDown={btnKey(removeAttachment)}
                                        role="button" tabIndex={0} data-testid="ask-attachment-remove"
                                        aria-label={`Remove the attached case "${attachment.title}"`}
                                        style={{ flexShrink: 0, fontSize: '12.5px', color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>
                                        Remove
                                    </span>
                                </div>
                                <p data-testid="ask-attachment-text" style={{ margin: 0, fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{attachmentContext}</p>
                            </div>
                        )}
                        {askSeeded && carriedFromMolly && (
                            <div style={{ fontSize: '11.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '8px' }}>Carried over from your Molly session</div>
                        )}
                        <textarea
                            ref={questionRef}
                            value={questionText}
                            onChange={(e) => setQuestionText(e.target.value)}
                            rows={5}
                            autoFocus
                            placeholder={attachment
                                ? 'What would you ask a CEO who has faced this?'
                                : 'e.g. Our biggest distributor wants exclusivity for a lower price. How do I negotiate the renewal without losing the account?'}
                            data-testid="question-input"
                            className="ed-input"
                            aria-describedby="ask-count"
                            style={{ borderRadius: '12px', padding: '15px 17px', resize: 'vertical', minHeight: '132px', lineHeight: 1.6, fontFamily: 'inherit' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', marginBottom: '16px' }}>
                            {/* The ceiling, as they approach it. It used to be
                                silent, so the only way to learn a question was
                                too long was to press send and have the server
                                refuse it — after writing the whole thing. */}
                            <span style={{ fontSize: '12px', color: tooLong ? 'var(--claret)' : 'var(--text-meta)', fontWeight: tooLong ? 600 : 400 }}>
                                {asked < ASK_MIN_CHARS
                                    ? 'Give them at least a sentence'
                                    : tooLong
                                        ? `${questionText.length.toLocaleString()} characters — ${(questionText.length - ASK_MAX_CHARS).toLocaleString()} over the limit`
                                        : questionText.length > ASK_MAX_CHARS * 0.8
                                            ? `${questionText.length.toLocaleString()} of ${ASK_MAX_CHARS.toLocaleString()} characters`
                                            : `${questionText.length.toLocaleString()} characters`}
                            </span>
                            <span style={{ fontSize: '12px', color: 'var(--text-meta)' }}>Uses 1 of {questionsRemaining} remaining today</span>
                        </div>

                        {ceoTopics.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '7px' }}>
                                    What is this about? Picking one sends it to a CEO who has done it.
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }} role="radiogroup" aria-label="Subject">
                                    {ceoTopics.map(t => (
                                        <span key={t.topic}
                                            onClick={() => setTopic(cur => cur === t.topic ? null : t.topic)}
                                            onKeyDown={btnKey(() => setTopic(cur => cur === t.topic ? null : t.topic))}
                                            role="radio" aria-checked={topic === t.topic} tabIndex={0}
                                            data-testid="ask-topic"
                                            style={{
                                                fontSize: '12.5px', padding: '6px 11px', borderRadius: '999px', cursor: 'pointer',
                                                border: `1px solid ${topic === t.topic ? 'var(--gold-dark)' : 'var(--border-input)'}`,
                                                background: topic === t.topic ? 'var(--white)' : 'transparent',
                                                color: topic === t.topic ? 'var(--navy)' : 'var(--text-secondary)',
                                                fontWeight: topic === t.topic ? 600 : 500
                                            }}>
                                            {t.topic}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <button onClick={submitToCeo} disabled={disabled} className="ed-btn ed-btn-navy" style={{ width: '100%' }} data-testid="submit-question-btn">
                            {sending ? 'Sending…' : 'Send to a CEO'}
                        </button>
                        {/* Said BEFORE they send, because this is where the
                            consent is given: the queue is readable by every
                            advisor on the roster, and they are told exactly what
                            travels with it — including their name, which does
                            (docs/CEO_MENTORS.md). A different bargain from the
                            one Molly offers, and it has to be stated as one.
                            "Your industry" only when it is in the body: it used
                            to be promised while the app never sent it. */}
                        {/* Since 30 Sep 2026 what travels is the member's
                            choice (src/sharing.ts), and the sentence is built
                            from the same three cases the worker discloses —
                            plus "your industry" only when one is sent and the
                            level lets it through. */}
                        <div data-testid="ask-consent" style={{ marginTop: '14px', background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '13px 15px', fontSize: '12.5px', color: 'var(--text-body)', lineHeight: 1.6 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'baseline', marginBottom: '4px' }}>
                                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>🔒 Confidential · {share.label}</span>
                                {!viewingAs && (
                                    <span role="button" tabIndex={0} data-testid="ask-share-change"
                                        onClick={() => setChangingShare(v => !v)} onKeyDown={btnKey(() => setChangingShare(v => !v))}
                                        style={{ flexShrink: 0, color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>
                                        {changingShare ? 'Done' : 'Change'}
                                    </span>
                                )}
                            </div>
                            Your question goes to the CEO mentors on our roster so one of them can pick it up.{' '}
                            {share.id === 'full'
                                ? `Advisors who can take it see your name, your company and business details${worldIndustryLabel ? ', your industry' : ''} and anything attached here — never your notes or your work with Molly.`
                                : share.id === 'identity'
                                    ? 'Advisors who can take it see your name and role and anything attached here — no business details, never your notes or your work with Molly.'
                                    : `Advisors who can take it see a code name${worldIndustryLabel ? ', your industry' : ''}, your company size and stage and anything attached here — never your name, your company, your notes or your work with Molly. Leave names out of the question to keep it that way.`}
                            {changingShare && (
                                <div style={{ marginTop: '12px' }}>
                                    <SharingChoice value={share.id} onChange={pickShare} />
                                </div>
                            )}
                        </div>
                    </div>
                );
            }

            // ── Landing ──
            const rosterEmpty = ceoRosterSize === 0;

            // ── The advisors ──
            // Who they are, by name. Which advisor answered a given question is
            // still never named (docs/CEO_MENTORS.md): this list is not tied to
            // any answer, and the routing roster is not read to build it.
            if (view === 'advisors') {
                return (
                    <div key="advisors" className="ed-pagein" data-testid="ask-advisors-view" style={{ padding: '26px 0 32px' }}>
                        {backLink}
                        <div style={{ ...kicker, marginBottom: '6px' }}>Our advisors</div>
                        <h1 ref={advisorsHeadingRef} tabIndex={-1} style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', lineHeight: 1.3, color: 'var(--navy)', fontWeight: 600, margin: '0 0 10px', outline: 'none' }}>The Inaugural {ADVISOR_COUNT} {BRAND.name} Advisors</h1>
                        <p style={{ fontSize: '14.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 22px' }}>
                            Chief executives who have built and led companies at every scale. The advisor who answers your question stays anonymous — you see their experience, never their name.
                        </p>
                        {ADVISORS.tiers.map(tier => (
                            <section key={tier.name} aria-labelledby={`ed-advisors-${tier.name.toLowerCase()}`} style={{ marginBottom: '22px' }}>
                                <h2 id={`ed-advisors-${tier.name.toLowerCase()}`} style={{ ...kicker, fontWeight: 700, margin: '0 0 2px' }}>{tier.name}</h2>
                                <div style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', marginBottom: '10px' }}>{tier.range}</div>
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                                    {tier.advisors.map(a => (
                                        <li key={a.name} data-testid="ask-advisor" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '13px 15px' }}>
                                            <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--navy)' }}>{a.name}</div>
                                            <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.45, marginTop: '2px' }}>{a.role}</div>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                        {!rosterEmpty && questionsRemaining > 0 && (
                            <button onClick={handleStartQuestion} className="ed-btn ed-btn-navy" data-testid="advisors-ask-btn" style={{ width: '100%' }}>
                                Ask a CEO
                            </button>
                        )}
                    </div>
                );
            }
            return (
                <div key="landing" className="ed-pagein" data-testid="ceo-wisdom-view" style={{ padding: '26px 0 32px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ ...kicker, marginBottom: '6px' }}>Ask a CEO</div>
                    <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', lineHeight: 1.3, color: 'var(--navy)', fontWeight: 600, margin: '0 0 10px' }}>Put your question to a real CEO.</h1>
                    <p style={{ fontSize: '14.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
                        A former chief executive on our roster reads it and answers it themselves — usually within a day, in their own words. Want to think it through right now instead? {mollyLink('Molly is always in session')}.
                    </p>

                    {/* Front and centre, before the first question (tester
                        feedback, 30 Sep 2026): without it people ask generic
                        questions, and generic questions get generic answers. */}
                    <div data-testid="ask-privacy" style={{ background: 'var(--navy)', color: 'var(--text-on-navy)', borderRadius: '14px', padding: '16px 18px', marginBottom: '18px' }}>
                        <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700, marginBottom: '6px' }}>🔒 Confidential by design</div>
                        <div style={{ fontSize: '13.5px', lineHeight: 1.6 }}>{PRIVACY_PROMISE}</div>
                        <div style={{ fontSize: '13px', lineHeight: 1.55, marginTop: '8px', opacity: 0.9 }}>
                            Advisors see: <strong>{share.label}</strong>{share.id === 'anonymized' ? ' — a code name and your business context' : share.id === 'identity' ? ' — your name and role, no business details' : ' — your name and business details'}.{' '}
                            {!viewingAs && (
                                <span role="button" tabIndex={0} data-testid="ask-privacy-change"
                                    onClick={() => setChangingShare(v => !v)} onKeyDown={btnKey(() => setChangingShare(v => !v))}
                                    style={{ color: 'var(--gold)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                                    {changingShare ? 'Done' : 'Change'}
                                </span>
                            )}
                        </div>
                        {changingShare && (
                            <div style={{ marginTop: '12px' }}>
                                <SharingChoice value={share.id} onChange={pickShare} />
                            </div>
                        )}
                    </div>

                    <div style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '16px 20px', marginBottom: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '13.5px', color: 'var(--text-body)' }}>Questions today</div>
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            {[0, 1, 2].map(i => (
                                <div key={i} style={{ width: '9px', height: '9px', borderRadius: '50%', background: i < questionsRemaining ? 'var(--gold)' : 'transparent', border: `1px solid ${i < questionsRemaining ? 'var(--gold)' : 'var(--border-input)'}` }}></div>
                            ))}
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)', marginLeft: '5px' }}>{questionsRemaining} of {MAX_QUESTIONS_PER_DAY} remaining</span>
                        </div>
                    </div>

                    {rosterEmpty ? (
                        <div data-testid="roster-empty" style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '18px 20px', textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--navy)', fontWeight: 600, marginBottom: '5px' }}>No CEO is taking questions yet.</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                                The roster is being set up. In the meantime, {mollyLink('Molly is always in session')}.
                            </div>
                        </div>
                    ) : questionsRemaining > 0 ? (
                        <button onClick={handleStartQuestion} className="ed-btn ed-btn-navy" data-testid="start-question-btn">
                            Ask a CEO
                        </button>
                    ) : (
                        <div style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '18px 20px', textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--navy)', fontWeight: 600, marginBottom: '5px' }}>Three questions today — that is the day's allowance.</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                                A real person is at the other end of each one. It renews at midnight UTC — in the meantime, {mollyLink('Molly is always in session')}.
                            </div>
                        </div>
                    )}

                    {droppedCase && (
                        <p data-testid="ask-attachment-dropped" role="status"
                            style={{ margin: '12px 0 0', fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.55, textAlign: 'center' }}>
                            {`Your case${droppedCase.title ? ` "${droppedCase.title}"` : ''} wasn't attached — ${droppedCase.why === 'roster'
                                ? 'no CEO is taking questions right now.'
                                : 'today\'s questions are used up.'}`}
                        </p>
                    )}

                    <div role="button" tabIndex={0} data-testid="ask-advisors"
                        onClick={openAdvisors} onKeyDown={btnKey(openAdvisors)}
                        style={{ marginTop: '18px', background: 'var(--white)', border: '1px solid var(--border-card)', borderLeft: '3px solid var(--gold)', borderRadius: '12px', padding: '14px 16px', cursor: 'pointer' }}>
                        <div style={{ fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.55 }}>
                            {ADVISOR_COUNT} chief executives, from global enterprises to founder-led companies.
                        </div>
                        <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--gold-darker)', marginTop: '4px' }}>
                            View who our {BRAND.name} advisors are →
                        </div>
                    </div>

                    {humanLoadFailed && (
                        <div data-testid="human-answers-failed" role="status"
                            style={{ marginTop: '26px', background: 'var(--cream-dark)', border: '1px solid var(--gold)', borderRadius: '12px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <span style={{ flex: 1, minWidth: '200px', fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.55 }}>
                                We could not load your questions. Nothing has been lost — they are still there.
                            </span>
                            <span
                                onClick={loadHumanQuestions}
                                onKeyDown={btnKey(loadHumanQuestions)}
                                role="button" tabIndex={0}
                                style={{ fontSize: '13px', fontWeight: 700, color: 'var(--gold-darker)', cursor: 'pointer' }}>
                                Try again
                            </span>
                        </div>
                    )}

                    {humanQuestions && humanQuestions.length > 0 && (
                        <div style={{ marginTop: '26px' }} data-testid="human-answers">
                            <div style={{ fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '10px' }}>
                                Your questions to real CEOs
                            </div>
                            {humanQuestions.map(q => {
                                const thread = (q.messages || []);
                                const answers = thread.filter(m => m.author_role === 'mentor');
                                const waiting = answers.length === 0;
                                const cancelled = q.status === 'cancelled';
                                const busy = qBusy === q.id;
                                return (
                                    <div key={q.id} data-testid="human-question"
                                        style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderLeft: `3px solid ${cancelled ? 'var(--border-card)' : waiting ? (q.overdue ? 'var(--claret)' : 'var(--gold)') : 'var(--navy)'}`, borderRadius: '12px', padding: '15px 17px', marginBottom: '10px', opacity: cancelled ? 0.6 : 1 }}>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', marginBottom: '6px' }}>{q.question}</div>

                                        {/* Where it is and when it is due — said plainly,
                                            including that a late one has gone back to the
                                            whole roster. */}
                                        {cancelled ? (
                                            <div style={{ fontSize: '12.5px', color: 'var(--text-meta)' }}>
                                                You withdrew this one.
                                            </div>
                                        ) : waiting ? (
                                            <div style={{ fontSize: '12.5px', color: q.overdue ? 'var(--claret)' : 'var(--text-meta)', lineHeight: 1.6 }}>
                                                {q.overdue
                                                    ? `Asked ${humanAgo(q.createdAt)} — past our ${q.slaHours || 24}-hour promise. It has gone back to the whole roster so anyone can pick it up.`
                                                    : q.status === 'open'
                                                        ? `Asked ${humanAgo(q.createdAt)} · in the queue · CEOs usually answer within ${q.slaHours || 24} hours.`
                                                        : `Asked ${humanAgo(q.createdAt)} · a CEO has it${q.dueAt ? ` · expected ${humanUntil(q.dueAt)}` : ''}.`}
                                            </div>
                                        ) : (
                                            <>
                                                {/* WHAT answered, not who.
                                                    An advisor's name is not shown to the person
                                                    who asked — they answer strangers' questions on
                                                    that footing, and their name is theirs to give
                                                    inside an answer if they want to. So this is
                                                    their standing, and the fact that the same one
                                                    has been here before, which is continuity
                                                    without identity. */}
                                                {q.mentor && q.mentor.headline && (
                                                    <div style={{ marginBottom: '8px' }}>
                                                        <div style={{ fontSize: '11.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 }}>
                                                            {q.mentor.headline}
                                                        </div>
                                                        {q.mentor.answeredForYou > 1 && (
                                                            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                                                The same advisor has answered you {q.mentor.answeredForYou} times
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {/* The whole thread, not just their answers: a
                                                    follow-up you sent has to be visible or the
                                                    reply box is a void with a send button. */}
                                                {thread.slice(1).map((m, i) => (
                                                    <p key={i} style={{
                                                        margin: '0 0 8px', fontSize: '14px', lineHeight: 1.7, whiteSpace: 'pre-line',
                                                        color: m.author_role === 'mentor' ? 'var(--text-body)' : 'var(--text-secondary)',
                                                        paddingLeft: m.author_role === 'mentor' ? 0 : '12px',
                                                        borderLeft: m.author_role === 'mentor' ? 'none' : '2px solid var(--border-card)'
                                                    }}>{m.text}</p>
                                                ))}
                                            </>
                                        )}

                                        {/* Replying. Offered only when the server says so, so
                                            this never shows a box the API will refuse. */}
                                        {q.canReply && (
                                            <div style={{ marginTop: '12px', borderTop: '1px solid var(--border-card)', paddingTop: '12px' }}>
                                                <textarea
                                                    value={qReply[q.id] || ''}
                                                    onChange={(e) => setQReply(prev => ({ ...prev, [q.id]: e.target.value }))}
                                                    rows={3}
                                                    data-testid="human-reply-input"
                                                    placeholder="Come back to them — what does this leave you unsure about?"
                                                    className="ed-input"
                                                    style={{ borderRadius: '10px', padding: '11px 13px', resize: 'none', lineHeight: 1.6, fontFamily: 'inherit', fontSize: '13.5px' }}
                                                />
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginTop: '8px' }}>
                                                    <span style={{ fontSize: '11.5px', color: 'var(--text-tertiary)' }}>
                                                        {q.followupsLeft} follow-up{q.followupsLeft === 1 ? '' : 's'} left on this one
                                                    </span>
                                                    <span
                                                        onClick={() => sendHumanReply(q.id)}
                                                        onKeyDown={btnKey(() => sendHumanReply(q.id))}
                                                        role="button" tabIndex={0} data-testid="human-reply-send"
                                                        aria-disabled={busy || !String(qReply[q.id] || '').trim()}
                                                        style={{
                                                            background: 'var(--navy)', color: 'var(--text-on-navy)', borderRadius: '9px',
                                                            padding: '8px 14px', fontSize: '13px', fontWeight: 600,
                                                            cursor: busy ? 'default' : 'pointer',
                                                            opacity: busy || !String(qReply[q.id] || '').trim() ? 0.5 : 1
                                                        }}>
                                                        {busy ? 'Sending…' : 'Send'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Taking it back. Shown only in the two windows the
                                            server will honour. */}
                                        {q.canCancel && (
                                            <div style={{ marginTop: '10px' }}>
                                                <span
                                                    onClick={() => cancelHumanQuestion(q.id)}
                                                    onKeyDown={btnKey(() => cancelHumanQuestion(q.id))}
                                                    role="button" tabIndex={0} data-testid="human-cancel"
                                                    style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', textDecoration: 'underline', textUnderlineOffset: '2px', cursor: 'pointer' }}>
                                                    {busy ? 'Withdrawing…' : 'Withdraw this question'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {humanQuestions && humanQuestions.length === 0 && !humanLoadFailed && !rosterEmpty && (
                        <div style={{ marginTop: '26px', fontSize: '13px', color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
                            Nothing asked yet. Your questions, and the answers that come back, will sit here.
                        </div>
                    )}
                </div>
            );
        };

        // ============================================
        // PROFILE VIEW
        // ============================================
        // ============================================
        // SAVED NEXT MOVES VIEW
        // ============================================
        const SavedMovesView = () => {
            const { savedNextMoves, toggleNextMoveDone, removeSavedNextMove, setCurrentView, setCiSeed } = useApp();
            const [confirmRemove, setConfirmRemove] = useState(null);

            const openCount = savedNextMoves.filter(m => !m.done).length;
            const openLine = openCount === 0 ? '' : openCount === 1 ? 'One commitment still open.' : `${openCount} commitments still open.`;

            const discussWithMolly = (move) => {
                setCiSeed({ text: `I want to talk through a commitment I saved: "${move.text}"`, fromSim: false });
                setCurrentView('ci-mentor');
            };

            return (
                <div className="ed-pagein" data-testid="saved-moves-view" style={{ padding: '26px 0 32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                        <div
                            onClick={() => setCurrentView('profile')}
                            onKeyDown={btnKey(() => setCurrentView('profile'))}
                            role="button" tabIndex={0}
                            style={{ fontSize: '13px', color: 'var(--text-tertiary)', cursor: 'pointer', marginBottom: '12px' }}>← Profile</div>
                        <div style={{ fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 600, marginBottom: '6px' }}>Your commitments</div>
                        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>Saved next moves</h1>
                        <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>Counsel only counts when it becomes action. {openLine}</p>
                    </div>

                    {savedNextMoves.length === 0 && (
                        <div style={{ background: 'var(--cream-dark)', border: '1px dashed var(--border-input)', borderRadius: '14px', padding: '26px 22px', textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '17px', color: 'var(--navy)', fontWeight: 600, marginBottom: '5px' }}>Nothing saved yet.</div>
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                                When Molly hands you a next move worth keeping, tap <strong style={{ color: 'var(--gold-darker)' }}>Save</strong> — it will wait for you here.
                            </div>
                            <div
                                onClick={() => setCurrentView('ci-mentor')}
                                onKeyDown={btnKey(() => setCurrentView('ci-mentor'))}
                                role="button" tabIndex={0}
                                style={{ marginTop: '12px', fontSize: '13.5px', color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>
                                Start a session with Molly →
                            </div>
                        </div>
                    )}

                    {savedNextMoves.map(move => (
                        <div key={move.id} style={{
                            background: 'var(--white)', border: '1px solid var(--border-card)',
                            borderTop: `3px solid ${move.done ? 'var(--border-card)' : 'var(--gold)'}`,
                            borderRadius: '12px', padding: '15px 18px', opacity: move.done ? 0.62 : 1
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '12px' }}>
                                <span style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-meta)', fontWeight: 600 }}>{move.source}</span>
                                <span style={{ fontSize: '11.5px', color: 'var(--text-meta)' }}>
                                    {move.date ? new Date(move.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                                </span>
                            </div>
                            <p style={{ margin: '0 0 12px', fontSize: '14px', color: 'var(--navy)', lineHeight: 1.6, fontWeight: 500, textDecoration: move.done ? 'line-through' : 'none' }}>{move.text}</p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div
                                    onClick={() => toggleNextMoveDone(move.id)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleNextMoveDone(move.id); } }}
                                    role="checkbox" aria-checked={!!move.done} tabIndex={0}
                                    style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minHeight: '32px' }}>
                                    <div style={{ width: '19px', height: '19px', borderRadius: '6px', border: `1.5px solid ${move.done ? 'var(--success-ink)' : 'var(--text-faint)'}`, background: move.done ? 'var(--success-ink)' : 'transparent', color: 'var(--cream)', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{move.done ? '✓' : ''}</div>
                                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: move.done ? 'var(--success-ink)' : 'var(--text-tertiary)' }}>{move.done ? 'Done' : 'Mark done'}</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                    <span
                                        onClick={() => discussWithMolly(move)}
                                        onKeyDown={btnKey(() => discussWithMolly(move))}
                                        role="button" tabIndex={0}
                                        style={{ fontSize: '12.5px', color: 'var(--gold-darker)', fontWeight: 600, cursor: 'pointer' }}>Discuss with Molly →</span>
                                    {confirmRemove === move.id ? (
                                        <span style={{ fontSize: '12.5px', display: 'flex', gap: '10px' }}>
                                            <span role="button" tabIndex={0}
                                                onClick={() => { removeSavedNextMove(move.id); setConfirmRemove(null); }}
                                                onKeyDown={btnKey(() => { removeSavedNextMove(move.id); setConfirmRemove(null); })}
                                                style={{ color: 'var(--error-accent, #b3392f)', fontWeight: 700, cursor: 'pointer' }}>Remove</span>
                                            <span role="button" tabIndex={0}
                                                onClick={() => setConfirmRemove(null)}
                                                onKeyDown={btnKey(() => setConfirmRemove(null))}
                                                style={{ color: 'var(--text-tertiary)', cursor: 'pointer' }}>Keep</span>
                                        </span>
                                    ) : (
                                        <span
                                            onClick={() => setConfirmRemove(move.id)}
                                            onKeyDown={btnKey(() => setConfirmRemove(move.id))}
                                            role="button" tabIndex={0} aria-label={`Remove: ${move.text}`}
                                            data-testid="remove-next-move"
                                            style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', cursor: 'pointer' }}>Remove</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        };

        // ============================================
        // PROFILE VIEW
        // ============================================
        const ProfileView = () => {
            const { powerIndex, streak, userProfile, setUserProfile, completedSimulations, savedNextMoves, savedMoments, setCurrentView, handleLogout, currentUser, setFabSuppressed, requestFeedback, setConvosBackTo,
                world, decisionProfile, refreshDecisionProfile, mentorEnabled, setMentorOpening, setCeoSeed, launchCase, journalFocus, setJournalFocus, setWorldSheetOpen, database, authToken } = useApp();
            const [editing, setEditing] = useState(false);
            const [draft, setDraft] = useState(null);
            const [saving, setSaving] = useState(false);
            const [savedNote, setSavedNote] = useState(false);
            const savedNoteTimer = useRef(null);
            const impersonating = !!(currentUser && currentUser.impersonatedBy);

            useEffect(() => () => clearTimeout(savedNoteTimer.current), []);

            // The edit card's Save/Cancel sit where the FAB floats — hide it while editing.
            useEffect(() => {
                setFabSuppressed(editing);
                return () => setFabSuppressed(false);
            }, [editing, setFabSuppressed]);

            // Tier thresholds mirror the app's scoring bands
            const getRankInfo = (score) => {
                if (score >= 90) return { title: 'CEO Ready', next: null, pointsNeeded: 0 };
                if (score >= 70) return { title: 'Executive Track', next: 'CEO Ready', pointsNeeded: 90 - score };
                if (score >= 50) return { title: 'Rising Leader', next: 'Executive Track', pointsNeeded: 70 - score };
                if (score >= 30) return { title: 'Emerging', next: 'Rising Leader', pointsNeeded: 50 - score };
                return { title: 'Foundation', next: 'Emerging', pointsNeeded: 30 - score };
            };
            const rankInfo = getRankInfo(powerIndex);

            // The record (§4, §6): the server's, from simulation_attempts. While it
            // is loading or could not be had, each block says so — the picture
            // falls back to what this device holds, and nothing else is guessed.
            const record = decisionProfile && decisionProfile.profile ? decisionProfile : null;
            const profile = record ? record.profile : null;
            const history = record && Array.isArray(record.history) ? record.history : [];

            // One count of cases on the page: the record's, once it is here. The
            // local list is what this device has, and a replay can overwrite it.
            const simulationsCount = profile && profile.totals && typeof profile.totals.decisions === 'number'
                ? profile.totals.decisions : (completedSimulations?.length || 0);
            const movesCount = savedNextMoves?.length || 0;
            const playbookCount = (savedMoments || []).length || null;

            const userName = userProfile.name || (currentUser && currentUser.name) || 'Executive';
            const worldLine = worldLineOf(world);

            // What this device knows about how someone decides — the Picture so
            // far's fallback while the record is loading or unavailable.
            //
            // This used to be a card of its own showing four "Decision
            // disciplines" — Pattern recognition, Risk calibration, Decision
            // reflexes, Emotional discipline — each with its own bar and a
            // verdict including the literal claim "Strong · top quartile". All
            // four were powerIndex plus a constant (+6, -8, -1, -14): perfectly
            // rank-correlated by construction, moving in lockstep forever,
            // measuring nothing. What the app genuinely has is the attempt log:
            // which calls matched the CEO's, and when. The record (above) is that
            // log, read on the server; this is the local copy of it, which can't
            // tell a replay from a first attempt — so it is only the stand-in.
            const decided = (completedSimulations || []).filter(c => c && typeof c.correct === 'boolean');
            const hits = decided.filter(c => c.correct).length;
            const hitRate = decided.length ? Math.round((hits / decided.length) * 100) : null;
            const recent = decided.slice(-10);
            const recentHits = recent.filter(c => c.correct).length;
            const recentRate = recent.length >= 5 ? Math.round((recentHits / recent.length) * 100) : null;

            // ---- the edit card ---------------------------------------------------
            // No pre-filled defaults: an empty field stays empty until the member
            // picks something. Save is what makes the world count (contextSetAt).
            const startEdit = () => {
                if (impersonating) return;
                setDraft({
                    name: userProfile.name || (currentUser && currentUser.name) || '',
                    company: userProfile.company || '',
                    ...worldDraftFrom(userProfile)
                });
                setSavedNote(false);
                setEditing(true);
            };

            const cancelEdit = () => { setEditing(false); setDraft(null); setSaving(false); };

            const saveProfile = () => {
                if (saving || impersonating) return;
                setSaving(true);
                setTimeout(() => {
                    const next = {
                        ...userProfile,
                        name: draft.name,
                        company: draft.company,
                        industries: draft.industries.slice(0, 3),
                        role: draft.role || null,
                        companyStage: draft.companyStage || null,
                        companySize: draft.companySize || null,
                        focusTypes: draft.focusTypes.slice(0, 3),
                        industry: industryLabel(draft.industries[0]),
                        contextSetAt: new Date().toISOString()
                    };
                    setUserProfile(next);
                    trackContextSet(next, 'profile');
                    setEditing(false);
                    setDraft(null);
                    setSaving(false);
                    setSavedNote(true);
                    savedNoteTimer.current = setTimeout(() => setSavedNote(false), 2500);
                }, 700);
            };
            const toggleUpToThree = (key, slug) => setDraft(d => d[key].includes(slug)
                ? { ...d, [key]: d[key].filter(x => x !== slug) }
                : d[key].length >= 3 ? d : { ...d, [key]: [...d[key], slug] });

            const fieldLabel = { fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-tertiary)', fontWeight: 600, marginBottom: '6px', display: 'block' };
            const selectStyle = { border: '1px solid var(--border-input)', borderRadius: '10px', padding: '12px', fontSize: '14.5px', color: 'var(--navy)', background: 'var(--cream)', outline: 'none', minHeight: '46px', width: '100%', fontFamily: 'inherit' };

            // ---- the decision journal ----------------------------------------------
            const [journalShown, setJournalShown] = useState(3);
            // The one note being written: { attemptId, text, saved, status }.
            const [note, setNote] = useState(null);
            const libraryCase = (id) => (database.decisionSimulator.scenarios || []).find(s => s && s.id === id) || null;
            const openNote = (row) => setNote({ attemptId: row.attemptId, text: row.note || '', saved: row.note || '', status: 'idle' });
            const noteOf = (row) => note && note.attemptId === row.attemptId ? { open: true, ...note } : { open: false };
            const saveNote = (row) => {
                if (!note || note.attemptId !== row.attemptId || !API_URL || !authToken || note.status === 'saving') return;
                const text = String(note.text || '').trim().slice(0, REFLECTION_NOTE_MAX);
                const attemptId = row.attemptId;
                setNote(n => (n && n.attemptId === attemptId ? { ...n, status: 'saving' } : n));
                fetch(`${API_URL}/api/simulations/${attemptId}/reflection`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                    body: JSON.stringify({ note: text || null })
                })
                    .then(r => r.json().catch(() => null).then(res => ({ status: r.status, res })))
                    .then(({ status, res }) => {
                        const settle = (patch) => setNote(n => (n && n.attemptId === attemptId ? { ...n, ...patch } : n));
                        if (!res || !res.success) { settle({ status: status === 403 ? 'readonly' : 'error' }); return; }
                        // From the journal there is no run: the case rides on the entity.
                        if (text) Telemetry.track('sim_reflected', { kind: 'note', changed: null, towardCeo: null }, { entityType: 'scenario', entityId: row.scenarioId });
                        settle({ status: 'saved', saved: text, text });
                        refreshDecisionProfile();
                    })
                    .catch(() => setNote(n => (n && n.attemptId === attemptId ? { ...n, status: 'error' } : n)));
            };
            const talkAbout = (row) => {
                const opening = revisitOpening(row);
                if (!opening) return;
                setMentorOpening(opening);
                setCurrentView('ci-mentor');
            };
            // The attachment is built from the attempt — the calls as they were
            // made — with the brief from the library, which the record doesn't carry.
            const askAbout = (row) => {
                const lib = libraryCase(row.scenarioId) || {};
                const scenario = {
                    ...lib,
                    id: row.scenarioId,
                    title: row.title || lib.title,
                    industry: row.industry || lib.industry || null,
                    decisionType: row.decisionType || lib.decisionType || null,
                    scenarioType: row.scenarioType || lib.scenarioType,
                    ceo: row.ceo || lib.ceo,
                    company: row.company || lib.company,
                    actualChoice: row.correctChoice || lib.actualChoice,
                    options: Array.isArray(row.options) && row.options.length ? row.options : lib.options
                };
                setCeoSeed({ text: '', attachment: caseAttachmentFor(scenario, row.chosen) });
                setCurrentView('ceo-wisdom');
            };

            // Home's "Add a note" lands here: open that entry's note once the
            // record is in, paging far enough to show it.
            const focusTimer = useRef(null);
            useEffect(() => () => clearTimeout(focusTimer.current), []);
            useEffect(() => {
                if (journalFocus == null || decisionProfile === undefined) return;
                const target = journalFocus;
                setJournalFocus(null);
                const rows = decisionProfile && Array.isArray(decisionProfile.history) ? decisionProfile.history : [];
                const i = rows.findIndex(h => h && Number(h.attemptId) === Number(target));
                if (i < 0 || impersonating) return;
                if (i >= 3) setJournalShown(Math.max(10, Math.ceil((i + 1) / 10) * 10));
                openNote(rows[i]);
                focusTimer.current = setTimeout(() => {
                    const el = document.getElementById('ed-reflect-note');
                    if (!el) return;
                    el.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
                    el.focus({ preventScroll: true });
                }, 80);
            }, [journalFocus, decisionProfile]);

            const passedOver = profile ? passedOverLines(profile.passedOver) : [];
            const sureLine = profile ? confidenceLine(profile.confidence) : null;
            const trend = profile ? trendLines(profile.trend) : null;
            const afterReveal = profile ? secondThoughtsLine(profile.secondThoughts) : null;
            const byType = profile && Array.isArray(profile.byType) ? profile.byType.filter(r => r && decisionTypeLabel(r.key)) : [];
            const flagged = (list, key) => !!profile && Array.isArray(list) && list.some(r => r && r.key === key);
            const readLine = { margin: 0, fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.6 };
            const readMeta = { margin: '8px 0 0', fontSize: '12.5px', color: 'var(--text-meta)', lineHeight: 1.5 };

            return (
                <div className="ed-pagein" data-testid="profile-view" style={{ padding: '26px 0 32px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {/* Header */}
                    <div data-testid="profile-header">
                        <div style={{ fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 600, marginBottom: '6px' }}>Your decision profile</div>
                        <h1 data-testid="profile-name" style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', color: 'var(--navy)', fontWeight: 600, margin: 0 }}>{userName}</h1>
                        {!editing && (
                            <>
                                <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                    {worldLine ? (
                                        <span data-testid="profile-world-line">{worldLine}</span>
                                    ) : impersonating ? (
                                        <span>No world chosen yet</span>
                                    ) : (
                                        <span onClick={() => setWorldSheetOpen('profile')} onKeyDown={btnKey(() => setWorldSheetOpen('profile'))}
                                            role="button" tabIndex={0} className="ed-link" data-testid="profile-world-open"
                                            style={{ fontWeight: 600, padding: '4px 2px' }}>Tell us your world →</span>
                                    )}
                                    {!impersonating && (
                                        <>
                                            {' '}
                                            <span
                                                onClick={startEdit}
                                                onKeyDown={btnKey(() => startEdit())}
                                                role="button" tabIndex={0} data-testid="profile-edit"
                                                style={{ color: 'var(--gold-darker)', cursor: 'pointer', fontWeight: 600, padding: '4px 2px' }}>Edit</span>
                                        </>
                                    )}
                                </div>
                                {savedNote && (
                                    <div role="status" style={{ display: 'inline-block', marginTop: '8px', fontSize: '12px', fontWeight: 600, color: 'var(--success-ink)', background: 'var(--success-surface)', border: '1px solid var(--success-border)', borderRadius: '8px', padding: '5px 10px' }}>Profile updated ✓</div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Edit card */}
                    {editing && draft && !impersonating && (
                        <div className="ed-pagein" data-testid="user-info-form" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '16px', padding: '20px', boxShadow: 'var(--e2-light)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                                <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 }}>Edit profile</div>
                                <div
                                    onClick={cancelEdit}
                                    onKeyDown={btnKey(() => cancelEdit())}
                                    role="button" tabIndex={0}
                                    style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '4px' }}>Cancel</div>
                            </div>
                            <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: 'var(--text-meta)', lineHeight: 1.5 }}>Today's decision is picked from your industries, focus areas and stage, and Molly uses them to translate a case into your world.</p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'var(--ed-edit-grid, 1fr)', gap: '14px 16px' }}>
                                <div>
                                    <label style={fieldLabel} htmlFor="ed-prof-name">Full name</label>
                                    <input id="ed-prof-name" className="ed-input ed-input--inset" value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} style={{ padding: '12px 14px', fontSize: '14.5px' }} />
                                </div>
                                <div>
                                    <label style={fieldLabel} htmlFor="ed-prof-company">Company</label>
                                    <input id="ed-prof-company" className="ed-input ed-input--inset" value={draft.company} onChange={(e) => setDraft(d => ({ ...d, company: e.target.value }))} style={{ padding: '12px 14px', fontSize: '14.5px' }} />
                                </div>
                                <div>
                                    <label style={fieldLabel} htmlFor="ed-prof-role">Role</label>
                                    <select id="ed-prof-role" style={selectStyle} value={draft.role || ''} onChange={(e) => setDraft(d => ({ ...d, role: e.target.value || null }))}>
                                        <option value="">Not set</option>
                                        {PRACTICE_VOCAB.roles.map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={fieldLabel} htmlFor="ed-prof-stage">Company stage</label>
                                    <select id="ed-prof-stage" style={selectStyle} value={draft.companyStage || ''} onChange={(e) => setDraft(d => ({ ...d, companyStage: e.target.value || null }))}>
                                        <option value="">Not set</option>
                                        {PRACTICE_VOCAB.stages.map(([slug, label]) => <option key={slug} value={slug}>{label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={fieldLabel} htmlFor="ed-prof-size">Company size</label>
                                    <select id="ed-prof-size" style={selectStyle} value={draft.companySize || ''} onChange={(e) => setDraft(d => ({ ...d, companySize: e.target.value || null }))}>
                                        <option value="">Not set</option>
                                        {PRACTICE_VOCAB.sizes.map(size => <option key={size} value={size}>{size}</option>)}
                                    </select>
                                </div>
                            </div>
                            <fieldset style={{ border: 'none', margin: '16px 0 0', padding: 0 }}>
                                <legend style={PRACTICE_FIELD_LEGEND}>Industries · up to 3</legend>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {PRACTICE_VOCAB.industries.map(([slug, label]) => {
                                        const on = draft.industries.includes(slug);
                                        return <PracticeChip key={slug} on={on} disabled={!on && draft.industries.length >= 3} onClick={() => toggleUpToThree('industries', slug)} testid={`edit-industry-${slug}`}>{label}</PracticeChip>;
                                    })}
                                </div>
                            </fieldset>
                            <fieldset style={{ border: 'none', margin: '16px 0 0', padding: 0 }}>
                                <legend style={PRACTICE_FIELD_LEGEND}>Decisions to practise · up to 3</legend>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {PRACTICE_VOCAB.decisionTypes.map(([slug, label]) => {
                                        const on = draft.focusTypes.includes(slug);
                                        return <PracticeChip key={slug} on={on} disabled={!on && draft.focusTypes.length >= 3} onClick={() => toggleUpToThree('focusTypes', slug)} testid={`edit-focus-${slug}`}>{label}</PracticeChip>;
                                    })}
                                </div>
                            </fieldset>
                            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
                                <button onClick={saveProfile} disabled={saving} className="ed-btn ed-btn-navy" data-testid="profile-save" style={{ flex: 1, padding: '13px', fontSize: '14.5px', minHeight: '46px' }}>
                                    {saving ? 'Saving…' : 'Save changes'}
                                </button>
                                <button onClick={cancelEdit} className="ed-btn ed-btn-quiet" style={{ padding: '13px 18px', fontSize: '14.5px', minHeight: '46px' }}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 1. Picture so far — the record's totals and the reads still to open.
                        While the record is loading or unavailable, the local list
                        stands in, and says it is only that. */}
                    <ProfileReadCard testid="picture-so-far" title="Picture so far">
                        {profile ? (
                            <>
                                {profile.totals && profile.totals.decisions === 0 ? (
                                    <p style={readLine}>Work your first case and this fills in — your calls, the CEO's, and what they add up to.</p>
                                ) : (
                                    <p data-testid="picture-line" style={readLine}>{pictureSoFarLine(profile, savedNextMoves, { mentorEnabled })}</p>
                                )}
                                <ul data-testid="unlock-checklist" style={{ listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    {PROFILE_READS.map(([key, title]) => {
                                        const u = (Array.isArray(profile.unlocks) ? profile.unlocks : []).find(x => x && x.key === key);
                                        const line = u ? practiceUnlockLine(u) : null;
                                        return (
                                            <li key={key} style={{ display: 'flex', gap: '8px', fontSize: '12.5px', lineHeight: 1.5, color: line ? 'var(--text-tertiary)' : 'var(--navy)' }}>
                                                <span aria-hidden="true" style={{ color: line ? 'var(--text-faint)' : 'var(--success-ink)', flexShrink: 0 }}>{line ? '○' : '✓'}</span>
                                                <span>{line || `${title} — open`}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </>
                        ) : (
                            <>
                                <div role="status" style={{ ...readMeta, margin: '0 0 10px' }}>
                                    {decisionProfile === undefined ? 'Loading your record…' : 'Your record couldn\'t be loaded just now, so this is what this device has.'}
                                    {decisionProfile === null && (
                                        <span onClick={refreshDecisionProfile} onKeyDown={btnKey(refreshDecisionProfile)} role="button" tabIndex={0} className="ed-link" style={{ fontWeight: 700, marginLeft: '6px' }}>Try again</span>
                                    )}
                                </div>
                                {hitRate === null ? (
                                    <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                        Work your first case and this fills in — how often your call matched the CEO's.
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '13px' }}>
                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                                <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--navy)' }}>Matched the CEO's call</span>
                                                <span style={{ fontSize: '12.5px', color: 'var(--text-tertiary)' }}>{hits} of {decided.length}</span>
                                            </div>
                                            <div style={{ height: '5px', background: 'var(--track)', borderRadius: '3px', position: 'relative' }}>
                                                <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${hitRate}%`, background: 'linear-gradient(90deg, var(--gold-dark), var(--gold))', borderRadius: '3px' }}></div>
                                            </div>
                                        </div>
                                        {/* Only once there are five: two is not a
                                            window at all. Counts side by side, no verdict. */}
                                        {recentRate !== null && (
                                            <div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                                    <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--navy)' }}>Last {recent.length}</span>
                                                    <span style={{ fontSize: '12.5px', color: 'var(--text-tertiary)' }}>{recentHits} of {recent.length}</span>
                                                </div>
                                                <div style={{ height: '5px', background: 'var(--track)', borderRadius: '3px', position: 'relative' }}>
                                                    <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${recentRate}%`, background: 'linear-gradient(90deg, var(--gold-dark), var(--gold))', borderRadius: '3px' }}></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </ProfileReadCard>

                    {/* 2. Decision journal */}
                    <ProfileReadCard testid="decision-journal" title="Decision journal">
                        {decisionProfile === undefined && <div role="status" style={readMeta}>Loading your journal…</div>}
                        {decisionProfile === null && <LoadFailedCard what="your decision journal" onRetry={refreshDecisionProfile} />}
                        {record && history.length === 0 && (
                            <p style={readLine}>Every case you work is kept here — your call, the CEO's, and anything you add.</p>
                        )}
                        {record && history.length > 0 && (
                            <>
                                <ul style={{ margin: '-14px 0 0', padding: 0 }}>
                                    {history.slice(0, journalShown).map(row => (
                                        <JournalEntry key={row.attemptId} row={row}
                                            scenario={(() => { const s = libraryCase(row.scenarioId); return isPracticeCase(s) ? s : null; })()}
                                            mentorEnabled={mentorEnabled} impersonating={impersonating}
                                            note={noteOf(row)}
                                            onOpenNote={() => openNote(row)}
                                            onNoteChange={(text) => setNote(n => (n && n.attemptId === row.attemptId ? { ...n, text, status: n.status === 'saving' ? n.status : 'idle' } : n))}
                                            onSaveNote={() => saveNote(row)}
                                            onTalk={() => talkAbout(row)}
                                            onAsk={() => askAbout(row)}
                                            onWorkAgain={() => launchCase(row.scenarioId, 'journal')} />
                                    ))}
                                </ul>
                                {history.length > journalShown && (
                                    <button type="button" onClick={() => setJournalShown(n => (n < 10 ? 10 : n + 10))} className="ed-btn ed-btn-quiet" data-testid="journal-more"
                                        style={{ marginTop: '10px', minHeight: '40px', padding: '8px 16px', fontSize: '13px' }}>
                                        {journalShown < 10 ? 'See all' : 'Show 10 more'}
                                    </button>
                                )}
                            </>
                        )}
                    </ProfileReadCard>

                    {/* 3. By decision type — strongest and hardest only where §6's rule flagged them */}
                    {byType.length > 0 && (
                        <ProfileReadCard testid="profile-by-type" title="By decision type">
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {byType.map(r => (
                                    <li key={r.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '10px', fontSize: '13.5px' }}>
                                        <span style={{ color: 'var(--navy)', fontWeight: 600 }}>
                                            {decisionTypeLabel(r.key)}
                                            {flagged(profile.strengths, r.key) && <span style={{ marginLeft: '8px', fontSize: '11.5px', color: 'var(--success-ink)' }}>Strongest so far</span>}
                                            {flagged(profile.struggles, r.key) && <span style={{ marginLeft: '8px', fontSize: '11.5px', color: 'var(--gold-darker)' }}>Hardest so far</span>}
                                        </span>
                                        <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{r.called} of {r.n}</span>
                                    </li>
                                ))}
                            </ul>
                            <p style={readMeta}>On the cases you've worked so far: called = matched the CEO, or caught the bad call.</p>
                        </ProfileReadCard>
                    )}

                    {/* 4. When you differed */}
                    {passedOver.length > 0 && (
                        <ProfileReadCard testid="profile-passed-over" title="When you differed">
                            <p style={{ ...readMeta, margin: '0 0 12px' }}>In this library the CEOs' calls are usually the boldest on the table, so this looks only at the times you differed.</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {passedOver.map(a => (
                                    <div key={a.axis}>
                                        <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--navy)', marginBottom: '2px' }}>{a.title}</div>
                                        <p style={readLine}>{a.line}</p>
                                        <div style={{ fontSize: '12px', color: 'var(--text-meta)', marginTop: '2px' }}>{a.detail}</div>
                                    </div>
                                ))}
                            </div>
                        </ProfileReadCard>
                    )}

                    {/* 5. Sure vs. right — count lines only */}
                    {sureLine && (
                        <ProfileReadCard testid="profile-confidence" title="Sure vs. right">
                            <p style={readLine}>{sureLine}</p>
                        </ProfileReadCard>
                    )}

                    {/* 6. Earlier vs. recent cases */}
                    {trend && (
                        <ProfileReadCard testid="profile-trend" title="Earlier vs. recent cases">
                            {trend.map(line => <p key={line} style={readLine}>{line}</p>)}
                            <p style={readMeta}>Recent cases are picked for your world, so they may be a different mix.</p>
                        </ProfileReadCard>
                    )}

                    {/* 7. After the reveal */}
                    {afterReveal && (
                        <ProfileReadCard testid="profile-second-thoughts" title="After the reveal">
                            <p style={readLine}>{afterReveal}</p>
                        </ProfileReadCard>
                    )}

                    {/* 8. Standing — the index card, then the tiles and the menu.
                        The calls card that sat here is gone: its match count read
                        the local list, where a replay overwrites `correct`, and
                        two counts of the same thing on one page is one too many. */}
                    <div data-testid="rank-card" style={{ background: 'var(--navy)', borderRadius: '16px', padding: '22px 24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '14px' }}>
                            <div>
                                <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700, marginBottom: '4px' }}>Standing</div>
                                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', color: 'var(--text-on-navy)', fontWeight: 600 }}>{rankInfo.title}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-on-navy-dim)' }}>Decision index</div>
                                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', color: 'var(--gold)', fontWeight: 600 }}>{powerIndex}</div>
                            </div>
                        </div>
                        <div style={{ height: '4px', background: 'rgba(255,255,255,0.12)', borderRadius: '3px', position: 'relative', marginBottom: '8px' }}>
                            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${Math.min(100, Math.round(powerIndex / 90 * 100))}%`, background: 'linear-gradient(90deg, var(--gold-dark), var(--gold))', borderRadius: '3px' }}></div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: 'var(--text-on-navy-dim)' }}>
                            {/* The bar is powerIndex/90. It is not a comparison
                                with anyone, and it said it was. */}
                            <span>Progress toward CEO Ready</span>
                            <span style={{ color: 'var(--gold)' }}>CEO Ready at 90</span>
                        </div>
                    </div>

                    {/* Stat tiles */}
                    <div data-testid="stats-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        <div style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '14px 16px', textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', color: 'var(--navy)', fontWeight: 600 }}>{simulationsCount}</div>
                            <div style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: '2px' }}>Cases worked</div>
                        </div>
                        <div style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '14px 16px', textAlign: 'center' }}>
                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', color: 'var(--navy)', fontWeight: 600 }}>{streak}</div>
                            <div style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: '2px' }}>Day practice</div>
                        </div>
                        <div
                            onClick={() => setCurrentView('moves')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setCurrentView('moves'); } }}
                            role="button" tabIndex={0} className="ed-hover-gold-border"
                            style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '14px 16px', textAlign: 'center', cursor: 'pointer' }}>
                            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', color: 'var(--navy)', fontWeight: 600 }}>{movesCount}</div>
                            <div style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: '2px' }}>Next moves saved</div>
                        </div>
                    </div>

                    {/* Menu */}
                    <div data-testid="menu-list" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', overflow: 'hidden' }}>
                        {[
                            // Playlists travels with the library: a playlist is a
                            // sequence of interviews, and with the library shut it
                            // is a screen for arranging nothing.
                            ...(canSeeLibrary(currentUser) ? [
                                { label: 'Interviews', count: null, action: () => setCurrentView('interviews') },
                                { label: 'Playlists', count: null, action: () => setCurrentView('playlists') },
                            ] : []),
                            // "Sessions with Molly" read like a list of past ones
                            // and opened a new chat, one line above the list of
                            // past ones.
                            { label: 'Talk to Molly', count: null, action: () => setCurrentView('ci-mentor') },
                            // What Molly reads about them, in their words (docs/design/06).
                            { label: 'Who you are', count: null, action: () => setCurrentView('who-you-are') },
                            { label: 'Your prompt', count: memberPromptOf(userProfile).lines.length || null, action: () => setCurrentView('your-prompt') },
                            // Molly's lessons and what they left behind (docs/design/06).
                            ...(mentorEnabled ? [{ label: 'AI at work · lessons', count: null, action: () => setCurrentView('molly-learn') }] : []),
                            { label: 'Prompt library', count: promptLibraryOf(userProfile).length || null, action: () => setCurrentView('prompt-library') },
                            { label: 'Your conversations', count: null, action: () => { setConvosBackTo('profile'); setCurrentView('conversations'); } },
                            { label: 'Archived conversations', count: null, action: () => setCurrentView('archives') },
                            { label: 'Transformative moments', count: null, action: () => setCurrentView('moments') },
                            { label: 'My Pivot Playbook', count: playbookCount, action: () => setCurrentView('playbook') },
                            { label: 'Saved next moves', count: movesCount, action: () => setCurrentView('moves') },
                            // Always available, never throttled: the one entry point
                            // that doesn't wait for a journey to end.
                            { label: 'Send feedback', count: null, action: () => requestFeedback('manual', { type: 'view', id: 'profile', extra: { view: 'profile' } }, { force: true }) },
                            { label: 'Data & privacy', count: null, action: () => setCurrentView('policy') },
                            // The other doors this person holds a hat for (docs/IDENTITY.md).
                            // Also in the nav now; kept here because this is the
                            // list of every door this person holds, and the same
                            // word has to name it in both places.
                            ...(otherDoors(currentUser).portal ? [{ label: 'Mentor desk', count: null, action: () => openDoor('portal') }] : []),
                            ...(otherDoors(currentUser).console ? [{ label: 'Admin console', count: null, action: () => openDoor('console') }] : []),
                            { label: 'Sign out', count: null, action: handleLogout, signout: true }
                        ].map((item, index, arr) => (
                            <div key={item.label}
                                onClick={item.action}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.action(); } }}
                                role="button" tabIndex={0} className="ed-menu-row" data-testid={`menu-item-${index}`}
                                style={{ padding: '15px 20px', borderBottom: index < arr.length - 1 ? '1px solid var(--hairline)' : 'none', display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: item.signout ? 'var(--gold-darker)' : 'var(--navy)', cursor: 'pointer', minHeight: '48px', alignItems: 'center' }}>
                                <span>{item.label}</span>
                                <span style={{ color: 'var(--text-faint)' }}>{item.count !== null ? `${item.count} →` : '→'}</span>
                            </div>
                        ))}
                    </div>

                </div>
            );
        };

        // ============================================
        // INTERVIEW LIBRARY
        // ============================================
        // Scaffolding for the full interview archive: browse, search, filter,
        // favourite, collect into playlists, and play the WHOLE recording (the
        // simulator's short clips are a separate content path and untouched).
        // The visual design is deliberately plain here — docs/DESIGN_SPEC_INTERVIEWS.md
        // is the brief for the real treatment.

        // One place that knows how to talk to the library API, so every view
        // gets the same auth header, the same JSON handling, and the same
        // "expired session" behaviour.
        const useLibraryApi = () => {
            const { authToken, handleSessionExpired } = useApp();
            return useCallback(async (path, options = {}) => {
                if (!API_URL || !authToken) return null;
                const res = await fetch(`${API_URL}${path}`, {
                    ...options,
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${authToken}`,
                        ...(options.headers || {})
                    }
                });
                if (res.status === 401) { handleSessionExpired?.(); return null; }
                const body = await res.json().catch(() => null);
                if (!body || body.success !== true) {
                    throw new Error(body?.error || `Request failed (${res.status})`);
                }
                return body.data;
            }, [authToken, handleSessionExpired]);
        };

        // Cover art is an R2 key under covers/, served by the worker. Mirrors
        // fixAudioPath — the same normalisation problem, a different prefix.
        const coverUrl = (key) => {
            if (!key) return null;
            if (key.startsWith('http')) return key;
            const m = key.match(/\/api\/images\/(.+)$/);
            const bare = m ? m[1] : key;
            return API_URL ? `${API_URL}/api/images/${bare}` : null;
        };

        const CoverArt = ({ src, title, height = 150, radius = '12px 12px 0 0' }) => {
            const [failed, setFailed] = useState(false);
            const url = coverUrl(src);
            // Initials, not a broken-image icon — an interview with no art yet
            // should still look deliberate.
            if (!url || failed) {
                const initials = (title || '?').split(' ').filter(Boolean).slice(0, 2)
                    .map(w => w[0]).join('').toUpperCase();
                return (
                    <div aria-hidden="true" style={{
                        height: `${height}px`, borderRadius: radius,
                        background: 'linear-gradient(135deg, var(--navy), var(--navy-light, #24304a))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--gold)', fontFamily: 'var(--font-heading)', fontSize: '26px', letterSpacing: '0.06em'
                    }}>{initials}</div>
                );
            }
            return (
                <img src={url} alt="" loading="lazy" onError={() => setFailed(true)}
                    style={{ width: '100%', height: `${height}px`, objectFit: 'cover', borderRadius: radius, display: 'block' }} />
            );
        };

        const FavoriteButton = ({ active, onToggle, label }) => (
            <div role="button" tabIndex={0}
                aria-pressed={active}
                aria-label={active ? `Remove ${label} from favorites` : `Add ${label} to favorites`}
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                onKeyDown={btnKey((e) => { e.stopPropagation(); onToggle(); })}
                style={{
                    width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    background: active ? 'var(--gold)' : 'rgba(255,255,255,0.9)',
                    color: active ? 'var(--navy)' : 'var(--text-tertiary)',
                    border: '1px solid var(--border-card)', fontSize: '14px', lineHeight: 1
                }}>
                {active ? '★' : '☆'}
            </div>
        );

        /**
         * Full-length player. AudioClipCard is built for 20-second excerpts —
         * a 50-minute interview needs a scrubber, a readout and skip controls.
         */
        const FullAudioPlayer = ({ src, title }) => {
            const ref = useRef(null);
            const [playing, setPlaying] = useState(false);
            const [pos, setPos] = useState(0);
            const [dur, setDur] = useState(0);

            useEffect(() => () => { if (ref.current) ref.current.pause(); }, []);

            const toggle = () => {
                const el = ref.current;
                if (!el) return;
                if (playing) { el.pause(); setPlaying(false); }
                else { el.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
            };
            const skip = (delta) => {
                const el = ref.current;
                if (!el || !isFinite(el.duration)) return;
                el.currentTime = Math.max(0, Math.min(el.duration, el.currentTime + delta));
            };

            if (!src) return null;
            const pct = dur > 0 ? (pos / dur) * 100 : 0;

            return (
                <div data-testid="full-audio-player" style={{ background: 'var(--navy)', borderRadius: '14px', padding: '16px 18px', color: 'var(--text-on-navy)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div role="button" tabIndex={0} onClick={toggle} onKeyDown={btnKey(toggle)}
                            aria-label={playing ? 'Pause interview' : 'Play interview'}
                            style={{
                                width: '46px', height: '46px', borderRadius: '50%', flexShrink: 0, cursor: 'pointer',
                                background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))', color: 'var(--navy)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px'
                            }}>
                            {playing ? '❚❚' : '▶'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 600, marginBottom: '7px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {title}
                            </div>
                            <input type="range" min="0" max={dur || 0} step="1" value={pos}
                                aria-label="Seek within the interview"
                                onChange={(e) => { const v = Number(e.target.value); if (ref.current) ref.current.currentTime = v; setPos(v); }}
                                style={{ width: '100%', accentColor: 'var(--gold)', background: `linear-gradient(to right, var(--gold) ${pct}%, rgba(255,255,255,0.25) ${pct}%)` }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--gold-rose, rgba(255,255,255,0.7))', marginTop: '3px' }}>
                                <span>{fmtTime(pos)}</span>
                                <span>{dur ? fmtTime(dur) : '—:—'}</span>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        {[[-15, '↺ 15s'], [30, '30s ↻']].map(([d, lbl]) => (
                            <div key={d} role="button" tabIndex={0} onClick={() => skip(d)} onKeyDown={btnKey(() => skip(d))}
                                style={{
                                    flex: 1, textAlign: 'center', padding: '7px', borderRadius: '8px', cursor: 'pointer',
                                    border: '1px solid rgba(255,255,255,0.22)', fontSize: '11.5px', letterSpacing: '0.04em'
                                }}>{lbl}</div>
                        ))}
                    </div>
                    <audio ref={ref} src={src} preload="metadata"
                        onLoadedMetadata={(e) => setDur(isFinite(e.target.duration) ? e.target.duration : 0)}
                        onTimeUpdate={(e) => setPos(e.target.currentTime)}
                        onEnded={() => setPlaying(false)}
                        style={{ display: 'none' }} />
                </div>
            );
        };

        const InterviewCard = ({ item, onOpen, onToggleFavorite }) => (
            <div data-testid={`interview-card-${item.id}`} className="ed-hover-gold-border"
                role="button" tabIndex={0} onClick={onOpen} onKeyDown={btnKey(onOpen)}
                style={{
                    background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '12px',
                    overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.15s ease', position: 'relative'
                }}>
                <div style={{ position: 'relative' }}>
                    <CoverArt src={item.coverImage} title={item.ceo} height={128} />
                    <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                        <FavoriteButton active={item.isFavorite} onToggle={onToggleFavorite} label={item.title} />
                    </div>
                    {item.hasVideo && (
                        <span style={{
                            position: 'absolute', bottom: '8px', left: '8px', background: 'rgba(11,20,38,0.85)',
                            color: 'var(--gold)', fontSize: '9.5px', letterSpacing: '0.1em', fontWeight: 700,
                            padding: '3px 7px', borderRadius: '4px', textTransform: 'uppercase'
                        }}>Video</span>
                    )}
                </div>
                <div style={{ padding: '11px 12px 13px' }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1.32, marginBottom: '3px' }}>
                        {item.title}
                    </div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                        {item.ceo}{item.company ? ` · ${item.company}` : ''}
                    </div>
                    {item.durationS ? (
                        <div style={{ fontSize: '11px', color: 'var(--text-meta)', marginTop: '4px' }}>{fmtTime(item.durationS)}</div>
                    ) : null}
                </div>
            </div>
        );

        /** Add-to-playlist sheet. Creates a playlist inline so an empty account isn't a dead end. */
        const PlaylistSheet = ({ interview, memberOf, onClose, onChanged }) => {
            const api = useLibraryApi();
            const { showToast } = useApp();
            const [lists, setLists] = useState(null);
            const [inSet, setInSet] = useState(new Set(memberOf || []));
            const [newName, setNewName] = useState('');
            const [busy, setBusy] = useState(false);

            useEffect(() => {
                api('/api/playlists').then(d => setLists(d?.items || [])).catch(() => setLists([]));
            }, [api]);

            const toggle = async (pl) => {
                const has = inSet.has(pl.id);
                setBusy(true);
                try {
                    if (has) {
                        await api(`/api/playlists/${pl.id}/items/${interview.id}`, { method: 'DELETE' });
                        setInSet(s => { const n = new Set(s); n.delete(pl.id); return n; });
                    } else {
                        await api(`/api/playlists/${pl.id}/items`, {
                            method: 'POST', body: JSON.stringify({ interviewId: interview.id })
                        });
                        setInSet(s => new Set(s).add(pl.id));
                    }
                    onChanged?.();
                } catch (e) {
                    showToast(e.message || 'Could not update the playlist', 'error');
                } finally { setBusy(false); }
            };

            const create = async () => {
                const name = newName.trim();
                if (!name) return;
                setBusy(true);
                try {
                    const created = await api('/api/playlists', { method: 'POST', body: JSON.stringify({ name }) });
                    await api(`/api/playlists/${created.id}/items`, {
                        method: 'POST', body: JSON.stringify({ interviewId: interview.id })
                    });
                    setLists(l => [{ id: created.id, name, itemCount: 1 }, ...(l || [])]);
                    setInSet(s => new Set(s).add(created.id));
                    setNewName('');
                    onChanged?.();
                } catch (e) {
                    showToast(e.message || 'Could not create the playlist', 'error');
                } finally { setBusy(false); }
            };

            return (
                <ModalPortal onEscape={onClose}>
                    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(11,20,38,0.55)', zIndex: 900 }} />
                    <div role="dialog" aria-label="Add to playlist" data-testid="playlist-sheet"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 0, width: '100%', maxWidth: '430px',
                            background: 'var(--white)', borderRadius: '18px 18px 0 0', padding: '20px 20px 28px', zIndex: 901, maxHeight: '76vh', overflowY: 'auto'
                        }}>
                        <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', color: 'var(--navy)', marginBottom: '3px' }}>Add to playlist</div>
                        <div style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', marginBottom: '15px' }}>{interview.title}</div>

                        {lists === null && <div style={{ fontSize: '13px', color: 'var(--text-meta)' }}>Loading…</div>}
                        {lists && lists.length === 0 && (
                            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                                You have no playlists yet. Name one below.
                            </div>
                        )}
                        {(lists || []).map(pl => (
                            <div key={pl.id} role="button" tabIndex={0} onClick={() => !busy && toggle(pl)} onKeyDown={btnKey(() => !busy && toggle(pl))}
                                data-testid={`playlist-option-${pl.id}`}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', marginBottom: '7px',
                                    border: '1px solid var(--border-card)', borderRadius: '10px', cursor: busy ? 'wait' : 'pointer',
                                    background: inSet.has(pl.id) ? 'var(--cream-dark)' : 'var(--white)'
                                }}>
                                <span aria-hidden="true" style={{ color: inSet.has(pl.id) ? 'var(--gold-darker)' : 'var(--text-meta)', fontSize: '15px' }}>
                                    {inSet.has(pl.id) ? '✓' : '＋'}
                                </span>
                                <span style={{ flex: 1, fontSize: '13.5px', color: 'var(--navy)', fontWeight: 600 }}>{pl.name}</span>
                                <span style={{ fontSize: '11.5px', color: 'var(--text-meta)' }}>{pl.itemCount || 0}</span>
                            </div>
                        ))}

                        <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                            <input value={newName} onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
                                placeholder="New playlist name" aria-label="New playlist name"
                                style={{ flex: 1, padding: '11px 12px', borderRadius: '9px', border: '1px solid var(--border-card)', fontSize: '13.5px', fontFamily: 'inherit' }} />
                            <button onClick={create} disabled={busy || !newName.trim()}
                                style={{
                                    padding: '11px 15px', borderRadius: '9px', border: 'none', cursor: 'pointer',
                                    background: 'var(--navy)', color: 'var(--text-on-navy)', fontSize: '13px', fontWeight: 600,
                                    opacity: (busy || !newName.trim()) ? 0.5 : 1
                                }}>Create</button>
                        </div>

                        <div role="button" tabIndex={0} onClick={onClose} onKeyDown={btnKey(onClose)}
                            style={{ textAlign: 'center', marginTop: '16px', fontSize: '13.5px', color: 'var(--gold-darker)', fontWeight: 600, cursor: 'pointer' }}>
                            Done
                        </div>
                    </div>
                </ModalPortal>
            );
        };

        const InterviewDetail = ({ id, onBack, onFavoriteChanged }) => {
            const api = useLibraryApi();
            const { showToast } = useApp();
            const [item, setItem] = useState(null);
            const [error, setError] = useState(null);
            const [sheetOpen, setSheetOpen] = useState(false);

            useEffect(() => {
                let cancelled = false;
                api(`/api/interviews/${id}`)
                    .then(d => { if (!cancelled) setItem(d); })
                    .catch(e => { if (!cancelled) setError(e.message || 'Could not load this interview'); });
                return () => { cancelled = true; };
            }, [api, id]);

            const toggleFavorite = async () => {
                if (!item) return;
                const next = !item.isFavorite;
                setItem(p => ({ ...p, isFavorite: next }));    // optimistic
                try {
                    await api(`/api/interviews/${item.id}/favorite`, { method: next ? 'PUT' : 'DELETE' });
                    onFavoriteChanged?.(item.id, next);
                } catch (e) {
                    setItem(p => ({ ...p, isFavorite: !next }));
                    showToast(e.message || 'Could not update favorites', 'error');
                }
            };

            const back = (
                <div role="button" tabIndex={0} onClick={onBack} onKeyDown={btnKey(onBack)}
                    style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                    ← All interviews
                </div>
            );

            if (error) return <div className="ed-pagein" style={{ padding: '26px 0' }}>{back}<p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{error}</p></div>;
            if (!item) return <div className="ed-pagein" style={{ padding: '26px 0' }}>{back}<p style={{ fontSize: '13.5px', color: 'var(--text-meta)' }}>Loading…</p></div>;

            const audioSrc = fixAudioPath(item.audioFileFull);

            return (
                <div className="ed-pagein" data-testid="interview-detail" style={{ padding: '26px 0 32px' }}>
                    {back}
                    <CoverArt src={item.coverImage} title={item.ceo} height={190} radius="14px" />

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', margin: '16px 0 4px' }}>
                        <h2 style={{ flex: 1, fontFamily: 'var(--font-heading)', fontSize: '22px', color: 'var(--navy)', fontWeight: 600, margin: 0, lineHeight: 1.28 }}>
                            {item.title}
                        </h2>
                        <FavoriteButton active={item.isFavorite} onToggle={toggleFavorite} label={item.title} />
                    </div>
                    <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)', marginBottom: '2px' }}>
                        {item.ceo}{item.ceoTitle ? `, ${item.ceoTitle}` : ''}
                    </div>
                    <div style={{ fontSize: '12.5px', color: 'var(--text-meta)', marginBottom: '16px' }}>
                        {[item.company, item.year, item.durationS ? fmtTime(item.durationS) : null].filter(Boolean).join(' · ')}
                    </div>

                    {audioSrc && <FullAudioPlayer src={audioSrc} title={`${item.ceo} — full interview`} />}

                    {(item.videoUrl || item.videoFile) && (
                        <div data-testid="interview-video" style={{ marginTop: '14px', borderRadius: '12px', overflow: 'hidden', background: 'var(--navy)' }}>
                            {item.videoUrl ? (
                                <iframe src={item.videoUrl} title={`${item.title} — video`} allowFullScreen
                                    style={{ width: '100%', aspectRatio: '16 / 9', border: 'none', display: 'block' }} />
                            ) : (
                                <video src={fixAudioPath(item.videoFile)} controls preload="metadata"
                                    style={{ width: '100%', display: 'block' }} />
                            )}
                        </div>
                    )}

                    <div role="button" tabIndex={0} onClick={() => setSheetOpen(true)} onKeyDown={btnKey(() => setSheetOpen(true))}
                        data-testid="add-to-playlist"
                        style={{
                            marginTop: '14px', padding: '12px', textAlign: 'center', cursor: 'pointer',
                            border: '1px solid var(--border-card)', borderRadius: '10px',
                            fontSize: '13.5px', fontWeight: 600, color: 'var(--navy)', background: 'var(--white)'
                        }}>
                        ＋ Add to playlist
                    </div>

                    {item.topics?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '16px' }}>
                            {item.topics.map(t => (
                                <span key={t} style={{
                                    fontSize: '11px', padding: '4px 10px', borderRadius: '20px',
                                    background: 'var(--cream-dark)', color: 'var(--text-secondary)'
                                }}>{t}</span>
                            ))}
                        </div>
                    )}

                    {item.description && (
                        <div style={{ marginTop: '18px' }}>
                            <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '8px' }}>
                                About this interview
                            </div>
                            {/* Author copy: paragraph breaks preserved, no HTML rendered. */}
                            {item.description.split(/\n{2,}/).map((para, i) => (
                                <p key={i} style={{ fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.68, margin: '0 0 11px' }}>{para}</p>
                            ))}
                        </div>
                    )}

                    {sheetOpen && (
                        <PlaylistSheet interview={item} memberOf={item.inPlaylists}
                            onClose={() => setSheetOpen(false)} onChanged={() => {}} />
                    )}
                </div>
            );
        };

        const InterviewsView = () => {
            const api = useLibraryApi();
            const { setCurrentView, showToast } = useApp();
            const [items, setItems] = useState(null);
            const [total, setTotal] = useState(0);
            const [facets, setFacets] = useState(null);
            const [query, setQuery] = useState('');
            const [topic, setTopic] = useState(null);
            const [favesOnly, setFavesOnly] = useState(false);
            const [openId, setOpenId] = useState(null);
            const [loadFailed, setLoadFailed] = useState(false);
            const [reloadTick, setReloadTick] = useState(0);
            // The list asked for 48 and then told the user how many there
            // ARE — "181 interviews" over a grid holding 48, with no next page
            // and nothing saying so. The endpoint has always taken an offset.
            const PAGE = 48;
            const [page, setPage] = useState(0);

            // Debounced so typing doesn't fire a request per keystroke.
            const [debounced, setDebounced] = useState('');
            useEffect(() => {
                const t = setTimeout(() => setDebounced(query.trim()), 250);
                return () => clearTimeout(t);
            }, [query]);

            useEffect(() => {
                api('/api/interviews/filters').then(setFacets).catch(() => setFacets(null));
            }, [api]);

            useEffect(() => {
                let cancelled = false;
                const p = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE) });
                if (debounced) p.set('q', debounced);
                if (topic) p.set('topic', topic);
                if (favesOnly) p.set('favorites', '1');
                // Page 0 clears; later pages append, so "Show more" adds to the
                // list rather than replacing it.
                if (page === 0) setItems(null);
                setLoadFailed(false);
                api(`/api/interviews?${p}`)
                    .then(d => {
                        if (cancelled) return;
                        const batch = d?.items || [];
                        setItems(prev => (page === 0 || !prev) ? batch : [...prev, ...batch]);
                        setTotal(d?.total || 0);
                    })
                    // NOT the empty state: "the library is being prepared" is a
                    // claim about the library, and a dropped request is not
                    // evidence for it.
                    .catch(() => { if (!cancelled) { if (page === 0) { setItems([]); setTotal(0); } setLoadFailed(true); } });
                return () => { cancelled = true; };
            }, [api, debounced, topic, favesOnly, reloadTick, page]);

            // A changed filter starts again at the first page.
            useEffect(() => { setPage(0); }, [debounced, topic, favesOnly]);

            const toggleFavorite = async (item) => {
                const next = !item.isFavorite;
                setItems(list => list.map(i => i.id === item.id ? { ...i, isFavorite: next } : i));
                try {
                    await api(`/api/interviews/${item.id}/favorite`, { method: next ? 'PUT' : 'DELETE' });
                } catch (e) {
                    setItems(list => list.map(i => i.id === item.id ? { ...i, isFavorite: !next } : i));
                    showToast(e.message || 'Could not update favorites', 'error');
                }
            };

            if (openId) {
                return <InterviewDetail id={openId} onBack={() => setOpenId(null)}
                    onFavoriteChanged={(id, fav) => setItems(l => (l || []).map(i => i.id === id ? { ...i, isFavorite: fav } : i))} />;
            }

            const chip = (label, active, onClick, testid) => (
                <div key={label} role="button" tabIndex={0} aria-pressed={active} data-testid={testid}
                    onClick={onClick} onKeyDown={btnKey(onClick)}
                    style={{
                        padding: '6px 13px', borderRadius: '20px', cursor: 'pointer', whiteSpace: 'nowrap',
                        fontSize: '12px', fontWeight: 600,
                        background: active ? 'var(--navy)' : 'var(--white)',
                        color: active ? 'var(--text-on-navy)' : 'var(--text-secondary)',
                        border: `1px solid ${active ? 'var(--navy)' : 'var(--border-card)'}`
                    }}>{label}</div>
            );

            return (
                <div className="ed-pagein" data-testid="interviews-view" style={{ padding: '26px 0 32px' }}>
                    <div role="button" tabIndex={0} onClick={() => setCurrentView('profile')} onKeyDown={btnKey(() => setCurrentView('profile'))}
                        style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                        ← Profile
                    </div>
                    <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                        Interviews
                    </h2>
                    <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
                        Every conversation in the library, in full.
                        <span role="button" tabIndex={0} onClick={() => setCurrentView('playlists')} onKeyDown={btnKey(() => setCurrentView('playlists'))}
                            style={{ color: 'var(--gold-darker)', cursor: 'pointer', fontWeight: 600 }}> Your playlists →</span>
                    </p>

                    <input value={query} onChange={(e) => setQuery(e.target.value)}
                        type="search" placeholder="Search by name, company or topic" aria-label="Search interviews"
                        data-testid="interviews-search"
                        style={{
                            width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid var(--border-card)',
                            fontSize: '14px', fontFamily: 'inherit', marginBottom: '11px', background: 'var(--white)'
                        }} />

                    <div style={{ display: 'flex', gap: '7px', overflowX: 'auto', paddingBottom: '4px', marginBottom: '16px' }}>
                        {chip('All', !favesOnly && !topic, () => { setFavesOnly(false); setTopic(null); }, 'chip-all')}
                        {chip('★ Favorites', favesOnly, () => setFavesOnly(f => !f), 'chip-favorites')}
                        {(facets?.topics || []).slice(0, 12).map(t =>
                            chip(t.value, topic === t.value, () => setTopic(cur => cur === t.value ? null : t.value)))}
                    </div>

                    {items === null && <div style={{ fontSize: '13.5px', color: 'var(--text-meta)' }}>Loading…</div>}

                    {loadFailed && <LoadFailedCard what="the interview library" onRetry={() => setReloadTick(t => t + 1)} />}

                    {!loadFailed && items && items.length === 0 && (
                        <div data-testid="interviews-empty" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '24px', textAlign: 'center' }}>
                            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                {favesOnly ? 'You have not favorited an interview yet.'
                                    : (debounced || topic) ? 'No interviews match that search.'
                                    : 'The interview library is being prepared. Check back shortly.'}
                            </div>
                        </div>
                    )}

                    {items && items.length > 0 && (
                        <>
                            <div style={{ fontSize: '11.5px', color: 'var(--text-meta)', marginBottom: '10px' }}>
                                {items.length < total
                                    ? `Showing ${items.length} of ${total} interviews`
                                    : `${total} interview${total === 1 ? '' : 's'}`}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                {items.map(it => (
                                    <InterviewCard key={it.id} item={it}
                                        onOpen={() => setOpenId(it.id)}
                                        onToggleFavorite={() => toggleFavorite(it)} />
                                ))}
                            </div>
                            {items.length < total && (
                                <div
                                    onClick={() => setPage(pg => pg + 1)}
                                    onKeyDown={btnKey(() => setPage(pg => pg + 1))}
                                    role="button" tabIndex={0} data-testid="interviews-more"
                                    style={{ marginTop: '16px', textAlign: 'center', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-card)', color: 'var(--gold-darker)', fontWeight: 600, cursor: 'pointer' }}>
                                    Show more ({total - items.length} to go)
                                </div>
                            )}
                        </>
                    )}
                </div>
            );
        };

        const PlaylistsView = () => {
            const api = useLibraryApi();
            const { setCurrentView, showToast } = useApp();
            const [lists, setLists] = useState(null);
            const [open, setOpen] = useState(null);
            const [newName, setNewName] = useState('');
            const [loadFailed, setLoadFailed] = useState(false);
            const [confirmDelete, setConfirmDelete] = useState(null);

            const load = useCallback(() => {
                setLoadFailed(false);
                api('/api/playlists')
                    .then(d => setLists(d?.items || []))
                    // "No playlists yet" is a statement about the user's own
                    // work. A failed request is not evidence for it.
                    .catch(() => { setLists([]); setLoadFailed(true); });
            }, [api]);
            useEffect(() => { load(); }, [load]);

            const create = async () => {
                const name = newName.trim();
                if (!name) return;
                try {
                    await api('/api/playlists', { method: 'POST', body: JSON.stringify({ name }) });
                    setNewName('');
                    load();
                } catch (e) { showToast(e.message || 'Could not create the playlist', 'error'); }
            };

            const openList = async (id) => {
                try { setOpen(await api(`/api/playlists/${id}`)); }
                catch (e) { showToast(e.message || 'Could not open the playlist', 'error'); }
            };

            const removeItem = async (interviewId) => {
                try {
                    await api(`/api/playlists/${open.id}/items/${interviewId}`, { method: 'DELETE' });
                    setOpen(p => ({ ...p, items: p.items.filter(i => i.id !== interviewId) }));
                    load();
                } catch (e) { showToast(e.message || 'Could not remove that interview', 'error'); }
            };

            const destroy = async (id) => {
                try {
                    await api(`/api/playlists/${id}`, { method: 'DELETE' });
                    setOpen(null);
                    load();
                } catch (e) { showToast(e.message || 'Could not delete the playlist', 'error'); }
            };

            if (open) {
                return (
                    <div className="ed-pagein" data-testid="playlist-detail" style={{ padding: '26px 0 32px' }}>
                        <div role="button" tabIndex={0} onClick={() => setOpen(null)} onKeyDown={btnKey(() => setOpen(null))}
                            style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                            ← All playlists
                        </div>
                        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 4px' }}>
                            {open.name}
                        </h2>
                        <div style={{ fontSize: '12.5px', color: 'var(--text-meta)', marginBottom: '18px' }}>
                            {open.items.length} interview{open.items.length === 1 ? '' : 's'}
                        </div>

                        {open.items.length === 0 && (
                            <div style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '22px', textAlign: 'center', fontSize: '14px', color: 'var(--text-secondary)' }}>
                                Nothing here yet. Add interviews from the library.
                            </div>
                        )}

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                            {open.items.map(it => (
                                <div key={it.id} style={{ display: 'flex', gap: '11px', alignItems: 'center', background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '11px', padding: '9px 11px' }}>
                                    <div style={{ width: '48px', flexShrink: 0 }}>
                                        <CoverArt src={it.coverImage} title={it.ceo} height={48} radius="7px" />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1.3 }}>{it.title}</div>
                                        <div style={{ fontSize: '11.5px', color: 'var(--text-tertiary)' }}>{it.ceo}{it.durationS ? ` · ${fmtTime(it.durationS)}` : ''}</div>
                                    </div>
                                    <div role="button" tabIndex={0} aria-label={`Remove ${it.title} from ${open.name}`}
                                        onClick={() => removeItem(it.id)} onKeyDown={btnKey(() => removeItem(it.id))}
                                        style={{ padding: '6px 9px', fontSize: '15px', color: 'var(--text-meta)', cursor: 'pointer' }}>×</div>
                                </div>
                            ))}
                        </div>

                        {/* Asks first. Deleting a playlist was a single tap with no
                            confirmation and no undo — one mis-tap and a list the
                            user had built over weeks was gone. */}
                        {confirmDelete === open.id ? (
                            <div style={{ marginTop: '22px', textAlign: 'center', fontSize: '13px' }}>
                                <div style={{ color: 'var(--text-body)', marginBottom: '8px' }}>
                                    Delete “{open.name}”? The interviews stay in the library.
                                </div>
                                <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                                    <span role="button" tabIndex={0} data-testid="delete-playlist-confirm"
                                        onClick={() => destroy(open.id)} onKeyDown={btnKey(() => destroy(open.id))}
                                        style={{ color: 'var(--error-accent, #b3392f)', cursor: 'pointer', fontWeight: 700, padding: '4px 8px' }}>
                                        Delete it
                                    </span>
                                    <span role="button" tabIndex={0}
                                        onClick={() => setConfirmDelete(null)} onKeyDown={btnKey(() => setConfirmDelete(null))}
                                        style={{ color: 'var(--text-tertiary)', cursor: 'pointer', padding: '4px 8px' }}>
                                        Keep it
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div role="button" tabIndex={0} onClick={() => setConfirmDelete(open.id)} onKeyDown={btnKey(() => setConfirmDelete(open.id))}
                                data-testid="delete-playlist"
                                style={{ marginTop: '22px', textAlign: 'center', fontSize: '13px', color: 'var(--error-accent, #b3392f)', cursor: 'pointer', fontWeight: 600 }}>
                                Delete this playlist
                            </div>
                        )}
                    </div>
                );
            }

            return (
                <div className="ed-pagein" data-testid="playlists-view" style={{ padding: '26px 0 32px' }}>
                    <div role="button" tabIndex={0} onClick={() => setCurrentView('interviews')} onKeyDown={btnKey(() => setCurrentView('interviews'))}
                        style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                        ← Interviews
                    </div>
                    <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                        Playlists
                    </h2>
                    <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 16px' }}>
                        Group interviews into sequences worth returning to.
                    </p>

                    <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
                        <input value={newName} onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
                            placeholder="New playlist name" aria-label="New playlist name" data-testid="new-playlist-name"
                            style={{ flex: 1, padding: '11px 12px', borderRadius: '9px', border: '1px solid var(--border-card)', fontSize: '13.5px', fontFamily: 'inherit' }} />
                        <button onClick={create} disabled={!newName.trim()}
                            style={{
                                padding: '11px 16px', borderRadius: '9px', border: 'none', cursor: 'pointer',
                                background: 'var(--navy)', color: 'var(--text-on-navy)', fontSize: '13px', fontWeight: 600,
                                opacity: newName.trim() ? 1 : 0.5
                            }}>Create</button>
                    </div>

                    {lists === null && <div style={{ fontSize: '13.5px', color: 'var(--text-meta)' }}>Loading…</div>}
                    {loadFailed && <LoadFailedCard what="your playlists" onRetry={load} />}
                    {!loadFailed && lists && lists.length === 0 && (
                        <div data-testid="playlists-empty" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '24px', textAlign: 'center', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            No playlists yet. Name one above, then add interviews from the library.
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {(lists || []).map(pl => (
                            <div key={pl.id} role="button" tabIndex={0} data-testid={`playlist-row-${pl.id}`}
                                onClick={() => openList(pl.id)} onKeyDown={btnKey(() => openList(pl.id))}
                                className="ed-hover-gold-border"
                                style={{ display: 'flex', gap: '12px', alignItems: 'center', background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '10px 12px', cursor: 'pointer' }}>
                                <div style={{ width: '52px', flexShrink: 0 }}>
                                    <CoverArt src={pl.coverImage} title={pl.name} height={52} radius="8px" />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)' }}>{pl.name}</div>
                                    <div style={{ fontSize: '11.5px', color: 'var(--text-tertiary)' }}>
                                        {pl.itemCount} interview{pl.itemCount === 1 ? '' : 's'}
                                    </div>
                                </div>
                                <span aria-hidden="true" style={{ color: 'var(--text-meta)', fontSize: '17px' }}>›</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        };

        // ============================================
        // YOUR CONVERSATIONS
        // ============================================
        // The Data & privacy page has always said "the exchange is saved to your
        // account so you can come back to it" and "You can delete any
        // conversation yourself at any time, and it goes immediately." Neither
        // was reachable: GET /api/conversations, GET /api/conversations/:id and
        // DELETE /api/conversations/:id all existed in the worker and were
        // called by nothing. For the first 90 days a transcript was stored,
        // unreadable and undeletable, while a page in the product said
        // otherwise. (After 90 days it moves to Archives, which was reachable.)
        const ConversationsView = () => {
            const { authToken, setCurrentView, showToast, convosBackTo, setMentorResume, setMentorThread } = useApp();
            const [items, setItems] = useState(null);     // null = loading
            const [failed, setFailed] = useState(false);
            const [tick, setTick] = useState(0);
            const [open, setOpen] = useState(null);       // { convo, messages }
            const [busyId, setBusyId] = useState(null);
            const [confirmId, setConfirmId] = useState(null);

            useEffect(() => {
                if (!API_URL || !authToken) { setItems([]); return; }
                let cancelled = false;
                setFailed(false);
                fetch(`${API_URL}/api/conversations?limit=50`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => r.json())
                    .then(res => {
                        if (cancelled) return;
                        if (res && res.success) setItems(res.data.items || []);
                        else { setItems([]); setFailed(true); }
                    })
                    .catch(() => { if (!cancelled) { setItems([]); setFailed(true); } });
                return () => { cancelled = true; };
            }, [authToken, tick]);

            const openOne = async (id) => {
                setBusyId(id);
                try {
                    const res = await fetch(`${API_URL}/api/conversations/${id}`, {
                        headers: { Authorization: `Bearer ${authToken}` }
                    }).then(r => r.json());
                    if (!res || !res.success) throw new Error((res && res.error) || 'could not open it');
                    setOpen(res.data);
                } catch (e) {
                    showToast('We could not open that conversation.', 'error');
                } finally {
                    setBusyId(null);
                }
            };

            const remove = async (id) => {
                setBusyId(id);
                try {
                    const res = await fetch(`${API_URL}/api/conversations/${id}`, {
                        method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` }
                    }).then(r => r.json());
                    if (!res || !res.success) throw new Error((res && res.error) || 'could not delete it');
                    setItems(list => (list || []).filter(c => c.id !== id));
                    if (open && open.id === id) setOpen(null);
                    // Molly's thread may still be writing into this row.
                    setMentorThread(t => threadAfterDelete(t, id));
                    showToast('Deleted.', 'success');
                } catch (e) {
                    showToast('We could not delete that conversation.', 'error');
                } finally {
                    setBusyId(null);
                    setConfirmId(null);
                }
            };

            const when = (iso) => {
                if (!iso) return '';
                const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
                return isNaN(d) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
            };

            if (open) {
                const msgs = Array.isArray(open.messages) ? open.messages : [];
                return (
                    <div className="ed-pagein" style={{ padding: '26px 0 32px' }}>
                        <div onClick={() => setOpen(null)} onKeyDown={btnKey(() => setOpen(null))}
                            role="button" tabIndex={0}
                            style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                            ← Your conversations
                        </div>
                        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 4px' }}>
                            {open.title || (open.kind === 'askCeo' ? 'Ask a CEO' : open.kind === 'lesson' ? 'Lesson with Molly' : 'Conversation with Molly')}
                        </h2>
                        <div style={{ fontSize: '12.5px', color: 'var(--text-meta)', marginBottom: '18px' }}>
                            {when(open.startedAt)} · {open.messageCount} messages
                            {open.stageReached ? ` · reached ${open.stageReached}` : ''}
                        </div>
                        {/* A transcript you can only read is a museum. Molly holds
                            the thread in state and appends to the same D1 row, so
                            picking one up is the same conversation continuing. */}
                        {open.kind !== 'askCeo' && (
                            <button
                                onClick={() => {
                                    // Handed over in the API's own shape: Molly
                                    // maps it, the same way she maps a
                                    // conversation she looked up herself.
                                    setMentorResume({
                                        id: open.id,
                                        title: open.title || null,
                                        stage: open.stageReached || null,
                                        messages: msgs,
                                        // undefined from a worker that does not send it
                                        scenarioId: open.scenarioId
                                    });
                                    setCurrentView('ci-mentor');
                                }}
                                className="ed-btn ed-btn-gold" data-testid="convo-continue"
                                style={{ width: '100%', fontSize: '14px', padding: '13px', marginBottom: '18px' }}>
                                Pick this up with Molly →
                            </button>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {msgs.map((m, i) => (
                                <div key={i} style={{
                                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                                    maxWidth: '88%',
                                    background: m.role === 'user' ? 'var(--navy)' : 'var(--white)',
                                    color: m.role === 'user' ? 'var(--text-on-navy)' : 'var(--text-body)',
                                    border: m.role === 'user' ? 'none' : '1px solid var(--border-card)',
                                    borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '4px 14px 14px 14px',
                                    padding: '12px 15px', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-line'
                                }}>
                                    {m.role === 'bot' ? (() => {
                                        // A stored reply still carries its closing Action line and its clip
                                        // markers; the live screen turns those into "Your next move" and chips.
                                        const { main, action } = pastReplyParts(m.text);
                                        return (<>
                                            {main}
                                            {action && (
                                                <div data-testid="past-next-move" style={{ marginTop: '10px', paddingTop: '9px', borderTop: '1px dashed var(--border-card)', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                                    <span style={{ display: 'block', fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', marginBottom: '3px' }}>Your next move</span>
                                                    {action}
                                                </div>
                                            )}
                                        </>);
                                    })() : m.text}
                                    {/* The clips were stored with the turn and then
                                        thrown away on the way to the screen. Half of
                                        what Molly said was a CEO's own voice. */}
                                    {m.role === 'bot' && storedSourcesOf(m.sources).quotes && storedSourcesOf(m.sources).quotes.map((q, qi) => (
                                        <MollyQuote key={`q-${qi}`} quote={q} heardBelow={(storedSourcesOf(m.sources).sources || []).some(src => src.audioFile && sameCeo(src.ceo, q.ceo))} />
                                    ))}
                                    {m.role === 'bot' && (storedSourcesOf(m.sources).sources || []).map((src, si) => (storedSourcesOf(m.sources).quotes || []).some(q => sameRecording(q, src)) ? null : (
                                        <AudioClipCard
                                            key={`s-${si}`}
                                            title={`${src.ceo}${src.company ? ' · ' + src.company : ''}`}
                                            meta={[src.startS == null && src.topic ? `On ${src.topic}` : null, src.why === 'library' ? 'From the library' : src.cited ? 'Cited by Molly' : src.why === 'named' ? (src.startS == null && !src.transcript ? 'Named by Molly · the whole interview' : 'Named by Molly') : null].filter(Boolean).join(' · ') || null}
                                            src={fixAudioPath(src.audioFile)}
                                            fullSrc={wholeOf(src) ? fixAudioPath(wholeOf(src)) : null}
                                            transcript={src.transcript || src.audioTranscript || null}
                                            startS={src.startS ?? null}
                                            endS={src.endS ?? null}
                                        />
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                );
            }

            return (
                <div className="ed-pagein" data-testid="conversations-view" style={{ padding: '26px 0 32px' }}>
                    <div onClick={() => setCurrentView(convosBackTo || 'profile')} onKeyDown={btnKey(() => setCurrentView(convosBackTo || 'profile'))}
                        role="button" tabIndex={0}
                        style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                        {convosBackTo === 'ci-mentor' ? '← Molly' : '← Profile'}
                    </div>
                    <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                        Your conversations
                    </h2>
                    <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 18px' }}>
                        Everything you have worked through with Molly or asked a CEO. Delete any of it whenever you like — it goes immediately.
                    </p>

                    {items === null && <div style={{ fontSize: '13.5px', color: 'var(--text-meta)' }}>Loading…</div>}
                    {failed && <LoadFailedCard what="your conversations" onRetry={() => setTick(t => t + 1)} />}
                    {!failed && items && items.length === 0 && (
                        <div style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '22px', textAlign: 'center', fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            Nothing yet. Work a challenge with Molly and it will be here.
                        </div>
                    )}

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {(items || []).map(c => (
                            <div key={c.id} style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '15px 17px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start' }}>
                                    <div
                                        onClick={() => openOne(c.id)} onKeyDown={btnKey(() => openOne(c.id))}
                                        role="button" tabIndex={0}
                                        style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
                                        <div style={{ fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '4px' }}>
                                            {c.kind === 'askCeo' ? 'Ask a CEO' : c.kind === 'lesson' ? 'Lesson' : 'Molly'}
                                        </div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {c.title || 'Untitled'}
                                        </div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-meta)' }}>
                                            {when(c.lastActiveAt)} · {c.messageCount} messages
                                            {c.stageReached ? ` · reached ${c.stageReached}` : ''}
                                        </div>
                                    </div>
                                    {confirmId === c.id ? (
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0 }}>
                                            <span
                                                onClick={() => remove(c.id)} onKeyDown={btnKey(() => remove(c.id))}
                                                role="button" tabIndex={0}
                                                style={{ fontSize: '12.5px', color: 'var(--error-accent)', fontWeight: 700, cursor: 'pointer' }}>
                                                {busyId === c.id ? 'Deleting…' : 'Delete'}
                                            </span>
                                            <span
                                                onClick={() => setConfirmId(null)} onKeyDown={btnKey(() => setConfirmId(null))}
                                                role="button" tabIndex={0}
                                                style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
                                                Keep
                                            </span>
                                        </div>
                                    ) : (
                                        <span
                                            onClick={() => setConfirmId(c.id)} onKeyDown={btnKey(() => setConfirmId(c.id))}
                                            role="button" tabIndex={0} aria-label={`Delete ${c.title || 'this conversation'}`}
                                            style={{ fontSize: '12.5px', color: 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0 }}>
                                            Delete
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        };

        // ============================================
        // ARCHIVED CONVERSATIONS
        // ============================================
        // Conversations quiet for 90 days live in R2, with only an index row in
        // D1. The list below is that index — no archive body is fetched until
        // the user opens one, which is the whole point of the split.
        const ArchivesView = () => {
            const { authToken, setCurrentView, showToast } = useApp();
            const [items, setItems] = useState(null);      // null = still loading
            const [open, setOpen] = useState(null);        // the opened archive
            const [loadingId, setLoadingId] = useState(null);
            const [archivesFailed, setArchivesFailed] = useState(false);
            const [archivesTick, setArchivesTick] = useState(0);

            useEffect(() => {
                if (!API_URL || !authToken) { setItems([]); return; }
                let cancelled = false;
                setArchivesFailed(false);
                fetch(`${API_URL}/api/archives?limit=25`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => r.json())
                    .then(res => {
                        if (cancelled) return;
                        if (res && res.success) setItems(res.data.items);
                        else { setItems([]); setArchivesFailed(true); }
                    })
                    .catch(() => { if (!cancelled) { setItems([]); setArchivesFailed(true); } });
                return () => { cancelled = true; };
            }, [authToken, archivesTick]);

            const openArchive = async (id) => {
                setLoadingId(id);
                try {
                    const res = await fetch(`${API_URL}/api/archives/${id}`, {
                        headers: { Authorization: `Bearer ${authToken}` }
                    });
                    const data = await res.json();
                    if (data?.success) setOpen(data.data);
                    else showToast('That conversation could not be opened', 'error');
                } catch {
                    showToast('Could not reach the archive', 'error');
                } finally {
                    setLoadingId(null);
                }
            };

            const when = (iso) => {
                try { return new Date(iso.replace(' ', 'T') + (iso.includes('Z') ? '' : 'Z'))
                    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
                catch { return iso; }
            };

            if (open) {
                const msgs = (open.body && open.body.messages) || [];
                return (
                    <div className="ed-pagein" data-testid="archive-detail" style={{ padding: '26px 0 32px' }}>
                        <div onClick={() => setOpen(null)} onKeyDown={btnKey(() => setOpen(null))}
                            role="button" tabIndex={0}
                            style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                            ← All archived conversations
                        </div>
                        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '22px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px', lineHeight: 1.3 }}>
                            {open.title || (open.kind === 'askCeo' ? 'Ask a CEO' : open.kind === 'lesson' ? 'Lesson with Molly' : 'Conversation with Molly')}
                        </h2>
                        <div style={{ fontSize: '12.5px', color: 'var(--text-meta)', marginBottom: '18px' }}>
                            Archived {when(open.archivedAt)} · {open.messageCount} messages
                            {open.stageReached ? ` · reached ${open.stageReached}` : ''}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {msgs.map((m, i) => (
                                <div key={i} style={{
                                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                                    maxWidth: '88%',
                                    background: m.role === 'user' ? 'var(--navy)' : 'var(--white)',
                                    color: m.role === 'user' ? 'var(--text-on-navy)' : 'var(--text-body)',
                                    border: m.role === 'user' ? 'none' : '1px solid var(--border-card)',
                                    borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '4px 14px 14px 14px',
                                    padding: '12px 15px', fontSize: '14px', lineHeight: 1.6
                                }}>{m.text}</div>
                            ))}
                        </div>
                        {/* Read-only by design: an archive is a record, not a thread to continue. */}
                        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '12.5px', color: 'var(--text-meta)' }}>
                            This conversation is archived and can no longer be continued.
                        </div>
                    </div>
                );
            }

            return (
                <div className="ed-pagein" data-testid="archives-view" style={{ padding: '26px 0 32px' }}>
                    <div onClick={() => setCurrentView('profile')} onKeyDown={btnKey(() => setCurrentView('profile'))}
                        role="button" tabIndex={0}
                        style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                        ← Profile
                    </div>
                    <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                        Archived conversations
                    </h2>
                    <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
                        Conversations you have not touched for 90 days are moved here.
                        They stay readable for 18 months. <span
                            onClick={() => setCurrentView('policy')} onKeyDown={btnKey(() => setCurrentView('policy'))}
                            role="button" tabIndex={0}
                            style={{ color: 'var(--gold-darker)', cursor: 'pointer', fontWeight: 600 }}>How we handle your data →</span>
                    </p>

                    {items === null && (
                        <div style={{ fontSize: '13.5px', color: 'var(--text-meta)' }}>Loading…</div>
                    )}
                    {archivesFailed && <LoadFailedCard what="your archived conversations" onRetry={() => setArchivesTick(t => t + 1)} />}
                    {!archivesFailed && items && items.length === 0 && (
                        <div data-testid="archives-empty" style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '22px', textAlign: 'center' }}>
                            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                                Conversations older than 90 days are archived here.
                            </div>
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {(items || []).map(a => (
                            <div key={a.id}
                                onClick={() => openArchive(a.id)} onKeyDown={btnKey(() => openArchive(a.id))}
                                role="button" tabIndex={0} className="ed-hover-gold-border"
                                data-testid={`archive-row-${a.id}`}
                                style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '14px 16px', cursor: 'pointer', transition: 'border-color 0.15s ease' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                    <span style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-meta)', fontWeight: 700 }}>
                                        Archived{a.kind === 'askCeo' ? ' · Ask a CEO' : a.kind === 'lesson' ? ' · Lesson' : ' · Molly'}
                                    </span>
                                    <span style={{ fontSize: '11.5px', color: 'var(--text-meta)' }}>{when(a.archivedAt)}</span>
                                </div>
                                <div style={{ fontSize: '14.5px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1.35 }}>
                                    {a.title || (a.kind === 'askCeo' ? 'Ask a CEO' : 'Conversation with Molly')}
                                </div>
                                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '3px' }}>
                                    {a.messageCount} messages{a.stageReached ? ` · reached ${a.stageReached}` : ''}
                                    {loadingId === a.id ? ' · opening…' : ''}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        };

        // ============================================
        // DATA & PRIVACY POLICY
        // ============================================
        // Retention is stated plainly and in the app, not buried in a document
        // nobody opens. The numbers here are the ones the code actually enforces
        // (ARCHIVE_AFTER_DAYS / RETENTION_MONTHS in the worker).
        /**
         * Who you are (docs/design/06): six short rounds that end in a prompt in
         * the member's own words. Rounds 1 to 5 are forms; round 6 is Molly
         * checking the answers against the record; then Molly drafts the lines,
         * and the member edits, switches and saves them on Your prompt. The
         * rounds are data so the copy is one place and the tests can read it.
         */
        const PROMPT_ROUNDS = {
            pairs: [
                ['A plan I trust', 'A team I trust'],
                ['Decide today, adjust tomorrow', 'Wait a week, decide once'],
                ['Numbers first', 'People first'],
                ['Tell everyone early', 'Tell everyone when it is settled'],
                ['The bold move', 'The safe move, then the bold one'],
                ['I go first', 'I go last'],
                ['Fewer, bigger bets', 'Many small ones'],
                ['Say it in the room', 'Say it one to one']
            ],
            monday: {
                buckets: ['Now', 'Today', 'This week', 'Someone else'],
                cards: [
                    'A customer worth a fifth of revenue wants a call today',
                    'Your best manager asks for ten minutes, “nothing urgent”',
                    'The bank wants updated numbers by Friday',
                    'A supplier says the price goes up next month'
                ]
            },
            sentences: [
                { start: 'When a decision is mine alone, I…', endings: ['sleep on it once, then move', 'make it, then adjust in public', 'call two people I trust first', 'want more data than I will ever get'] },
                { start: 'The decisions I get wrong are usually…', endings: ['the ones I made too fast', 'the ones I put off', 'about people', 'about money'] },
                { start: 'When the team disagrees with me, I…', endings: ['hear everyone, then decide', 'decide, then explain', 'look for the one who is quiet', 'change my mind more than they think'] },
                { start: 'A good week is one where I…', endings: ['closed something', 'built something', 'fixed something', 'was not needed'] }
            ],
            proudest: { call: 'The call you are proudest of, in a line or two', nearly: 'What nearly stopped you' },
            keepsUp: ['Cash', 'A hire I have to make', 'A person I have to let go', 'A customer we could lose', 'Growing too fast', 'Growing too slowly', 'My own energy', 'Being the bottleneck', 'A decision I keep deferring']
        };
        const PROMPT_ROUND_TITLES = ['This or that', 'Your Monday', 'Finish the sentence', 'The call you are proudest of', 'What keeps you up', 'Molly checks'];
        const emptyRoundsState = () => ({ pairs: {}, monday: {}, sentences: {}, own: {}, proudest: { call: '', nearly: '' }, keepsUp: [], check: {} });
        const cleanAnswer = (t) => String(t || '').replace(/\s+/g, ' ').trim().slice(0, 400);

        // The rounds' state as the answers the drafter reads: one line per
        // thing said, tagged with its round, nothing for a round left blank.
        const promptAnswersFrom = (st, checkQuestions = []) => {
            const out = [];
            const pairs = PROMPT_ROUNDS.pairs.map((pair, i) => (st.pairs[i] === 0 || st.pairs[i] === 1) ? pair[st.pairs[i]] : null).filter(Boolean);
            if (pairs.length) out.push({ round: 1, question: 'This or that', answer: pairs.join('; ') });
            const monday = PROMPT_ROUNDS.monday.buckets.map(b => {
                const cards = PROMPT_ROUNDS.monday.cards.filter((_, i) => st.monday[i] === b);
                return cards.length ? `${b}: ${cards.join(' / ')}` : null;
            }).filter(Boolean);
            if (monday.length) out.push({ round: 2, question: 'Your Monday: four interruptions, where each one goes', answer: monday.join('; ') });
            PROMPT_ROUNDS.sentences.forEach((sen, i) => {
                const own = cleanAnswer(st.own[i]);
                const pick = typeof st.sentences[i] === 'number' ? sen.endings[st.sentences[i]] : null;
                const answer = own || pick;
                if (answer) out.push({ round: 3, question: sen.start, answer });
            });
            const call = cleanAnswer(st.proudest.call), nearly = cleanAnswer(st.proudest.nearly);
            if (call) out.push({ round: 4, question: PROMPT_ROUNDS.proudest.call, answer: call });
            if (nearly) out.push({ round: 4, question: PROMPT_ROUNDS.proudest.nearly, answer: nearly });
            if (st.keepsUp.length) out.push({ round: 5, question: 'What keeps you up, in order', answer: st.keepsUp.map((k, i) => `${i + 1} ${k}`).join('; ') });
            checkQuestions.forEach((q, i) => {
                const reply = cleanAnswer(st.check[i]);
                if (reply) out.push({ round: 6, question: q.question, answer: reply });
            });
            return out;
        };

        // Lines without Molly: the sentences, the proudest call and the top of
        // the list, said straight. What the member gets when Molly is off, at
        // capacity, or drafts nothing usable; edited on Your prompt like any draft.
        const promptLinesFromAnswers = (st) => {
            const lines = [];
            PROMPT_ROUNDS.sentences.forEach((sen, i) => {
                const own = cleanAnswer(st.own[i]);
                const pick = typeof st.sentences[i] === 'number' ? sen.endings[st.sentences[i]] : null;
                const ending = own || pick;
                if (ending) lines.push({ text: `${sen.start.replace(/…$/, '')} ${ending.replace(/\.$/, '')}.`, source: 'round-3', shared: true });
            });
            const call = cleanAnswer(st.proudest.call);
            if (call) lines.push({ text: `The call I am proudest of: ${call.replace(/\.$/, '')}.`, source: 'round-4', shared: true });
            if (st.keepsUp.length) {
                const k = st.keepsUp.slice(0, 3).map(x => x.toLowerCase());
                lines.push({ text: `What keeps me up right now: ${k.length > 1 ? k.slice(0, -1).join(', ') + ' and ' + k[k.length - 1] : k[0]}.`, source: 'round-5', shared: true });
            }
            return lines.slice(0, MEMBER_PROMPT_MAX_LINES);
        };

        const WhoYouAreView = () => {
            const { setCurrentView, userProfile, currentUser, mentorEnabled, decisionProfile, world, authToken, setPromptDraft, showToast } = useApp();
            const impersonating = !!(currentUser && currentUser.impersonatedBy);
            const [stage, setStage] = useState('intro');   // intro · 1..5 · check · draft
            const [st, setSt] = useState(emptyRoundsState);
            const [questions, setQuestions] = useState([]);
            const [busy, setBusy] = useState(false);
            const [note, setNote] = useState(null);
            useEffect(() => { Telemetry.track('ui_open', { what: 'who' }); }, []);
            const record = decisionProfile && decisionProfile.profile ? decisionProfile.profile : null;
            const kicker = { fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 };
            const card = { background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' };
            const h = (t) => <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', color: 'var(--navy)', fontWeight: 600, margin: 0, lineHeight: 1.25 }}>{t}</h2>;
            const p = (t) => <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>{t}</p>;
            const textarea = (value, onChange, testid, placeholder) => (
                <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} maxLength={400} placeholder={placeholder} data-testid={testid}
                    style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--border-input)', background: 'var(--white)', color: 'var(--navy)', fontSize: '14px', lineHeight: 1.5, fontFamily: 'var(--font-body)', resize: 'vertical' }} />
            );
            const post = async (mode, extraQuestions = []) => {
                const res = await fetch(`${API_URL}/api/prompt/draft`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
                    body: JSON.stringify({ mode, answers: promptAnswersFrom(st, extraQuestions) })
                });
                const data = await res.json().catch(() => null);
                if (!res.ok || !data || !data.success) throw new Error((data && data.error) || `HTTP ${res.status}`);
                return data.data;
            };
            const answered = promptAnswersFrom(st).length;
            // Round 6: Molly's questions, or straight on to the draft when she
            // cannot be asked (off for the account, at capacity, offline).
            const toCheck = async () => {
                if (!mentorEnabled || !API_URL || !authToken || answered === 0) { setQuestions([]); setStage('check'); return; }
                setBusy(true); setNote(null);
                try {
                    const data = await post('check');
                    setQuestions(Array.isArray(data.questions) ? data.questions : []);
                } catch (e) {
                    setQuestions([]);
                    setNote('Molly could not check your answers just now, so this round is skipped.');
                }
                setBusy(false); setStage('check');
            };
            // The draft: Molly's lines when she can, the answers said straight when
            // she cannot. Either way the member edits before anything is saved.
            const toDraft = async () => {
                setBusy(true); setNote(null);
                let lines = null, from = 'you';
                if (mentorEnabled && API_URL && authToken && promptAnswersFrom(st, questions).length) {
                    try {
                        const data = await post('draft', questions);
                        if (Array.isArray(data.lines) && data.lines.length) { lines = data.lines.map(l => ({ text: l.text, source: l.source || 'rounds', shared: true })); from = 'molly'; }
                    } catch (e) { /* the fallback below */ }
                }
                if (!lines || !lines.length) lines = promptLinesFromAnswers(st);
                setBusy(false);
                if (!lines.length) { setNote('Nothing to draft yet. Answer a round or two first.'); return; }
                setPromptDraft({ lines, from });
                if (showToast) showToast(from === 'molly' ? 'Molly drafted your lines. Edit anything.' : 'Your answers, said straight. Edit anything.', 'success');
                setCurrentView('your-prompt');
            };
            const back = () => setCurrentView('profile');
            const progress = (n) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '4px' }}>{PROMPT_ROUND_TITLES.map((t, i) => <span key={t} aria-hidden="true" style={{ flex: 1, height: '6px', borderRadius: '3px', background: i < n ? 'var(--navy)' : 'var(--border-card)' }} />)}</div>
                    <div style={{ fontSize: '12.5px', color: 'var(--navy)', fontWeight: 700 }}>Round {n} of 6 · {PROMPT_ROUND_TITLES[n - 1]}</div>
                </div>
            );
            const nav = (onNext, nextLabel = 'Next', canSkip = true) => (
                <div style={{ display: 'flex', gap: '10px', paddingTop: '6px' }}>
                    {canSkip && <button type="button" onClick={onNext} className="ed-btn ed-btn-quiet" style={{ padding: '12px 16px', fontSize: '14px', minHeight: '46px' }}>Skip</button>}
                    <button type="button" onClick={onNext} disabled={busy} className="ed-btn ed-btn-navy" data-testid="who-next" style={{ flex: 1, padding: '12px', fontSize: '14.5px', minHeight: '46px' }}>{busy ? 'One moment…' : nextLabel}</button>
                </div>
            );
            if (impersonating) {
                return (
                    <div className="ed-pagein" data-testid="who-you-are-view" style={{ padding: '26px 0 32px' }}>
                        <div onClick={back} onKeyDown={btnKey(back)} role="button" tabIndex={0} style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', fontWeight: 600, marginBottom: '14px' }}>← Profile</div>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Not available while viewing as this member. The rounds are theirs to play.</p>
                    </div>
                );
            }
            const known = [];
            if (world.set) known.push(['Your world', [world.industries.map(industryLabel).filter(Boolean).join(' · '), roleLabel(world.role), stageLabel(world.companyStage)].filter(Boolean).join(' · '), 'you told us']);
            if (record && record.totals && record.totals.decisions > 0) known.push(['Your record', `${record.totals.decisions} decision${record.totals.decisions === 1 ? '' : 's'} across ${record.byType.length} of 9 decision types, ${record.totals.days} practice day${record.totals.days === 1 ? '' : 's'}`, 'from your cases']);
            if (record && record.confidence) known.push(['Sure vs. right', `Sure: ${record.confidence.sure.called} of ${record.confidence.sure.n} called · Guessing: ${record.confidence.guessing.called} of ${record.confidence.guessing.n}`, 'from your cases']);
            if (record && record.totals && record.totals.discussedCases > 0) known.push(['With Molly', `${record.totals.discussedCases} case${record.totals.discussedCases === 1 ? '' : 's'} talked through`, 'counts only, never the words']);
            return (
                <div className="ed-pagein" data-testid="who-you-are-view" style={{ padding: '26px 0 32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div onClick={back} onKeyDown={btnKey(back)} role="button" tabIndex={0} style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', fontWeight: 600, alignSelf: 'flex-start' }}>← Profile</div>
                    {stage === 'intro' && (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}><div style={kicker}>Who you are</div>{h('In your own words, with a little help.')}</div>
                            {p(`Six short rounds, about three minutes. ${mentorEnabled ? 'Molly drafts a description of you from your answers and your record. ' : ''}You edit every line before anything is saved, and every line has a switch.`)}
                            {known.length > 0 && (
                                <div style={card}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><div style={kicker}>What we already know</div><span style={{ fontSize: '11.5px', color: 'var(--text-secondary)' }}>nothing guessed</span></div>
                                    {known.map(([k, v, src]) => (
                                        <div key={k} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', borderTop: '1px solid var(--hairline)', paddingTop: '8px' }}>
                                            <div style={{ flex: 1 }}><div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--navy)' }}>{k}</div><div style={{ fontSize: '13px', color: 'var(--text-body)', lineHeight: 1.45 }}>{v}</div></div>
                                            <span style={{ flex: 'none', fontSize: '10.5px', fontWeight: 700, padding: '3px 7px', borderRadius: '6px', background: 'var(--cream-dark)', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{src}</span>
                                        </div>
                                    ))}
                                    <div style={{ fontSize: '12.5px', color: 'var(--text-body)', lineHeight: 1.5 }}>These are counts. What they mean is the part only you can say, and that is what the rounds ask.</div>
                                </div>
                            )}
                            <div style={card}>
                                <div style={kicker}>The rounds</div>
                                {PROMPT_ROUND_TITLES.map((t, i) => (
                                    <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: '32px' }}>
                                        <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: i === 0 ? 'var(--navy)' : 'var(--cream-dark)', color: i === 0 ? 'var(--white)' : 'var(--navy)', fontSize: '11px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</span>
                                        <span style={{ fontSize: '13.5px', fontWeight: 700, color: i === 5 && !mentorEnabled ? 'var(--text-tertiary)' : 'var(--navy)' }}>{t}{i === 5 && !mentorEnabled ? ' · not for your account' : ''}</span>
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={() => setStage(1)} className="ed-btn ed-btn-gold" data-testid="who-play" style={{ padding: '13px', fontSize: '15px', minHeight: '48px', fontWeight: 700 }}>Play round 1</button>
                            <div onClick={() => setCurrentView('your-prompt')} onKeyDown={btnKey(() => setCurrentView('your-prompt'))} role="button" tabIndex={0} style={{ alignSelf: 'center', minHeight: '40px', display: 'inline-flex', alignItems: 'center', fontSize: '13.5px', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer' }}>Not now, write the lines myself</div>
                        </>
                    )}
                    {stage === 1 && (
                        <>
                            {progress(1)}
                            {h('Tap the one that is more you.')}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {PROMPT_ROUNDS.pairs.map((pair, i) => (
                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                        {pair.map((opt, j) => <PracticeChip key={opt} on={st.pairs[i] === j} onClick={() => setSt(x => ({ ...x, pairs: { ...x.pairs, [i]: x.pairs[i] === j ? null : j } }))} testid={`who-pair-${i}-${j}`}>{opt}</PracticeChip>)}
                                    </div>
                                ))}
                            </div>
                            {nav(() => setStage(2))}
                        </>
                    )}
                    {stage === 2 && (
                        <>
                            {progress(2)}
                            {h('It is Monday. Four things land at once. Where does each one go?')}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {PROMPT_ROUNDS.monday.cards.map((c, i) => (
                                    <div key={i} style={card}>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', lineHeight: 1.4 }}>{c}</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {PROMPT_ROUNDS.monday.buckets.map(b => <PracticeChip key={b} on={st.monday[i] === b} onClick={() => setSt(x => ({ ...x, monday: { ...x.monday, [i]: x.monday[i] === b ? null : b } }))} testid={`who-monday-${i}-${b.replace(/\s+/g, '-').toLowerCase()}`}>{b}</PracticeChip>)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {nav(() => setStage(3))}
                        </>
                    )}
                    {stage === 3 && (
                        <>
                            {progress(3)}
                            {h('Finish the sentence.')}
                            {p('Tap an ending, or write your own underneath. Your own words win.')}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {PROMPT_ROUNDS.sentences.map((sen, i) => (
                                    <div key={i} style={card}>
                                        <div style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', color: 'var(--navy)', fontWeight: 600 }}>{sen.start}</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {sen.endings.map((e, j) => <PracticeChip key={e} on={st.sentences[i] === j} onClick={() => setSt(x => ({ ...x, sentences: { ...x.sentences, [i]: x.sentences[i] === j ? null : j } }))} testid={`who-sentence-${i}-${j}`}>…{e}.</PracticeChip>)}
                                        </div>
                                        {textarea(st.own[i] || '', (v) => setSt(x => ({ ...x, own: { ...x.own, [i]: v } })), `who-own-${i}`, '…or in your own words')}
                                    </div>
                                ))}
                            </div>
                            {nav(() => setStage(4))}
                        </>
                    )}
                    {stage === 4 && (
                        <>
                            {progress(4)}
                            {h('The call you are proudest of.')}
                            {p('A line or two. No names needed: what it was, and what nearly stopped you.')}
                            <div style={card}>
                                <label htmlFor="who-call" style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)' }}>{PROMPT_ROUNDS.proudest.call}</label>
                                {textarea(st.proudest.call, (v) => setSt(x => ({ ...x, proudest: { ...x.proudest, call: v } })), 'who-call', 'Keeping the plant open when the bank said close it.')}
                                <label htmlFor="who-nearly" style={{ fontSize: '13px', fontWeight: 700, color: 'var(--navy)' }}>{PROMPT_ROUNDS.proudest.nearly}</label>
                                {textarea(st.proudest.nearly, (v) => setSt(x => ({ ...x, proudest: { ...x.proudest, nearly: v } })), 'who-nearly', 'The numbers, and two people I respect.')}
                            </div>
                            {nav(() => setStage(5))}
                        </>
                    )}
                    {stage === 5 && (
                        <>
                            {progress(5)}
                            {h('What keeps you up right now?')}
                            {p('Tap up to three, in order. The first one you tap is the biggest.')}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {PROMPT_ROUNDS.keepsUp.map(k => {
                                    const at = st.keepsUp.indexOf(k);
                                    return <PracticeChip key={k} on={at >= 0} disabled={at < 0 && st.keepsUp.length >= 3} onClick={() => setSt(x => ({ ...x, keepsUp: at >= 0 ? x.keepsUp.filter(y => y !== k) : [...x.keepsUp, k] }))} testid={`who-keeps-${PROMPT_ROUNDS.keepsUp.indexOf(k)}`}>{at >= 0 ? `${at + 1} · ` : ''}{k}</PracticeChip>;
                                })}
                            </div>
                            {nav(toCheck, mentorEnabled ? 'Next: Molly checks' : 'Next: your lines')}
                        </>
                    )}
                    {stage === 'check' && (
                        <>
                            {progress(6)}
                            {questions.length > 0 ? (
                                <>
                                    {h('Molly, checking your answers against your record.')}
                                    {p('A question, never a verdict. Answer the ones that are worth answering.')}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {questions.map((q, i) => (
                                            <div key={i} style={card}>
                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                                    <span aria-hidden="true" style={{ flex: 'none', width: '28px', height: '28px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>M</span>
                                                    <div style={{ fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.55 }} data-testid={`who-q-${i}`}>{q.question}</div>
                                                </div>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                    {['Yes, that is me', 'Partly', 'No, that is the cases, not me'].map(r => <PracticeChip key={r} on={st.check[i] === r} onClick={() => setSt(x => ({ ...x, check: { ...x.check, [i]: x.check[i] === r ? '' : r } }))} testid={`who-check-${i}-${['yes', 'partly', 'no'][['Yes, that is me', 'Partly', 'No, that is the cases, not me'].indexOf(r)]}`}>{r}</PracticeChip>)}
                                                </div>
                                                {textarea(['Yes, that is me', 'Partly', 'No, that is the cases, not me'].includes(st.check[i]) ? '' : (st.check[i] || ''), (v) => setSt(x => ({ ...x, check: { ...x.check, [i]: v } })), `who-check-own-${i}`, '…or say it your way')}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {h(mentorEnabled ? 'Nothing that did not add up.' : 'Your lines, from your answers.')}
                                    {p(note || (mentorEnabled ? 'Your answers and your record agree, so there is nothing to check. On to the draft.' : 'Molly is not on for your account, so your answers become lines as you gave them. Edit anything on the next screen.'))}
                                </>
                            )}
                            {nav(toDraft, mentorEnabled ? 'Draft my prompt' : 'See my lines', false)}
                        </>
                    )}
                    {note && stage !== 'check' && <div style={{ fontSize: '13px', color: 'var(--claret)' }}>{note}</div>}
                </div>
            );
        };

        /**
         * Your prompt (docs/design/06, step 1): every line, editable, with its
         * switch; Save writes a new version and keeps the last five. Read-only
         * while an admin is viewing as the member — the worker refuses the
         * write anyway, and the screen should not pretend otherwise.
         */
        // Molly's personal-assistant profile (worker src/memberPrompt.ts,
        // migration 0031). The member sees EXACTLY what Molly reads, edits any
        // line (an edited line is theirs and locked — Molly never changes it),
        // and can undo anything Molly learned. Molly tunes it from what they
        // say in conversations; names follow their sharing level.
        const PROMPT_SOURCE_LABEL = { you: 'You wrote this', learned: 'Molly learned this', rounds: 'From your rounds' };
        const promptWhen = (iso) => {
            const t = Date.parse(iso || '');
            return isFinite(t) ? new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
        };
        const YourPromptView = () => {
            const { setCurrentView, currentUser, showToast, mentorEnabled, promptDraft, setPromptDraft, authToken, handleSessionExpired } = useApp();
            const impersonating = !!(currentUser && currentUser.impersonatedBy);
            const [data, setData] = useState(null);          // the server's view
            const [failed, setFailed] = useState(false);
            const [draft, setDraft] = useState(null);        // sections being edited
            const [dirty, setDirty] = useState(false);
            const [saving, setSaving] = useState(false);
            const [showRendered, setShowRendered] = useState(false);
            // A draft handed over from the rounds lands in "About me", unsaved, once.
            const [fromDraft] = useState(() => (promptDraft && Array.isArray(promptDraft.lines) && promptDraft.lines.length ? promptDraft : null));
            useEffect(() => { if (promptDraft) setPromptDraft(null); }, []);

            const adopt = (d, keepDraftLines = false) => {
                setData(d);
                const secs = d.sections.map(s => ({ key: s.key, title: s.title, hint: s.hint, lines: s.lines.map(l => ({ ...l })) }));
                if (keepDraftLines && fromDraft) {
                    const about = secs.find(s => s.key === 'about');
                    for (const l of fromDraft.lines) {
                        if (about.lines.length < d.limits.perSection && l.text && !about.lines.some(x => x.text === l.text)) {
                            about.lines.push({ id: '', text: l.text, source: 'you', locked: true, on: true });
                        }
                    }
                    setDirty(true);
                }
                setDraft(secs);
            };
            const call = async (method, path, body) => {
                const res = await fetch(`${API_URL}${path}`, {
                    method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                    ...(body ? { body: JSON.stringify(body) } : {})
                });
                if (res.status === 401) { handleSessionExpired(); throw new Error('expired'); }
                const j = await res.json().catch(() => ({}));
                return { res, j };
            };
            useEffect(() => {
                if (!API_URL || !authToken) return;
                let cancelled = false;
                call('GET', '/api/me/prompt').then(({ res, j }) => {
                    if (cancelled) return;
                    if (res.ok && j.success) adopt(j.data, true); else setFailed(true);
                }).catch(() => { if (!cancelled) setFailed(true); });
                return () => { cancelled = true; };
            }, [authToken]);

            const edit = (si, li, patch) => { setDraft(d => d.map((s, i) => i !== si ? s : { ...s, lines: s.lines.map((l, j) => j === li ? { ...l, ...patch } : l) })); setDirty(true); };
            const remove = (si, li) => { setDraft(d => d.map((s, i) => i !== si ? s : { ...s, lines: s.lines.filter((_, j) => j !== li) })); setDirty(true); };
            const add = (si) => { setDraft(d => d.map((s, i) => i !== si ? s : { ...s, lines: [...s.lines, { id: '', text: '', source: 'you', locked: true, on: true }] })); setDirty(true); };
            const save = async () => {
                if (!dirty || saving || impersonating) return;
                setSaving(true);
                try {
                    const { res, j } = await call('PUT', '/api/me/prompt', {
                        version: data.version,
                        sections: draft.map(s => ({ key: s.key, lines: s.lines.filter(l => (l.text || '').trim()).map(l => ({ id: l.id || undefined, text: l.text, locked: !!l.locked, on: l.on !== false })) }))
                    });
                    if (res.ok && j.success) {
                        adopt(j.data); setDirty(false);
                        Telemetry.track('ui_open', { what: 'prompt_save' });
                        showToast('Saved. Molly reads this from your next message.', 'success');
                    } else if (res.status === 409 && j.data) {
                        adopt(j.data); setDirty(false);
                        showToast(j.error || 'Molly updated your profile while you were editing — here is the latest.', 'info');
                    } else {
                        showToast((j && j.error) || 'That did not save. Try again in a moment.', 'error');
                    }
                } catch (e) {
                    if (e.message !== 'expired') showToast('That did not save. Try again in a moment.', 'error');
                }
                setSaving(false);
            };
            const undoChange = async (c) => {
                try {
                    const { res, j } = await call('POST', `/api/me/prompt/changes/${c.id}/undo`);
                    if (res.ok && j.success) { adopt(j.data); setDirty(false); showToast('Undone.', 'success'); Telemetry.track('ui_open', { what: 'prompt_undo' }); }
                    else showToast((j && j.error) || 'That could not be undone.', 'error');
                } catch (e) { if (e.message !== 'expired') showToast('That could not be undone.', 'error'); }
            };

            const label = { fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700 };
            const cardStyle = { background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '10px' };
            const chip = (on) => ({ fontSize: '11px', padding: '3px 9px', borderRadius: '999px', border: `1px solid ${on ? 'var(--navy)' : 'var(--border-input)'}`, background: on ? 'var(--navy)' : 'transparent', color: on ? 'var(--text-on-navy)' : 'var(--text-secondary)', cursor: impersonating ? 'default' : 'pointer', fontFamily: 'var(--font-body)', minHeight: '26px' });
            const privacyNote = !data ? null : data.shareLevel === 'full'
                ? 'You share with full disclosure, so your name and your company’s name can be kept here.'
                : data.shareLevel === 'identity'
                    ? 'You share your identity only, so your company’s name is left out — describe the company instead of naming it.'
                    : 'You share anonymized, so your name and your company’s name are left out — describe your company instead of naming it (“a 200-person logistics firm”).';
            const sectionTitle = (k) => (data && (data.sections.find(s => s.key === k) || {}).title) || k;
            const changeLine = (c) => c.kind === 'add' ? `Added to ${sectionTitle(c.section)}: “${c.after}”`
                : c.kind === 'remove' ? `Removed from ${sectionTitle(c.section)}: “${c.before}”`
                : `${sectionTitle(c.section)}: “${c.before}” → “${c.after}”`;

            return (
                <div className="ed-pagein" data-testid="your-prompt-view" style={{ padding: '26px 0 32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div onClick={() => setCurrentView('profile')} onKeyDown={btnKey(() => setCurrentView('profile'))} role="button" tabIndex={0}
                        style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', fontWeight: 600, alignSelf: 'flex-start' }}>← Profile</div>
                    <div>
                        <div style={label}>Your prompt</div>
                        <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', color: 'var(--navy)', fontWeight: 600, margin: '4px 0 6px' }}>What Molly knows about you.</h2>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
                            {mentorEnabled ? 'Molly reads this with every message, so her answers are about your company and your goals — not anyone’s. ' : 'Nothing reads it until Molly is on for your account. '}
                            <strong style={{ color: 'var(--navy)' }}>Every conversation with Molly tunes it further:</strong> when you tell her something lasting — a new hire, a bigger team, a goal — she updates it here, and you can undo any change. Edit any line yourself; a line you write or edit is yours, locked, and Molly never changes it.
                        </p>
                    </div>
                    {privacyNote && (
                        <div data-testid="prompt-privacy" style={{ background: 'var(--cream-dark)', border: '1px solid var(--border-card)', borderRadius: '12px', padding: '10px 14px', fontSize: '12.5px', color: 'var(--text-body)', lineHeight: 1.55 }}>
                            🔒 {privacyNote}{' '}
                            <span role="button" tabIndex={0} onClick={() => setCurrentView('policy')} onKeyDown={btnKey(() => setCurrentView('policy'))} style={{ color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>Data &amp; privacy</span>
                        </div>
                    )}
                    {fromDraft && (
                        <div data-testid="prompt-from-draft" style={{ background: 'var(--gold-transparent)', border: '1px solid var(--gold-lighter)', borderRadius: '12px', padding: '10px 14px', fontSize: '13px', color: 'var(--navy)', lineHeight: 1.5 }}>
                            {fromDraft.from === 'molly' ? 'Molly drafted lines from your rounds into “About me”. ' : 'Your answers, said straight, are in “About me”. '}Nothing is saved until you press Save.
                        </div>
                    )}
                    {failed && <LoadFailedCard what="your profile" onRetry={() => { setFailed(false); call('GET', '/api/me/prompt').then(({ res, j }) => (res.ok && j.success ? adopt(j.data) : setFailed(true))).catch(() => setFailed(true)); }} />}
                    {!data && !failed && <div style={{ fontSize: '13.5px', color: 'var(--text-secondary)' }}>Loading…</div>}

                    {draft && draft.map((s, si) => (
                        <div key={s.key} style={cardStyle} data-testid={`prompt-section-${s.key}`}>
                            <div>
                                <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--navy)' }}>{s.title}</div>
                                <div style={{ fontSize: '12.5px', color: 'var(--text-tertiary)' }}>{s.hint}</div>
                            </div>
                            {s.lines.length === 0 && <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Nothing yet. Molly will add to this as you talk — or write a line yourself.</div>}
                            {s.lines.map((l, li) => (
                                <div key={l.id || `n${li}`} style={{ borderTop: '1px solid var(--hairline)', paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <textarea value={l.text} rows={2} maxLength={data.limits.lineChars} disabled={impersonating}
                                        onChange={(e) => edit(si, li, { text: e.target.value, ...(l.id ? {} : {}) })}
                                        aria-label={`${s.title}, line ${li + 1}`} data-testid={`prompt-line-${s.key}-${li}`}
                                        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: '10px', border: '1px solid var(--border-input)', background: l.on === false ? 'var(--cream-dark)' : 'var(--white)', color: l.on === false ? 'var(--text-secondary)' : 'var(--navy)', fontSize: '14px', lineHeight: 1.5, fontFamily: 'var(--font-body)', resize: 'vertical' }} />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '11.5px', color: l.source === 'learned' ? 'var(--gold-darker)' : 'var(--text-tertiary)', fontWeight: l.source === 'learned' ? 700 : 500, flex: 1, minWidth: '140px' }}>
                                            {PROMPT_SOURCE_LABEL[l.source] || 'You wrote this'}{l.at && l.id ? ` · ${promptWhen(l.at)}` : ''}
                                        </span>
                                        <button type="button" disabled={impersonating} onClick={() => edit(si, li, { locked: !l.locked })} style={chip(l.locked)}
                                            aria-pressed={!!l.locked} title={l.locked ? 'Molly will never change this line' : 'Molly can update this line as she learns'}>
                                            {l.locked ? '🔒 Locked' : 'Molly can update'}
                                        </button>
                                        <button type="button" disabled={impersonating} onClick={() => edit(si, li, { on: l.on === false })} style={chip(l.on !== false)} aria-pressed={l.on !== false}>
                                            {l.on === false ? 'Off — Molly skips it' : 'On'}
                                        </button>
                                        {!impersonating && <button type="button" onClick={() => remove(si, li)} style={{ ...chip(false), border: 'none' }}>Remove</button>}
                                    </div>
                                </div>
                            ))}
                            {!impersonating && s.lines.length < data.limits.perSection && (
                                <button type="button" onClick={() => add(si)} className="ed-btn ed-btn-quiet" data-testid={`prompt-add-${s.key}`} style={{ alignSelf: 'flex-start', padding: '8px 12px', fontSize: '13px', minHeight: '38px' }}>+ Add a line</button>
                            )}
                        </div>
                    ))}

                    {draft && (impersonating
                        ? <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)' }}>Read-only while viewing as this member.</div>
                        : <button type="button" onClick={save} disabled={!dirty || saving} className="ed-btn ed-btn-navy" data-testid="prompt-save" style={{ padding: '12px', fontSize: '14.5px', minHeight: '46px', position: 'sticky', bottom: '84px' }}>
                            {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
                        </button>)}

                    {data && (
                        <div style={cardStyle} data-testid="prompt-changes">
                            <div style={label}>What changed recently</div>
                            {data.changes.length === 0 && <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>No changes yet. Talk to Molly about what you are working on and she will start filling this in.</div>}
                            {data.changes.slice(0, 15).map(c => (
                                <div key={c.id} style={{ borderTop: '1px solid var(--hairline)', paddingTop: '8px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: '13px', color: c.undoneAt ? 'var(--text-tertiary)' : 'var(--navy)', lineHeight: 1.5, textDecoration: c.undoneAt ? 'line-through' : 'none', overflowWrap: 'anywhere' }}>{changeLine(c)}</div>
                                        <div style={{ fontSize: '11.5px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                            {c.by === 'tuner' ? 'Molly, from a conversation' : c.by === 'privacy' ? 'Removed by your privacy setting' : 'You'}{c.createdAt ? ` · ${promptWhen(String(c.createdAt).replace(' ', 'T') + 'Z')}` : ''}{c.undoneAt ? ' · undone' : ''}
                                        </div>
                                        {c.evidence && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: '2px', overflowWrap: 'anywhere' }}>You said: “{c.evidence}”</div>}
                                    </div>
                                    {!impersonating && !c.undoneAt && c.by !== 'privacy' && (
                                        <button type="button" onClick={() => undoChange(c)} style={chip(false)} data-testid="prompt-undo">Undo</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {data && (
                        <div style={cardStyle}>
                            <div onClick={() => setShowRendered(v => !v)} onKeyDown={btnKey(() => setShowRendered(v => !v))} role="button" tabIndex={0} style={{ ...label, cursor: 'pointer' }}>
                                {showRendered ? '▾' : '▸'} Exactly what Molly reads
                            </div>
                            {showRendered && (
                                <pre data-testid="prompt-rendered" style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'var(--font-body)', fontSize: '12.5px', color: 'var(--text-body)', lineHeight: 1.6, background: 'var(--cream)', borderRadius: '10px', padding: '12px' }}>
                                    {data.rendered || '(Nothing yet — Molly reads nothing about you until a line is here.)'}
                                </pre>
                            )}
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                Molly receives it as your own words, alongside your message. A real CEO never sees it.
                                {!impersonating && mentorEnabled && <> <span role="button" tabIndex={0} onClick={() => setCurrentView('who-you-are')} onKeyDown={btnKey(() => setCurrentView('who-you-are'))} data-testid="prompt-play" style={{ color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer' }}>Play the rounds</span> to give her a head start.</>}
                            </div>
                        </div>
                    )}
                </div>
            );
        };

        // A member's copy of their record (docs/FEEDBACK_ROADMAP.md §5): every
        // row the decision-profile route returned, in words rather than ids,
        // with the note where the route sent one. It sends `noted` in place of
        // the note to anyone who is not the member (the console, an admin
        // viewing as them), so an export taken under impersonation carries no
        // note text by construction. Built from what the page already holds;
        // nothing is fetched or sent.
        const decisionExportOf = (decisionProfile) => {
            const rows = decisionProfile && Array.isArray(decisionProfile.history) ? decisionProfile.history : [];
            const call = (row, id) => {
                if (!id) return null;
                const label = optionLabelOf({ options: row.options || [] }, id);
                return label ? `${id} — ${label}` : String(id);
            };
            return {
                exportedAt: new Date().toISOString(),
                about: 'Your ' + BRAND.name + ' decision history. For a match case, ceoCall is what the CEO actually did and matched says whether your call was the same; for a bad-call case, ceoCall is the call that went wrong and matched says whether you caught it. Replays are included and marked counted: false.',
                decisions: rows.map(row => ({
                    date: row.createdAt || null,
                    case: row.title || null,
                    ceo: row.ceo || null,
                    company: row.company || null,
                    year: row.year == null ? null : row.year,
                    industry: practiceLabel(PRACTICE_VOCAB.industries, row.industry),
                    decisionType: practiceLabel(PRACTICE_VOCAB.decisionTypes, row.decisionType),
                    companyStage: practiceLabel(PRACTICE_VOCAB.stages, row.companyStage),
                    kind: row.scenarioType === 'spotBad' ? 'bad call' : 'match',
                    counted: row.counted !== false,
                    yourCall: call(row, row.chosen),
                    ceoCall: call(row, row.correctChoice),
                    matched: row.correct == null ? null : !!row.correct,
                    howSure: JOURNAL_CONFIDENCE[row.confidence] || null,
                    secondThought: row.secondChoice ? call(row, row.secondChoice) : null,
                    note: typeof row.note === 'string' ? row.note : null,
                    noteWrittenAt: row.reflectedAt || null,
                    talkedThroughWithMolly: !!row.discussed,
                    askedARealCeo: !!row.askedCeo
                }))
            };
        };

        const PolicyView = () => {
            const { setCurrentView, decisionProfile, shareLevel, saveShareLevel, showToast, currentUser } = useApp();
            const viewingAs = !!(currentUser && currentUser.impersonatedBy);
            const pickShare = async (id) => {
                const ok = await saveShareLevel(id);
                showToast(ok ? `Saved — advisors will see: ${shareLevelOf(id).label.toLowerCase()}.` : 'That did not save. Try again in a moment.', ok ? 'success' : 'error');
            };
            const history = decisionProfile && Array.isArray(decisionProfile.history) ? decisionProfile.history : null;
            const download = () => {
                if (!history) return;
                const data = decisionExportOf(decisionProfile);
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `edify-decisions-${data.exportedAt.slice(0, 10)}.json`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            };
            const Section = ({ title, children }) => (
                <div style={{ background: 'var(--white)', border: '1px solid var(--border-card)', borderRadius: '14px', padding: '18px 20px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '8px' }}>{title}</div>
                    <div style={{ fontSize: '14px', color: 'var(--text-body)', lineHeight: 1.65 }}>{children}</div>
                </div>
            );
            return (
                <div className="ed-pagein" data-testid="policy-view" style={{ padding: '26px 0 32px' }}>
                    <div onClick={() => setCurrentView('profile')} onKeyDown={btnKey(() => setCurrentView('profile'))}
                        role="button" tabIndex={0}
                        style={{ fontSize: '13.5px', color: 'var(--gold-darker)', cursor: 'pointer', marginBottom: '14px', fontWeight: 600 }}>
                        ← Profile
                    </div>
                    <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '26px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                        Data &amp; privacy
                    </h2>
                    <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
                        What we keep, for how long, and how to remove it.
                    </p>

                    <div data-testid="policy-promise" style={{ background: 'var(--navy)', color: 'var(--text-on-navy)', borderRadius: '14px', padding: '18px 20px', marginBottom: '12px' }}>
                        <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700, marginBottom: '8px' }}>🔒 Our promise</div>
                        <div style={{ fontSize: '14.5px', lineHeight: 1.65 }}>{PRIVACY_PROMISE}</div>
                    </div>

                    {/* What src/sharing.ts discloses, chosen here. Frozen onto each
                        question when it is asked, so changing it never reveals
                        more on a question already sent. */}
                    <Section title="What CEO advisors see when you ask">
                        <div style={{ marginBottom: '12px' }}>
                            You choose. It applies to each question from the moment you send it, and changing it later never reveals more on a question you already asked. Advisors never see your email, your notes or your conversations with Molly.
                        </div>
                        <SharingChoice value={shareLevel || 'anonymized'} onChange={pickShare} disabled={viewingAs || shareLevel === undefined} />
                        <div style={{ marginTop: '10px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
                            Whatever you choose, what you write in the question itself is read as written — leave names out to stay anonymous.
                        </div>
                    </Section>

                    <Section title="Your conversations with Molly">
                        When you work a challenge with Molly, the exchange is saved to your
                        account so you can come back to it. These are your words about your
                        business, and they are visible only to you — no other user can read
                        them.
                    </Section>

                    <Section title="90 days · moved to the archive">
                        A conversation you have not added to for <strong>90 days</strong> is
                        moved into your archive. Nothing is lost: it stays fully readable
                        under <em>Profile → Archived conversations</em>. It simply cannot be
                        continued.
                    </Section>

                    <Section title="18 months · deleted for good">
                        <strong>18 months</strong> after a conversation starts, it is deleted
                        permanently — the transcript and the archived copy both. This is
                        automatic and cannot be undone, so copy out anything you want to
                        keep before then.
                    </Section>

                    <Section title="Deleting sooner">
                        You can delete any conversation yourself at any time, and it goes
                        immediately — Profile → Your conversations. If your account is closed,
                        everything attached to it — conversations, archives, your profile and
                        your simulator history — is removed with it.
                    </Section>

                    {/* Word for word what the worker enforces (docs/DECISION_PRACTICE.md
                        §4, §5): the decision-profile routes send `noted`, never the
                        note, to staff and to an admin viewing as the member; Molly's
                        member block carries the world and the counts and nothing else. */}
                    <Section title="Your decisions and notes">
                        Your notes are visible only to you. {BRAND.name} staff can see which option you chose, how sure you said you were, and whether you wrote a note, but not the note.
                    </Section>

                    <Section title="What Molly knows about you">
                        Your prompt — what you and Molly have written about you, your company, what you are working on and how you like to work — with the industries, role, stage, size and focus areas you chose and your case counts, sent with each message to the AI model. Molly updates your prompt from what you tell her; you can read, edit or undo every line under Profile → Your prompt. Your name stays out of it unless you share with full disclosure or identity only, and your company’s name unless you share with full disclosure. Never your notes.
                    </Section>

                    {/* What mollyMemory.ts carries, word for word. */}
                    <Section title="What Molly remembers">
                        Nothing from an earlier conversation unless you press <em>Remember this conversation</em> in it. Then what you said there, and Molly's last reply, from your three most recent remembered conversations, go with your messages as background. Press <em>Forget</em> to stop it at any time.
                    </Section>

                    <Section title="A copy of your decisions">
                        Every decision on your record, the case, your call, the CEO's, how sure you were, and your notes, as one file you keep. It is built from what this app already shows you; nothing is sent anywhere.
                        <div style={{ marginTop: '10px' }}>
                            <button type="button" onClick={download} disabled={!history} className="ed-btn ed-btn-quiet" data-testid="export-decisions"
                                style={{ padding: '10px 16px', fontSize: '14px', minHeight: '42px' }}>
                                {history
                                    ? `Download my decision history (${history.length} entr${history.length === 1 ? 'y' : 'ies'}, JSON)`
                                    : decisionProfile === null ? 'Your record isn\'t available right now' : 'Loading your record…'}
                            </button>
                        </div>
                    </Section>

                    <Section title="What else we store">
                        Your profile details, your Decision Index and streak, which cases you
                        have completed, and the moves you have saved. Your password is stored
                        only as a salted hash and is never readable, by us or anyone else.
                    </Section>

                    <div style={{ fontSize: '12.5px', color: 'var(--text-meta)', lineHeight: 1.6, marginTop: '6px' }}>
                        Questions about your data? Contact your program director.
                    </div>
                </div>
            );
        };

        // ============================================
        // TEST RUNNER - Core Infrastructure, Home Dashboard & CI Mentor Tests
        // ============================================
        const TestRunner = ({ onComplete }) => {
            const [testResults, setTestResults] = useState([]);
            const [isRunning, setIsRunning] = useState(false);
            const [showPanel, setShowPanel] = useState(false);
            const [activeTestGroup, setActiveTestGroup] = useState('all');
            const { currentView, setCurrentView, powerIndex, streak, userProfile, database, showToast, completedSimulations, currentUser, world, decisionProfile } = useApp();

            const runTests = useCallback(async () => {
                setIsRunning(true);
                setShowPanel(true);
                const results = [];

                // ==========================================
                // INFRASTRUCTURE TESTS (Task Group 2)
                // ==========================================

                // Test 1: AppContext provides expected values to children
                try {
                    const hasContext = currentView !== undefined &&
                        typeof setCurrentView === 'function' &&
                        powerIndex !== undefined &&
                        streak !== undefined &&
                        userProfile !== undefined &&
                        database !== undefined &&
                        typeof showToast === 'function';

                    results.push({
                        group: 'infrastructure',
                        name: 'AppContext provides expected values to children',
                        passed: hasContext,
                        message: hasContext ? 'All context values accessible' : 'Missing context values'
                    });
                } catch (e) {
                    results.push({
                        group: 'infrastructure',
                        name: 'AppContext provides expected values to children',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 2: View navigation state changes correctly
                try {
                    results.push({
                        group: 'infrastructure',
                        name: 'View navigation state changes correctly',
                        passed: typeof setCurrentView === 'function',
                        message: 'setCurrentView function available and callable'
                    });
                } catch (e) {
                    results.push({
                        group: 'infrastructure',
                        name: 'View navigation state changes correctly',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 3: localStorage persistence saves and retrieves data
                try {
                    const testKey = 'edify_test_key';
                    const testValue = { test: 'value', timestamp: Date.now() };
                    storage.set(testKey, testValue);
                    const retrieved = storage.get(testKey);
                    storage.remove(testKey);

                    const passed = retrieved && retrieved.test === 'value';
                    results.push({
                        group: 'infrastructure',
                        name: 'localStorage persistence saves and retrieves data',
                        passed,
                        message: passed ? 'Data persisted correctly' : 'Data persistence failed'
                    });
                } catch (e) {
                    results.push({
                        group: 'infrastructure',
                        name: 'localStorage persistence saves and retrieves data',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 4: Toast notification appears and auto-dismisses
                try {
                    showToast('Test notification', 'info');
                    await new Promise(r => setTimeout(r, 100));

                    results.push({
                        group: 'infrastructure',
                        name: 'Toast notification appears and auto-dismisses',
                        passed: true,
                        message: 'Toast shown successfully (auto-dismisses in 3s)'
                    });
                } catch (e) {
                    results.push({
                        group: 'infrastructure',
                        name: 'Toast notification appears and auto-dismisses',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 5: DATABASE object contains all required content
                try {
                    // Content is API-hydrated (counts grow) — assert presence, not
                    // the embedded-fallback's exact sizes.
                    const hasCI = database.ciMentor &&
                        database.ciMentor.ceoInsights?.length >= 1 &&
                        database.ciMentor.processSteps?.length === 4;

                    const hasSimulator = database.decisionSimulator &&
                        database.decisionSimulator.scenarios?.length >= 1 &&
                        database.decisionSimulator.powerIndex?.scoreRanges?.length === 5;

                    const hasMoments = database.transformativeMoments &&
                        database.transformativeMoments.moments?.length >= 1 &&
                        database.transformativeMoments.tenKeyQuestions?.length === 10;

                    const passed = hasCI && hasSimulator && hasMoments;
                    results.push({
                        group: 'infrastructure',
                        name: 'DATABASE object contains all required content',
                        passed,
                        message: passed ? 'All database content present' : 'Missing database content'
                    });
                } catch (e) {
                    results.push({
                        group: 'infrastructure',
                        name: 'DATABASE object contains all required content',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 6: Navigation between all views works
                try {
                    const views = ['home', 'ci-mentor', 'simulator', 'moments', 'profile'];
                    let allValid = true;

                    for (const view of views) {
                        if (!views.includes(view)) {
                            allValid = false;
                            break;
                        }
                    }

                    results.push({
                        group: 'infrastructure',
                        name: 'All 5 navigation views are defined',
                        passed: allValid,
                        message: allValid ? 'All views available' : 'Missing views'
                    });
                } catch (e) {
                    results.push({
                        group: 'infrastructure',
                        name: 'All 5 navigation views are defined',
                        passed: false,
                        message: e.message
                    });
                }

                // ==========================================
                // HOME DASHBOARD TESTS (Task Group 3)
                // ==========================================

                // Test 7: Welcome section renders with correct content
                try {
                    const welcomeSection = document.querySelector('[data-testid="welcome-section"]');
                    const h1 = welcomeSection?.querySelector('h1');
                    const hasGreeting = /^(Good morning|Good afternoon|Good evening), .+\.$/.test(h1?.textContent?.trim() || '');
                    const hasDateKicker = /[A-Za-z]+, [A-Za-z]+ \d{1,2}/.test(welcomeSection?.textContent || '');

                    const passed = welcomeSection !== null && hasGreeting && hasDateKicker;
                    results.push({
                        group: 'home-dashboard',
                        name: 'Welcome section renders with correct content',
                        passed,
                        message: passed ? 'Welcome section displays correctly' : 'Welcome section missing or has incorrect content'
                    });
                } catch (e) {
                    results.push({
                        group: 'home-dashboard',
                        name: 'Welcome section renders with correct content',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 8: Power Index card displays current score and tier
                try {
                    const powerIndexCard = document.querySelector('[data-testid="power-index-card"]');
                    const scoreElement = document.querySelector('[data-testid="power-index-score"]');
                    const tierBadge = document.querySelector('[data-testid="tier-badge"]');

                    const hasScore = scoreElement?.textContent === String(powerIndex);
                    const hasTier = tierBadge !== null;

                    const passed = powerIndexCard !== null && hasScore && hasTier;
                    results.push({
                        group: 'home-dashboard',
                        name: 'Power Index card displays current score and tier',
                        passed,
                        message: passed ? `Score: ${powerIndex}, Standing present` : 'Decision index card missing elements'
                    });
                } catch (e) {
                    results.push({
                        group: 'home-dashboard',
                        name: 'Power Index card displays current score and tier',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 9: Feature cards navigate to correct views on click
                try {
                    const ciMentorCard = document.querySelector('[data-testid="feature-card-ci-mentor"]');
                    const simulatorCard = document.querySelector('[data-testid="feature-card-simulator"]');
                    const wisdomCard = document.querySelector('[data-testid="feature-card-ceo-wisdom"]');

                    // a2: the Molly card is there only when Molly is on for this
                    // member, and once today's practice is done the hero is a
                    // section holding its own buttons rather than one button.
                    const mollyOff = !!(currentUser && currentUser.mentorEnabled === false);
                    const heroDone = !!simulatorCard?.textContent?.includes("Today's practice is done");
                    const allCardsPresent = (ciMentorCard !== null || mollyOff) && simulatorCard !== null && wisdomCard !== null;

                    // Redesign: cards are role="button" surfaces
                    const allClickable =
                        (mollyOff || ciMentorCard?.getAttribute('role') === 'button') &&
                        (heroDone || simulatorCard?.getAttribute('role') === 'button') &&
                        wisdomCard?.getAttribute('role') === 'button';

                    const passed = allCardsPresent && allClickable;
                    results.push({
                        group: 'home-dashboard',
                        name: 'Feature cards navigate to correct views on click',
                        passed,
                        message: passed ? 'Case hero + both feature cards present and clickable' : 'Feature cards missing or not clickable'
                    });
                } catch (e) {
                    results.push({
                        group: 'home-dashboard',
                        name: 'Feature cards navigate to correct views on click',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 10: Quick stats section displays content counts
                try {
                    const homeView = document.querySelector('[data-testid="home-view"]');
                    const text = homeView?.textContent || '';
                    const hasInterviews = text.includes('CEO interviews');
                    const hasDecisions = text.includes('documented decisions');
                    const hasIndustries = text.includes('industries');

                    const passed = homeView !== null && hasInterviews && hasDecisions && hasIndustries;
                    results.push({
                        group: 'home-dashboard',
                        name: 'Quick stats section displays content counts',
                        passed,
                        message: passed ? 'Corpus stat row displays correctly' : 'Corpus stat row missing or has incorrect content'
                    });
                } catch (e) {
                    results.push({
                        group: 'home-dashboard',
                        name: 'Quick stats section displays content counts',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 11: Vision banner displays with gold accent
                try {
                    const heroCard = document.querySelector('[data-testid="feature-card-simulator"]');
                    const hasKicker = heroCard?.textContent?.includes("Today's decision");
                    // Or, once today's practice is done, the done state in its place.
                    const hasCta = heroCard?.textContent?.includes('Open the case file') || heroCard?.textContent?.includes("Today's practice is done");

                    const passed = heroCard !== null && hasKicker && hasCta;
                    results.push({
                        group: 'home-dashboard',
                        name: 'Vision banner displays with gold accent',
                        passed,
                        message: passed ? "Today's-decision hero card displays correctly" : 'Hero case card missing or incorrect content'
                    });
                } catch (e) {
                    results.push({
                        group: 'home-dashboard',
                        name: 'Vision banner displays with gold accent',
                        passed: false,
                        message: e.message
                    });
                }

                // ==========================================
                // CI MENTOR TESTS (Task Group 4)
                // ==========================================

                // Navigate to CI Mentor view for testing
                setCurrentView('ci-mentor');
                await new Promise(r => setTimeout(r, 700)); // Wait for view transition and initial greeting

                // Test 12: Initial greeting message appears from Molly
                try {
                    const ciMentorView = document.querySelector('[data-testid="ci-mentor-view"]');
                    const botAvatar = document.querySelector('[data-testid="bot-avatar"]');

                    // Molly's greeting is scheduled at 500ms + a 1000-2000ms
                    // "typing" delay, so a fixed 1500ms wait loses the race
                    // whenever the random draw lands high. Poll to the worst
                    // case instead of sleeping for less than it.
                    let updatedBotMessages = document.querySelectorAll('[data-testid="bot-message"]');
                    for (let waited = 0; waited < 4000 && updatedBotMessages.length === 0; waited += 100) {
                        await new Promise(r => setTimeout(r, 100));
                        updatedBotMessages = document.querySelectorAll('[data-testid="bot-message"]');
                    }
                    const hasGreeting = updatedBotMessages.length >= 1;
                    const hasAvatar = botAvatar !== null || document.querySelector('[data-testid="bot-avatar"]') !== null;

                    const passed = ciMentorView !== null && hasGreeting;
                    results.push({
                        group: 'ci-mentor',
                        name: 'Initial greeting message appears from Molly',
                        passed,
                        message: passed ? 'Molly greeting appears on view mount' : 'Greeting message not found'
                    });
                } catch (e) {
                    results.push({
                        group: 'ci-mentor',
                        name: 'Initial greeting message appears from Molly',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 13: Category selection populates input with sample challenge
                try {
                    const mentorView = document.querySelector('[data-testid="ci-mentor-view"]');
                    const hasMasthead = mentorView?.textContent?.includes('CI Mentor');
                    const hasDisclaimer = mentorView?.textContent?.includes('Illustrative guidance');

                    const passed = mentorView !== null && hasMasthead && hasDisclaimer;
                    results.push({
                        group: 'ci-mentor',
                        name: 'Category selection grid displays 6 categories',
                        passed,
                        message: passed ? 'Masthead + standing disclaimer displayed' : 'Mentor masthead/disclaimer not rendered'
                    });
                } catch (e) {
                    results.push({
                        group: 'ci-mentor',
                        name: 'Category selection grid displays 6 categories',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 14: Chat input and send button present
                try {
                    const chatInput = document.querySelector('[data-testid="chat-input"]');
                    const sendButton = document.querySelector('[data-testid="send-button"]');
                    const chatInputArea = document.querySelector('[data-testid="chat-input-area"]');

                    const hasInput = chatInput !== null;
                    const hasSendButton = sendButton !== null;
                    const hasInputArea = chatInputArea !== null;

                    const passed = hasInput && hasSendButton && hasInputArea;
                    results.push({
                        group: 'ci-mentor',
                        name: 'Chat input area with send button present',
                        passed,
                        message: passed ? 'Input and send button available' : 'Chat input area missing elements'
                    });
                } catch (e) {
                    results.push({
                        group: 'ci-mentor',
                        name: 'Chat input area with send button present',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 15: Process step indicator displays 4 steps
                try {
                    const processSteps = document.querySelector('[data-testid="process-steps"]');
                    const step1 = document.querySelector('[data-testid="process-step-1"]');
                    const step2 = document.querySelector('[data-testid="process-step-2"]');
                    const step3 = document.querySelector('[data-testid="process-step-3"]');
                    const step4 = document.querySelector('[data-testid="process-step-4"]');

                    const hasAllSteps = step1 !== null && step2 !== null && step3 !== null && step4 !== null;

                    const passed = processSteps !== null && hasAllSteps;
                    results.push({
                        group: 'ci-mentor',
                        name: 'Process step indicator displays 4 steps',
                        passed,
                        message: passed ? 'All 4 process steps displayed' : 'Process steps missing'
                    });
                } catch (e) {
                    results.push({
                        group: 'ci-mentor',
                        name: 'Process step indicator displays 4 steps',
                        passed: false,
                        message: e.message
                    });
                }

                // Test 16: Chat container is scrollable
                try {
                    const chatContainer = document.querySelector('[data-testid="chat-container"]');
                    const hasOverflow = chatContainer !== null;

                    const computedStyle = chatContainer ? window.getComputedStyle(chatContainer) : null;
                    const isScrollable = computedStyle?.overflowY === 'auto' || computedStyle?.overflowY === 'scroll';

                    const passed = hasOverflow && isScrollable;
                    results.push({
                        group: 'ci-mentor',
                        name: 'Chat container is scrollable',
                        passed,
                        message: passed ? 'Chat container configured for scrolling' : 'Chat container not scrollable'
                    });
                } catch (e) {
                    results.push({
                        group: 'ci-mentor',
                        name: 'Chat container is scrollable',
                        passed: false,
                        message: e.message
                    });
                }

                // ==========================================
                // DECISION SIMULATOR TESTS (scenario types + stepper)
                // ==========================================

                // Test 17: Scenario modes are well-formed
                try {
                    const scenarios = database.decisionSimulator.scenarios || [];
                    const typesOk = scenarios.every(s =>
                        s.scenarioType === undefined || s.scenarioType === 'match' || s.scenarioType === 'spotBad'
                    );
                    const spotBadOk = scenarios.filter(isSpotBad).every(s =>
                        Array.isArray(s.options) && s.options.length >= 2 &&
                        s.options.some(o => o.id === s.actualChoice)
                    );
                    const passed = typesOk && spotBadOk;
                    results.push({
                        group: 'simulator',
                        name: 'Scenario modes are well-formed',
                        passed,
                        message: passed ? 'All scenario types valid; spotBad answers point at real options' : 'Invalid scenarioType or spotBad actualChoice not among options'
                    });
                } catch (e) {
                    results.push({ group: 'simulator', name: 'Scenario modes are well-formed', passed: false, message: e.message });
                }

                // Test 18: isSpotBad defaults to match
                try {
                    const passed = isSpotBad({}) === false &&
                        isSpotBad(null) === false &&
                        isSpotBad({ scenarioType: 'match' }) === false &&
                        isSpotBad({ scenarioType: 'spotBad' }) === true;
                    results.push({
                        group: 'simulator',
                        name: 'isSpotBad defaults to match mode',
                        passed,
                        message: passed ? 'Only scenarioType "spotBad" flags the mode' : 'isSpotBad misclassifies a scenario'
                    });
                } catch (e) {
                    results.push({ group: 'simulator', name: 'isSpotBad defaults to match mode', passed: false, message: e.message });
                }

                // Test 19: the run's rail — Decide · The CEO · Takeaway, plus Molly
                // only when Molly is on — with Decide current and nothing done yet
                try {
                    setCurrentView('simulator');
                    await new Promise(r => setTimeout(r, 400));
                    document.querySelector('[data-testid="start-simulation-btn"]')?.click();
                    await new Promise(r => setTimeout(r, 400));
                    const rail = document.querySelector('[data-testid="practice-rail"]');
                    const steps = ['decide', 'ceo', 'takeaway'].map(id => document.querySelector(`[data-testid="rail-${id}"]`));
                    const mollyOn = !currentUser || currentUser.mentorEnabled !== false;
                    const mollyOk = !!document.querySelector('[data-testid="rail-molly"]') === mollyOn;
                    const decideCurrent = steps[0]?.getAttribute('aria-current') === 'step';
                    const noneDone = !!rail && !(rail.textContent || '').includes('✓');
                    const passed = !!rail && steps.every(Boolean) && mollyOk && decideCurrent && noneDone;
                    results.push({
                        group: 'simulator',
                        name: 'Rail shows Decide · The CEO · Takeaway with Decide current',
                        passed,
                        message: passed ? 'Rail renders its sections (Molly only when on), Decide current, none done' : 'Rail missing a section, Molly shown against the switch, or Decide not current'
                    });
                } catch (e) {
                    results.push({ group: 'simulator', name: 'Rail shows Decide · The CEO · Takeaway with Decide current', passed: false, message: e.message });
                }

                // Test 20: Bad Call branding on the home hero (only when today's case is spotBad)
                try {
                    setCurrentView('home');
                    await new Promise(r => setTimeout(r, 400));
                    const eligible = (database.decisionSimulator.scenarios || []).filter(
                        s => s.active !== false && Array.isArray(s.options) && s.options.length > 0
                    );
                    // Context state, not localStorage — after login the profile
                    // hydrate replaces context without rewriting the storage key,
                    // and the hero picks from context.
                    const completedIds = (completedSimulations || []).map(s => s.id);
                    const todays = pickPracticeCase(eligible, completedIds, world, practiceOptsOf(decisionProfile)).scenario;
                    if (!isSpotBad(todays)) {
                        results.push({
                            group: 'simulator',
                            name: 'Bad Call branding on home hero',
                            passed: true,
                            message: 'n/a — today\'s case is match mode'
                        });
                    } else {
                        const badge = document.querySelector('[data-testid="spotbad-badge-home"]');
                        results.push({
                            group: 'simulator',
                            name: 'Bad Call branding on home hero',
                            passed: !!badge,
                            message: badge ? 'Hero shows The Bad Call badge' : 'Today\'s case is spotBad but the hero badge is missing'
                        });
                    }
                } catch (e) {
                    results.push({ group: 'simulator', name: 'Bad Call branding on home hero', passed: false, message: e.message });
                }

                // Test 21: the retention policy page states the periods the worker
                // actually enforces. These two numbers are a promise to the user;
                // if src/db/archives.ts changes, this test must fail loudly.
                try {
                    setCurrentView('policy');
                    await new Promise(r => setTimeout(r, 400));
                    const view = document.querySelector('[data-testid="policy-view"]');
                    const text = (view?.textContent || '').toLowerCase();
                    const has90 = text.includes('90 days');
                    const has18 = text.includes('18 months');
                    const hasDelete = text.includes('delete');
                    results.push({
                        group: 'archive',
                        name: 'Retention policy is stated in-app',
                        passed: !!view && has90 && has18 && hasDelete,
                        message: !view ? 'Policy view did not render'
                            : (has90 && has18 && hasDelete)
                                ? 'States 90-day archive, 18-month deletion, and how to delete'
                                : `Missing: ${[!has90 && '90 days', !has18 && '18 months', !hasDelete && 'deletion'].filter(Boolean).join(', ')}`
                    });
                } catch (e) {
                    results.push({ group: 'archive', name: 'Retention policy is stated in-app', passed: false, message: e.message });
                }

                // Test 22: the archive list renders without an authenticated API —
                // an offline or signed-out user must get the empty state, not a crash.
                try {
                    setCurrentView('archives');
                    await new Promise(r => setTimeout(r, 600));
                    const view = document.querySelector('[data-testid="archives-view"]');
                    const rows = document.querySelectorAll('[data-testid^="archive-row-"]').length;
                    const empty = document.querySelector('[data-testid="archives-empty"]');
                    results.push({
                        group: 'archive',
                        name: 'Archived conversations view renders',
                        passed: !!view && (rows > 0 || !!empty),
                        message: !view ? 'Archives view did not render'
                            : rows > 0 ? `${rows} archived conversation(s) listed`
                            : 'Empty state shown'
                    });
                } catch (e) {
                    results.push({ group: 'archive', name: 'Archived conversations view renders', passed: false, message: e.message });
                }

                // Test 23: the interview library renders and never crashes on an
                // empty/offline API — an executive opening an unpopulated library
                // must get the "being prepared" copy, not a blank screen. While
                // the library is admin-only, a member's run tests the OTHER half
                // of that promise: that the door is shut and leads home rather
                // than nowhere.
                const libraryOpen = canSeeLibrary(currentUser);
                try {
                    setCurrentView('interviews');
                    await new Promise(r => setTimeout(r, 900));
                    const view = document.querySelector('[data-testid="interviews-view"]');
                    const cards = document.querySelectorAll('[data-testid^="interview-card-"]').length;
                    const empty = document.querySelector('[data-testid="interviews-empty"]');
                    const search = document.querySelector('[data-testid="interviews-search"]');
                    const home = document.querySelector('[data-testid="home-view"]');
                    results.push({
                        group: 'library',
                        name: libraryOpen ? 'Interview library renders' : 'Interview library is closed to members',
                        passed: libraryOpen
                            ? (!!view && !!search && (cards > 0 || !!empty))
                            : (!view && !!home),
                        message: !libraryOpen
                            ? (view ? 'Library rendered for a non-admin' : home ? 'Sent home' : 'Neither the library nor home rendered')
                            : !view ? 'Interviews view did not render'
                            : !search ? 'Search field missing'
                            : cards > 0 ? `${cards} interview(s) listed`
                            : 'Empty state shown'
                    });
                } catch (e) {
                    results.push({ group: 'library', name: 'Interview library renders', passed: false, message: e.message });
                }

                // Test 24: playlists reachable and safe when the user has none.
                try {
                    setCurrentView('playlists');
                    await new Promise(r => setTimeout(r, 900));
                    const view = document.querySelector('[data-testid="playlists-view"]');
                    const rows = document.querySelectorAll('[data-testid^="playlist-row-"]').length;
                    const empty = document.querySelector('[data-testid="playlists-empty"]');
                    const home = document.querySelector('[data-testid="home-view"]');
                    results.push({
                        group: 'library',
                        name: libraryOpen ? 'Playlists view renders' : 'Playlists are closed to members',
                        passed: libraryOpen ? (!!view && (rows > 0 || !!empty)) : (!view && !!home),
                        message: !libraryOpen
                            ? (view ? 'Playlists rendered for a non-admin' : home ? 'Sent home' : 'Neither playlists nor home rendered')
                            : !view ? 'Playlists view did not render'
                            : rows > 0 ? `${rows} playlist(s)` : 'Empty state shown'
                    });
                } catch (e) {
                    results.push({ group: 'library', name: 'Playlists view renders', passed: false, message: e.message });
                }

                // Test 25: the library must not have disturbed the simulator's
                // content path — these are separate tables and separate screens.
                try {
                    const scenarios = database.decisionSimulator.scenarios || [];
                    const intact = scenarios.length > 0 &&
                        scenarios.every(s => Array.isArray(s.options) && typeof s.actualChoice === 'string');
                    results.push({
                        group: 'library',
                        name: 'Simulator content unaffected by the library',
                        passed: intact,
                        message: intact ? `${scenarios.length} scenarios still well-formed`
                            : 'Scenario shape changed — the library must not touch simulator content'
                    });
                } catch (e) {
                    results.push({ group: 'library', name: 'Simulator content unaffected by the library', passed: false, message: e.message });
                }

                // Navigate back to home for other tests
                setCurrentView('home');
                await new Promise(r => setTimeout(r, 300));

                setTestResults(results);
                setIsRunning(false);

                if (onComplete) {
                    const allPassed = results.every(r => r.passed);
                    onComplete(allPassed, results);
                }
            }, [currentView, setCurrentView, powerIndex, streak, userProfile, database, showToast, onComplete, completedSimulations]);

            const filteredResults = activeTestGroup === 'all'
                ? testResults
                : testResults.filter(r => r.group === activeTestGroup);

            return (
                <>
                    <button
                        onClick={runTests}
                        disabled={isRunning}
                        style={{
                            position: 'fixed',
                            bottom: 'calc(var(--bottom-nav-height) + var(--space-4))',
                            right: 'var(--space-4)',
                            background: 'var(--navy)',
                            color: 'var(--white)',
                            padding: 'var(--space-2) var(--space-3)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-medium)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            zIndex: 'var(--z-dropdown)',
                            cursor: 'pointer',
                            border: 'none'
                        }}
                    >
                        <i className={`fa-solid ${isRunning ? 'fa-spinner fa-spin' : 'fa-flask-vial'}`} />
                        {isRunning ? 'Running...' : 'Run Tests'}
                    </button>

                    <div className={`test-panel ${showPanel ? 'show' : ''}`}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 'var(--space-3)'
                        }}>
                            <h3 style={{
                                fontFamily: 'var(--font-heading)',
                                fontSize: 'var(--text-base)',
                                color: 'var(--navy)'
                            }}>
                                Test Results
                            </h3>
                            <button
                                onClick={() => setShowPanel(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--gray-500)',
                                    cursor: 'pointer',
                                    padding: 'var(--space-1)'
                                }}
                            >
                                <i className="fa-solid fa-times" />
                            </button>
                        </div>

                        {/* Test Group Filter */}
                        <div style={{
                            display: 'flex',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-3)',
                            flexWrap: 'wrap'
                        }}>
                            {['all', 'infrastructure', 'home-dashboard', 'ci-mentor', 'simulator', 'archive', 'library'].map(group => (
                                <button
                                    key={group}
                                    onClick={() => setActiveTestGroup(group)}
                                    style={{
                                        padding: 'var(--space-1) var(--space-2)',
                                        borderRadius: 'var(--radius-sm)',
                                        border: 'none',
                                        fontSize: 'var(--text-xs)',
                                        cursor: 'pointer',
                                        background: activeTestGroup === group ? 'var(--navy)' : 'var(--gray-200)',
                                        color: activeTestGroup === group ? 'var(--white)' : 'var(--gray-700)'
                                    }}
                                >
                                    {group === 'all' ? 'All' : group === 'infrastructure' ? 'Infra' : group === 'home-dashboard' ? 'Home' : group === 'ci-mentor' ? 'CI Mentor' : group === 'simulator' ? 'Simulator' : group === 'archive' ? 'Archive' : 'Library'}
                                </button>
                            ))}
                        </div>

                        {testResults.length === 0 ? (
                            <p style={{ color: 'var(--gray-500)', fontSize: 'var(--text-sm)' }}>
                                Click "Run Tests" to execute tests.
                            </p>
                        ) : (
                            <>
                                <div style={{
                                    marginBottom: 'var(--space-3)',
                                    padding: 'var(--space-2)',
                                    background: filteredResults.every(r => r.passed) ? 'var(--success)' : 'var(--error)',
                                    color: 'var(--white)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    textAlign: 'center'
                                }}>
                                    {filteredResults.filter(r => r.passed).length}/{filteredResults.length} Tests Passed
                                </div>
                                {filteredResults.map((result, index) => (
                                    <div key={index} className="test-item">
                                        <i className={`fa-solid ${result.passed ? 'fa-check-circle test-pass' : 'fa-times-circle test-fail'}`} />
                                        <div>
                                            <div style={{ fontWeight: 'var(--font-medium)' }}>{result.name}</div>
                                            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--gray-500)' }}>
                                                {result.message}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </>
            );
        };

        // ============================================
        // LOGIN VIEW COMPONENT
        // ============================================
        // ============================================
        // THE SIGN-IN LINK FROM AN EMAIL (docs/EMAIL.md)
        // #email=…[&code=…][&reset=1] — read ONCE, at load, and wiped from the
        // address bar at once, before any screen decides anything: so it is
        // not left in history, and a sign-out later in this tab (which shows
        // the sign-in screen again without a reload) cannot find it and use a
        // stale code. LoginView takes it with takeSigninLink(); App checks it
        // against a session already stored on this device.
        // ============================================
        let SIGNIN_LINK = (() => {
            try {
                const params = new URLSearchParams(String(window.location.hash || '').replace(/^#/, ''));
                const email = String(params.get('email') || '').trim().toLowerCase();
                const code = String(params.get('code') || '').replace(/\D/g, '');
                if (!email && !code) return null;
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
                return {
                    email: /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) ? email : '',
                    code: code.length === 6 ? code : '',
                    reset: params.get('reset') === '1',
                    at: Date.now()
                };
            } catch (e) {
                return null;
            }
        })();
        // It lives as long as the code in it would: a link kept for a session
        // that later turns out to have expired is still useful, an hour-old
        // one is not.
        const takeSigninLink = () => {
            const link = SIGNIN_LINK;
            SIGNIN_LINK = null;
            return link && Date.now() - link.at < 15 * 60 * 1000 ? link : null;
        };

        const LoginView = ({ onLogin, sessionExpired }) => {
            const librarySize = useLibrarySize();
            const [username, setUsername] = useState('');
            const [password, setPassword] = useState('');
            const [isLoggingIn, setIsLoggingIn] = useState(false);
            const [mfaToken, setMfaToken] = useState(null);   // the half-signed-in step
            const [mfaCode, setMfaCode] = useState('');
            const [showModal, setShowModal] = useState(false);
            const [loginError, setLoginError] = useState(null); // { title, body } | null

            // EMAILED SIGN-IN CODES (docs/EMAIL.md). The default way in: type
            // your address, get a 6-digit code by email, no password needed.
            // An admin still sets a password on every account, but nobody has
            // to SEND it to anybody — and after a code sign-in the member may
            // choose their own, after which either way works.
            const [mode, setMode] = useState('code');          // 'code' | 'password'
            // 'ask' | 'enter' | 'link' — the last is a one-click link waiting
            // for its click (see takeSigninLink).
            const [codeStage, setCodeStage] = useState('ask');
            const [codeSentTo, setCodeSentTo] = useState(null); // the address a code went to THIS visit
            const [code, setCode] = useState('');
            const [resendAt, setResendAt] = useState(0);
            const [, setTick] = useState(0);                    // re-renders the resend countdown
            // "Forgot password" is the code door too — it ends on the
            // choose-a-password step whether or not they had one.
            const [forgot, setForgot] = useState(false);
            // Signed in by code, offered a password: { token, user, setupToken }.
            const [setup, setSetup] = useState(null);
            const [newPassword, setNewPassword] = useState('');
            const [newPassword2, setNewPassword2] = useState('');

            // Escape closes the request-access dialog
            useEffect(() => {
                if (!showModal) return;
                const onKey = (e) => { if (e.key === 'Escape') setShowModal(false); };
                window.addEventListener('keydown', onKey);
                return () => window.removeEventListener('keydown', onKey);
            }, [showModal]);

            // Focus follows the step. The link that was clicked to change it
            // has just been unmounted, which would otherwise drop focus to the
            // top of the document. Not on the first render: on a phone, a
            // keyboard springing up on arrival hides half the screen.
            const firstStepRef = useRef(true);
            useEffect(() => {
                if (firstStepRef.current) { firstStepRef.current = false; return; }
                const id = mode === 'password' ? 'ed-login-email'
                    : codeStage === 'link' ? 'ed-login-continue'
                    : codeStage === 'enter' ? 'ed-login-email-code' : 'ed-login-email';
                const el = document.getElementById(id);
                if (el && el.focus) el.focus();
            }, [mode, codeStage]);

            // The resend countdown only ticks while there is one to show.
            useEffect(() => {
                if (codeStage !== 'enter' || resendAt <= Date.now()) return;
                const t = setInterval(() => setTick(n => n + 1), 1000);
                return () => clearInterval(t);
            }, [codeStage, resendAt]);

            const CRED_ERROR_TITLE = 'That email and password don’t match our records.';
            const CRED_ERROR_BODY = 'Check for typos, or if your access was recently provisioned, allow a few minutes.';

            // Where every way in ends — password, two-factor, emailed code,
            // a newly chosen password.
            //
            // A mentor signing in HERE stays here. This used to hand the
            // session straight to the portal when the account's home door was
            // the portal — which read as helpful and meant a mentor could not
            // reach the member app by signing in at all. They are a member too
            // (docs/IDENTITY.md): they work cases, talk to Molly and read the
            // library like anyone else, and the whole point of the hats is
            // that one person moves between the two. The door they signed in
            // at is the door they land in, and Mentor desk in the nav takes
            // them across.
            const finish = (token, user) => onLogin(token, user);

            const handleLogin = async (e) => {
                e.preventDefault();
                if (isLoggingIn) return;
                if (!username.trim().includes('@') || !password.trim()) {
                    setLoginError({ title: CRED_ERROR_TITLE, body: CRED_ERROR_BODY });
                    return;
                }

                setIsLoggingIn(true);
                setLoginError(null);
                try {
                    const res = await fetch(`${API_URL}/api/auth/login`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: username.trim().toLowerCase(), password })
                    });
                    const data = await res.json().catch(() => ({}));
                    // An admin account with two-factor on gets a CHALLENGE here,
                    // not a session: { mfaRequired: true, mfaToken }. Without
                    // this branch the response has no `token`, falls through to
                    // the bottom, and the app tells someone with the right
                    // password that their credentials are wrong.
                    if (res.ok && data.success && data.data && data.data.mfaRequired) {
                        setMfaToken(data.data.mfaToken);
                        setMfaCode('');
                        setIsLoggingIn(false);
                        return;
                    }
                    if (res.ok && data.success && data.data && data.data.token) {
                        finish(data.data.token, data.data.user);
                    } else if (res.status === 429) {
                        setLoginError({ title: 'Too many sign-in attempts.', body: 'For your security, further attempts are paused. Try again later, or contact your program director.' });
                        setIsLoggingIn(false);
                    } else if (res.status >= 500) {
                        setLoginError({ title: 'Sign-in is temporarily unavailable.', body: 'This is on our side, not yours. Please try again shortly.' });
                        setIsLoggingIn(false);
                    } else {
                        setLoginError({ title: CRED_ERROR_TITLE, body: CRED_ERROR_BODY });
                        setIsLoggingIn(false);
                    }
                } catch (err) {
                    setLoginError({ title: 'Could not reach the server.', body: 'Check your connection and try again.' });
                    setIsLoggingIn(false);
                }
            };


            // The second step. Only reached when the account has two-factor on.
            const handleVerify = async () => {
                const code = mfaCode.replace(/\D/g, '');
                if (code.length !== 6) {
                    setLoginError({ title: 'Enter the 6-digit code.', body: 'It is in your authenticator app.' });
                    return;
                }
                setIsLoggingIn(true);
                setLoginError(null);
                try {
                    const res = await fetch(`${API_URL}/api/auth/mfa`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ mfaToken, code })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (res.ok && data.success && data.data && data.data.token) {
                        finish(data.data.token, data.data.user);
                        return;
                    }
                    // The five-minute challenge has expired: send them back to
                    // the password rather than leaving them typing codes into a
                    // door that has closed.
                    if (res.status === 401 && /expired/i.test((data && data.error) || '')) {
                        startAgain();
                        setLoginError({ title: 'That sign-in timed out.', body: 'Start again.' });
                    } else {
                        setLoginError({ title: 'That code was not accepted.', body: (data && data.error) || 'Check your authenticator and try the next one.' });
                    }
                    setIsLoggingIn(false);
                } catch (err) {
                    setLoginError({ title: 'Could not reach the server.', body: 'Check your connection and try again.' });
                    setIsLoggingIn(false);
                }
            };

            // Back to the beginning of whichever door they came in by — never
            // to a "check your email" screen holding a code already spent.
            const startAgain = () => {
                setMfaToken(null);
                setMfaCode('');
                setPassword('');
                setCode('');
                setCodeSentTo(null);
                setCodeStage('ask');
                setLoginError(null);
            };

            // ---- emailed codes ------------------------------------------------

            const requestCode = async (e) => {
                if (e && e.preventDefault) e.preventDefault();
                if (isLoggingIn) return;
                const email = username.trim().toLowerCase();
                if (!email.includes('@')) {
                    setLoginError({ title: 'Enter your email address.', body: 'Use the address your invitation was sent to.' });
                    return;
                }
                setIsLoggingIn(true);
                setLoginError(null);
                try {
                    const res = await fetch(`${API_URL}/api/auth/code`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, reset: forgot })
                    });
                    const data = await res.json().catch(() => ({}));
                    // A worker that predates email sign-in answers 404: the same
                    // as "not available" — the password door still works.
                    const unavailable = (data && data.emailUnavailable) || res.status === 404 || res.status === 405;
                    if (res.ok && data.success) {
                        // The server answers the same whether or not the
                        // address has an account, so this screen does too.
                        setCodeSentTo(email);
                        setCodeStage('enter');
                        setCode('');
                        setResendAt(Date.now() + 30000);
                        // "Send a new code" disables itself for the countdown,
                        // which drops focus; the code box is where it belongs.
                        setTimeout(() => {
                            const el = document.getElementById('ed-login-email-code');
                            if (el && el.focus) el.focus();
                        }, 0);
                    } else if (unavailable && forgot) {
                        // Not "use your password" — it is the thing they forgot.
                        setLoginError({ title: 'Password resets by email are not available right now.', body: 'Your program director can reset it for you.' });
                    } else if (unavailable) {
                        setMode('password');
                        setLoginError({ title: 'Email sign-in is not available right now.', body: 'Sign in with your password instead.' });
                    } else if (res.status === 429) {
                        // The server says which limit: "wait a few minutes" is
                        // true of one and false of the daily ones.
                        setLoginError({ title: 'Too many codes asked for.', body: (data && data.error) || 'Wait a few minutes, then use the newest code you were sent.' });
                    } else if (res.status === 400) {
                        setLoginError({ title: 'Enter your email address.', body: (data && data.error) || 'Use the address your invitation was sent to.' });
                    } else {
                        setLoginError({ title: 'Sign-in is temporarily unavailable.', body: 'This is on our side, not yours. Please try again shortly.' });
                    }
                } catch (err) {
                    setLoginError({ title: 'Could not reach the server.', body: 'Check your connection and try again.' });
                }
                setIsLoggingIn(false);
            };

            const verifyCode = async (emailArg, codeArg, cameForReset) => {
                const email = String(emailArg || '').trim().toLowerCase();
                const digits = String(codeArg || '').replace(/\D/g, '');
                if (!email.includes('@')) {
                    setLoginError({ title: 'Enter your email address.', body: 'The one the code was sent to.' });
                    return;
                }
                if (digits.length !== 6) {
                    setLoginError({ title: 'Enter the 6-digit code.', body: 'It is in the email we sent you.' });
                    return;
                }
                setIsLoggingIn(true);
                setLoginError(null);
                try {
                    const res = await fetch(`${API_URL}/api/auth/code/verify`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email, code: digits })
                    });
                    const data = await res.json().catch(() => ({}));
                    // A code is a first factor: staff with two-factor on still
                    // get the authenticator step after it.
                    if (res.ok && data.success && data.data && data.data.mfaRequired) {
                        setMfaToken(data.data.mfaToken);
                        setMfaCode('');
                        setIsLoggingIn(false);
                        return;
                    }
                    if (res.ok && data.success && data.data && data.data.token) {
                        const { token, user, passwordSetupToken } = data.data;
                        // Offer a password of their own when they have none —
                        // the one on the account is the admin's, and they were
                        // never told it — and always after "Forgot password".
                        if (passwordSetupToken && (cameForReset || !(user && user.passwordChosen))) {
                            // Signed in NOW, not after this step: a phone that
                            // reloads the tab (a trip to the password manager
                            // will do it) must not lose a session whose code is
                            // already spent. A reload simply opens the app.
                            storage.set(STORAGE_KEYS.APP_TOKEN, token);
                            storage.set(STORAGE_KEYS.APP_USER, user);
                            setSetup({ token, user, setupToken: passwordSetupToken });
                            setNewPassword('');
                            setNewPassword2('');
                            setIsLoggingIn(false);
                            return;
                        }
                        finish(token, user);
                        return;
                    }
                    // A one-click link that did not work falls back to the
                    // ordinary form, address showing, so it can be corrected.
                    setCodeStage(s => (s === 'link' ? 'enter' : s));
                    if (data && data.staffPasswordOnly) {
                        setMode('password');
                        setLoginError({ title: 'Staff accounts sign in with a password.', body: data.error || '' });
                    } else if (res.status === 429) {
                        setLoginError({ title: 'Too many sign-in attempts.', body: 'For your security, further attempts are paused. Try again later, or contact your program director.' });
                    } else if (res.status >= 500 || res.status === 404) {
                        setLoginError({ title: 'Sign-in is temporarily unavailable.', body: 'This is on our side, not yours. Please try again shortly.' });
                    } else {
                        setLoginError({ title: 'That code didn’t work.', body: (data && data.error) || 'Check the newest email, or ask for a new code.' });
                    }
                    setIsLoggingIn(false);
                } catch (err) {
                    setLoginError({ title: 'Could not reach the server.', body: 'Check your connection and try again.' });
                    setIsLoggingIn(false);
                }
            };

            // The link from an email (read and wiped at load — takeSigninLink).
            // A Welcome email's carries only the address, which fills the box.
            // A code email's also carries the code, and waits for a CLICK:
            // corporate mail scanners open links to inspect them, and one that
            // signed in on arrival would spend the code before its owner saw
            // it — and would sign a victim into an account of an attacker's
            // choosing with no click at all.
            useEffect(() => {
                const link = takeSigninLink();
                if (!link) return;
                if (link.email) setUsername(link.email);
                if (link.reset) setForgot(true);
                if (link.code) {
                    setMode('code');
                    setCode(link.code);
                    setCodeSentTo(null);
                    setCodeStage(link.email ? 'link' : 'enter');
                }
            }, []);

            const savePassword = async (e) => {
                if (e && e.preventDefault) e.preventDefault();
                if (isLoggingIn || !setup) return;
                if (newPassword.length < 8) {
                    setLoginError({ title: 'Use at least 8 characters.', body: 'A short sentence is easier to remember than a jumble.' });
                    return;
                }
                if (newPassword !== newPassword2) {
                    setLoginError({ title: 'The two passwords don’t match.', body: 'Type the same one in both boxes.' });
                    return;
                }
                setIsLoggingIn(true);
                setLoginError(null);
                try {
                    const res = await fetch(`${API_URL}/api/auth/password`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ setupToken: setup.setupToken, password: newPassword })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (res.ok && data.success && data.data && data.data.token) {
                        finish(data.data.token, data.data.user);
                        return;
                    }
                    setLoginError({
                        title: 'Your password was not saved.',
                        body: (data && data.error) || 'You are still signed in — skip this for now and try again next time.'
                    });
                    setIsLoggingIn(false);
                } catch (err) {
                    setLoginError({ title: 'Could not reach the server.', body: 'Check your connection and try again.' });
                    setIsLoggingIn(false);
                }
            };

            const label = {
                fontSize: '11.5px', letterSpacing: '0.12em', textTransform: 'uppercase',
                color: 'var(--text-tertiary)', fontWeight: 600, marginBottom: '7px', display: 'block'
            };
            // The quiet secondary actions: buttons, so they are reachable by
            // keyboard and announced as actions, styled as the links they read as.
            const linkBtn = {
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                fontSize: '13px', fontFamily: 'inherit', textDecoration: 'none'
            };
            const hint = { fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.55, margin: '10px 0 0', textAlign: 'center' };
            const resendIn = Math.max(0, Math.ceil((resendAt - Date.now()) / 1000));
            const toPassword = () => { setMode('password'); setForgot(false); setLoginError(null); };
            const toCode = (stage) => { setMode('code'); setCodeStage(stage); setLoginError(null); };
            const emailField = (
                <>
                    <label style={label} htmlFor="ed-login-email">Email</label>
                    <input
                        id="ed-login-email"
                        type="email"
                        className="ed-input"
                        style={{ marginBottom: '18px' }}
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        autoComplete="email"
                        placeholder="you@company.com"
                    />
                </>
            );

            return (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 28px', background: 'var(--cream)', overflowY: 'auto' }}>
                    {/* auto margins centre the card when it fits and collapse when it
                        doesn't, so an error/session card can never push the Sign in
                        button out of reach on a short viewport */}
                    <div className="ed-pagein" style={{ width: '100%', maxWidth: '360px', margin: 'auto 0', display: 'flex', flexDirection: 'column' }}>
                        <BrandLockup size={56} byline style={{ marginBottom: '28px', alignSelf: 'center' }} />

                        {sessionExpired && (
                            <div role="status" style={{ background: 'var(--warning-surface)', border: '1px solid var(--warning-border)', borderRadius: '12px', padding: '14px 16px', marginBottom: '24px' }}>
                                <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold-darker)', fontWeight: 700, marginBottom: '4px' }}>Session ended</div>
                                <div style={{ fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.55 }}>For your security, sessions close after 24 hours. Sign back in to pick up where you left off — your work with Molly is saved.</div>
                            </div>
                        )}

                        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '27px', lineHeight: 1.3, color: 'var(--navy)', fontWeight: 600, margin: '0 0 8px' }}>Decision intelligence for emerging chief executives.</h1>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 30px' }} data-testid="login-library-size">Grounded in {librarySize ? `${librarySize.toLocaleString()}+ ` : ''}verbatim CEO interviews.</p>

                        {loginError && (
                            <div role="alert" style={{ background: 'var(--error-surface)', border: '1px solid var(--error-border)', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' }}>
                                <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--error-ink)', marginBottom: '3px' }}>{loginError.title}</div>
                                <div style={{ fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                                    {loginError.body}{' '}
                                    <a href="#" className="ed-link" style={{ fontWeight: 600 }} onClick={(e) => { e.preventDefault(); setShowModal(true); }}>Contact your program director</a>
                                </div>
                            </div>
                        )}

                        {/* The second step, when the account has two-factor on.
                            Only admin accounts can, and they sign in here to
                            look at the member app — so this exists to stop the
                            app telling somebody with the right password that
                            their credentials are wrong. */}
                        {mfaToken ? (
                        <form onSubmit={(e) => { e.preventDefault(); handleVerify(); }} noValidate style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={label} htmlFor="ed-login-code">Authentication code</label>
                            <input
                                id="ed-login-code"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                placeholder="000000"
                                className="ed-input"
                                style={{ marginBottom: '26px', letterSpacing: '0.35em', textAlign: 'center', fontSize: '20px' }}
                                value={mfaCode}
                                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                autoFocus
                            />
                            <button type="submit" className="ed-btn ed-btn-navy" style={{ fontWeight: 600 }} disabled={isLoggingIn}>
                                {isLoggingIn ? 'Checking…' : 'Verify'}
                            </button>
                            <button
                                type="button"
                                className="ed-link"
                                style={{ background: 'none', border: 'none', marginTop: '16px', fontSize: '12.5px', color: 'var(--text-secondary)', cursor: 'pointer' }}
                                onClick={startAgain}
                            >
                                Start again
                            </button>
                        </form>
                        ) : setup ? (
                        /* Signed in by code. The password on the account is one an
                           admin set and never told them; this is where they get
                           one of their own. Skippable — the code works every time. */
                        <form onSubmit={savePassword} noValidate style={{ display: 'flex', flexDirection: 'column' }} data-testid="login-set-password">
                            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                                {forgot ? 'Choose a new password' : 'You’re in. Set a password?'}
                            </h2>
                            <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
                                {forgot
                                    ? 'Your old password stops working, and you are signed out everywhere else.'
                                    : 'Next time you can sign in with it, or with an emailed code — whichever is easier. Saving one signs you out on your other devices.'}
                            </p>
                            <label style={label} htmlFor="ed-login-new-password">New password</label>
                            <input
                                id="ed-login-new-password"
                                type="password"
                                className="ed-input"
                                style={{ marginBottom: '18px' }}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                                autoFocus
                            />
                            <label style={label} htmlFor="ed-login-new-password2">Type it again</label>
                            <input
                                id="ed-login-new-password2"
                                type="password"
                                className="ed-input"
                                style={{ marginBottom: '26px' }}
                                value={newPassword2}
                                onChange={(e) => setNewPassword2(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                            />
                            <button type="submit" className="ed-btn ed-btn-navy" style={{ fontWeight: 600 }} disabled={isLoggingIn}>
                                {isLoggingIn ? 'Saving…' : 'Save password'}
                            </button>
                            <button type="button" className="ed-link" style={{ ...linkBtn, marginTop: '16px', alignSelf: 'center', color: 'var(--text-secondary)' }}
                                onClick={() => finish(setup.token, setup.user)} disabled={isLoggingIn}>
                                {forgot ? 'Not now' : 'Skip for now — I’ll use email codes'}
                            </button>
                        </form>
                        ) : mode === 'code' && codeStage === 'link' ? (
                        <form onSubmit={(e) => { e.preventDefault(); verifyCode(username, code, forgot); }} noValidate style={{ display: 'flex', flexDirection: 'column' }} data-testid="login-code-link">
                            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                                {forgot ? 'Reset your password' : 'Sign in'}
                            </h2>
                            <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
                                Continue as <strong style={{ color: 'var(--navy)' }}>{username}</strong> with the code from your email.
                            </p>
                            <button type="submit" id="ed-login-continue" className="ed-btn ed-btn-navy" style={{ fontWeight: 600 }} disabled={isLoggingIn}>
                                {isLoggingIn ? 'Checking…' : 'Continue'}
                            </button>
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                                <button type="button" className="ed-link" style={linkBtn}
                                    onClick={() => { setUsername(''); setCode(''); setForgot(false); toCode('ask'); }}>
                                    Not you? Use a different email
                                </button>
                            </div>
                        </form>
                        ) : mode === 'code' && codeStage === 'enter' ? (
                        <form onSubmit={(e) => { e.preventDefault(); verifyCode(codeSentTo || username, code, forgot); }} noValidate style={{ display: 'flex', flexDirection: 'column' }} data-testid="login-code-enter">
                            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 6px' }}>
                                {codeSentTo ? 'Check your email' : 'Enter your code'}
                            </h2>
                            <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '0 0 20px' }}>
                                {codeSentTo
                                    ? <>If <strong style={{ color: 'var(--navy)' }}>{codeSentTo}</strong> has an account, a 6-digit code is on its way. It lasts 15 minutes.</>
                                    : 'Type the address the code was sent to, and the 6-digit code from the email.'}
                            </p>
                            {!codeSentTo && emailField}
                            <label style={label} htmlFor="ed-login-email-code">Sign-in code</label>
                            <input
                                id="ed-login-email-code"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                // No maxLength: it would cut a pasted "Your code: 819 659"
                                // BEFORE the digits are picked out of it below.
                                placeholder="000000"
                                className="ed-input"
                                style={{ marginBottom: '26px', letterSpacing: '0.35em', textAlign: 'center', fontSize: '20px' }}
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                autoFocus
                            />
                            <button type="submit" className="ed-btn ed-btn-navy" style={{ fontWeight: 600 }} disabled={isLoggingIn}>
                                {isLoggingIn ? 'Checking…' : 'Sign in'}
                            </button>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '16px' }}>
                                {codeSentTo ? (
                                    <button type="button" className="ed-link" style={linkBtn} disabled={isLoggingIn || resendIn > 0} onClick={requestCode}>
                                        {resendIn > 0 ? `Send a new code (${resendIn}s)` : 'Send a new code'}
                                    </button>
                                ) : (
                                    <button type="button" className="ed-link" style={linkBtn} onClick={() => toCode('ask')}>Email me a code instead</button>
                                )}
                                <button type="button" className="ed-link" style={linkBtn}
                                    onClick={() => { setCodeSentTo(null); setCode(''); toCode('ask'); }}>
                                    Use a different email
                                </button>
                            </div>
                        </form>
                        ) : mode === 'code' ? (
                        <form onSubmit={requestCode} noValidate style={{ display: 'flex', flexDirection: 'column' }} data-testid="login-code-ask">
                            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 16px' }}>
                                {forgot ? 'Reset your password' : 'Sign in'}
                            </h2>
                            {forgot && (
                                <p style={{ fontSize: '13.5px', color: 'var(--text-secondary)', lineHeight: 1.6, margin: '-8px 0 18px' }}>
                                    We’ll email you a code. Once you’re in, you can choose a new password. (Staff accounts are reset by an owner, from the Team tab.)
                                </p>
                            )}
                            {emailField}
                            <button type="submit" className="ed-btn ed-btn-navy" style={{ fontWeight: 600, marginTop: '8px' }} disabled={isLoggingIn}>
                                {isLoggingIn ? 'Sending…' : 'Email me a sign-in code'}
                            </button>
                            <p style={hint}>We’ll send a 6-digit code. No password needed.</p>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginTop: '18px' }}>
                                <button type="button" className="ed-link" style={linkBtn} onClick={() => { setCodeSentTo(null); setCode(''); toCode('enter'); }}>
                                    I already have a code
                                </button>
                                <button type="button" className="ed-link" style={linkBtn} onClick={toPassword}>
                                    Or sign in with a password
                                </button>
                            </div>
                        </form>
                        ) : (
                        <form onSubmit={handleLogin} noValidate style={{ display: 'flex', flexDirection: 'column' }} data-testid="login-password">
                            {emailField}
                            <label style={label} htmlFor="ed-login-password">Password</label>
                            <input
                                id="ed-login-password"
                                type="password"
                                className="ed-input"
                                style={{ marginBottom: '26px' }}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                            />
                            <button type="submit" className="ed-btn ed-btn-navy" style={{ fontWeight: 600 }} disabled={isLoggingIn}>
                                {isLoggingIn ? 'Signing in…' : 'Sign in'}
                            </button>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '16px' }}>
                                {/* Self-service now: a code by email, then a new password. */}
                                <button type="button" className="ed-link" style={linkBtn} onClick={() => { setForgot(true); setPassword(''); toCode('ask'); }}>
                                    Forgot password?
                                </button>
                                <button type="button" className="ed-link" style={linkBtn} onClick={() => { setForgot(false); toCode('ask'); }}>
                                    Email me a code instead
                                </button>
                            </div>
                        </form>
                        )}

                        {!setup && !mfaToken && (
                            <p style={{ ...hint, marginTop: '22px' }}>
                                Access is by invitation only.{' '}
                                <a href="#" className="ed-link" style={{ textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); setShowModal(true); }}>Request access</a>
                            </p>
                        )}

                        {/* Said before they sign in (tester feedback, 30 Sep 2026): trust is
                            built from the first screen, not found later in a menu. */}
                        <div data-testid="login-privacy" style={{ marginTop: '22px', paddingTop: '16px', borderTop: '1px solid var(--border-card)', fontSize: '12.5px', color: 'var(--text-secondary)', lineHeight: 1.6, textAlign: 'center' }}>
                            <strong style={{ color: 'var(--navy)' }}>🔒 Confidential by design.</strong> Your questions and business details are never shared outside {BRAND.name} unless you choose to share them.
                        </div>
                    </div>

                    {/* Request access / contact dialog — unified modal treatment */}
                    {/* Through ModalPortal like every other dialog: it had the
                        role and the aria-modal but none of the behaviour — no
                        Escape, focus left on the sign-in form behind it, and Tab
                        walking straight back out into that form. */}
                    {showModal && (
                        <ModalPortal onEscape={() => setShowModal(false)}>
                        <div onClick={() => setShowModal(false)} style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal-stats)', background: 'var(--overlay-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                            <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Access is by invitation" className="ed-pagein" style={{ width: '100%', maxWidth: '340px', background: 'var(--white)', borderRadius: '20px', padding: '24px', textAlign: 'center', boxShadow: 'var(--shadow-modal)' }}>
                                <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '19px', color: 'var(--navy)', fontWeight: 600, margin: '0 0 8px' }}>Access is by invitation</h3>
                                <p style={{ fontSize: '13.5px', color: 'var(--text-body)', lineHeight: 1.6, margin: '0 0 18px' }}>
                                    {BRAND.name} accounts are set up by your program director. If you were expecting an invitation, ask them to send it — or to check the address it went to. Forgotten your password? Use “Forgot password?” to reset it with an emailed code.
                                </p>
                                <button className="ed-btn ed-btn-gold" style={{ width: '100%', borderRadius: '12px', minHeight: '46px', fontWeight: 600 }} onClick={() => setShowModal(false)}>
                                    Got it
                                </button>
                            </div>
                        </div>
                        </ModalPortal>
                    )}
                </div>
            );
        };

        // ============================================
        // MAIN APP COMPONENT
        // ============================================
        function App() {
            // Login state — real auth (Phase 1.5). App is gated on a stored bearer token; no guest access.
            const [authToken, setAuthToken] = useState(() => storage.get(STORAGE_KEYS.APP_TOKEN, null));
            // The token signed in NOW, for work started from an older render.
            // Views call refreshDecisionProfile after an await (a lock-in, a
            // note, an attached question), and that closure keeps the token it
            // was rendered with — so a slow request from the member who just
            // signed out would count as the newest and land their record, notes
            // and all, in the next member's session. Kept current on every
            // render, and dropped at sign-out itself.
            const authTokenRef = useRef(authToken);
            authTokenRef.current = authToken;
            const [currentUser, setCurrentUser] = useState(() => storage.get(STORAGE_KEYS.APP_USER, null));
            const hydratedProfileRef = useRef(false); // becomes true once the server profile has loaded
            const hydrateTimerRef = useRef(null);
            // True once hydration has given up: saving is off for this session
            // and the app says so (see the banner in the shell).
            const [profileLoadFailed, setProfileLoadFailed] = useState(false);
            // Bumping this re-runs hydration without disturbing the token.
            const [authTokenTick, setAuthTokenTick] = useState(0);
            // Set for the duration of a retry so the hydrate effect knows not to
            // reset the session — see the clean-slate block.
            const isProfileRetryRef = useRef(false);
            const retryProfileLoad = useCallback(() => {
                isProfileRetryRef.current = true;
                setProfileLoadFailed(false);
                setAuthTokenTick(t => t + 1);
            }, []);
            const profileSyncRef = useRef(null);       // debounce timer for PUT /api/profile
            // The version of the profile row this session last saw. Sent with every
            // save so the server can tell a stale write from a current one: two of a
            // member's OWN sessions — the installed app and a desktop tab — used to
            // overwrite each other's prompt lines, kept prompts and lesson progress
            // silently, version history and all.
            const profileVersionRef = useRef(null);
            const syncWarnedRef = useRef(false);       // warn once per session on a failing sync
            const navSeenRef = useRef(false);          // telemetry: first nav of a session is 'initial'
            const [showApp, setShowApp] = useState(() => !!storage.get(STORAGE_KEYS.APP_TOKEN, null));
            // Redesign: session-expired arrivals get a designed notice on the login screen
            const [sessionExpired, setSessionExpired] = useState(false);
            // Redesign: ≥980px swaps the mobile chrome (topbar/bottom nav/FAB) for a sidebar
            const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 980);
            // Redesign: brief "Preparing your briefing…" skeleton on Home right after sign-in
            const [homeLoading, setHomeLoading] = useState(false);
            // Views can hide the floating Molly pill while it would cover their
            // own controls (Ask-a-CEO composer/conversation, Profile edit card).
            const [fabSuppressed, setFabSuppressed] = useState(false);

            useEffect(() => {
                const onResize = () => setIsDesktop(window.innerWidth >= 980);
                window.addEventListener('resize', onResize);
                return () => window.removeEventListener('resize', onResize);
            }, []);

            // Database state - load from API or use embedded
            const [database, setDatabase] = useState(DATABASE);
            // Only spin while we actually intend to fetch: the spinner renders
            // ahead of the login gate, so a signed-out visitor must not land on
            // it forever now that the content fetch waits for a token.
            const [isLoadingData, setIsLoadingData] = useState(!!API_URL && !!storage.get(STORAGE_KEYS.APP_TOKEN, null));

            // Load content once signed in, and again whenever the token changes
            // (login, or a re-login after expiry).
            useEffect(() => {
                if (!API_URL || !authToken) return;
                setIsLoadingData(true);
                loadDatabaseFromAPI(authToken).then(apiData => {
                    if (apiData) {
                        setDatabase(mergeAPIData(DATABASE, apiData));
                    }
                    setIsLoadingData(false);
                }).catch(err => {
                    console.warn('API load failed:', err);
                    setIsLoadingData(false);
                });
            }, [authToken]);

            // Handle login — LoginView passes the token + user returned by /api/auth/login
            const handleLogin = (token, user) => {
                storage.set(STORAGE_KEYS.APP_TOKEN, token);
                storage.set(STORAGE_KEYS.APP_USER, user);
                // A/B: the account decides which build this user gets. The token is
                // already stored, and localStorage is origin-scoped, so the other
                // build resumes the session without a second sign-in. Bail out of
                // the rest of the handler — this page is on its way out.
                if (routeToVariant(user && user.variant)) return;
                Telemetry.track('login', {});
                setAuthToken(token);
                setCurrentUser(user);
                setSessionExpired(false);
                // Honour a ?view= deep link the visitor arrived on (initialView is
                // 'home' when there isn't one); resetting between users is logout's job.
                setCurrentView(initialView);
                setShowApp(true);
                // Briefing skeleton while the fresh session settles in
                setHomeLoading(true);
                setTimeout(() => setHomeLoading(false), 1100);
            };

            /**
             * Viewing as somebody else — docs/IMPERSONATION.md.
             *
             * The banner is driven by `currentUser.impersonatedBy`, which comes
             * from GET /api/me and therefore from the SIGNED claim on the token
             * being used. Nothing the console wrote into localStorage on its way
             * out could be trusted for this: a page's claim about itself is
             * clearable and forgeable, and would be missing entirely if somebody
             * pasted a token straight in. The banner is present exactly when the
             * session really is an impersonation.
             */
            const impersonatedBy = currentUser && currentUser.impersonatedBy;

            const endImpersonation = async () => {
                // Best effort: the audit trail wants a closing bracket, but a
                // failed request must not trap somebody inside the session.
                try {
                    await fetch(`${API_URL}/api/impersonate/end`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${authToken}` }
                    });
                } catch (e) { /* going back to the console regardless */ }
                // NOT handleLogout(): that fires a `logout` event, which would be
                // the admin's exit recorded as the member signing out. Same
                // cleanup, no telemetry.
                storage.remove(STORAGE_KEYS.APP_TOKEN);
                storage.remove(STORAGE_KEYS.APP_USER);
                [STORAGE_KEYS.USER_PROFILE, STORAGE_KEYS.POWER_INDEX, STORAGE_KEYS.STREAK,
                 STORAGE_KEYS.COMPLETED_SIMULATIONS, STORAGE_KEYS.CI_SESSIONS,
                 STORAGE_KEYS.SAVED_MOMENTS, STORAGE_KEYS.CEO_ANSWERS,
                 STORAGE_KEYS.CEO_WISDOM_CONVERSATIONS, STORAGE_KEYS.SAVED_NEXT_MOVES,
                 STORAGE_KEYS.QUOTE_SHOWN_DATE, STORAGE_KEYS.FEEDBACK_STATE,
                 STORAGE_KEYS.LAST_ACTIVE_DATE].forEach(k => storage.remove(k));
                // The member's record is memory only; drop it, and any answer
                // still on its way, before the page goes.
                setDecisionProfile(undefined);
                decisionProfileSeqRef.current++;
                window.location.href = '/admin';
            };

            const handleLogout = () => {
                // Flush any debounced profile PUT before dropping the token, so the
                // user's last change (e.g. "mark done") isn't discarded on sign-out.
                if (profileSyncRef.current) {
                    clearTimeout(profileSyncRef.current);
                    profileSyncRef.current = null;
                    if (authToken && hydratedProfileRef.current) {
                        fetch(`${API_URL}/api/profile`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                            body: JSON.stringify({ userProfile, powerIndex, streak, completedSimulations, ciSessions, savedMoments, savedNextMoves })
                        }).catch(() => {});
                    }
                }
                // Last chance to send with THIS token: the queue survives sign-out
                // in localStorage, but a stranded batch would only go on the next
                // sign-in, stamped then rather than now.
                Telemetry.track('logout', {});
                Telemetry.flush();
                Telemetry.newSession();
                navSeenRef.current = false; // the next sign-in's landing view is 'initial' again
                storage.remove(STORAGE_KEYS.APP_TOKEN);
                storage.remove(STORAGE_KEYS.APP_USER);
                authTokenRef.current = null;
                setAuthToken(null);
                setCurrentUser(null);
                setShowApp(false);
                // Reset per-user state AND clear the (global, non-user-scoped) localStorage
                // caches so the next user on this browser never inherits the prior user's data.
                hydratedProfileRef.current = false;
                [STORAGE_KEYS.USER_PROFILE, STORAGE_KEYS.POWER_INDEX, STORAGE_KEYS.STREAK,
                 STORAGE_KEYS.COMPLETED_SIMULATIONS, STORAGE_KEYS.CI_SESSIONS,
                 STORAGE_KEYS.SAVED_MOMENTS, STORAGE_KEYS.CEO_ANSWERS,
                 STORAGE_KEYS.CEO_WISDOM_CONVERSATIONS, STORAGE_KEYS.SAVED_NEXT_MOVES,
                 STORAGE_KEYS.QUOTE_SHOWN_DATE, STORAGE_KEYS.FEEDBACK_STATE,
                 STORAGE_KEYS.LAST_ACTIVE_DATE].forEach(k => storage.remove(k));
                setFeedbackReq(null);
                setUserProfileState(DATABASE.userProfile);
                setPowerIndexState(STARTING_INDEX);
                setStreakState(STARTING_STREAK);
                setCompletedSimulations([]);
                setCISessions([]);
                setSavedMoments([]);
                setSavedNextMoves([]);
                setMentorMsgs([]);
                setMentorStep(0);
                setMentorTyping(false);
                setMentorLogged(false);
                setDecisionProfile(undefined);
                decisionProfileSeqRef.current++;
                setProfileReady(false);
                setSimLaunch(null);
                setMentorThread(null);
                setMentorOpening(null);
                // Molly's session flags belong to the member who set them: the
                // next one on this browser must not see "Picked up where you left
                // off" with the last member's conversation title, or skip their
                // own interrupted conversation because the last one pressed New.
                setMentorRated({});
                setMentorFresh(false);
                setMentorResumedFrom(null);
                setMentorResume(null);
                setJournalFocus(null);
                setWorldSheetOpen(null);
                setAskConversations([]);
                setAskPendingIds([]);
                setCurrentView('home');
            };

            // A sign-in link for SOMEBODY ELSE, opened where a session is
            // stored (a shared phone, a family laptop): the link is the intent,
            // so the stored session is signed out and the sign-in screen takes
            // the link. Their own link, while already signed in, is simply used
            // up — there is nothing to sign in to.
            useEffect(() => {
                if (!SIGNIN_LINK || !authToken) return;
                // An admin viewing as somebody: never ended from here — that
                // has its own exit, with its own audit entry. The link is
                // simply set aside.
                if (currentUser && currentUser.impersonatedBy) { takeSigninLink(); return; }
                const mine = currentUser && String(currentUser.email || '').toLowerCase();
                if (SIGNIN_LINK.email && mine && SIGNIN_LINK.email !== mine) handleLogout();
                // Their OWN link is left where it is: if this stored session
                // turns out to have expired (24 hours is short), the sign-in
                // screen that replaces it takes the link and offers the code,
                // rather than an empty form.
            }, []);

            // ---- the notification channel (docs/NOTIFICATIONS.md) ----------
            //
            // A poll, not a socket. What is being asked is "is anything waiting
            // for me", by an app that is already open — and a request every 45
            // seconds answers that exactly as well as a Durable Object per
            // signed-in user, for none of the standing cost.
            //
            // It is also the PRESENCE HEARTBEAT, which is what decides whether
            // somebody is emailed about their answer or simply shown it. The
            // two questions are asked by the same app at the same moment, so
            // they are one request.
            const [notifications, setNotifications] = useState([]);
            const [notifUnseen, setNotifUnseen] = useState(0);

            // The member's sharing level (src/sharing.ts): undefined until it
            // is read, null when they have never chosen — which is what puts
            // PrivacyWelcome in front of them. Read-only while viewing as.
            const [shareLevel, setShareLevel] = useState(undefined);
            const [privacyLater, setPrivacyLater] = useState(false);
            useEffect(() => {
                if (!showApp || !authToken || !API_URL) { setShareLevel(undefined); return; }
                let cancelled = false;
                fetch(`${API_URL}/api/me/sharing`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => r.json())
                    .then(res => { if (!cancelled && res && res.success) setShareLevel(res.data.level ?? null); })
                    .catch(() => { /* unknown stays unknown: no prompt rather than a wrong one */ });
                return () => { cancelled = true; };
            }, [showApp, authToken]);
            const saveShareLevel = useCallback(async (level) => {
                if (!API_URL || !authToken) return false;
                try {
                    const res = await fetch(`${API_URL}/api/me/sharing`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({ level })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok || !data.success) return false;
                    setShareLevel(level);
                    Telemetry.track('ui_open', { what: 'share_level' }, { entityType: 'share_level', entityId: level });
                    return true;
                } catch (e) {
                    return false;
                }
            }, [authToken]);
            const notifHighWater = useRef(0);

            /**
             * Poll for anything waiting, and say so out loud the first time.
             *
             * The toast fires only for things that arrived WHILE THEY WERE
             * HERE — `notifHighWater` starts at whatever the first poll
             * returns, so opening the app to four old notifications does not
             * throw four toasts at somebody. The badge carries the backlog
             * instead, which is what a badge is for.
             */
            useEffect(() => {
                if (!showApp || !authToken || !API_URL) return;
                let stop = false;
                let timer = null;
                const tick = async () => {
                    try {
                        const res = await fetch(`${API_URL}/api/notifications`, {
                            headers: { Authorization: `Bearer ${authToken}` }
                        }).then(r => r.json());
                        if (stop || !res || !res.success) return;
                        const items = res.data.items || [];
                        setNotifications(items);
                        setNotifUnseen(res.data.unseen || 0);
                        const newest = items.length ? Math.max(...items.map(i => i.id)) : 0;
                        const fresh = items.filter(i => i.id > notifHighWater.current && !i.seen);
                        if (notifHighWater.current > 0 && fresh.length) {
                            showToast(fresh[0].title, 'success');
                        }
                        notifHighWater.current = Math.max(notifHighWater.current, newest);
                    } catch (e) {
                        // Offline, or a worker behind on 0022. The product is
                        // not built on this; it is an addition to it.
                    } finally {
                        if (!stop) timer = setTimeout(tick, 45000);
                    }
                };
                tick();
                return () => { stop = true; if (timer) clearTimeout(timer); };
            }, [showApp, authToken]);

            /** Opening the screen the notification points at is what quiets it. */
            const markNotificationsSeen = useCallback(async () => {
                if (!API_URL || !authToken || !notifUnseen) return;
                setNotifUnseen(0);
                try {
                    await fetch(`${API_URL}/api/notifications/seen`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({})
                    });
                } catch (e) {
                    // Includes the deliberate 403 while an admin is viewing as
                    // somebody (docs/IMPERSONATION.md): their badge is not the
                    // admin's to clear. The screen still works.
                }
            }, [authToken, notifUnseen]);

            // A 401 from any authenticated call means the token expired or the account
            // was revoked — drop to the login screen instead of a silently-broken shell.
            // The login screen renders the designed "Session ended" notice.
            const handleSessionExpired = () => {
                if (!storage.get(STORAGE_KEYS.APP_TOKEN)) return; // already logged out
                // An impersonation that ran out its thirty minutes is not a
                // member whose session ended — it is an ADMIN, and dropping them
                // on a member sign-in form is both confusing and an invitation
                // to type their own password into somebody else's app. Send them
                // back where they came from. `impersonatedBy` comes from the
                // last /api/me, so this survives the token already being dead.
                if (currentUser && currentUser.impersonatedBy) {
                    endImpersonation();
                    return;
                }
                setSessionExpired(true);
                handleLogout();
            };

            // Handle URL params for direct linking (e.g., ?view=simulator&id=1)
            const urlParams = new URLSearchParams(window.location.search);
            const urlView = urlParams.get('view');
            const urlId = urlParams.get('id');
            const initialView = ['home', 'simulator', 'moments', 'profile'].includes(urlView) ? urlView : 'home';

            // Load persisted data on initialization
            const [currentView, setCurrentView] = useState(initialView);
            const [userProfile, setUserProfileState] = useState(() => {
                const saved = storage.get(STORAGE_KEYS.USER_PROFILE);
                return saved || DATABASE.userProfile;
            });
            const [powerIndex, setPowerIndexState] = useState(() => {
                return storage.get(STORAGE_KEYS.POWER_INDEX, STARTING_INDEX);
            });
            // No floor. There used to be one at 18, which meant a user whose real
            // streak was 3 was shown 18 — and it wrote the invented number back
            // to localStorage, so it survived a reload as if it were earned.
            const [streak, setStreakState] = useState(() => {
                return storage.get(STORAGE_KEYS.STREAK, STARTING_STREAK);
            });
            // The server's answer to "when did they last complete a case"
            // (YYYY-MM-DD, migration 0017). Display only — the streak itself is
            // the server's to move.
            const [lastActiveDate, setLastActiveDate] = useState(null);
            const [completedSimulations, setCompletedSimulations] = useState(() => {
                return storage.get(STORAGE_KEYS.COMPLETED_SIMULATIONS, []);
            });
            const [savedMoments, setSavedMoments] = useState(() => {
                return storage.get(STORAGE_KEYS.SAVED_MOMENTS, []);
            });
            const [ciSessions, setCISessions] = useState(() => {
                return storage.get(STORAGE_KEYS.CI_SESSIONS, []);
            });
            // Redesign: "Saved next moves" — commitments captured from Molly / the
            // simulator / Ask a CEO. Shape: { id, text, source, date (ISO), done }.
            const [savedNextMoves, setSavedNextMoves] = useState(() => {
                return storage.get(STORAGE_KEYS.SAVED_NEXT_MOVES, []);
            });
            // Redesign: Molly's session lives in the app store so leaving and
            // returning resumes the thread instead of restarting it.
            const [mentorMsgs, setMentorMsgs] = useState([]);
            const [mentorStep, setMentorStep] = useState(0);
            // In-flight + session-logged flags live beside the transcript: leaving
            // the view unmounts CIMentorView, and a per-mount flag would drop the
            // concurrency guard and re-log the same session.
            const [mentorTyping, setMentorTyping] = useState(false);
            const [mentorLogged, setMentorLogged] = useState(false);
            // The thumbs belong to the transcript, not to the mount. They used to
            // be per-mount state, so stepping out to the library and back showed
            // "Helpful?" again on turns already rated — and rating twice sent a
            // second mentor_rated for the same turn.
            const [mentorRated, setMentorRated] = useState({});
            // "New session" is a decision. Without a record of it, walking out
            // of Molly and back in would find the conversation they had just
            // put down and walk them straight back into it.
            const [mentorFresh, setMentorFresh] = useState(false);
            // Which conversation was picked up, and how many of the turns on
            // screen came back with it. Beside the transcript, not inside the
            // view, because both facts have to outlive a trip to the library.
            const [mentorResumedFrom, setMentorResumedFrom] = useState(null);
            // A conversation picked out of Your conversations, handed to Molly to
            // carry on. Consumed once, like ciSeed.
            const [mentorResume, setMentorResume] = useState(null);
            // Your conversations is reachable from two places now; the back link
            // has to go where the reader came from.
            const [convosBackTo, setConvosBackTo] = useState('profile');
            // Ask-a-CEO conversations live here too, so a reply that lands after
            // the user navigates away is never lost or overwritten.
            const [askConversations, setAskConversations] = useState(() => {
                const saved = storage.get(STORAGE_KEYS.CEO_WISDOM_CONVERSATIONS, []);
                return Array.isArray(saved) ? saved : [];
            });
            const [askPendingIds, setAskPendingIds] = useState([]);

            const [toast, setToast] = useState(null);
            // The feedback prompt is app-level, not per-view: the surfaces that ask
            // for it (a finished case, a finished Molly session, a landed CEO answer)
            // all unmount or navigate away moments later.
            const [feedbackReq, setFeedbackReq] = useState(null);
            // Phase 1: the company issue handed from the Decision Simulator's final
            // page into the CI Mentor. Set on handoff, consumed + cleared on CI Mentor mount.
            const [ciSeed, setCiSeed] = useState(null);
            // Phase 1: the challenge carried from the CI Mentor into the "Ask a CEO" view.
            // A string (Molly's first user turn) or { text, attachment } (a case);
            // CEOWisdomView normalises on read.
            const [ceoSeed, setCeoSeed] = useState(null);

            // ---- decision practice (docs/DECISION_PRACTICE.md §7.0) ----------
            // Memory only — never localStorage, never the profile blob, no new
            // storage keys. The record is the server's (simulation_attempts);
            // everything else here is a hand-off between two views.
            //
            // undefined while loading, null when it could not be had (non-2xx,
            // offline), else GET /api/decision-profile's { profile, history,
            // today }. While it is either of the first two, nothing about today
            // is inferred from completedSimulations and "done" is never shown:
            // the blob can't tell a replay from a first attempt, or a local day.
            const [decisionProfile, setDecisionProfile] = useState(undefined);
            const decisionProfileSeqRef = useRef(0);  // only the newest request may land
            const decisionProfileAtRef = useRef(0);   // last request started or answered
            // hydratedProfileRef as state, so a view can wait on it.
            const [profileReady, setProfileReady] = useState(false);
            // A case to open: { id|null, source }, consumed once by SimulatorView.
            const [simLaunch, setSimLaunch] = useState(null);
            // Molly's thread as App state — { conversationId, scenarioId, choice } —
            // so a remounted CIMentorView carries on the same row with the case.
            const [mentorThread, setMentorThread] = useState(null);
            // An opening line handed to the page Molly (journal, Home), from
            // revisitOpening(attempt). Read once, by the mount it opens: the
            // effect below clears it after that mount's effects have run (a
            // child's run first), so the next visit to her page resumes the
            // thread instead of starting the same case over again.
            const [mentorOpening, setMentorOpening] = useState(null);
            useEffect(() => {
                if (mentorOpening && currentView === 'ci-mentor') setMentorOpening(null);
            }, [mentorOpening, currentView]);
            // The journal entry whose note should open when Profile renders.
            const [journalFocus, setJournalFocus] = useState(null);
            // The world sheet (§7.3): null when closed, else the place it was
            // opened from ('home' | 'simulator' | 'profile'), which context_set
            // reports.
            const [worldSheetOpen, setWorldSheetOpen] = useState(null);
            // Stable, because it is the sheet's onEscape: ModalPortal re-runs its
            // focus effect when that changes, and an App re-render (the
            // notification poll, a toast) would pull focus back to the first chip.
            const closeWorldSheet = useCallback(() => setWorldSheetOpen(null), []);
            // A Simulator run is open (not its landing): the content column
            // widens so the case can sit beside the decision (§7.4).
            const [simulatorRun, setSimulatorRun] = useState(false);
            // The open run's phase, for the pill: null when no case is open.
            const [practicePhase, setPracticePhase] = useState(null);
            // Lines Molly drafted from the rounds (docs/design/06), handed to the
            // Your prompt screen once and cleared there. Memory only.
            const [promptDraft, setPromptDraft] = useState(null);
            // The world as the pick, the why line and Ask a CEO read it: empty
            // until the member has saved it (see worldOf).
            const world = React.useMemo(() => worldOf(userProfile), [userProfile]);

            const refreshDecisionProfile = useCallback(async () => {
                // Checked BEFORE the seq moves: a stale caller that bumped it would
                // throw away the current member's own request and leave them loading.
                if (!API_URL || !authToken || authTokenRef.current !== authToken) return;
                const seq = ++decisionProfileSeqRef.current;
                decisionProfileAtRef.current = Date.now();
                let tz = '';
                try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { /* the worker falls back to UTC */ }
                let next = null;
                try {
                    const res = await fetch(`${API_URL}/api/decision-profile?tz=${encodeURIComponent(tz)}`, {
                        headers: { Authorization: `Bearer ${authToken}` }
                    });
                    const body = res.ok ? await res.json().catch(() => null) : null;
                    // The usual { success, data } envelope; a bare payload reads the same.
                    const data = body && body.success && body.data ? body.data : body;
                    if (data && typeof data === 'object' && data.profile && data.today) next = data;
                } catch (e) { /* offline: unavailable, not empty */ }
                // A newer request, a sign-out or a different member since this
                // one started: its answer is somebody else's or stale.
                if (seq !== decisionProfileSeqRef.current) return;
                if (authTokenRef.current !== authToken) return;
                decisionProfileAtRef.current = Date.now();
                setDecisionProfile(next);
            }, [authToken]);

            // launchCase(id|null, source): open the Simulator straight into a run
            // — no intro page. source: 'home' | 'picker' | 'journal' (the
            // Simulator's own are 'today' | 'deeplink' | 'next').
            const launchCase = useCallback((id, source) => {
                setSimLaunch({ id: id == null ? null : id, source });
                setCurrentView('simulator');
            }, []);

            // Home and Profile show the record; one older than a minute is fetched
            // again on the way in. The first fetch is the hydrate's.
            useEffect(() => {
                if (!hydratedProfileRef.current || (currentView !== 'home' && currentView !== 'profile')) return;
                if (Date.now() - decisionProfileAtRef.current > 60000) refreshDecisionProfile();
            }, [currentView]);
            // …and on the way back to the tab, by the same rule: a record fetched
            // before a night away still says yesterday's practice is done.
            const currentViewRef = useRef(currentView);
            currentViewRef.current = currentView;
            useEffect(() => {
                const onVisible = () => {
                    if (document.visibilityState !== 'visible' || !hydratedProfileRef.current) return;
                    if (currentViewRef.current !== 'home' && currentViewRef.current !== 'profile') return;
                    if (Date.now() - decisionProfileAtRef.current > 60000) refreshDecisionProfile();
                };
                document.addEventListener('visibilitychange', onVisible);
                return () => document.removeEventListener('visibilitychange', onVisible);
            }, [refreshDecisionProfile]);

            // <main> is the one scroll container and outlives every view, so a
            // view opened from deep in a long page (the exits under a worked
            // case) opened scrolled down with it. Each view starts at its top,
            // before its first paint; 'instant' overrides the stylesheet's
            // smooth scrolling, which would animate the jump.
            const mainRef = useRef(null);
            React.useLayoutEffect(() => {
                const m = mainRef.current;
                if (!m) return;
                if (typeof m.scrollTo === 'function') m.scrollTo({ top: 0, behavior: 'instant' });
                else m.scrollTop = 0;
            }, [currentView]);

            // The topbar sits sticky INSIDE <main>, so a jump to a section
            // heading (scrollIntoView, block 'start') landed the heading, and
            // its focus, underneath it. <main> pads its scrollport by the bar's
            // measured height (text zoom, a wrapped pill) — see scrollPaddingTop.
            const [topbarHeight, setTopbarHeight] = useState(0);
            const topbarStopRef = useRef(null);
            const topbarRef = useCallback((el) => {
                if (topbarStopRef.current) { topbarStopRef.current(); topbarStopRef.current = null; }
                if (!el) return;
                const measure = () => setTopbarHeight(el.offsetHeight || 0);
                measure();
                if (typeof ResizeObserver === 'undefined') {
                    window.addEventListener('resize', measure);
                    topbarStopRef.current = () => window.removeEventListener('resize', measure);
                    return;
                }
                const ro = new ResizeObserver(measure);
                ro.observe(el);
                topbarStopRef.current = () => ro.disconnect();
            }, []);

            // Debounced save functions
            const debouncedSaveProfile = useCallback(
                debounce((profile) => {
                    storage.set(STORAGE_KEYS.USER_PROFILE, profile);
                }, 500),
                []
            );

            // Persist userProfile changes
            const setUserProfile = useCallback((newProfile) => {
                setUserProfileState(newProfile);
                debouncedSaveProfile(newProfile);
            }, [debouncedSaveProfile]);

            // Persist powerIndex changes. Accepts a value OR a functional updater
            // (callers pass `prev => prev + 3`); resolve the function before
            // persisting so localStorage never stores `undefined` (which silently
            // reset Power Index/Streak to baseline on every reload).
            const setPowerIndex = useCallback((arg) => {
                setPowerIndexState(prev => {
                    const next = typeof arg === 'function' ? arg(prev) : arg;
                    storage.set(STORAGE_KEYS.POWER_INDEX, next);
                    return next;
                });
            }, []);

            // Persist streak changes (same value-or-functional-updater handling)
            const setStreak = useCallback((arg) => {
                setStreakState(prev => {
                    const next = typeof arg === 'function' ? arg(prev) : arg;
                    storage.set(STORAGE_KEYS.STREAK, next);
                    return next;
                });
            }, []);

            // Add completed simulation.
            //
            // A REPLAY updates the existing record rather than appending a
            // second one. The server already refuses to score the same case
            // twice (`counted: false, pointsEarned: 0`), but the client appended
            // regardless — so Profile's "Cases worked" counted one scenario
            // twice and Home's "▲ N this week" added points the server had
            // explicitly declined to award.
            const addCompletedSimulation = useCallback((simulation) => {
                const stamped = { ...simulation, date: new Date().toISOString() };
                const at = completedSimulations.findIndex(s => s && s.id === stamped.id);
                const updated = at === -1
                    ? [...completedSimulations, stamped]
                    : completedSimulations.map((s, i) => i === at
                        // Keep the FIRST attempt's points — they are what the
                        // server counted — and record the latest outcome.
                        ? { ...s, ...stamped, pointsEarned: s.pointsEarned, replayedAt: stamped.date }
                        : s);
                setCompletedSimulations(updated);
                storage.set(STORAGE_KEYS.COMPLETED_SIMULATIONS, updated);
            }, [completedSimulations]);

            // Add saved moment
            const addSavedMoment = useCallback((momentId) => {
                if (!savedMoments.includes(momentId)) {
                    const updated = [...savedMoments, momentId];
                    setSavedMoments(updated);
                    storage.set(STORAGE_KEYS.SAVED_MOMENTS, updated);
                }
            }, [savedMoments]);

            // Remove saved moment
            const removeSavedMoment = useCallback((momentId) => {
                const updated = savedMoments.filter(id => id !== momentId);
                setSavedMoments(updated);
                storage.set(STORAGE_KEYS.SAVED_MOMENTS, updated);
            }, [savedMoments]);

            // Add CI session
            const addCISession = useCallback((session) => {
                const updated = [...ciSessions, { ...session, date: new Date().toISOString() }];
                setCISessions(updated);
                storage.set(STORAGE_KEYS.CI_SESSIONS, updated);
            }, [ciSessions]);

            // Save a next move (dedupe on text; newest first)
            // Both emit an event (docs/PULSE.md): a saved move is intent, a move
            // ticked done is the strongest "it changed what I did" signal in the
            // product, and until now it lived only in the profile blob.
            const addSavedNextMove = useCallback((text, source) => {
                setSavedNextMoves(prev => {
                    if (prev.some(m => m.text === text)) return prev;
                    const updated = [{
                        id: Date.now() + '-' + Math.random().toString(36).slice(2, 7),
                        text, source, date: new Date().toISOString(), done: false
                    }, ...prev];
                    storage.set(STORAGE_KEYS.SAVED_NEXT_MOVES, updated);
                    Telemetry.track('next_move_saved', { source: source || null });
                    return updated;
                });
            }, []);

            // Saved moves could be ticked but never removed: a commitment you
            // decided against, or saved twice, stayed on the list forever with
            // "Done" as the only way to make it stop being open.
            const removeSavedNextMove = useCallback((id) => {
                setSavedNextMoves(prev => {
                    const updated = prev.filter(m => m.id !== id);
                    if (updated.length === prev.length) return prev;
                    storage.set(STORAGE_KEYS.SAVED_NEXT_MOVES, updated);
                    Telemetry.track('next_move_removed', {});
                    return updated;
                });
            }, []);

            const toggleNextMoveDone = useCallback((id) => {
                setSavedNextMoves(prev => {
                    const current = prev.find(m => m.id === id);
                    if (!current) return prev;
                    const updated = prev.map(m => m.id === id ? { ...m, done: !m.done } : m);
                    storage.set(STORAGE_KEYS.SAVED_NEXT_MOVES, updated);
                    Telemetry.track('next_move_done', { done: !current.done });
                    return updated;
                });
            }, []);

            // Toast notification
            const showToast = useCallback((message, type = 'info') => {
                setToast({ message, type });
            }, []);

            const hideToast = useCallback(() => {
                setToast(null);
            }, []);

            // Ask for feedback about `surface`, carrying WHERE the user was:
            //   context = { type, id, label, extra }
            // Cadence is enforced here (every third completion of that surface) so
            // no call site has to know the policy. `force` is for the user pressing
            // "Send feedback" themselves — that is never throttled or counted.
            const requestFeedback = useCallback((surface, context = {}, { force = false, counted = false } = {}) => {
                if (!FEEDBACK_SURFACES[surface]) return;
                if (!force) {
                    // The counter always advances (cadence is per surface, lifetime);
                    // the prompt itself is shown at most ONCE per session. Four cases
                    // in one sitting earned two prompts seven minutes apart, and both
                    // were dismissed — the second ask is the one that costs goodwill.
                    // `counted`: the caller already bumped the counter (the simulator
                    // counts at completion and asks later) — only the session rule.
                    const due = counted || bumpFeedbackCounter(surface);
                    if (!due) return;
                    if (feedbackShownThisSession()) return;
                    markFeedbackShownThisSession();
                }
                // One prompt at a time: a mentor session that ends while a case's
                // prompt is still open must not replace it mid-answer.
                setFeedbackReq(prev => prev || { surface, context, key: Date.now() });
            }, []);

            // Check and update streak on load — ONLY when not logged in, where
            // there is no server to own it. When signed in the server is the
            // single writer (migration 0017): it moves the streak on a real day
            // boundary, and the client only displays what it is told.
            useEffect(() => {
                if (authToken) return;
                const lastActive = storage.get(STORAGE_KEYS.LAST_ACTIVE_DATE);
                const today = new Date().toDateString();

                if (lastActive) {
                    const lastDate = new Date(lastActive);
                    const daysDiff = Math.floor((new Date() - lastDate) / (1000 * 60 * 60 * 24));

                    if (daysDiff > 1) {
                        // Streak broken
                        setStreak(1);
                    } else if (daysDiff === 1 && lastActive !== today) {
                        // New day, increment streak
                        setStreak(streak + 1);
                    }
                }

                storage.set(STORAGE_KEYS.LAST_ACTIVE_DATE, today);
            }, []);

            // Telemetry rides the token: nothing is sent without one, and a queue
            // built up before sign-in goes the moment it arrives. session_start is
            // once per tab (sessionStorage), so a reload does not double it.
            useEffect(() => {
                Telemetry.setToken(authToken);
                if (!authToken) return;
                const KEY = 'edify_events_session_started';
                let started = false;
                try { started = window.sessionStorage.getItem(KEY) === Telemetry.sessionId; } catch (e) { /* private mode */ }
                if (!started) {
                    try { window.sessionStorage.setItem(KEY, Telemetry.sessionId); } catch (e) { /* fine */ }
                    // Once per session, so every event in it can be split by
                    // device without carrying the user agent on each row.
                    const w = window.innerWidth || 0;
                    let tz = null;
                    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (e) { /* old engines */ }
                    Telemetry.track('session_start', {
                        device: w < 680 ? 'mobile' : w < 980 ? 'tablet' : 'desktop',
                        viewportW: w,
                        viewportH: window.innerHeight || 0,
                        touch: ('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0,
                        ua: String(navigator.userAgent || '').slice(0, 300),
                        tz
                    });
                }
            }, [authToken]);

            // Every view change is a nav event — BottomNav, Sidebar, a Home card, a
            // programmatic hand-off — because this effect watches the state, not
            // the buttons. `initial` marks the landing view of a session.
            useEffect(() => {
                if (!showApp) return;
                Telemetry.track('nav', { view: currentView, initial: !navSeenRef.current }, { entityType: 'view', entityId: currentView });
                navSeenRef.current = true;
            }, [currentView, showApp]);

            // A/B: an already-signed-in visitor can land on the wrong build — a
            // bookmark to /a/, or an admin moving them to B mid-pilot. The stored
            // user is checked first (no flash of the wrong variant), then confirmed
            // against the server, which is the authority on the assignment.
            useEffect(() => {
                if (!authToken) return;
                if (routeToVariant(currentUser && currentUser.variant)) return;
                if (!API_URL) return;
                let cancelled = false;
                fetch(`${API_URL}/api/me`, { headers: { Authorization: `Bearer ${authToken}` } })
                    .then(r => r.ok ? r.json() : null)
                    .then(res => {
                        if (cancelled || !res || !res.success || !res.data) return;
                        const me = res.data;
                        // Keep the cached user in step so the next cold start routes
                        // correctly even offline.
                        setCurrentUser(prev => {
                            const next = { ...(prev || {}), ...me };
                            storage.set(STORAGE_KEYS.APP_USER, next);
                            return next;
                        });
                        routeToVariant(me.variant);
                    })
                    .catch(() => { /* offline: the cached assignment stands */ });
                return () => { cancelled = true; };
            }, [authToken]);

            // Per-user profile: hydrate from the server on login.
            useEffect(() => {
                if (!authToken) return;
                let cancelled = false;
                // Clean slate FIRST so a prior user's cached state is never shown or synced,
                // even if the fetch is empty or fails.
                //
                // ONLY on a real sign-in, though. This effect also re-runs when
                // the user presses "Try again" on the could-not-reach-your-account
                // banner, and wiping here threw away everything they had done in
                // the meantime — a finished case, saved next moves, a whole mentor
                // conversation — before the retry had even resolved, and lost it
                // for good if the retry failed too. A retry re-fetches; it does
                // not start the session over.
                if (!isProfileRetryRef.current) {
                    hydratedProfileRef.current = false;
                    setUserProfileState(DATABASE.userProfile);
                    setPowerIndexState(STARTING_INDEX);
                    setStreakState(STARTING_STREAK);
                    setCompletedSimulations([]);
                    setCISessions([]);
                    setSavedMoments([]);
                    setSavedNextMoves([]);
                    setMentorMsgs([]);
                    setMentorStep(0);
                    setMentorTyping(false);
                    setMentorLogged(false);
                    setDecisionProfile(undefined);
                    decisionProfileSeqRef.current++;
                    setProfileReady(false);
                    setSimLaunch(null);
                    setMentorThread(null);
                    setMentorOpening(null);
                    setJournalFocus(null);
                    setWorldSheetOpen(null);
                    // As on sign-out: Molly's session flags are the last member's.
                    setMentorRated({});
                    setMentorFresh(false);
                    setMentorResumedFrom(null);
                    setMentorResume(null);
                }
                // Read BEFORE clearing: the apply branch below runs after an
                // await and needs to know whether this was a retry.
                const wasRetry = isProfileRetryRef.current;
                isProfileRetryRef.current = false;
                // Hydration decides whether ANYTHING saves this session: the sync
                // effect below refuses to write until it has succeeded, so that a
                // failed load cannot clobber the server with defaults. The cost of
                // that rule is that one blip used to disable saving silently for
                // the whole session — cases completed, next moves ticked, streak
                // earned, none of it reaching the account. So: retry, and if it
                // still will not load, SAY so rather than pretending.
                const attemptHydrate = async (attempt) => {
                    try {
                        const res = await fetch(`${API_URL}/api/profile`, {
                            headers: { Authorization: `Bearer ${authToken}` }
                        });
                        if (cancelled) return;
                        // Token expired or account revoked → force re-login instead of a broken shell.
                        if (res.status === 401) { handleSessionExpired(); return; }
                        const data = await res.json().catch(() => ({}));
                        // Only enable sync (and apply server values) on a real successful load —
                        // never after a network failure, so we can't clobber the server with defaults.
                        if (res.ok && data.success && data.data && typeof data.data === 'object') {
                            const p = data.data;
                            // A RETRY merges; a fresh sign-in replaces.
                            //
                            // Skipping the clean-slate wipe was only half the
                            // fix: this apply branch was unconditional, so a
                            // successful "Try again" overwrote the session's
                            // work with the pre-session server blob — the same
                            // loss the wipe used to cause, with its trigger
                            // moved from retry-start to retry-success. Sync has
                            // been disabled since the first failure, so the
                            // server copy is stale by exactly the work the
                            // banner said was not being saved.
                            const merging = wasRetry;
                            const union = (local, remote, keyOf) => {
                                if (!Array.isArray(remote)) return local;
                                if (!merging) return remote;
                                const seen = new Set((local || []).map(keyOf));
                                return [...(local || []), ...remote.filter(x => !seen.has(keyOf(x)))];
                            };
                            if (p.userProfile && typeof p.userProfile === 'object') {
                                // On a retry the local copy may carry edits made
                                // while saving was down; the server's cannot. Only
                                // edits, though: see mergeRetriedProfile.
                                setUserProfileState(prev => merging ? mergeRetriedProfile(prev, p.userProfile, DATABASE.userProfile) : p.userProfile);
                            }
                            if (typeof p.powerIndex === 'number') {
                                setPowerIndexState(prev => merging ? Math.max(prev, p.powerIndex) : p.powerIndex);
                            }
                            setCompletedSimulations(prev => union(prev, p.completedSimulations, x => `${x && x.id}|${x && x.date}`));
                            setCISessions(prev => union(prev, p.ciSessions, x => `${x && x.id}|${x && x.date}`));
                            setSavedMoments(prev => union(prev, p.savedMoments, x => String((x && (x.id ?? x.text)) ?? JSON.stringify(x))));
                            setSavedNextMoves(prev => union(prev, p.savedNextMoves, x => String((x && (x.text ?? x.id)) ?? JSON.stringify(x))));
                            // The server's streak, as-is. The client used to add a
                            // daily-login bump of its own on top — a second writer
                            // with its own date format, on a number the server was
                            // already (wrongly) incrementing per case. One owner:
                            // routes/simulations.ts, on a real day boundary.
                            const s = typeof p.streak === 'number' ? p.streak : STARTING_STREAK;
                            setStreakState(prev => merging ? Math.max(prev, s) : s);
                            setLastActiveDate(typeof p.lastActiveDate === 'string' ? p.lastActiveDate : null);
                            profileVersionRef.current = typeof p.updatedAt === 'string' ? p.updatedAt : null;
                            hydratedProfileRef.current = true;
                            setProfileReady(true);
                            setProfileLoadFailed(false);
                            refreshDecisionProfile();
                            return;
                        }
                        throw new Error(`HTTP ${res.status}`);
                    } catch (e) {
                        if (cancelled) return;
                        if (attempt < 2) {
                            // Two more goes, backing off — most of these are a
                            // dropped connection on a phone, not a dead server.
                            hydrateTimerRef.current = setTimeout(() => attemptHydrate(attempt + 1), attempt === 0 ? 1200 : 4000);
                            return;
                        }
                        // Out of tries: the session runs on local state only, and
                        // the user is told instead of discovering it later.
                        setProfileLoadFailed(true);
                        // Nor is the record coming: unavailable, not still loading.
                        setDecisionProfile(prev => prev === undefined ? null : prev);
                    }
                };
                attemptHydrate(0);
                return () => { cancelled = true; clearTimeout(hydrateTimerRef.current); };
            }, [authToken, authTokenTick]);

            // Ask-a-CEO conversations persist from the store, not the view, so a
            // reply that lands after the user navigates away is still written.
            useEffect(() => {
                storage.set(STORAGE_KEYS.CEO_WISDOM_CONVERSATIONS, askConversations);
            }, [askConversations]);

            // Debounced push of the profile blob whenever any per-user state changes
            // (only after hydration, so we never clobber the server with pre-load defaults).
            useEffect(() => {
                if (!authToken || !hydratedProfileRef.current) return;
                clearTimeout(profileSyncRef.current);
                profileSyncRef.current = setTimeout(() => {
                    fetch(`${API_URL}/api/profile`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                        body: JSON.stringify({ userProfile, powerIndex, streak, completedSimulations, ciSessions, savedMoments, savedNextMoves, ifUpdatedAt: profileVersionRef.current })
                    }).then(async res => {
                        // A silently-failing sync means the user's work stops saving
                        // with no signal — surface it (401 drops to the login screen,
                        // anything else warns once).
                        if (res.status === 401) { handleSessionExpired(); return; }
                        if (!res.ok && !syncWarnedRef.current) {
                            syncWarnedRef.current = true;
                            showToast('Your progress could not be saved to your account.', 'warning');
                            return;
                        }
                        if (!res.ok) return;
                        // Keep the version current, and if the server says this copy
                        // was behind — another of the member's own sessions had saved
                        // since — take back what it kept rather than carrying on with
                        // a copy we now know is wrong.
                        try {
                            const out = await res.json();
                            if (out && typeof out.updatedAt === 'string') profileVersionRef.current = out.updatedAt;
                            if (out && out.stale && out.data && out.data.userProfile) {
                                setUserProfile(prev => ({ ...prev, ...out.data.userProfile }));
                            }
                        } catch { /* the save landed; the version refreshes on the next one */ }
                    }).catch(() => {});
                }, 800);
                return () => clearTimeout(profileSyncRef.current);
            }, [authToken, userProfile, powerIndex, streak, completedSimulations, ciSessions, savedMoments, savedNextMoves]);

            // Is the CI Mentor open to this account (users.mentor_enabled)?
            //
            // ABSENT MEANS YES, and every layer agrees on that: a worker that
            // pre-dates migration 0009 sends no such key, and a user cached in
            // localStorage from before this build has none either. Reading
            // either as "hidden" would take a working feature away from every
            // existing user the moment this shipped. Only an explicit false
            // closes it, so the switch can never fail ON by accident.
            const mentorEnabled = !currentUser || currentUser.mentorEnabled !== false;

            // The stop introducing itself right now (TAB_INTROS), or null. Decided
            // from state, not from the nav buttons, so a Home card, a hand-off
            // and a deep link all count as opening the tab. Only once the profile
            // has loaded — before that the mark is simply not here yet, and a
            // returning member on a new phone would be welcomed twice. Never
            // while an admin is viewing as the member: it is the member's first
            // time, not theirs, and the write would not be kept.
            const [tabIntro, setTabIntro] = useState(null);
            useEffect(() => {
                const seen = (userProfile && userProfile.tabIntrosSeen) || {};
                const due = showApp && profileReady && !impersonatedBy && !!TAB_INTROS[currentView] && !seen[currentView]
                    && (currentView !== 'ci-mentor' || mentorEnabled);
                setTabIntro(due ? currentView : null);
            }, [currentView, showApp, profileReady, impersonatedBy, mentorEnabled, userProfile]);
            const closeTabIntro = useCallback(() => {
                if (!tabIntro) return;
                setTabIntro(null);
                const prior = (userProfile && userProfile.tabIntrosSeen) || {};
                setUserProfile({ ...(userProfile || {}), tabIntrosSeen: { ...prior, [tabIntro]: new Date().toISOString() } });
            }, [tabIntro, userProfile, setUserProfile]);

            const contextValue = {
                shareLevel,
                saveShareLevel,
                currentView,
                setCurrentView,
                userProfile,
                setUserProfile,
                powerIndex,
                setPowerIndex,
                streak,
                setStreak,
                completedSimulations,
                addCompletedSimulation,
                savedMoments,
                addSavedMoment,
                removeSavedMoment,
                ciSessions,
                addCISession,
                savedNextMoves,
                addSavedNextMove,
                toggleNextMoveDone,
                removeSavedNextMove,
                mentorMsgs,
                setMentorMsgs,
                mentorStep,
                setMentorStep,
                mentorTyping,
                setMentorTyping,
                mentorLogged,
                setMentorLogged,
                mentorRated,
                setMentorRated,
                mentorFresh,
                setMentorFresh,
                mentorResumedFrom,
                setMentorResumedFrom,
                mentorResume,
                setMentorResume,
                convosBackTo,
                setConvosBackTo,
                notifications, notifUnseen, markNotificationsSeen,
                askConversations,
                setAskConversations,
                askPendingIds,
                setAskPendingIds,
                profileLoadFailed,
                retryProfileLoad,
                ciSeed,
                setCiSeed,
                ceoSeed,
                setCeoSeed,
                // decision practice (§7.0)
                mentorEnabled,
                decisionProfile,
                refreshDecisionProfile,
                profileReady,
                tabIntro,
                world,
                launchCase,
                simLaunch,
                setSimLaunch,
                mentorThread,
                setMentorThread,
                mentorOpening,
                setMentorOpening,
                journalFocus,
                setJournalFocus,
                worldSheetOpen,
                setWorldSheetOpen,
                setSimulatorRun,
                practicePhase, setPracticePhase,
                promptDraft, setPromptDraft,
                authToken,
                currentUser,
                handleLogout,
                handleSessionExpired,
                database,
                showToast,
                requestFeedback,
                isDesktop,
                homeLoading,
                setFabSuppressed
            };

            // Render the current view
            const renderView = () => {
                switch (currentView) {
                    case 'home':
                        return <HomeView />;
                    case 'ci-mentor':
                        return mentorEnabled ? <CIMentorView opening={mentorOpening} /> : <MentorComingSoonView />;
                    case 'simulator':
                        return <SimulatorView />;
                    case 'moments':
                        return <MomentsView />;
                    case 'playbook':
                        return <MomentsView playbookOnly />;
                    case 'ceo-wisdom':
                        return <CEOWisdomView />;
                    case 'profile':
                        return <ProfileView />;
                    case 'moves':
                        return <SavedMovesView />;
                    case 'conversations':
                        return <ConversationsView />;
                    case 'archives':
                        return <ArchivesView />;
                    case 'policy':
                        return <PolicyView />;
                    case 'your-prompt':
                        return <YourPromptView />;
                    case 'who-you-are':
                        return <WhoYouAreView />;
                    case 'molly-learn':
                        return mentorEnabled ? <MollyLearnView /> : <MentorComingSoonView />;
                    case 'prompt-library':
                        return <PromptLibraryView />;
                    // A bookmark, a stale view in state, or the back button must
                    // not walk a member into a door that is shut for them.
                    case 'interviews':
                        return canSeeLibrary(currentUser) ? <InterviewsView /> : <HomeView />;
                    case 'playlists':
                        return canSeeLibrary(currentUser) ? <PlaylistsView /> : <HomeView />;
                    default:
                        return <HomeView />;
                }
            };

            // Show loading screen while fetching from API
            if (isLoadingData) {
                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        background: 'var(--cream)',
                        gap: 'var(--space-4)'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            border: '3px solid var(--gray-200)',
                            borderTopColor: 'var(--gold)',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                        <p style={{ color: 'var(--gray-600)', fontSize: 'var(--text-sm)' }}>
                            Loading content...
                        </p>
                        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    </div>
                );
            }

            // Show login screen if not logged in
            if (!showApp) {
                return <LoginView onLogin={handleLogin} sessionExpired={sessionExpired} />;
            }

            // The mentor's own chrome (no topbar — the chat owns the screen) is
            // for the CHAT. The coming-soon page is an ordinary page and keeps
            // the topbar, or a user on mobile would have no way back.
            const isMentor = currentView === 'ci-mentor' && mentorEnabled;
            // A run is one screen, with the case file beside the decision when
            // there is room: the column widens to 952px on desktop while a run
            // is open, and the run measures itself before splitting in two.
            const isSimulatorRun = currentView === 'simulator' && simulatorRun;
            const showTopbar = !isDesktop && !isMentor;
            const showBottomNav = !isDesktop;
            // (FAB also stays out of the dev test harness's way in ?test=true)
            const testMode = new URLSearchParams(window.location.search).get('test') === 'true';
            const showFab = !isDesktop && !fabSuppressed && !testMode && ['home', 'ceo-wisdom', 'profile'].includes(currentView);

            return (
                <AppContext.Provider value={contextValue}>
                    {/* Fixed, and the shell is padded by exactly its height, so
                        it can never be scrolled past or hidden behind the topbar
                        — the one thing a banner like this must not do. */}
                    {impersonatedBy && (
                        <div data-testid="impersonation-bar" role="status" style={{
                            position: 'fixed', top: 0, left: 0, right: 0, height: '44px', zIndex: 4000,
                            background: '#b3392f', color: '#fff', display: 'flex', alignItems: 'center',
                            gap: '12px', padding: '0 14px', fontSize: '13.5px', lineHeight: 1.3,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.18)'
                        }}>
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <strong>Viewing as {currentUser.displayName || currentUser.email}</strong>
                                <span style={{ opacity: 0.85 }}> · read-only · signed in as {impersonatedBy.email || 'an admin'}</span>
                            </span>
                            <button type="button" onClick={endImpersonation} style={{
                                flexShrink: 0, background: '#fff', color: '#b3392f', border: 'none',
                                borderRadius: '8px', padding: '7px 13px', fontSize: '13px',
                                fontWeight: 700, cursor: 'pointer', font: 'inherit'
                            }}>End impersonation</button>
                        </div>
                    )}
                    <div className="ed-shell-outer" style={impersonatedBy
                        ? { paddingTop: '44px', boxSizing: 'border-box' } : undefined}>
                        <div className="ed-shell">
                            {isDesktop && <Sidebar />}

                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%' }}>
                                {/* Toast Notification */}
                                {toast && (
                                    <Toast
                                        message={toast.message}
                                        type={toast.type}
                                        onClose={hideToast}
                                    />
                                )}

                                {/* Feedback prompt — portaled, so it sits above the
                                    nav/FAB wherever the journey that raised it ended. */}
                                {feedbackReq && (
                                    <FeedbackPrompt
                                        key={feedbackReq.key}
                                        request={feedbackReq}
                                        onClose={() => setFeedbackReq(null)}
                                    />
                                )}

                                {/* Privacy first (tester feedback, 30 Sep 2026):
                                    asked before anything else, once. Never while
                                    an admin is viewing as the member — the choice
                                    is theirs, and the write would be refused. */}
                                {shareLevel === null && !privacyLater && !impersonatedBy && (
                                    <PrivacyWelcome onLater={() => setPrivacyLater(true)} />
                                )}

                                {/* A stop's introduction, the first time it is
                                    opened (TAB_INTROS). After the privacy
                                    welcome, never over it. */}
                                {tabIntro && !(shareLevel === null && !privacyLater) && (
                                    <TabIntroModal tab={tabIntro} onClose={closeTabIntro} />
                                )}

                                {/* The world sheet (§7.3), opened from Home, the
                                    Simulator's landing or Profile. Never while an
                                    admin is viewing as the member. */}
                                {worldSheetOpen && !impersonatedBy && (
                                    <WorldSheet place={worldSheetOpen} onClose={closeWorldSheet} />
                                )}

                                {/* Saving is off — say so where it cannot be missed.
                                    The session still works; nothing in it will be
                                    kept, and that is not something to discover
                                    afterwards. */}
                                {profileLoadFailed && (
                                    <div data-testid="profile-offline" role="status"
                                        style={{ margin: '0 20px', padding: '11px 14px', background: 'var(--cream-dark)', border: '1px solid var(--gold)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: 'var(--text-body)' }}>
                                        <span style={{ flex: 1 }}>
                                            We could not reach your account, so nothing you do now will be saved.
                                        </span>
                                        <span
                                            onClick={retryProfileLoad}
                                            onKeyDown={btnKey(retryProfileLoad)}
                                            role="button" tabIndex={0}
                                            style={{ color: 'var(--gold-darker)', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            Try again
                                        </span>
                                    </div>
                                )}

                                {/* Main Content — the only scroll container. The CI Mentor
                                    manages its own internal thread scroll and pinned composer. */}
                                <main ref={mainRef} style={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflowY: isMentor ? 'hidden' : 'auto',
                                    WebkitOverflowScrolling: 'touch',
                                    // Added to each heading's own scroll-margin, so a
                                    // jump clears the sticky topbar (65px until measured).
                                    scrollPaddingTop: showTopbar ? `${topbarHeight || 65}px` : undefined
                                }}>
                                    {showTopbar && <Header barRef={topbarRef} />}
                                    {/* Bottom clearance lives on the inner wrapper: padding on the
                                        scroll container itself doesn't extend the scrollable area
                                        in Chromium's flex-column layout, leaving CTAs under the nav.
                                        The wrapper must be allowed to GROW past the viewport
                                        (flex-basis auto, no shrink) or the padding is overflowed
                                        past instead of scrolled to. The mentor is the exception:
                                        it locks to the viewport and scrolls its own thread. */}
                                    <div style={{
                                        width: '100%',
                                        maxWidth: isDesktop ? (isMentor ? '760px' : isSimulatorRun ? '952px' : '720px') : '430px',
                                        margin: '0 auto',
                                        padding: showBottomNav
                                            ? (showFab ? '0 20px 164px' : '0 20px 96px')
                                            : '0 20px',
                                        flex: isMentor ? '1 1 0%' : '1 0 auto',
                                        minHeight: isMentor ? 0 : undefined,
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        {renderView()}
                                    </div>
                                </main>

                                {/* Floating Molly entry (mobile; not on Simulator/Mentor) */}
                                {showFab && <MollyFab />}

                                {/* Bottom Navigation (mobile) */}
                                {showBottomNav && <BottomNav />}

                                {/* Test Runner - Only visible when ?test=true is in URL */}
                                {new URLSearchParams(window.location.search).get('test') === 'true' && (
                                    <TestRunner />
                                )}
                            </div>
                        </div>
                    </div>
                </AppContext.Provider>
            );
        }

        // Surface uncaught errors instead of failing silently (no telemetry today —
        // this is the single hook to wire Sentry / a Worker log endpoint to later).
        function reportError(error, info) {
            try { console.error('[edify] uncaught error:', error, info || ''); } catch (e) {}
        }
        window.addEventListener('error', (e) => reportError(e.error || e.message));
        window.addEventListener('unhandledrejection', (e) => reportError(e.reason));

        // Catch render-time errors so one malformed scenario/moment can't unmount
        // the whole tree to a blank page.
        class ErrorBoundary extends React.Component {
            constructor(props) { super(props); this.state = { error: null }; }
            static getDerivedStateFromError(error) { return { error }; }
            componentDidCatch(error, info) { reportError(error, info); }
            render() {
                if (this.state.error) {
                    return (
                        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', gap: '16px',
                            padding: '24px', textAlign: 'center', fontFamily: 'sans-serif', color: '#1a2744' }}>
                            <h2 style={{ margin: 0 }}>Something went wrong</h2>
                            <p style={{ margin: 0, color: '#667' }}>The app hit an unexpected error. Reloading usually fixes it.</p>
                            <button onClick={() => window.location.reload()}
                                style={{ padding: '10px 20px', borderRadius: '8px', border: 'none',
                                    background: '#1a2744', color: '#fff', cursor: 'pointer', fontSize: '15px' }}>
                                Reload
                            </button>
                        </div>
                    );
                }
                return this.props.children;
            }
        }

        // Render the App
        const root = ReactDOM.createRoot(document.getElementById('root'));
        root.render(<ErrorBoundary><App /></ErrorBoundary>);
